import _ from 'lodash';
import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
    Grid,
    Card,
    CardContent,
    Divider,
    Icon,
    Tooltip
} from "@mui/material";
import { Alert } from '@mui/material';
import { Button, Loading, Select, Input, Tabs, TabContainer, ToggleSwitch, Slider } from "@gpstrackit/react-controls";
import { useIntl } from "react-intl";
import CustomSelect from 'Components/Select';
import InputDate from 'Components/InputDate';
import moment from 'moment';
import { HasPermission } from 'Core/security';
import { isBiggerFirmware, getVidFleetConfigValues } from 'Core/data/Helpers';

import { NotificationManager } from "Components/Notifications";
import CustomSlider from "Components/Slider";

import { getDevices } from '../../redux/saga'
import ShowChangesHistory from 'Modules/vidfleet/.components/showChangesHistory';

const ConfigurationSecondStep = forwardRef((props, ref) => {

    const dispatch = useDispatch();
    const { messages } = useIntl();

    //const formRef = useRef(null);
    let defSettings = null;
    let defaults = null;
    let def = {
        settings: { // Option
            sounds: {
                power: "on", // "on" or "off" 
                eventTrigger: "on", // "on" or "off" 
                recording: "on", // "on" or "off"
                dms: { // Option,  only valid for secure 4K camera
                    NO_DRIVER: "off", // "on" or "off"
                    ASLEEP: "on",  // "on" or "off"
                    DROWSINESS: "on",  //  "on" or "off"
                    DAYDREAMING: "on",  //  "on" or "off"
                    YAWN: "on",  // "on" or "off"
                    USING_PHONE: "on",  // "on" or "off"
                    DISTRACTED: "on",  // "on" or "off"
                    SMOKING: "on",  // "on" or "off"
                    NO_SEATBELT: "on",  // "on" or "off"
                    ATTENTIVE: "off",  // "on" or "off"
                    IDLE_NO_DRIVER: "off",  // "on" or "off",
                    PASSENGERS_INSIDE: "off"  // "on" or "off",
                },
                adas: {
                    FORWARD_COLLISION_WARNING: "on",  // "on" or "off"
                    HEADWAY_MONITORING_WARNING: "on", // "on" or "off"
                    HEADWAY_MONITORING_EMERGENCY: "on", // "on" or "off"
                    LANE_DEPARTURE_WARNING: "on", // "on" or "off"
                    STOP_SIGN_VIOLATION: "off" // "on" or "off"
                }
            },
            eventThreshold: { //Option
                accValue: 280, //Int,
                brakeValue: -300, //Int,
                turnValue: 350, //Int,
                accDuration: 15, //Int,
                brakeDuration: 12, //Int,
                turnDuration: 15, //Int,
                steps: [200, 200, 200, 200, 200, 200]
            },
            eventLevel: { // Option  Map<category, level>
                HARD_ACCEL: "LOW", //"HIGH"  or "MEDIUM" or "LOW" or "OFF", default is LOW
                HARD_BRAKE: "LOW", //"HIGH"  or "MEDIUM" or "LOW" or "OFF",  default is LOW
                SHARP_TURN: "LOW", //"HIGH"  or "MEDIUM" or "LOW" or "OFF", default is LOW
                DRIVING_HIT: "HEAVY", // "HEAVY" or "OFF", default is HEAVY
                DMS: { // Option,  only valid for secure 4K camera
                    NO_DRIVER: "OFF", // "DEFAULT" or "OFF"
                    ASLEEP: "DEFAULT", // "DEFAULT" or "OFF"
                    DROWSINESS: "DEFAULT", // "DEFAULT" or "OFF"
                    DAYDREAMING: "DEFAULT", // "DEFAULT" or "OFF"
                    YAWN: "DEFAULT", // "DEFAULT" or "OFF"
                    USING_PHONE: "DEFAULT", // "DEFAULT" or "OFF"
                    DISTRACTED: "DEFAULT", // "DEFAULT" or "OFF"
                    SMOKING: "DEFAULT", // "DEFAULT" or "OFF"
                    NO_SEATBELT: "DEFAULT", // "DEFAULT" or "OFF"
                    IDLE_NO_DRIVER: "DEFAULT", // "DEFAULT" or "OFF"
                    PASSENGERS_INSIDE: "DEFAULT" // "DEFAULT" or "OFF"
                },
                ADAS: { //Option,  this setting will only take effect on camera that ADAS is enabled
                    FORWARD_COLLISION: "WARNING", // "WARNING" or "OFF"
                    HEADWAY_MONITORING: "EMERGENCY",  // "WARNING" or "EMERGENCY" or "OFF"
                    LANE_DEPARTURE: "WARNING",  // "WARNING" or "OFF",
                    STOP_SIGN_VIOLATION: "OFF" // "WARNING" or "OFF",
                },
                POWER_LOST: "DEFAULT",
                POTENTIAL_INCIDENT: "HIGH"
            },
            videoStreams: [ // Option
                // stream 0 and stream 1 must be set at the same time
                {
                    streamID: 0,
                    quality: "LOW", // for stream 0,  it must be one of "SUPER",  "HIGH", "NORMAL", "LOW", "SUPERLOW"
                    enable: true //enable must be true if streamID=0
                },
                {
                    streamID: 1,
                    quality: "NORMAL", // for stream 1,  it  must be one of "HIGH", "NORMAL", "LOW"
                    enable: true
                }
            ],
            delayedPowerOff: 600, // in seconds， 
            rotate: "upsidedown", // "normal" or "upsidedown" 
            nightVision: { //Option
                drivingNightVision: "auto", // "on" or "off" or "auto"
                drivingNightVisionTime: { //it is a must when "drivingNightVision" = "on"，and it must not be presented when "drivingNightVision" != "on"
                    from: 0, // minutes since 0 o'clock
                    to: 0  // minutes since 0 o'clock
                }
            },
            mic: "on", // "on" or "off" ,
            autoSnapshot: { // Option
                enable: true,
                interval: 600 // in second,  it is a must when "enable" = true，and it must not be presented when "enable" = false
            },
            trustAcc: true,
            liveStreamDuration: 120, // in second, must >0, it is the duration for once live streaming without keep or stop streaming command.
            recordConfig: { //Option, this is only for Secure4K camera (hardwareModel is "6B)
                config: "LOW", // must be the value from "recordConfigList" reported by camera
                codec: "DEFAULT", // "h464" or "DEFAULT"
            },
            markedClipDuration: { // require firmware later than 2.758.61.211.702
                accel: {
                    before: 5, // in second
                    after: 5 // in second
                },
                manual: {
                    before: 5, // in second
                    after: 5 // in second
                },
                dms: { // option,  only returned for Secure4K camera
                    before: 5, // in second
                    after: 5 // in second
                },
                adas: { // option,  only returned for camera that ADAS is enabled
                    before: 5, // in second
                    after: 5, // in second
                }
            },
            timezone: "+00:00", // Offsets from UTC in the format ±[hh]:[mm],  require firmware later than 2.758.61.211.702
            privacyMask: { // option,  only for Secure360, require firmware later than  2.765.64.271.746 ,
                enable: false // default is false, if true, will mask the driver side of the picture
            },
            obdII: { // option
                mode: "Auto", //"Auto", "VoltageOnly", "Classical", "J1939Only", "Passive" ,   case sensitive
                voltage: { // Option
                    on: 13600, // voltage in mV for detecting ignition on, voltage for ignition on, e.g. 13600
                    off: 11200, // voltage in mV for detecting ignition off,  e.g. 11200
                    check: 13300, //voltage in mV for detecting other, e.g. 13300
                }
            },
            dms: {
                level: "OFF",
                minSpeed: { //Option. minimum speed (km/h) for detection
                    NO_DRIVER: 40,
                    ASLEEP: 40,
                    DROWSINESS: 40,
                    DAYDREAMING: 40,
                    YAWN: 40,
                    USING_PHONE: 40,
                    DISTRACTED: 40,
                    SMOKING: 40,
                    NO_SEATBELT: 40,
                    ATTENTIVE: 40,
                    IDLE_NO_DRIVER: 0,
                },
                detectionDelay: { //Option.  
                    NO_DRIVER: 50,
                    ASLEEP: 5,
                    DROWSINESS: 50,
                    DAYDREAMING: 10,
                    YAWN: 5,
                    USING_PHONE: 50,
                    DISTRACTED: 30,
                    SMOKING: 10,
                    NO_SEATBELT: 200,
                    ATTENTIVE: 10,
                    IDLE_NO_DRIVER: 1800
                },
                repeatInterval: { //Option.  the for detection
                    NO_DRIVER: 3000,
                    ASLEEP: 100,
                    DROWSINESS: 1200,
                    DAYDREAMING: 100,
                    YAWN: 100,
                    USING_PHONE: 300,
                    DISTRACTED: 200,
                    SMOKING: 300,
                    NO_SEATBELT: 1200,
                    ATTENTIVE: 200,
                    IDLE_NO_DRIVER: 6000
                },
                detectionReset: {
                    NO_DRIVER: 0,
                    ASLEEP: 50,
                    DROWSINESS: 50,
                    DAYDREAMING: 50,
                    YAWN: 20,
                    USING_PHONE: 10,
                    DISTRACTED: 0,
                    SMOKING: 100,
                    NO_SEATBELT: 20,
                    ATTENTIVE: 0,
                    IDLE_NO_DRIVER: 10
                }
            },
            volume: {
                speaker: 10
            },
            runningInfo: { // Option
                enable: true,
                intervalInMin: 5,
            },
            advanced: { //Option
                sdCardEventSpace: "large",
                adas: {  //Option
                    level: "OFF",
                    timeRatio: {
                        FORWARD_COLLISION: 100, // form 20 to 200 
                        HEADWAY_MONITORING: 100, // from 20 to 200
                    },
                    cameraHeight: 1.19,
                    vehicleWidth: 1.919,
                    cameraPositionToCenter: 0,
                    minSpeed: {
                        FORWARD_COLLISION_WARNING: 40,
                        HEADWAY_MONITORING_WARNING: 40,
                        HEADWAY_MONITORING_EMERGENCY: 40,
                        LANE_DEPARTURE_WARNING: 40,
                        STOP_SIGN_VIOLATION: 10
                    },
                    repeatInterval: {
                        FORWARD_COLLISION_WARNING: 100,
                        HEADWAY_MONITORING_WARNING: 100,
                        HEADWAY_MONITORING_EMERGENCY: 100,
                        LANE_DEPARTURE_WARNING: 100,
                        STOP_SIGN_VIOLATION: 600
                    },
                    detectionDelay: {
                        FORWARD_COLLISION_WARNING: 5,
                        HEADWAY_MONITORING_WARNING: 100,
                        HEADWAY_MONITORING_EMERGENCY: 100,
                        LANE_DEPARTURE_WARNING: 100,
                        STOP_SIGN_VIOLATION: 10
                    },
                    detectionReset: {
                        FORWARD_COLLISION_WARNING: 100,
                        HEADWAY_MONITORING_WARNING: 100,
                        HEADWAY_MONITORING_EMERGENCY: 100,
                        LANE_DEPARTURE_WARNING: 100,
                        STOP_SIGN_VIOLATION: 10
                    }
                },
                ignition: {
                    virtualMode: true
                },
                lang: "English", // "English"  or "Spanish"
                aux: {
                    model: "NA"
                },
                parkingHeartbeat: {
                    enable: true,
                    intervalInMin: 60
                },
                privacyControl: {
                    level: "NONE"
                },
                subtitle: {
                    speed: "mph"
                },
                livePhoto: {
                    duration: 120,
                    interval: 5
                }
            },
            cev: { // Option, Cloud Event Validation
                enabledEvents: [] // "NO_SEATBELT" | "USING_PHONE" | "NO_DRIVER" | "PASSENGERS_NO_SEATBELT" | "SMOKING" | "POTENTIAL_INCIDENT"
            }
        }
    }

    if (props.settingsData) {
        defSettings = props.settingsData;
    } else {
        defSettings = JSON.parse(JSON.stringify(def));
    }

    if (props.defaults) {
        defaults = props.defaults;
    } else {
        defaults = {
            soundsDefault: true,
            eventLevelThresholdDefault: true,
            videoStreamDefault: true,
            videoResolutionDefault: true,
            delayedPowerOffDefault: true,
            liveStreamDurationDefault: true,
            autoSnapshotDefault: true,
            micDefault: true,
            privacyMaskDefault: true,
            rotateDefault: true,
            markedClipDurationDefault: true,
            minSpeedDefault: true,
            detectionDelayDefault: true,
            repeatIntervalDefault: true,
            detectionResetDefault: true,
            trustAccDefault: true,
            nightVisionDefault: true,
            volumeDefault: true,
            obdIIDefault: true,
            DMSEventsDefault: true,
            runningInfoDefault: true,
            advancedDefault: true,
            timezoneDefault: true,
            cevDefault: true
        }
    }

    const vidFleetConfigLimitDefault = getVidFleetConfigValues();

    //states reduxers
    const { loading, data, settingsToClone, loadingSettingsToClone } = useSelector(state => state.vidfleetConfigurationRedux);
    const openModalShowChangesHistory = useSelector(state => state.showChangesHistoryRedux.openModal);
    const user = useSelector(state => state.securityRedux.user);
    const lang = useSelector(state => state.commonRedux.language);

    //loacal states
    let [devices, setDevices] = useState(props.devices || []);
    let [bulk, setBulk] = useState(props.bulk || false);
    let [applyScopes, setApplyScopes] = useState(props.applyScopes);
    let [errorMessage, setErrorMessage] = useState(null);
    let [criterias, setCriterias] = useState([]);
    let [settingsData, setSettingsData] = useState(JSON.parse(JSON.stringify(defSettings)));
    let [settingsFromAPI, setSettingsFromAPI] = useState({});
    let [settingsDataChanged, setSettingsDataChanged] = useState(props.settingsDataChanged || { settings: {} });
    const [settingsFromAPIRaw, setSettingsFromAPIRaw] = useState(null);
    const [settingsFromFleetAPIRaw, setSettingsFromFleetAPIRaw] = useState(props.settingsFromFleetAPIRaw || null);
    const [settingsDefaultRaw, setSettingsDefaultRaw] = useState(null);
    let [saveSettingsAvailable, setSaveSettingsAvailable] = useState(false);
    let [obdIIOptions, setObdIIOptions] = useState([{ id: "Auto", name: "Auto" }, { id: "VoltageOnly", name: "Voltage Only" }, { id: "Classical", name: "Classical" }, { id: "J1939Only", name: "J1939 Only" }, { id: "Passive", name: "Passive" }]);
    let [vehicleClass, setVehicleClass] = useState(null);
    let [vehicleClassAdas, setVehicleClassAdas] = useState(null);
    let [timezoneByLabel, setTimezoneByLabel] = useState(false);
    let [vehicleClassOptions, setVehicleClassOptions] = useState([
        {
            id: 1,
            name: messages["vidfleet_configuration_heavy_duty"],
            eventThreshold: {
                accValue: 250,
                brakeValue: -335,
                turnValue: 295,
                accDuration: 16,
                brakeDuration: 16,
                turnDuration: 16,
                steps: [200, 200, 200, 200, 200, 200]
            },
            advanced: {
                adas: {
                    timeRatio: {
                        FORWARD_COLLISION: 80, // from 72 to 200
                        HEADWAY_MONITORING: 60, // from 20 to 200
                    },
                    cameraHeight: 2.5,
                    vehicleWidth: 2.5
                }
            }
        },
        {
            id: 2,
            name: messages["vidfleet_configuration_light_truck"],
            eventThreshold: {
                accValue: 280,
                brakeValue: -360,
                turnValue: 340,
                accDuration: 16,
                brakeDuration: 16,
                turnDuration: 16,
                steps: [200, 200, 200, 200, 200, 200]
            },
            advanced: {
                adas: {
                    timeRatio: {
                        FORWARD_COLLISION: 76, // from 72 to 200
                        HEADWAY_MONITORING: 55, // from 20 to 200
                    },
                    cameraHeight: 2,
                    vehicleWidth: 2
                }
            }
        },
        {
            id: 3,
            name: messages["vidfleet_configuration_sedan"],
            eventThreshold: {
                accValue: 300,
                brakeValue: -375,
                turnValue: 365,
                accDuration: 16,
                brakeDuration: 16,
                turnDuration: 16,
                steps: [200, 200, 200, 200, 200, 200]
            },
            advanced: {
                adas: {
                    timeRatio: {
                        FORWARD_COLLISION: 72, // from 72 to 200
                        HEADWAY_MONITORING: 50, // from 20 to 200
                    },
                    cameraHeight: 1.8,
                    vehicleWidth: 1.8
                }
            }
        }
    ]);
    //
    let [optionDefaults, setOptionDefaults] = useState({
        soundsDefault: defaults.soundsDefault,
        eventLevelThresholdDefault: defaults.eventLevelThresholdDefault,
        videoStreamDefault: defaults.videoStreamDefault,
        videoResolutionDefault: defaults.videoResolutionDefault,
        delayedPowerOffDefault: defaults.delayedPowerOffDefault,
        liveStreamDurationDefault: defaults.liveStreamDurationDefault,
        autoSnapshotDefault: defaults.autoSnapshotDefault,
        micDefault: defaults.micDefault,
        privacyMaskDefault: defaults.privacyMaskDefault,
        rotateDefault: defaults.rotateDefault,
        markedClipDurationDefault: defaults.markedClipDurationDefault,
        minSpeedDefault: defaults.minSpeedDefault,
        detectionDelayDefault: defaults.detectionDelayDefault,
        repeatIntervalDefault: defaults.repeatIntervalDefault,
        detectionResetDefault: defaults.detectionResetDefault,
        trustAccDefault: defaults.trustAccDefault,
        nightVisionDefault: defaults.nightVisionDefault,
        volumeDefault: defaults.volumeDefault,
        obdIIDefault: defaults.obdIIDefault,
        DMSEventsDefault: defaults.DMSEventsDefault,
        runningInfoDefault: defaults.runningInfoDefault,
        advancedDefault: defaults.advancedDefault,
        timezoneDefault: defaults.timezoneDefault,
        cevDefault: defaults.cevDefault
    });
    //
    let [optionCollapsed, setOptionCollapsed] = useState({
        collapsedSound: true,
        colapsedEventLevelThreshold: true,
        colapsedVideoStream: true,
        colapsedVideoResolution: true,
        colapsedDelayedPowerOff: true,
        colapsedLiveStreamDuration: true,
        colapsedAutoSnapshot: true,
        colapsedMic: true,
        colapsedPrivacyMask: true,
        colapsedRotate: true,
        colapsedMarkedClipDuration: true,
        colapsedMinSpeed: true,
        colapsedDetectionDelay: true,
        colapsedRepeatInterval: true,
        colapsedDetectionReset: true,
        colapsedTrustAcc: true,
        colapsedNightVision: true,
        colapsedVolume: true,
        colapsedObdII: true,
        colapsedDMSEvents: true,
        collapsedAll: true,
        colapsedRunningInfo: true,
        colapsedAdvanced: true,
        colapsedTimezone: true,
        colapsedCev: true
    });
    let [defaultSettingsData, setDefaultSettingsData] = useState(JSON.parse(JSON.stringify(def)));
    let [initialSettings, setInitialSettings] = useState(props.initialSettings ? JSON.parse(JSON.stringify(props.initialSettings)) : {});
    let [defaultManufacturer, setDefaultManufacturer] = useState(props.defaultManufacturer ? JSON.parse(JSON.stringify(props.defaultManufacturer)) : {});

    //settings options
    let [cloneSettings, setCloneSettings] = useState(props.cloneSettings || false);
    let [settToClone, setSettToClone] = useState(null);

    //device to clone settimgs
    let [deviceToClone, setDeviceToClone] = useState(props.deviceToClone);
    let [deviceInfo, setDeviceInfo] = useState({ recordConfigList: ["MAIN", "HIGH", "LOW", "FRONT_ONLY", "LOW_PRIV"] });
    let camerainfo = JSON.parse(devices[0]?.unitConfig?.cameraInfo || "{}")
    camerainfo = typeof camerainfo === "string" ? JSON.parse(camerainfo) : camerainfo;

    const [tab, setTab] = useState(0);
    const [tabAdas, setTabAdas] = useState(0);
    const [tabNav, setTabNav] = useState(0)
    const [optionLabels, setOptionLabels] = useState([]);
    let [restrictions, setRestrictions] = useState(props.restrictions);
    let [timezonesWaylens, setTimezonesWaylens] = useState(props.timezonesWaylens);

    const [modules, setModules] = useState([
        {
            key: "eventThreshold",
            label: "vidfleet_configuration_event_thresholds_levels",
            colapsedKey: "colapsedEventLevelThreshold",
            defaultKey: "eventLevelThresholdDefault"
        },
        {
            key: "sounds",
            label: "vidfleet_configuration_sounds",
            colapsedKey: "collapsedSound",
            defaultKey: "soundsDefault"
        },
        {
            key: "dms",
            label: "vidfleet_configuration_dms_events",
            colapsedKey: "colapsedDMSEvents",
            defaultKey: "DMSEventsDefault"
        },
        {
            key: "cev",
            label: "vidfleet_configuration_cev",
            colapsedKey: "colapsedCev",
            defaultKey: "cevDefault"
        },
        {
            key: "videoStreams",
            label: "vidfleet_configuration_video_streams",
            colapsedKey: "colapsedVideoStream",
            defaultKey: "videoStreamDefault"
        },
        {
            key: "recordConfig",
            label: "vidfleet_configuration_video_stream_4k",
            colapsedKey: "colapsedVideoResolution",
            defaultKey: "videoResolutionDefault"
        },
        {
            key: "delayedPowerOff",
            label: "vidfleet_configuration_delayed_power_off",
            colapsedKey: "colapsedDelayedPowerOff",
            defaultKey: "delayedPowerOffDefault"
        },
        {
            key: "nightVision",
            label: "vidfleet_configuration_night_vision",
            colapsedKey: "colapsedNightVision",
            defaultKey: "nightVisionDefault"
        },
        {
            key: "liveStreamDuration",
            label: "vidfleet_configuration_live_stream_duration",
            colapsedKey: "colapsedLiveStreamDuration",
            defaultKey: "liveStreamDurationDefault"
        },
        {
            key: "trustAcc",
            label: "vidfleet_configuration_trust_acc",
            colapsedKey: "colapsedTrustAcc",
            defaultKey: "trustAccDefault"
        }, {
            key: "autoSnapshot",
            label: "vidfleet_configuration_auto_snapshot",
            colapsedKey: "colapsedAutoSnapshot",
            defaultKey: "autoSnapshotDefault"
        },
        {
            key: "mic",
            label: "vidfleet_configuration_mic",
            colapsedKey: "colapsedMic",
            defaultKey: "micDefault"
        },
        {
            key: "privacyMask",
            label: "vidfleet_configuration_privacy_mask",
            colapsedKey: "colapsedPrivacyMask",
            defaultKey: "privacyMaskDefault"
        },
        {
            key: "rotate",
            label: "vidfleet_configuration_camera_orientation",
            colapsedKey: "colapsedRotate",
            defaultKey: "rotateDefault"
        },
        {
            key: "volume",
            label: "vidfleet_configuration_volume",
            colapsedKey: "colapsedVolume",
            defaultKey: "volumeDefault"
        },
        {
            key: "markedClipDuration",
            label: "vidfleet_configuration_marked_clip_duration",
            colapsedKey: "colapsedMarkedClipDuration",
            defaultKey: "markedClipDurationDefault"
        },
        {
            key: "obdII",
            label: "vidfleet_configuration_obdii",
            colapsedKey: "colapsedObdII",
            defaultKey: "obdIIDefault"
        },
        {
            key: "runningInfo",
            label: "vidfleet_configuration_running_info",
            colapsedKey: "colapsedRunningInfo",
            defaultKey: "runningInfoDefault"
        },
        {
            key: "advanced",
            label: "vidfleet_configuration_advanced",
            colapsedKey: "colapsedAdvanced",
            defaultKey: "advancedDefault"
        },
        {
            key: "timezone",
            label: "vidfleet_configuration_timezone",
            colapsedKey: "colapsedTimezone",
            defaultKey: "timezoneDefault"
        }
    ])

    // Sync cloneSettings state with parent prop when it changes
    useEffect(() => {
        if (props.cloneSettings !== undefined && props.cloneSettings !== cloneSettings) {
            setCloneSettings(props.cloneSettings);
        }
    }, [props.cloneSettings]);

    const saveChangedSettings = (settingsDataChanged) => {
        setSettingsDataChanged({ ...settingsDataChanged })
        if (props.setSettingsDataChanged)
            props.setSettingsDataChanged({ ...settingsDataChanged })
    }

    const onValidSubmit = () => {

        let data = buildCustomSettingsJsonBulk();

        let customSettings = data.customSettings;
        let defaults = {
            soundsDefault: optionDefaults.soundsDefault,
            eventLevelThresholdDefault: optionDefaults.eventLevelThresholdDefault,
            videoStreamDefault: optionDefaults.videoStreamDefault,
            videoResolutionDefault: optionDefaults.videoResolutionDefault,
            delayedPowerOffDefault: optionDefaults.delayedPowerOffDefault,
            liveStreamDurationDefault: optionDefaults.liveStreamDurationDefault,
            autoSnapshotDefault: optionDefaults.autoSnapshotDefault,
            micDefault: optionDefaults.micDefault,
            privacyMaskDefault: optionDefaults.privacyMaskDefault,
            rotateDefault: optionDefaults.rotateDefault,
            markedClipDurationDefault: optionDefaults.markedClipDurationDefault,
            minSpeedDefault: optionDefaults.minSpeedDefault,
            detectionDelayDefault: optionDefaults.detectionDelayDefault,
            repeatIntervalDefault: optionDefaults.repeatIntervalDefault,
            detectionResetDefault: optionDefaults.detectionResetDefault,
            trustAccDefault: optionDefaults.trustAccDefault,
            nightVisionDefault: optionDefaults.nightVisionDefault,
            volumeDefault: optionDefaults.volumeDefault,
            obdIIDefault: optionDefaults.obdIIDefault,
            DMSEventsDefault: optionDefaults.DMSEventsDefault,
            runningInfoDefault: optionDefaults.runningInfoDefault,
            advancedDefault: optionDefaults.advancedDefault,
            timezoneDefault: optionDefaults.timezoneDefault,
            cevDefault: optionDefaults.cevDefault
        }
        let currentSettings = JSON.parse(JSON.stringify(settingsData));
        let settingdToApplay = JSON.parse(JSON.stringify(settingsData));
        let initSettings = JSON.parse(JSON.stringify(initialSettings));
        let valid = true;
        let validSelected = !_.isEmpty(data.customSettings);

        if (settingdToApplay?.settings?.runningInfo?.enable === false)
            settingdToApplay.settings.runningInfo = { enable: false }

        if (!validSelected) {
            // the default config will be applied
            customSettings = JSON.parse(JSON.stringify(settingsFromFleetAPIRaw));
        }
        //Check if the combinations of the restrictions are allowed
        if (devices && devices[0]?.deviceModel?.name?.includes("360")) { } else {
            valid = checkRestrictions(customSettings)
        }

        // Ensure FORWARD_COLLISION timeRatio is at least 72 (API minimum)
        const settingsToSend = cloneSettings ? settingdToApplay.settings : customSettings;
        if (settingsToSend?.advanced?.adas?.timeRatio?.FORWARD_COLLISION !== undefined) {
            if (settingsToSend.advanced.adas.timeRatio.FORWARD_COLLISION < 72) {
                settingsToSend.advanced.adas.timeRatio.FORWARD_COLLISION = 72;
            }
        }

        if (valid) {
            if (props.dataChange) {
                props.dataChange(settingsToSend, data.optionLabels, cloneSettings, defaults, currentSettings, deviceToClone, initSettings, defaultManufacturer, settingsDataChanged, settingsFromFleetAPIRaw)
            }

            if (props.nextStep) {
                props.nextStep();
            }
        } else {
            NotificationManager.error(
                messages["vidfleet_configuration_title"],
                messages["vidfleet_configuration_combination_not_allowed"],
                3000,
                null,
                null,
                'error'
            );
        }
        // } else {
        //     NotificationManager.error(
        //         messages["vidfleet_configuration_title"],
        //         messages["vidfleet_configuration_empty"],
        //         3000,
        //         null,
        //         null,
        //         'error'
        //     );
        // }

    }

    useImperativeHandle(ref, () => ({
        updateSaveSettingsAvailable() {
            setSaveSettingsAvailable(false);
        },
        onSubmit() {
            onValidSubmit();
        }
    }))

    const getTimeFromMins = (mins) => {
        // do not include the first validation check if you want, for example,
        // getTimeFromMins(1530) to equal getTimeFromMins(90) (i.e. mins rollover)
        if (mins > 24 * 60 || mins < 0) {
            throw new RangeError("Valid input should be greater than or equal to 0 and less than 1440.");
        }
        var h = mins / 60 | 0,
            m = mins % 60 | 0;
        return moment().hours(h).minutes(m);
    }

    //review settings from API to enable custom settings 
    const enableCustomOptionsOnLoad = (settingsFromAPI, fleetSettingsFromAPI, defaultSettings) => {

        let mergedSettingsFleet = {};
        let mergedSettings = {};

        let fleet = fleetSettingsFromAPI ? false : true;

        if (devices.length === 0 && props.device) {
            devices.push(props.device)
            setDevices([...devices])
        }


        if (!defaultSettings?.settings?.rotate) {
            defaultSettings.settings.rotate = "upsidedown";
        }

        defaultManufacturer = JSON.parse(JSON.stringify(defaultSettings))
        setDefaultManufacturer(JSON.parse(JSON.stringify(defaultSettings)));
        if (settingsFromAPI) setSettingsFromAPIRaw(JSON.parse(JSON.stringify(settingsFromAPI)));
        if (fleetSettingsFromAPI) setSettingsFromFleetAPIRaw(JSON.parse(JSON.stringify(fleetSettingsFromAPI)));
        if (defaultSettings) setSettingsDefaultRaw(JSON.parse(JSON.stringify(defaultSettings)));


        // for config fleet
        if (fleet) {
            //merge the with the comming settings
            mergedSettings = mergeSettings(defaultSettings.settings, settingsFromAPI.settings);
            settingsData.settings = JSON.parse(JSON.stringify(mergedSettings));
            mergedSettingsFleet = JSON.parse(JSON.stringify(defaultSettings.settings));
            initialSettings = JSON.parse(JSON.stringify(settingsData))
            defaultSettings.settings = JSON.parse(JSON.stringify(defaultSettings.settings));

            let settingsChanged = replaceObjectvalues(JSON.parse(JSON.stringify(mergedSettings)))
            setSettingsDataChanged({ settings: settingsChanged })

        } else {
            //for device config
            //merge the with the comming settings
            mergedSettings = mergeSettings(defaultSettings.settings, settingsFromAPI.settings);
            settingsData.settings = JSON.parse(JSON.stringify(mergedSettings));

            mergedSettingsFleet = mergeSettings(defaultSettings.settings, fleetSettingsFromAPI?.settings);

            initialSettings = JSON.parse(JSON.stringify(settingsData));
            defaultSettings.settings = JSON.parse(JSON.stringify(mergedSettingsFleet));

            let settingsChanged = replaceObjectvalues(JSON.parse(JSON.stringify(mergedSettings)))
            setSettingsDataChanged({ settings: settingsChanged })


        }

        //sounds
        if (!_.isEqual(mergedSettings.sounds, mergedSettingsFleet.sounds)) {
            if (devices[0]?.deviceModel?.name !== "4K" || devices[0]?.deviceModel?.name !== "AIR2") { //
                if (mergedSettings.sounds.power && !_.isEqual(mergedSettings.sounds.power, mergedSettingsFleet.sounds.power)) {
                    optionDefaults.soundsDefault = false;
                } else if (mergedSettings.sounds.eventTrigger && !_.isEqual(mergedSettings.sounds.eventTrigger, mergedSettingsFleet.sounds.eventTrigger)) {
                    optionDefaults.soundsDefault = false;
                } else if (mergedSettings.sounds.recording && !_.isEqual(mergedSettings.sounds.recording, mergedSettingsFleet.sounds.recording)) {
                    optionDefaults.soundsDefault = false;
                } else if (mergedSettings.sounds.dms && !_.isEqual(mergedSettings.sounds.dms, mergedSettingsFleet.sounds.dms)) {
                    optionDefaults.soundsDefault = false;
                } else if (mergedSettings.sounds.adas && !_.isEqual(mergedSettings.sounds.adas, mergedSettingsFleet.sounds.adas)) {
                    optionDefaults.soundsDefault = false;
                } else if (mergedSettings.sounds.nav && !_.isEqual(mergedSettings.sounds.nav, mergedSettingsFleet.sounds.nav)) {
                    optionDefaults.soundsDefault = false;
                } else if (mergedSettings.sounds.acc && !_.isEqual(mergedSettings.sounds.acc, mergedSettingsFleet.sounds.acc)) {
                    optionDefaults.soundsDefault = false;
                } else
                    optionDefaults.soundsDefault = true;
            } else
                optionDefaults.soundsDefault = false;
        } else {
            optionDefaults.soundsDefault = true;
        }

        //event levels & Threshold
        if (!_.isEqual(mergedSettings.eventThreshold, mergedSettingsFleet.eventThreshold)) {
            optionDefaults.eventLevelThresholdDefault = false;
        } else if (!_.isEqual(mergedSettings.eventLevel, mergedSettingsFleet.eventLevel)) {
            optionDefaults.eventLevelThresholdDefault = false;
        } else {
            optionDefaults.eventLevelThresholdDefault = true;
        }

        //video streams
        if (!_.isEqual(mergedSettings.videoStreams, mergedSettingsFleet.videoStreams)) {
            optionDefaults.videoStreamDefault = false;
        } else {
            optionDefaults.videoStreamDefault = true;
        }

        // delayed power off
        if (!_.isEqual(mergedSettings.delayedPowerOff, mergedSettingsFleet.delayedPowerOff)) {
            optionDefaults.delayedPowerOffDefault = false;
        } else {
            optionDefaults.delayedPowerOffDefault = true;
        }

        // rotate
        if (!_.isEqual(mergedSettings.rotate, mergedSettingsFleet.rotate)) {
            optionDefaults.rotateDefault = false;
        } else {
            optionDefaults.rotateDefault = true;
        }

        //nigth vision
        if (!_.isEqual(mergedSettings.nightVision, mergedSettingsFleet.nightVision)) {
            optionDefaults.nightVisionDefault = false;
        } else {
            optionDefaults.nightVisionDefault = true;
        }

        //mic
        if (!_.isEqual(mergedSettings.mic, mergedSettingsFleet.mic)) {
            optionDefaults.micDefault = false;
        } else {
            optionDefaults.micDefault = true;
        }

        //autosnapshot
        if (!_.isEqual(mergedSettings.autoSnapshot, mergedSettingsFleet.autoSnapshot)) {
            optionDefaults.autoSnapshotDefault = false;
        } else {
            optionDefaults.autoSnapshotDefault = true;
        }

        //trustacc
        if (!_.isEqual(mergedSettings.trustAcc, mergedSettingsFleet.trustAcc)) {
            optionDefaults.trustAccDefault = false;
        } else {
            optionDefaults.trustAccDefault = true;
        }

        //live streaming duration
        if (!_.isEqual(mergedSettings.liveStreamDuration, mergedSettingsFleet.liveStreamDuration)) {
            optionDefaults.liveStreamDurationDefault = false;
        } else {
            optionDefaults.liveStreamDurationDefault = true;
        }

        //record config
        if (!_.isEqual(mergedSettings.recordConfig, mergedSettingsFleet.recordConfig)) {
            optionDefaults.videoResolutionDefault = false;
        } else {
            optionDefaults.videoResolutionDefault = true;
        }

        //marked clip duration
        if (!_.isEqual(mergedSettings.markedClipDuration, mergedSettingsFleet.markedClipDuration)) {
            if (devices[0]?.deviceModel?.name !== "4K" && devices[0]?.deviceModel?.name !== "AIR2") {
                if (!_.isEqual(mergedSettings.markedClipDuration.accel, mergedSettingsFleet.markedClipDuration.accel)) {
                    optionDefaults.markedClipDurationDefault = false;
                } else if (!_.isEqual(mergedSettings.markedClipDuration.manual, mergedSettingsFleet.markedClipDuration.manual)) {
                    optionDefaults.markedClipDurationDefault = false;
                } else
                    optionDefaults.markedClipDurationDefault = true;
            } else if (!_.isEqual(mergedSettings.markedClipDuration, mergedSettingsFleet.markedClipDuration))
                optionDefaults.markedClipDurationDefault = false;
            else
                optionDefaults.markedClipDurationDefault = true;
        } else {
            optionDefaults.markedClipDurationDefault = true;
        }

        //privacy mask
        if (!_.isEqual(mergedSettings.privacyMask, mergedSettingsFleet.privacyMask)) {
            optionDefaults.privacyMaskDefault = false;
        } else {
            optionDefaults.privacyMaskDefault = true;
        }

        //obdII
        if (!_.isEqual(mergedSettings.obdII, mergedSettingsFleet.obdII)) {
            optionDefaults.obdIIDefault = true;
        } else {
            optionDefaults.obdIIDefault = true;
        }

        //DMS
        if (!_.isEqual(mergedSettings.dms, mergedSettingsFleet.dms)) {
            optionDefaults.DMSEventsDefault = false;
        } else {
            optionDefaults.DMSEventsDefault = true;
        }

        //volume
        if (!_.isEqual(mergedSettings.volume, mergedSettingsFleet.volume)) {
            optionDefaults.volumeDefault = false;
        } else {
            optionDefaults.volumeDefault = true;
        }

        //runnig info
        if (!_.isEqual(mergedSettings.runningInfo, mergedSettingsFleet.runningInfo)) {
            optionDefaults.runningInfoDefault = false;
        } else {
            optionDefaults.runningInfoDefault = true;
        }

        //advanced
        const compareCommonFields = (obj1, obj2) => {
            function compareDeep(o1, o2) {
                const commonKeys = Object.keys(o1).filter(key => o2.hasOwnProperty(key));
                return commonKeys.every(key => {
                    if (_.isObject(o1[key]) && _.isObject(o2[key])) {
                        return compareDeep(o1[key], o2[key]);
                    }
                    return _.isEqual(o1[key], o2[key]);
                });
            }
            return compareDeep(obj1, obj2) && compareDeep(obj2, obj1);
        }

        if (!_.isEqual(mergedSettings.advanced, mergedSettingsFleet.advanced)) {
            if (devices[0]?.deviceModel?.name === "4K" || devices[0]?.deviceModel?.name === "AIR2") {
                if (!compareCommonFields(mergedSettings.advanced.sdCardEventSpace, mergedSettingsFleet.advanced.sdCardEventSpace)) {
                    optionDefaults.advancedDefault = false;
                } else if (!compareCommonFields(mergedSettings.advanced.ignition, mergedSettingsFleet.advanced.ignition)) {
                    optionDefaults.advancedDefault = false;
                } else if (!compareCommonFields(mergedSettings.advanced.subtitle, mergedSettingsFleet.advanced.subtitle)) {
                    optionDefaults.advancedDefault = false;
                } else if (!compareCommonFields(mergedSettings.advanced.lang, mergedSettingsFleet.advanced.lang)) {
                    optionDefaults.advancedDefault = false;
                } else if (!compareCommonFields(mergedSettings.advanced.parkingHeartbeat, mergedSettingsFleet.advanced.parkingHeartbeat)) {
                    optionDefaults.advancedDefault = false;
                } else if (!compareCommonFields(mergedSettings.advanced.aux, mergedSettingsFleet.advanced.aux)) {
                    optionDefaults.advancedDefault = false;
                } else if (!compareCommonFields(mergedSettings.advanced.adas, mergedSettingsFleet.advanced.adas)) {
                    optionDefaults.advancedDefault = false;
                } else if (!compareCommonFields(mergedSettings.advanced.safetyAssistance, mergedSettingsFleet.advanced.safetyAssistance)) {
                    optionDefaults.advancedDefault = false;
                } else if (!compareCommonFields(mergedSettings.advanced.privacyControl, mergedSettingsFleet.advanced.privacyControl)) {
                    optionDefaults.advancedDefault = false;
                } else if (!compareCommonFields(mergedSettings.advanced.livePhoto, mergedSettingsFleet.advanced.livePhoto)) {
                    optionDefaults.advancedDefault = false;
                } else if (!compareCommonFields(mergedSettings.advanced.driverID, mergedSettingsFleet.advanced.driverID)) {
                    optionDefaults.advancedDefault = false;
                } else {
                    optionDefaults.advancedDefault = true;
                }
            } else {
                optionDefaults.advancedDefault = true;
            }
        } else {
            optionDefaults.advancedDefault = true;
        }

        /* if (!_.isEqual(mergedSettings.advanced, mergedSettingsFleet.advanced)) {

            if (devices[0]?.deviceModel?.name !== "4K" || devices[0]?.deviceModel?.name !== "AIR2") {
                if (!_.isEqual(mergedSettings.advanced.sdCardEventSpace, mergedSettingsFleet.advanced.sdCardEventSpace)) {
                    optionDefaults.advancedDefault = false;
                } else if (!_.isEqual(mergedSettings.advanced.ignition, mergedSettingsFleet.advanced.ignition)) {
                    optionDefaults.advancedDefault = false;
                } else if (!_.isEqual(mergedSettings.advanced.subtitle, mergedSettingsFleet.advanced.subtitle)) {
                    optionDefaults.advancedDefault = false;
                } else if (!_.isEqual(mergedSettings.advanced.lang, mergedSettingsFleet.advanced.lang)) {
                    optionDefaults.advancedDefault = false;
                } else if (!_.isEqual(mergedSettings.advanced.parkingHeartbeat, mergedSettingsFleet.advanced.parkingHeartbeat)) {
                    optionDefaults.advancedDefault = false;
                } else if (!_.isEqual(mergedSettings.advanced.aux, mergedSettingsFleet.advanced.aux)) {
                    optionDefaults.advancedDefault = false;
                } else if (!_.isEqual(mergedSettings.advanced.adas, mergedSettingsFleet.advanced.adas)) {
                    optionDefaults.advancedDefault = false;
                } else if (!_.isEqual(mergedSettings.advanced.safetyAssistance, mergedSettingsFleet.advanced.safetyAssistance)) {
                    optionDefaults.advancedDefault = false;
                } else if (!_.isEqual(mergedSettings.advanced.privacyControl, mergedSettingsFleet.advanced.privacyControl)) {
                    optionDefaults.advancedDefault = false;
                } else if (!_.isEqual(mergedSettings.advanced.livePhoto, mergedSettingsFleet.advanced.livePhoto)) {
                    optionDefaults.advancedDefault = false;
                } else if (!_.isEqual(mergedSettings.advanced.driverID, mergedSettingsFleet.advanced.driverID)) {
                    optionDefaults.advancedDefault = false;
                } else
                    optionDefaults.advancedDefault = true;
            } else
                optionDefaults.advancedDefault = false;
        } else {
            optionDefaults.advancedDefault = true;
        } */


        //timezone
        if (!_.isEqual(mergedSettings.timezone, mergedSettingsFleet.timezone)) {
            optionDefaults.timezoneDefault = false;
        } else {
            optionDefaults.timezoneDefault = true;
        }
        
        //cev
        if (!_.isEqual(mergedSettings.cev, mergedSettingsFleet.cev)) {
            optionDefaults.cevDefault = false;
        } else {
            optionDefaults.cevDefault = true;
        }
        
        //offset or label
        let timezoneByLab = false;
        if (!mergedSettings?.timezone?.includes(":")) {
            timezoneByLab = true;
        }

        //Setting the vehicle class preset, in "Event & Thresholds"
        let vehicleClass = null;
        if (settingsData.settings.eventThreshold) {
            const currentEventThreshold = settingsData.settings.eventThreshold;
            vehicleClass = vehicleClassOptions.find(opt => {
                const eventThreshold = opt.eventThreshold;
                return eventThreshold &&
                    eventThreshold.accValue === currentEventThreshold.accValue &&
                    eventThreshold.brakeValue === currentEventThreshold.brakeValue &&
                    eventThreshold.turnValue === currentEventThreshold.turnValue &&
                    eventThreshold.accDuration === currentEventThreshold.accDuration &&
                    eventThreshold.brakeDuration === currentEventThreshold.brakeDuration &&
                    eventThreshold.turnDuration === currentEventThreshold.turnDuration
            });
        }

        //Setting the vehicle class present, in "Advance"
        let vehicleClassAdas = null;
        if (settingsData.settings.advanced.adas.timeRatio) {
            const currentAdvance = settingsData.settings.advanced;
            vehicleClassAdas = vehicleClassOptions.find(opt => {
                const advanced = opt.advanced;
                return advanced &&
                    advanced.adas.timeRatio.FORWARD_COLLISION === currentAdvance.adas.timeRatio.FORWARD_COLLISION &&
                    advanced.adas.timeRatio.HEADWAY_MONITORING === currentAdvance.adas.timeRatio.HEADWAY_MONITORING &&
                    advanced.adas.cameraHeight === currentAdvance.adas.cameraHeight &&
                    advanced.adas.vehicleWidth === currentAdvance.adas.vehicleWidth
            });
        }

        settingsData.settings = validateAttentiveConfig(settingsData.settings)

        setSettingsData({ ...settingsData });
        setVehicleClass({ ...vehicleClass });
        setVehicleClassAdas({ ...vehicleClassAdas });
        setInitialSettings({ ...initialSettings })
        setSettingsFromAPI({ ...settingsFromAPI });
        setDefaultManufacturer({ ...defaultManufacturer });
        setDefaultSettingsData({ ...defaultSettings })
        setOptionDefaults({ ...optionDefaults });
        setTimezoneByLabel(timezoneByLab)

    }

    //to build a config json only with the custom settings selected
    const buildCustomSettingsJsonBulk = () => {

        let customSettings = {}
        let optionLabels = []

        let settings = JSON.parse(JSON.stringify(settingsData.settings));
        let settingsChanged = JSON.parse(JSON.stringify(settingsDataChanged.settings));
        let settingsDefault = JSON.parse(JSON.stringify(initialSettings.settings));


        // soundsDefault: true,
        if (checkPropertyChange(settingsChanged.sounds)) {
            customSettings.sounds = {};

            if (settingsChanged.sounds.power) {
                customSettings.sounds.power = settings.sounds.power;
                optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ${messages["vidfleet_configuration_power"]}: ${settings.sounds.power}`);
            }

            // if (settingsChanged.sounds.eventTrigger) {
            //     customSettings.sounds.eventTrigger = settings.sounds.eventTrigger;
            //     optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ${messages["vidfleet_configuration_event_trigger"]}: ${settings.sounds.eventTrigger}`);
            // }

            if (settingsChanged.sounds.recording) {
                customSettings.sounds.recording = settings.sounds.recording;
                optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ${messages["vidfleet_configuration_recording"]}: ${settings.sounds.recording}`);
            }

            if (checkPropertyChange(settingsChanged.sounds.dms)) {
                customSettings.sounds.dms = {}

                if (settingsChanged.sounds.dms.NO_DRIVER) {
                    customSettings.sounds.dms.NO_DRIVER = settings.sounds.dms.NO_DRIVER;
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / NO_DRIVER: ${settings.sounds.dms.NO_DRIVER}`);
                }
                if (settingsChanged.sounds.dms.ASLEEP) {
                    customSettings.sounds.dms.ASLEEP = settings.sounds.dms.ASLEEP
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / ASLEEP: ${settings.sounds.dms.ASLEEP}`);
                }
                if (settingsChanged.sounds.dms.DROWSINESS) {
                    customSettings.sounds.dms.DROWSINESS = settings.sounds.dms.DROWSINESS
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / DROWSINESS: ${settings.sounds.dms.DROWSINESS}`);
                }
                if (settingsChanged.sounds.dms.DAYDREAMING) {
                    customSettings.sounds.dms.DAYDREAMING = settings.sounds.dms.DAYDREAMING
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / DAYDREAMING: ${settings.sounds.dms.DAYDREAMING}`);
                }
                if (settingsChanged.sounds.dms.YAWN) {
                    customSettings.sounds.dms.YAWN = settings.sounds.dms.YAWN
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / YAWN: ${settings.sounds.dms.YAWN}`);
                }
                if (settingsChanged.sounds.dms.USING_PHONE) {
                    customSettings.sounds.dms.USING_PHONE = settings.sounds.dms.USING_PHONE
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / USING_PHONE: ${settings.sounds.dms.USING_PHONE}`);
                }
                if (settingsChanged.sounds.dms.DISTRACTED) {
                    customSettings.sounds.dms.DISTRACTED = settings.sounds.dms.DISTRACTED
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / DISTRACTED: ${settings.sounds.dms.DISTRACTED}`);
                }
                if (settingsChanged.sounds.dms.SMOKING) {
                    customSettings.sounds.dms.SMOKING = settings.sounds.dms.SMOKING
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / SMOKING: ${settings.sounds.dms.SMOKING}`);
                }
                if (settingsChanged.sounds.dms.NO_SEATBELT) {
                    customSettings.sounds.dms.NO_SEATBELT = settings.sounds.dms.NO_SEATBELT
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / NO_SEATBELT: ${settings.sounds.dms.NO_SEATBELT}`);
                }
                if (settingsChanged.sounds.dms.ATTENTIVE) {
                    customSettings.sounds.dms.ATTENTIVE = settings.sounds.dms.ATTENTIVE
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / ATTENTIVE: ${settings.sounds.dms.ATTENTIVE}`);
                }
                if (settingsChanged.sounds.dms.IDLE_NO_DRIVER) {
                    customSettings.sounds.dms.IDLE_NO_DRIVER = settings.sounds.dms.IDLE_NO_DRIVER
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / IDLE_NO_DRIVER: ${settings.sounds.dms.IDLE_NO_DRIVER}`);
                }
                if (settingsChanged.sounds.dms.PASSENGERS_INSIDE) {
                    customSettings.sounds.dms.PASSENGERS_INSIDE = settings.sounds.dms.PASSENGERS_INSIDE
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / PASSENGERS_INSIDE: ${settings.sounds.dms.PASSENGERS_INSIDE}`);
                }
                if (settingsChanged.sounds.dms.PASSENGERS_NO_SEATBELT) {
                    customSettings.sounds.dms.PASSENGERS_NO_SEATBELT = settings.sounds.dms.PASSENGERS_NO_SEATBELT
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / DMS / PASSENGERS_NO_SEATBELT: ${settings.sounds.dms.PASSENGERS_NO_SEATBELT}`);
                }
            }

            if (checkPropertyChange(settingsChanged.sounds.adas)) {
                customSettings.sounds.adas = {}
                if (settingsChanged.sounds.adas.FORWARD_COLLISION_WARNING) {
                    customSettings.sounds.adas.FORWARD_COLLISION_WARNING = settings.sounds.adas.FORWARD_COLLISION_WARNING
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ADAS / FORWARD_COLLISION_WARNING: ${settings.sounds.adas.FORWARD_COLLISION_WARNING}`);
                }
                if (settingsChanged.sounds.adas.HEADWAY_MONITORING_WARNING) {
                    customSettings.sounds.adas.HEADWAY_MONITORING_WARNING = settings.sounds.adas.HEADWAY_MONITORING_WARNING
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ADAS / CLOSE_FOLLOWING: ${settings.sounds.adas.HEADWAY_MONITORING_WARNING}`);
                }
                if (settingsChanged.sounds.adas.HEADWAY_MONITORING_EMERGENCY) {
                    customSettings.sounds.adas.HEADWAY_MONITORING_EMERGENCY = settings.sounds.adas.HEADWAY_MONITORING_EMERGENCY
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ADAS / TAILGATING: ${settings.sounds.adas.HEADWAY_MONITORING_EMERGENCY}`);
                }
                if (settingsChanged.sounds.adas.LANE_DEPARTURE_WARNING) {
                    customSettings.sounds.adas.LANE_DEPARTURE_WARNING = settings.sounds.adas.LANE_DEPARTURE_WARNING
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ADAS / LANE_DEPARTURE_WARNING: ${settings.sounds.adas.LANE_DEPARTURE_WARNING}`);
                }
                if (settingsChanged.sounds.adas.STOP_SIGN_VIOLATION) {
                    customSettings.sounds.adas.STOP_SIGN_VIOLATION = settings.sounds.adas.STOP_SIGN_VIOLATION
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ADAS / STOP_SIGN_VIOLATION: ${settings.sounds.adas.STOP_SIGN_VIOLATION}`);
                }
                if (settingsChanged.sounds.adas.PEDESTRIAN_COLLISION) {
                    customSettings.sounds.adas.PEDESTRIAN_COLLISION = settings.sounds.adas.PEDESTRIAN_COLLISION
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ADAS / PEDESTRIAN_COLLISION: ${settings.sounds.adas.PEDESTRIAN_COLLISION}`);
                }
                if (settingsChanged.sounds.adas.CYCLIST_COLLISION) {
                    customSettings.sounds.adas.CYCLIST_COLLISION = settings.sounds.adas.CYCLIST_COLLISION
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ADAS / CYCLIST_COLLISION: ${settings.sounds.adas.CYCLIST_COLLISION}`);
                }
            }

            if (checkPropertyChange(settingsChanged.sounds.nav)) {
                customSettings.sounds.nav = {}
                if (settingsChanged?.sounds?.nav?.SPEEDING && settings?.sounds?.nav?.SPEEDING) {
                    customSettings.sounds.nav.SPEEDING = settings.sounds.nav.SPEEDING
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / NAV / SPEEDING: ${settings.sounds.nav.SPEEDING}`);
                }
                if (settingsChanged?.sounds?.nav?.OVER_MAX_SPEED && settings?.sounds?.nav?.OVER_MAX_SPEED) {
                    customSettings.sounds.nav.OVER_MAX_SPEED = settings.sounds.nav.OVER_MAX_SPEED
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / NAV / OVER_MAX_SPEED: ${settings.sounds.nav.OVER_MAX_SPEED}`);
                }
            }

            if (!settingsChanged.sounds.eventTrigger && checkPropertyChange(settingsChanged.sounds.acc)) {
                customSettings.sounds.acc = {}

                if (settingsChanged.sounds.acc.HARD_ACCEL) {
                    customSettings.sounds.acc.HARD_ACCEL = settings.sounds.acc.HARD_ACCEL
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ACC / HARD_ACCEL: ${settings.sounds.acc.HARD_ACCEL}`);
                }
                if (settingsChanged.sounds.acc.HARSH_ACCEL) {
                    customSettings.sounds.acc.HARSH_ACCEL = settings.sounds.acc.HARSH_ACCEL
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ACC / HARSH_ACCEL: ${settings.sounds.acc.HARSH_ACCEL}`);
                }
                if (settingsChanged.sounds.acc.SEVERE_ACCEL) {
                    customSettings.sounds.acc.SEVERE_ACCEL = settings.sounds.acc.SEVERE_ACCEL
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ACC / SEVERE_ACCEL: ${settings.sounds.acc.SEVERE_ACCEL}`);
                }

                if (settingsChanged.sounds.acc.HARD_BRAKE) {
                    customSettings.sounds.acc.HARD_BRAKE = settings.sounds.acc.HARD_BRAKE
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ACC / HARD_BRAKE: ${settings.sounds.acc.HARD_BRAKE}`);
                }
                if (settingsChanged.sounds.acc.HARSH_BRAKE) {
                    customSettings.sounds.acc.HARSH_BRAKE = settings.sounds.acc.HARSH_BRAKE
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ACC / HARSH_BRAKE: ${settings.sounds.acc.HARSH_BRAKE}`);
                }
                if (settingsChanged.sounds.acc.SEVERE_BRAKE) {
                    customSettings.sounds.acc.SEVERE_BRAKE = settings.sounds.acc.SEVERE_BRAKE
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ACC / SEVERE_BRAKE: ${settings.sounds.acc.SEVERE_BRAKE}`);
                }

                if (settingsChanged.sounds.acc.SHARP_TURN) {
                    customSettings.sounds.acc.SHARP_TURN = settings.sounds.acc.SHARP_TURN
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ACC / SHARP_TURN: ${settings.sounds.acc.SHARP_TURN}`);
                }
                if (settingsChanged.sounds.acc.HARSH_TURN) {
                    customSettings.sounds.acc.HARSH_TURN = settings.sounds.acc.HARSH_TURN
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ACC / HARSH_TURN: ${settings.sounds.acc.HARSH_TURN}`);
                }
                if (settingsChanged.sounds.acc.SEVERE_TURN) {
                    customSettings.sounds.acc.SEVERE_TURN = settings.sounds.acc.SEVERE_TURN
                    optionLabels.push(`${messages["vidfleet_configuration_sounds"]} / ACC / SEVERE_TURN: ${settings.sounds.acc.SEVERE_TURN}`);
                }
            }

        }
        // eventLevelThresholdDefault: true,
        if (checkPropertyChange(settingsChanged.eventLevel) || checkPropertyChange(settingsChanged.eventThreshold)) {

            //event level
            if (checkPropertyChange(settingsChanged.eventLevel)) {
                customSettings.eventLevel = {};
                if (settingsChanged.eventLevel.HARD_ACCEL) {
                    customSettings.eventLevel.HARD_ACCEL = settings.eventLevel.HARD_ACCEL;
                    optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_events"]} / HARD_ACCEL: ${settings.eventLevel.HARD_ACCEL}`);
                }
                if (settingsChanged.eventLevel.HARD_BRAKE) {
                    customSettings.eventLevel.HARD_BRAKE = settings.eventLevel.HARD_BRAKE;
                    optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_events"]} / HARD_BRAKE: ${settings.eventLevel.HARD_BRAKE}`);
                }
                if (settingsChanged.eventLevel.SHARP_TURN) {
                    customSettings.eventLevel.SHARP_TURN = settings.eventLevel.SHARP_TURN;
                    optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_events"]} / SHARP_TURN: ${settings.eventLevel.SHARP_TURN}`);
                }
                /* if (settingsChanged.eventLevel.DRIVING_HIT) {
                    customSettings.eventLevel.DRIVING_HIT = settings.eventLevel.DRIVING_HIT;
                    optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_events"]} / DRIVING_HIT: ${settings.eventLevel.DRIVING_HIT}`);
                } */

                if (settingsChanged.eventLevel.POWER_LOST) {
                    customSettings.eventLevel.POWER_LOST = settings.eventLevel.POWER_LOST;
                    optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_events"]} / POWER_LOST: ${settings.eventLevel.POWER_LOST}`);
                }

                if (settingsChanged.eventLevel.POTENTIAL_INCIDENT) {
                    customSettings.eventLevel.POTENTIAL_INCIDENT = settings.eventLevel.POTENTIAL_INCIDENT;
                    optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_events"]} / POTENTIAL_INCIDENT: ${settings.eventLevel.POTENTIAL_INCIDENT}`);
                }

                if (settingsChanged.eventLevel.PARKING_HIT) {
                    customSettings.eventLevel.PARKING_HIT = settings.eventLevel.PARKING_HIT;
                    optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_events"]} / PARKING_HIT: ${settings.eventLevel.PARKING_HIT}`);
                }

                if (checkPropertyChange(settingsChanged.eventLevel.DMS)) {
                    customSettings.eventLevel.DMS = {};
                    if (settingsChanged.eventLevel.DMS.NO_DRIVER) {
                        customSettings.eventLevel.DMS.NO_DRIVER = settings.eventLevel.DMS.NO_DRIVER;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / DMS / NO_DRIVER: ${settings.eventLevel.DMS.NO_DRIVER}`);
                    }
                    if (settingsChanged.eventLevel.DMS.ASLEEP) {
                        customSettings.eventLevel.DMS.ASLEEP = settings.eventLevel.DMS.ASLEEP;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / DMS / ASLEEP: ${settings.eventLevel.DMS.ASLEEP}`);
                    }
                    if (settingsChanged.eventLevel.DMS.DROWSINESS) {
                        customSettings.eventLevel.DMS.DROWSINESS = settings.eventLevel.DMS.DROWSINESS;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / DMS / DROWSINESS: ${settings.eventLevel.DMS.DROWSINESS}`);
                    }
                    if (settingsChanged.eventLevel.DMS.DAYDREAMING) {
                        customSettings.eventLevel.DMS.DAYDREAMING = settings.eventLevel.DMS.DAYDREAMING;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / DMS / DAYDREAMING: ${settings.eventLevel.DMS.DAYDREAMING}`);
                    }
                    if (settingsChanged.eventLevel.DMS.YAWN) {
                        customSettings.eventLevel.DMS.YAWN = settings.eventLevel.DMS.YAWN;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / DMS / YAWN: ${settings.eventLevel.DMS.YAWN}`);
                    }
                    if (settingsChanged.eventLevel.DMS.USING_PHONE) {
                        customSettings.eventLevel.DMS.USING_PHONE = settings.eventLevel.DMS.USING_PHONE;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / DMS / USING_PHONE: ${settings.eventLevel.DMS.USING_PHONE}`);
                    }
                    if (settingsChanged.eventLevel.DMS.DISTRACTED) {
                        customSettings.eventLevel.DMS.DISTRACTED = settings.eventLevel.DMS.DISTRACTED;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / DMS / DISTRACTED: ${settings.eventLevel.DMS.DISTRACTED}`);
                    }
                    if (settingsChanged.eventLevel.DMS.SMOKING) {
                        customSettings.eventLevel.DMS.SMOKING = settings.eventLevel.DMS.SMOKING;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / DMS / SMOKING: ${settings.eventLevel.DMS.SMOKING}`);
                    }
                    if (settingsChanged.eventLevel.DMS.NO_SEATBELT) {
                        customSettings.eventLevel.DMS.NO_SEATBELT = settings.eventLevel.DMS.NO_SEATBELT;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / DMS / NO_SEATBELT: ${settings.eventLevel.DMS.NO_SEATBELT}`);
                    }
                    if (settingsChanged.eventLevel.DMS.IDLE_NO_DRIVER) {
                        customSettings.eventLevel.DMS.IDLE_NO_DRIVER = settings.eventLevel.DMS.IDLE_NO_DRIVER;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / DMS / IDLE_NO_DRIVER: ${settings.eventLevel.DMS.IDLE_NO_DRIVER}`);
                    }
                    if (settingsChanged.eventLevel.DMS.PASSENGERS_INSIDE) {
                        customSettings.eventLevel.DMS.PASSENGERS_INSIDE = settings.eventLevel.DMS.PASSENGERS_INSIDE;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / DMS / PASSENGERS_INSIDE: ${settings.eventLevel.DMS.PASSENGERS_INSIDE}`);
                    }
                    if (settingsChanged.eventLevel.DMS.PASSENGERS_NO_SEATBELT) {
                        customSettings.eventLevel.DMS.PASSENGERS_NO_SEATBELT = settings.eventLevel.DMS.PASSENGERS_NO_SEATBELT;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / DMS / PASSENGERS_NO_SEATBELT: ${settings.eventLevel.DMS.PASSENGERS_NO_SEATBELT}`);
                    }

                }

                if (checkPropertyChange(settingsChanged.eventLevel.ADAS)) {
                    customSettings.eventLevel.ADAS = {};
                    if (settingsChanged.eventLevel.ADAS.FORWARD_COLLISION) {
                        customSettings.eventLevel.ADAS.FORWARD_COLLISION = settings.eventLevel.ADAS.FORWARD_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ADAS / FORWARD_COLLISION: ${settings.eventLevel.ADAS.FORWARD_COLLISION}`);
                    }
                    if (settingsChanged.eventLevel.ADAS.HEADWAY_MONITORING) {
                        customSettings.eventLevel.ADAS.HEADWAY_MONITORING = settings.eventLevel.ADAS.HEADWAY_MONITORING;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ADAS / HEADWAY_MONITORING: ${settings.eventLevel.ADAS.HEADWAY_MONITORING}`);
                    }
                    if (settingsChanged.eventLevel.ADAS.LANE_DEPARTURE) {
                        customSettings.eventLevel.ADAS.LANE_DEPARTURE = settings.eventLevel.ADAS.LANE_DEPARTURE;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ADAS / LANE_DEPARTURE: ${settings.eventLevel.ADAS.LANE_DEPARTURE}`);
                    }
                    if (settingsChanged.eventLevel.ADAS.STOP_SIGN_VIOLATION) {
                        customSettings.eventLevel.ADAS.STOP_SIGN_VIOLATION = settings.eventLevel.ADAS.STOP_SIGN_VIOLATION;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ADAS / STOP_SIGN_VIOLATION: ${settings.eventLevel.ADAS.STOP_SIGN_VIOLATION}`);
                    }
                    if (settingsChanged.eventLevel.ADAS.PEDESTRIAN_COLLISION) {
                        customSettings.eventLevel.ADAS.PEDESTRIAN_COLLISION = settings.eventLevel.ADAS.PEDESTRIAN_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ADAS / PEDESTRIAN_COLLISION: ${settings.eventLevel.ADAS.PEDESTRIAN_COLLISION}`);
                    }
                    if (settingsChanged.eventLevel.ADAS.CYCLIST_COLLISION) {
                        customSettings.eventLevel.ADAS.CYCLIST_COLLISION = settings.eventLevel.ADAS.CYCLIST_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ADAS / CYCLIST_COLLISION: ${settings.eventLevel.ADAS.CYCLIST_COLLISION}`);
                    }
                }

                if (checkPropertyChange(settingsChanged.eventLevel.NAV)) {
                    customSettings.eventLevel.NAV = {};
                    if (settingsChanged?.eventLevel?.NAV?.SPEEDING && settings?.eventLevel?.NAV?.SPEEDING) {
                        customSettings.eventLevel.NAV.SPEEDING = settings.eventLevel.NAV.SPEEDING;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / NAV / SPEEDING: ${settings.eventLevel.NAV.SPEEDING}`);
                    }
                    if (settingsChanged?.eventLevel?.NAV?.OVER_MAX_SPEED && settings?.eventLevel?.NAV?.OVER_MAX_SPEED) {
                        customSettings.eventLevel.NAV.OVER_MAX_SPEED = settings.eventLevel.NAV.OVER_MAX_SPEED;
                        optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / NAV / OVER_MAX_SPEED: ${settings.eventLevel.NAV.OVER_MAX_SPEED}`);
                    }
                }

            }

            //NOTE all parameters need to be send
            //event Threshold 
            if (checkPropertyChange(settingsChanged.eventThreshold)) {
                customSettings.eventThreshold = {};

                //if (!_.isEqual(settings.eventThreshold.accValue, settingsDefault.eventThreshold.accValue)) {
                customSettings.eventThreshold.accValue = settings.eventThreshold.accValue;
                optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_thresholds"]} / ${messages["vidfleet_configuration_acc_value"]}: ${settings.eventThreshold.accValue}`);
                //}
                //if (!_.isEqual(settings.eventThreshold.brakeValue, settingsDefault.eventThreshold.brakeValue)) {
                customSettings.eventThreshold.brakeValue = settings.eventThreshold.brakeValue;
                optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_thresholds"]} / ${messages["vidfleet_configuration_brake_value"]}: ${settings.eventThreshold.brakeValue}`);
                //}
                //if (!_.isEqual(settings.eventThreshold.turnValue, settingsDefault.eventThreshold.turnValue)) {
                customSettings.eventThreshold.turnValue = settings.eventThreshold.turnValue;
                optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_thresholds"]} / ${messages["vidfleet_configuration_turn_value"]}: ${settings.eventThreshold.turnValue}`);
                //}
                //if (!_.isEqual(settings.eventThreshold.accDuration, settingsDefault.eventThreshold.accDuration)) {
                customSettings.eventThreshold.accDuration = settings.eventThreshold.accDuration;
                optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_thresholds"]} / ${messages["vidfleet_configuration_acc_duration"]}: ${settings.eventThreshold.accDuration}`);
                //}
                //if (!_.isEqual(settings.eventThreshold.brakeDuration, settingsDefault.eventThreshold.brakeDuration)) {
                customSettings.eventThreshold.brakeDuration = settings.eventThreshold.brakeDuration;
                optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_thresholds"]} / ${messages["vidfleet_configuration_brake_duration"]}: ${settings.eventThreshold.brakeDuration}`);
                //}
                //if (!_.isEqual(settings.eventThreshold.turnDuration, settingsDefault.eventThreshold.turnDuration)) {
                customSettings.eventThreshold.turnDuration = settings.eventThreshold.turnDuration;
                optionLabels.push(`${messages["vidfleet_configuration_event_thresholds_levels"]} / ${messages["vidfleet_configuration_thresholds"]} / ${messages["vidfleet_configuration_turn_duration"]}: ${settings.eventThreshold.turnDuration}`);
                //}
                //if (!_.isEqual(settings.eventThreshold.steps, settingsDefault.eventThreshold.steps)) {
                customSettings.eventThreshold.steps = settings.eventThreshold.steps;
                // }
            }

        }
        // videoStreamDefault: true,
        if (checkPropertyChange(settingsChanged.videoStreams)) {
            customSettings.videoStreams = settings.videoStreams;
            if (settingsChanged.videoStreams[0].quality) {
                optionLabels.push(`${messages["vidfleet_configuration_video_streams"]} / ${messages["vidfleet_configuration_stream0"]} / ${messages["vidfleet_configuration_quality"]}: ${settings.videoStreams[0].quality}`);
            }
            if (settingsChanged.videoStreams[0].enable) {
                optionLabels.push(`${messages["vidfleet_configuration_video_streams"]} / ${messages["vidfleet_configuration_stream0"]} / ${messages["common_enabled"]}: ${settings.videoStreams[0].enable}`);
            }

            if (settingsChanged.videoStreams[1].quality) {
                optionLabels.push(`${messages["vidfleet_configuration_video_streams"]} / ${messages["vidfleet_configuration_stream1"]} / ${messages["vidfleet_configuration_quality"]}: ${settings.videoStreams[1].quality}`);
            }
            if (settingsChanged.videoStreams[1].enable) {
                optionLabels.push(`${messages["vidfleet_configuration_video_streams"]} / ${messages["vidfleet_configuration_stream1"]} / ${messages["common_enabled"]}: ${settings.videoStreams[1].enable}`);
            }
        }
        // videoResolutionDefault: true,
        if (checkPropertyChange(settingsChanged.recordConfig)) {
            customSettings.recordConfig = settings.recordConfig;
            optionLabels.push(`${messages["vidfleet_configuration_video_resolution"]}: ${settings.recordConfig.config}`);
        }
        // delayedPowerOffDefault: true,
        if (settingsChanged.delayedPowerOff) {
            customSettings.delayedPowerOff = settings.delayedPowerOff;
            optionLabels.push(`${messages["vidfleet_configuration_delayed_power_off"]}: ${settings.delayedPowerOff}`);
        }
        // liveStreamDurationDefault: true,
        if (settingsChanged.liveStreamDuration) {
            customSettings.liveStreamDuration = settings.liveStreamDuration;
            optionLabels.push(`${messages["vidfleet_configuration_live_stream_duration"]}: ${settings.liveStreamDuration}`);
        }
        // autoSnapshotDefault: true,
        if (checkPropertyChange(settingsChanged.autoSnapshot)) {
            if (settings.autoSnapshot.enable) {
                customSettings.autoSnapshot = settings.autoSnapshot;
                optionLabels.push(`${messages["vidfleet_configuration_auto_snapshot"]} / ${messages["common_enabled"]}: ${settings.autoSnapshot.enable}`);
                optionLabels.push(`${messages["vidfleet_configuration_auto_snapshot"]} / ${messages["vidfleet_configuration_interval"]}: ${settings.autoSnapshot.interval}`);
            } else {
                customSettings.autoSnapshot = {};
                customSettings.autoSnapshot.enable = false;
                optionLabels.push(`${messages["vidfleet_configuration_auto_snapshot"]} / ${messages["common_enabled"]}: ${settings.autoSnapshot.enable}`);
            }
        }
        // runningInfoDefault: true,
        if (checkPropertyChange(settingsChanged.runningInfo)) {
            if (settings.runningInfo && settings.runningInfo.enable) {
                customSettings.runningInfo = settings.runningInfo;
                optionLabels.push(`${messages["vidfleet_configuration_running_info"]} / ${messages["common_enabled"]}: ${settings.runningInfo.enable}`);
                optionLabels.push(`${messages["vidfleet_configuration_running_info"]} / ${messages["vidfleet_configuration_interval_in_min"]}: ${settings.runningInfo.intervalInMin}`);
            } else {
                customSettings.runningInfo = {};
                customSettings.runningInfo.enable = false;
                optionLabels.push(`${messages["vidfleet_configuration_running_info"]} / ${messages["common_enabled"]}: ${settings.runningInfo.enable}`);
            }
        }
        // micDefault: true,
        if (settingsChanged.mic) {
            customSettings.mic = settings.mic;
            optionLabels.push(`${messages["vidfleet_configuration_mic"]}: ${settings.mic}`);
        }
        // privacyMaskDefault: true,
        if (checkPropertyChange(settingsChanged.privacyMask)) {
            customSettings.privacyMask = settings.privacyMask;
            optionLabels.push(`${messages["vidfleet_configuration_privacy_mask"]} / ${messages["common_enabled"]}: ${settings.privacyMask.enable}`);
        }
        // rotateDefault: true,
        if (settingsChanged.rotate) {
            customSettings.rotate = settings.rotate;
            optionLabels.push(`${messages["vidfleet_configuration_rotate"]}: ${settings.rotate}`);
        }
        // cevDefault: true,
        if (checkPropertyChange(settingsChanged.cev)) {
            customSettings.cev = settings.cev;
            optionLabels.push(`${messages["vidfleet_configuration_cev"]}: ${settings.cev.enabledEvents.length ? settings.cev.enabledEvents.join(", ") : messages["vidfleet_configuration_disabled"]}`);
        }
        // markedClipDurationDefault: true,
        if (checkPropertyChange(settingsChanged.markedClipDuration)) {

            customSettings.markedClipDuration = {}

            if (checkPropertyChange(settingsChanged.markedClipDuration.accel)) {
                customSettings.markedClipDuration.accel = settings.markedClipDuration.accel;

                //accel
                if (settingsChanged.markedClipDuration.accel.after || settingsChanged.markedClipDuration.accel.before) {
                    optionLabels.push(`${messages["vidfleet_configuration_marked_clip_duration"]} / ${messages["vidfleet_configuration_acceleration"]} / ${messages["vidfleet_configuration_after"]}: ${settings.markedClipDuration.accel.after}`);
                    optionLabels.push(`${messages["vidfleet_configuration_marked_clip_duration"]} / ${messages["vidfleet_configuration_acceleration"]} / ${messages["vidfleet_configuration_before"]}: ${settings.markedClipDuration.accel.before}`);
                }
            }

            if (checkPropertyChange(settingsChanged.markedClipDuration.manual)) {
                customSettings.markedClipDuration.manual = settings.markedClipDuration.manual;
                //manual
                if (settingsChanged.markedClipDuration.manual.after || settingsChanged.markedClipDuration.manual.before) {
                    optionLabels.push(`${messages["vidfleet_configuration_marked_clip_duration"]} / ${messages["vidfleet_configuration_manual"]} / ${messages["vidfleet_configuration_after"]}: ${settings.markedClipDuration.manual.after}`);
                    optionLabels.push(`${messages["vidfleet_configuration_marked_clip_duration"]} / ${messages["vidfleet_configuration_manual"]} / ${messages["vidfleet_configuration_before"]}: ${settings.markedClipDuration.manual.before}`);
                }
            }

            if (checkPropertyChange(settingsChanged.markedClipDuration.dms)) {
                customSettings.markedClipDuration.dms = settings.markedClipDuration.dms;

                //dms
                if (settingsChanged.markedClipDuration.dms.after || settingsChanged.markedClipDuration.dms.before) {
                    optionLabels.push(`${messages["vidfleet_configuration_marked_clip_duration"]} / ${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_after"]}: ${settings.markedClipDuration.dms.after}`);
                    optionLabels.push(`${messages["vidfleet_configuration_marked_clip_duration"]} / ${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_before"]}: ${settings.markedClipDuration.dms.before}`);
                }
            }

            if (checkPropertyChange(settingsChanged.markedClipDuration.adas)) {
                customSettings.markedClipDuration.adas = settings.markedClipDuration.adas;

                //adas
                if (settingsChanged.markedClipDuration.adas.after || settingsChanged.markedClipDuration.adas.before) {
                    optionLabels.push(`${messages["vidfleet_configuration_marked_clip_duration"]} / ${messages["vidfleet_configuration_adas"]} / ${messages["vidfleet_configuration_after"]}: ${settings.markedClipDuration.adas.after}`);
                    optionLabels.push(`${messages["vidfleet_configuration_marked_clip_duration"]} / ${messages["vidfleet_configuration_adas"]} / ${messages["vidfleet_configuration_before"]}: ${settings.markedClipDuration.adas.before}`);
                }
            }

            if (checkPropertyChange(settingsChanged.markedClipDuration.nav)) {
                customSettings.markedClipDuration.nav = settings.markedClipDuration.nav;

                //nav
                if (settingsChanged.markedClipDuration.nav.after || settingsChanged.markedClipDuration.nav.before) {
                    optionLabels.push(`${messages["vidfleet_configuration_marked_clip_duration"]} / ${messages["vidfleet_configuration_nav"]} / ${messages["vidfleet_configuration_after"]}: ${settings.markedClipDuration.nav.after}`);
                    optionLabels.push(`${messages["vidfleet_configuration_marked_clip_duration"]} / ${messages["vidfleet_configuration_nav"]} / ${messages["vidfleet_configuration_before"]}: ${settings.markedClipDuration.nav.before}`);
                }
            }

            if (settingsChanged.markedClipDuration.enableEventThumbnail) {
                customSettings.markedClipDuration.enableEventThumbnail = settings.markedClipDuration.enableEventThumbnail;
                optionLabels.push(`${messages["vidfleet_configuration_marked_clip_duration"]} / ${messages["vidfleet_configuration_enable_event_thumbnail"]}: ${settings.markedClipDuration.enableEventThumbnail}`);
            }
        }
        // trustAccDefault: true,
        if (settingsChanged.trustAcc) {
            customSettings.trustAcc = settings.trustAcc;
            optionLabels.push(`${messages["vidfleet_configuration_trust_acc"]}: ${settings.trustAcc}`);
        }
        // nightVisionDefault: true,
        if (checkPropertyChange(settingsChanged.nightVision)) {
            if (settings.nightVision.drivingNightVision !== "on") {
                customSettings.nightVision = {};
                customSettings.nightVision.drivingNightVision = settings.nightVision.drivingNightVision;
                optionLabels.push(`${messages["vidfleet_configuration_night_vision"]} / ${messages["vidfleet_configuration_night_vision"]}: ${settings.nightVision.drivingNightVision}`);
            } else {
                customSettings.nightVision = settings.nightVision;
                optionLabels.push(`${messages["vidfleet_configuration_night_vision"]} / ${messages["vidfleet_configuration_from"]}: ${settings.nightVision.drivingNightVisionTime.from}`);
                optionLabels.push(`${messages["vidfleet_configuration_night_vision"]} / ${messages["vidfleet_configuration_to"]}: ${settings.nightVision.drivingNightVisionTime.to}`);
            }
        }
        // volumeDefault: true,
        if (checkPropertyChange(settingsChanged.volume)) {
            customSettings.volume = settings.volume;
            optionLabels.push(`${messages["vidfleet_configuration_volume"]} / ${messages["vidfleet_configuration_volume_level"]}: ${settings.volume.speaker}`);
        }
        // obdIIDefault: true,
        if (checkPropertyChange(settingsChanged.obdII)) {
            if (settings.obdII.mode !== "Auto") {
                customSettings.obdII = settings.obdII;
                optionLabels.push(`${messages["vidfleet_configuration_obdii"]} / ${messages["vidfleet_configuration_obdii_mode"]}: ${settings.obdII.mode}`);
                if (settings.obdII.voltage) {
                    optionLabels.push(`${messages["vidfleet_configuration_obdii"]} / ${messages["vidfleet_configuration_voltage"]} / ${messages["vidfleet_configuration_voltage_off"]}: ${settings.obdII.voltage.off}`);
                    optionLabels.push(`${messages["vidfleet_configuration_obdii"]} / ${messages["vidfleet_configuration_voltage"]} / ${messages["vidfleet_configuration_voltage_check"]}: ${settings.obdII.voltage.check}`);
                    optionLabels.push(`${messages["vidfleet_configuration_obdii"]} / ${messages["vidfleet_configuration_voltage"]} / ${messages["vidfleet_configuration_voltage_on"]}: ${settings.obdII.voltage.on}`);
                }
            } else {
                customSettings.obdII = {}
                customSettings.obdII.mode = "Auto"
                optionLabels.push(`${messages["vidfleet_configuration_obdii"]} / ${messages["vidfleet_configuration_obdii_mode"]}: ${settings.obdII.mode}`);
            }

            if (settingsChanged.obdII.dataUpload) {
                customSettings.obdII.dataUpload = settings.obdII.dataUpload;
                optionLabels.push(`${messages["vidfleet_configuration_obdii"]} / ${messages["vidfleet_configuration_data_upload"]}: ${settings.obdII.dataUpload}`);
            }

            if (settingsChanged.obdII.uploadInterval) {
                customSettings.obdII.uploadInterval = settings.obdII.uploadInterval;
                optionLabels.push(`${messages["vidfleet_configuration_obdii"]} / ${messages["vidfleet_configuration_upload_interval"]}: ${settings.obdII.uploadInterval}`);
            }

            if (settingsChanged.obdII.uploadMode) {
                customSettings.obdII.uploadMode = settings.obdII.uploadMode;
                optionLabels.push(`${messages["vidfleet_configuration_obdii"]} / ${messages["vidfleet_configuration_upload_mode"]}: ${settings.obdII.uploadMode}`);
            }

        }

        // DMS Events,
        if (checkPropertyChange(settingsChanged.dms)) {
            if (!customSettings.dms)
                customSettings.dms = {}

            //DMS level
            if (settingsChanged.dms.level) {
                customSettings.dms.level = settings.dms.level;
                optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_level"]} : ${settings.dms.level}`);
            }

            //minSpeed
            if (checkPropertyChange(settingsChanged.dms.minSpeed)) {
                customSettings.dms.minSpeed = {}
                if (settingsChanged.dms.minSpeed.NO_DRIVER) {
                    customSettings.dms.minSpeed.NO_DRIVER = settings.dms.minSpeed.NO_DRIVER;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / NO_DRIVER: ${settings.dms.minSpeed.NO_DRIVER}`);
                }
                if (settingsChanged.dms.minSpeed.ASLEEP) {
                    customSettings.dms.minSpeed.ASLEEP = settings.dms.minSpeed.ASLEEP;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / ASLEEP: ${settings.dms.minSpeed.ASLEEP}`);
                }
                if (settingsChanged.dms.minSpeed.DROWSINESS) {
                    customSettings.dms.minSpeed.DROWSINESS = settings.dms.minSpeed.DROWSINESS;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / DROWSINESS: ${settings.dms.minSpeed.DROWSINESS}`);
                }
                if (settingsChanged.dms.minSpeed.DAYDREAMING) {
                    customSettings.dms.minSpeed.DAYDREAMING = settings.dms.minSpeed.DAYDREAMING;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / DAYDREAMING: ${settings.dms.minSpeed.DAYDREAMING}`);
                }
                if (settingsChanged.dms.minSpeed.YAWN) {
                    customSettings.dms.minSpeed.YAWN = settings.dms.minSpeed.YAWN;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / YAWN: ${settings.dms.minSpeed.YAWN}`);
                }
                if (settingsChanged.dms.minSpeed.USING_PHONE) {
                    customSettings.dms.minSpeed.USING_PHONE = settings.dms.minSpeed.USING_PHONE;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / USING_PHONE: ${settings.dms.minSpeed.USING_PHONE}`);
                }
                if (settingsChanged.dms.minSpeed.DISTRACTED) {
                    customSettings.dms.minSpeed.DISTRACTED = settings.dms.minSpeed.DISTRACTED;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / DISTRACTED: ${settings.dms.minSpeed.DISTRACTED}`);
                }
                if (settingsChanged.dms.minSpeed.SMOKING) {
                    customSettings.dms.minSpeed.SMOKING = settings.dms.minSpeed.SMOKING;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / SMOKING: ${settings.dms.minSpeed.SMOKING}`);
                }
                if (settingsChanged.dms.minSpeed.NO_SEATBELT) {
                    customSettings.dms.minSpeed.NO_SEATBELT = settings.dms.minSpeed.NO_SEATBELT;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / NO_SEATBELT: ${settings.dms.minSpeed.NO_SEATBELT}`);
                }
                if (settingsChanged.dms.minSpeed.ATTENTIVE && settings.eventLevel.DMS.ATTENTIVE !== "OFF") {
                    customSettings.dms.minSpeed.ATTENTIVE = settings.dms.minSpeed.ATTENTIVE;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / ATTENTIVE: ${settings.dms.minSpeed.ATTENTIVE}`);
                }
                if (settingsChanged.dms.minSpeed.IDLE_NO_DRIVER) {
                    customSettings.dms.minSpeed.IDLE_NO_DRIVER = settings.dms.minSpeed.IDLE_NO_DRIVER;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / IDLE_NO_DRIVER: ${settings.dms.minSpeed.IDLE_NO_DRIVER}`);
                }
                if (settingsChanged.dms.minSpeed.PASSENGERS_INSIDE) {
                    customSettings.dms.minSpeed.PASSENGERS_INSIDE = settings.dms.minSpeed.PASSENGERS_INSIDE;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / PASSENGERS_INSIDE: ${settings.dms.minSpeed.PASSENGERS_INSIDE}`);
                }
                if (settingsChanged.dms.minSpeed.PASSENGERS_NO_SEATBELT) {
                    customSettings.dms.minSpeed.PASSENGERS_NO_SEATBELT = settings.dms.minSpeed.PASSENGERS_NO_SEATBELT;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_min_speed"]} / PASSENGERS_NO_SEATBELT: ${settings.dms.minSpeed.PASSENGERS_NO_SEATBELT}`);
                }

            }

            //detectionDelay
            if (checkPropertyChange(settingsChanged.dms.detectionDelay)) {
                customSettings.dms.detectionDelay = {}
                if (settingsChanged.dms.detectionDelay.NO_DRIVER) {
                    customSettings.dms.detectionDelay.NO_DRIVER = settings.dms.detectionDelay.NO_DRIVER;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / NO_DRIVER: ${settings.dms.detectionDelay.NO_DRIVER}`);
                }
                if (settingsChanged.dms.detectionDelay.ASLEEP) {
                    customSettings.dms.detectionDelay.ASLEEP = settings.dms.detectionDelay.ASLEEP;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / ASLEEP: ${settings.dms.detectionDelay.ASLEEP}`);
                }
                if (settingsChanged.dms.detectionDelay.DROWSINESS) {
                    customSettings.dms.detectionDelay.DROWSINESS = settings.dms.detectionDelay.DROWSINESS;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / DROWSINESS: ${settings.dms.detectionDelay.DROWSINESS}`);
                }
                if (settingsChanged.dms.detectionDelay.DAYDREAMING) {
                    customSettings.dms.detectionDelay.DAYDREAMING = settings.dms.detectionDelay.DAYDREAMING;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / DAYDREAMING: ${settings.dms.detectionDelay.DAYDREAMING}`);
                }
                if (settingsChanged.dms.detectionDelay.YAWN) {
                    customSettings.dms.detectionDelay.YAWN = settings.dms.detectionDelay.YAWN;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / YAWN: ${settings.dms.detectionDelay.YAWN}`);
                }
                if (settingsChanged.dms.detectionDelay.USING_PHONE) {
                    customSettings.dms.detectionDelay.USING_PHONE = settings.dms.detectionDelay.USING_PHONE;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / USING_PHONE: ${settings.dms.detectionDelay.USING_PHONE}`);
                }
                if (settingsChanged.dms.detectionDelay.DISTRACTED) {
                    customSettings.dms.detectionDelay.DISTRACTED = settings.dms.detectionDelay.DISTRACTED;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / DISTRACTED: ${settings.dms.detectionDelay.DISTRACTED}`);
                }
                if (settingsChanged.dms.detectionDelay.SMOKING) {
                    customSettings.dms.detectionDelay.SMOKING = settings.dms.detectionDelay.SMOKING;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / SMOKING: ${settings.dms.detectionDelay.SMOKING}`);
                }
                if (settingsChanged.dms.detectionDelay.NO_SEATBELT) {
                    customSettings.dms.detectionDelay.NO_SEATBELT = settings.dms.detectionDelay.NO_SEATBELT;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / NO_SEATBELT: ${settings.dms.detectionDelay.NO_SEATBELT}`);
                }
                if (settingsChanged.dms.detectionDelay.ATTENTIVE && settings.eventLevel.DMS.ATTENTIVE !== "OFF") {
                    customSettings.dms.detectionDelay.ATTENTIVE = settings.dms.detectionDelay.ATTENTIVE === 0 ? 5 : settings.dms.detectionDelay.ATTENTIVE;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / ATTENTIVE: ${customSettings.dms.detectionDelay.ATTENTIVE}`);
                }
                if (settingsChanged.dms.detectionDelay.IDLE_NO_DRIVER) {
                    customSettings.dms.detectionDelay.IDLE_NO_DRIVER = settings.dms.detectionDelay.IDLE_NO_DRIVER;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / IDLE_NO_DRIVER: ${settings.dms.detectionDelay.IDLE_NO_DRIVER}`);
                }
                if (settingsChanged.dms.detectionDelay.PASSENGERS_INSIDE) {
                    customSettings.dms.detectionDelay.PASSENGERS_INSIDE = settings.dms.detectionDelay.PASSENGERS_INSIDE;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / PASSENGERS_INSIDE: ${settings.dms.detectionDelay.PASSENGERS_INSIDE}`);
                }
                if (settingsChanged.dms.detectionDelay.PASSENGERS_NO_SEATBELT) {
                    customSettings.dms.detectionDelay.PASSENGERS_NO_SEATBELT = settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_delay"]} / PASSENGERS_NO_SEATBELT: ${settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT}`);
                }
            }

            //repeatInterval
            if (checkPropertyChange(settingsChanged.dms.repeatInterval) && HasPermission(user, "chngrptintrvl", "u")) {
                customSettings.dms.repeatInterval = {}
                if (settingsChanged.dms.repeatInterval.NO_DRIVER) {
                    customSettings.dms.repeatInterval.NO_DRIVER = settings.dms.repeatInterval.NO_DRIVER;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / NO_DRIVER: ${settings.dms.repeatInterval.NO_DRIVER}`);
                }
                if (settingsChanged.dms.repeatInterval.ASLEEP) {
                    customSettings.dms.repeatInterval.ASLEEP = settings.dms.repeatInterval.ASLEEP;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / ASLEEP: ${settings.dms.repeatInterval.ASLEEP}`);
                }
                if (settingsChanged.dms.repeatInterval.DROWSINESS) {
                    customSettings.dms.repeatInterval.DROWSINESS = settings.dms.repeatInterval.DROWSINESS;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / DROWSINESS: ${settings.dms.repeatInterval.DROWSINESS}`);
                }
                if (settingsChanged.dms.repeatInterval.DAYDREAMING) {
                    customSettings.dms.repeatInterval.DAYDREAMING = settings.dms.repeatInterval.DAYDREAMING;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / DAYDREAMING: ${settings.dms.repeatInterval.DAYDREAMING}`);
                }
                if (settingsChanged.dms.repeatInterval.YAWN) {
                    customSettings.dms.repeatInterval.YAWN = settings.dms.repeatInterval.YAWN;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / YAWN: ${settings.dms.repeatInterval.YAWN}`);
                }
                if (settingsChanged.dms.repeatInterval.USING_PHONE) {
                    customSettings.dms.repeatInterval.USING_PHONE = settings.dms.repeatInterval.USING_PHONE;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / USING_PHONE: ${settings.dms.repeatInterval.USING_PHONE}`);
                }
                if (settingsChanged.dms.repeatInterval.DISTRACTED) {
                    customSettings.dms.repeatInterval.DISTRACTED = settings.dms.repeatInterval.DISTRACTED;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / DISTRACTED: ${settings.dms.repeatInterval.DISTRACTED}`);
                }
                if (settingsChanged.dms.repeatInterval.SMOKING) {
                    customSettings.dms.repeatInterval.SMOKING = settings.dms.repeatInterval.SMOKING;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / SMOKING: ${settings.dms.repeatInterval.SMOKING}`);
                }
                if (settingsChanged.dms.repeatInterval.NO_SEATBELT) {
                    customSettings.dms.repeatInterval.NO_SEATBELT = settings.dms.repeatInterval.NO_SEATBELT;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / NO_SEATBELT: ${settings.dms.repeatInterval.NO_SEATBELT}`);
                }
                if (settingsChanged.dms.repeatInterval.ATTENTIVE && settings.eventLevel.DMS.ATTENTIVE !== "OFF") {
                    customSettings.dms.repeatInterval.ATTENTIVE = settings.dms.repeatInterval.ATTENTIVE;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / ATTENTIVE: ${settings.dms.repeatInterval.ATTENTIVE}`);
                }
                if (settingsChanged.dms.repeatInterval.IDLE_NO_DRIVER) {
                    customSettings.dms.repeatInterval.IDLE_NO_DRIVER = settings.dms.repeatInterval.IDLE_NO_DRIVER;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / IDLE_NO_DRIVER: ${settings.dms.repeatInterval.IDLE_NO_DRIVER}`);
                }
                if (settingsChanged.dms.repeatInterval.PASSENGERS_INSIDE) {
                    customSettings.dms.repeatInterval.PASSENGERS_INSIDE = settings.dms.repeatInterval.PASSENGERS_INSIDE;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / PASSENGERS_INSIDE: ${settings.dms.repeatInterval.PASSENGERS_INSIDE}`);
                }
                if (settingsChanged.dms.repeatInterval.PASSENGERS_NO_SEATBELT) {
                    customSettings.dms.repeatInterval.PASSENGERS_NO_SEATBELT = settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_repeat_interval"]} / PASSENGERS_NO_SEATBELT: ${settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT}`);
                }
            }

            //detectionReset
            if (checkPropertyChange(settingsChanged.dms.detectionReset)) {
                customSettings.dms.detectionReset = {}
                if (settingsChanged.dms.detectionReset.NO_DRIVER) {
                    customSettings.dms.detectionReset.NO_DRIVER = settings.dms.detectionReset.NO_DRIVER;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / NO_DRIVER: ${settings.dms.detectionReset.NO_DRIVER}`);
                }
                if (settingsChanged.dms.detectionReset.ASLEEP) {
                    customSettings.dms.detectionReset.ASLEEP = settings.dms.detectionReset.ASLEEP;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / ASLEEP: ${settings.dms.detectionReset.ASLEEP}`);
                }
                if (settingsChanged.dms.detectionReset.DROWSINESS) {
                    customSettings.dms.detectionReset.DROWSINESS = settings.dms.detectionReset.DROWSINESS;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / DROWSINESS: ${settings.dms.detectionReset.DROWSINESS}`);
                }
                if (settingsChanged.dms.detectionReset.DAYDREAMING) {
                    customSettings.dms.detectionReset.DAYDREAMING = settings.dms.detectionReset.DAYDREAMING;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / DAYDREAMING: ${settings.dms.detectionReset.DAYDREAMING}`);
                }
                if (settingsChanged.dms.detectionReset.YAWN) {
                    customSettings.dms.detectionReset.YAWN = settings.dms.detectionReset.YAWN;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / YAWN: ${settings.dms.detectionReset.YAWN}`);
                }
                if (settingsChanged.dms.detectionReset.USING_PHONE) {
                    customSettings.dms.detectionReset.USING_PHONE = settings.dms.detectionReset.USING_PHONE;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / USING_PHONE: ${settings.dms.detectionReset.USING_PHONE}`);
                }

                if (settingsChanged.dms.detectionReset.DISTRACTED) {
                    customSettings.dms.detectionReset.DISTRACTED = settings.dms.detectionReset.DISTRACTED;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / DISTRACTED: ${settings.dms.detectionReset.DISTRACTED}`);
                }
                if (settingsChanged.dms.detectionReset.SMOKING) {
                    customSettings.dms.detectionReset.SMOKING = settings.dms.detectionReset.SMOKING;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / SMOKING: ${settings.dms.detectionReset.SMOKING}`);
                }
                if (settingsChanged.dms.detectionReset.NO_SEATBELT) {
                    customSettings.dms.detectionReset.NO_SEATBELT = settings.dms.detectionReset.NO_SEATBELT;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / NO_SEATBELT: ${settings.dms.detectionReset.NO_SEATBELT}`);
                }
                if (settingsChanged.dms.detectionReset.ATTENTIVE && settings.eventLevel.DMS.ATTENTIVE !== "OFF") {
                    customSettings.dms.detectionReset.ATTENTIVE = settings.dms.detectionReset.ATTENTIVE;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / ATTENTIVE: ${settings.dms.detectionReset.ATTENTIVE}`);
                }
                if (settingsChanged.dms.detectionReset.IDLE_NO_DRIVER) {
                    customSettings.dms.detectionReset.IDLE_NO_DRIVER = settings.dms.detectionReset.IDLE_NO_DRIVER;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / IDLE_NO_DRIVER: ${settings.dms.detectionReset.IDLE_NO_DRIVER}`);
                }
                if (settingsChanged.dms.detectionReset.PASSENGERS_INSIDE) {
                    customSettings.dms.detectionReset.PASSENGERS_INSIDE = settings.dms.detectionReset.PASSENGERS_INSIDE;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / PASSENGERS_INSIDE: ${settings.dms.detectionReset.PASSENGERS_INSIDE}`);
                }
                if (settingsChanged.dms.detectionReset.PASSENGERS_NO_SEATBELT) {
                    customSettings.dms.detectionReset.PASSENGERS_NO_SEATBELT = settings.dms.detectionReset.PASSENGERS_NO_SEATBELT;
                    optionLabels.push(`${messages["vidfleet_configuration_dms"]} / ${messages["vidfleet_configuration_detection_reset"]} / PASSENGERS_NO_SEATBELT: ${settings.dms.detectionReset.PASSENGERS_NO_SEATBELT}`);
                }
            }

        }

        // advancedDefault: true,
        if (checkPropertyChange(settingsChanged.advanced)) {
            customSettings.advanced = {};
            if (settingsChanged.advanced.sdCardEventSpace) {
                customSettings.advanced.sdCardEventSpace = settings.advanced.sdCardEventSpace;
                optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_sd_card_event"]}: ${settings.advanced.sdCardEventSpace}`);
            }

            if (checkPropertyChange(settingsChanged.advanced.parkingHeartbeat)) {
                customSettings.advanced.parkingHeartbeat = settings.advanced.parkingHeartbeat;
                if (customSettings.advanced.parkingHeartbeat.enable) {
                    customSettings.advanced.parkingHeartbeat = settings.advanced.parkingHeartbeat;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced_parking_heart_beat"]} / ${messages["common_enabled"]}: ${settings.advanced.parkingHeartbeat.enable}`);
                    optionLabels.push(`${messages["vidfleet_configuration_advanced_parking_heart_beat"]} / ${messages["vidfleet_configuration_interval"]}: ${settings.advanced.parkingHeartbeat.intervalInMin}`);
                } else {
                    customSettings.advanced.parkingHeartbeat = {};
                    customSettings.advanced.parkingHeartbeat.enable = false;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced_parking_heart_beat"]} / ${messages["common_enabled"]}: ${settings.advanced.parkingHeartbeat.enable}`);
                }
            }

            if (checkPropertyChange(settingsChanged.advanced.adas)) {
                customSettings.advanced.adas = {};

                if (checkPropertyChange(settingsChanged.advanced.adas.timeRatio)) {
                    customSettings.advanced.adas.timeRatio = {};

                    if (settingsChanged.advanced.adas.timeRatio.FORWARD_COLLISION) {
                        customSettings.advanced.adas.timeRatio.FORWARD_COLLISION = settings.advanced.adas.timeRatio.FORWARD_COLLISION < 72 ? 72 : settings.advanced.adas.timeRatio.FORWARD_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_time_ratio"]} / COLLISION_WARNING: ${transformTimeRatio(settings.advanced.adas.timeRatio.FORWARD_COLLISION, false, null)} s`);
                    }

                    if (settingsChanged.advanced.adas.timeRatio.HEADWAY_MONITORING) {
                        customSettings.advanced.adas.timeRatio.HEADWAY_MONITORING = settings.advanced.adas.timeRatio.HEADWAY_MONITORING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_time_ratio"]} / FOLLOWING_TOO_CLOSELY: ${transformTimeRatio(settings.advanced.adas.timeRatio.HEADWAY_MONITORING, false, null)} s`);
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_time_ratio"]} / TAILGATING: ${transformTimeRatio(settings.advanced.adas.timeRatio.HEADWAY_MONITORING, true, null)} s`);
                    }

                    if (settingsChanged.advanced.adas.timeRatio.PEDESTRIAN_COLLISION) {
                        customSettings.advanced.adas.timeRatio.PEDESTRIAN_COLLISION = settings.advanced.adas.timeRatio.PEDESTRIAN_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_time_ratio"]} / PEDESTRIAN_COLLISION: ${transformTimeRatio(settings.advanced.adas.timeRatio.PEDESTRIAN_COLLISION, false, null)} s`);
                    }

                    if (settingsChanged.advanced.adas.timeRatio.CYCLIST_COLLISION) {
                        customSettings.advanced.adas.timeRatio.CYCLIST_COLLISION = settings.advanced.adas.timeRatio.CYCLIST_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_time_ratio"]} / CYCLIST_COLLISION: ${transformTimeRatio(settings.advanced.adas.timeRatio.CYCLIST_COLLISION, false, null)} s`);
                    }

                }

                if (settingsChanged.advanced.adas.cameraHeight) {
                    customSettings.advanced.adas.cameraHeight = settings.advanced.adas.cameraHeight;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_camera_height"]}: ${settings.advanced.adas.cameraHeight}`);
                }

                if (settingsChanged.advanced.adas.vehicleWidth) {
                    customSettings.advanced.adas.vehicleWidth = settings.advanced.adas.vehicleWidth;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_vehicle_width"]}: ${settings.advanced.adas.vehicleWidth}`);
                }

                if (settingsChanged.advanced.adas.cameraPositionToCenter) {
                    customSettings.advanced.adas.cameraPositionToCenter = settings.advanced.adas.cameraPositionToCenter;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_camera_position_to_center"]}: ${settings.advanced.adas.cameraPositionToCenter}`);
                }

                if (checkPropertyChange(settingsChanged.advanced.adas.minSpeed)) {
                    customSettings.advanced.adas.minSpeed = {};

                    if (settingsChanged.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING) {
                        customSettings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING = settings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_min_speed"]} / FORWARD_COLLISION_WARNING: ${settings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING}`);
                    }

                    if (settingsChanged.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING) {
                        customSettings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING = settings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_min_speed"]} / HEADWAY_MONITORING_WARNING: ${settings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING}`);
                    }

                    if (settingsChanged.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY) {
                        customSettings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY = settings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_min_speed"]} / HEADWAY_MONITORING_EMERGENCY: ${settings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY}`);
                    }

                    if (settingsChanged.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING) {
                        customSettings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING = settings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_min_speed"]} / LANE_DEPARTURE_WARNING: ${settings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING}`);
                    }

                    if (settingsChanged.advanced.adas.minSpeed.STOP_SIGN_VIOLATION) {
                        customSettings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION = settings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_min_speed"]} / STOP_SIGN_VIOLATION: ${settings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION}`);
                    }

                    if (settingsChanged.advanced.adas.minSpeed.PEDESTRIAN_COLLISION) {
                        customSettings.advanced.adas.minSpeed.PEDESTRIAN_COLLISION = settings.advanced.adas.minSpeed.PEDESTRIAN_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_min_speed"]} / PEDESTRIAN_COLLISION: ${settings.advanced.adas.minSpeed.PEDESTRIAN_COLLISION}`);
                    }

                    if (settingsChanged.advanced.adas.minSpeed.CYCLIST_COLLISION) {
                        customSettings.advanced.adas.minSpeed.CYCLIST_COLLISION = settings.advanced.adas.minSpeed.CYCLIST_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_min_speed"]} / CYCLIST_COLLISION: ${settings.advanced.adas.minSpeed.CYCLIST_COLLISION}`);
                    }
                }

                if (checkPropertyChange(settingsChanged.advanced.adas.repeatInterval) && HasPermission(user, "chngrptintrvl", "u")) {
                    customSettings.advanced.adas.repeatInterval = {};

                    if (settingsChanged.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING) {
                        customSettings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING = settings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_repeat_interval"]} / FORWARD_COLLISION_WARNING: ${settings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING}`);
                    }

                    if (settingsChanged.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING) {
                        customSettings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING = settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_repeat_interval"]} / HEADWAY_MONITORING_WARNING: ${settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING}`);
                    }

                    if (settingsChanged.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY) {
                        customSettings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY = settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_repeat_interval"]} / HEADWAY_MONITORING_EMERGENCY: ${settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY}`);
                    }

                    if (settingsChanged.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING) {
                        customSettings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING = settings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_repeat_interval"]} / LANE_DEPARTURE_WARNING: ${settings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING}`);
                    }

                    if (settingsChanged.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION) {
                        customSettings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION = settings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_repeat_interval"]} / STOP_SIGN_VIOLATION: ${settings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION}`);
                    }

                    if (settingsChanged.advanced.adas.repeatInterval.PEDESTRIAN_COLLISION) {
                        customSettings.advanced.adas.repeatInterval.PEDESTRIAN_COLLISION = settings.advanced.adas.repeatInterval.PEDESTRIAN_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_repeat_interval"]} / PEDESTRIAN_COLLISION: ${settings.advanced.adas.repeatInterval.PEDESTRIAN_COLLISION}`);
                    }

                    if (settingsChanged.advanced.adas.repeatInterval.CYCLIST_COLLISION) {
                        customSettings.advanced.adas.repeatInterval.CYCLIST_COLLISION = settings.advanced.adas.repeatInterval.CYCLIST_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_repeat_interval"]} / CYCLIST_COLLISION: ${settings.advanced.adas.repeatInterval.CYCLIST_COLLISION}`);
                    }
                }

                if (checkPropertyChange(settingsChanged.advanced.adas.detectionReset)) {
                    customSettings.advanced.adas.detectionReset = {};

                    if (settingsChanged.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING) {
                        customSettings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING = settings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_reset"]} / FORWARD_COLLISION_WARNING: ${settings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING}`);
                    }

                    if (settingsChanged.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING) {
                        customSettings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING = settings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_reset"]} / HEADWAY_MONITORING_WARNING: ${settings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING}`);
                    }

                    if (settingsChanged.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY) {
                        customSettings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY = settings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_reset"]} / HEADWAY_MONITORING_EMERGENCY: ${settings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY}`);
                    }

                    if (settingsChanged.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING) {
                        customSettings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING = settings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_reset"]} / LANE_DEPARTURE_WARNING: ${settings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING}`);
                    }

                    if (settingsChanged.advanced.adas.detectionReset.STOP_SIGN_VIOLATION) {
                        customSettings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION = settings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_reset"]} / STOP_SIGN_VIOLATION: ${settings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION}`);
                    }

                    if (settingsChanged.advanced.adas.detectionReset.PEDESTRIAN_COLLISION) {
                        customSettings.advanced.adas.detectionReset.PEDESTRIAN_COLLISION = settings.advanced.adas.detectionReset.PEDESTRIAN_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_reset"]} / PEDESTRIAN_COLLISION: ${settings.advanced.adas.detectionReset.PEDESTRIAN_COLLISION}`);
                    }

                    if (settingsChanged.advanced.adas.detectionReset.CYCLIST_COLLISION) {
                        customSettings.advanced.adas.detectionReset.CYCLIST_COLLISION = settings.advanced.adas.detectionReset.CYCLIST_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_reset"]} / CYCLIST_COLLISION: ${settings.advanced.adas.detectionReset.CYCLIST_COLLISION}`);
                    }
                }

                if (checkPropertyChange(settingsChanged.advanced.adas.detectionDelay)) {
                    customSettings.advanced.adas.detectionDelay = {};

                    if (settingsChanged.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING) {
                        customSettings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING = settings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_delay"]} / FORWARD_COLLISION_WARNING: ${settings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING}`);
                    }

                    if (settingsChanged.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING) {
                        customSettings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING = settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_delay"]} / HEADWAY_MONITORING_WARNING: ${settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING}`);
                    }

                    if (settingsChanged.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY) {
                        customSettings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY = settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_delay"]} / HEADWAY_MONITORING_EMERGENCY: ${settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY}`);
                    }

                    if (settingsChanged.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING) {
                        customSettings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING = settings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_delay"]} / LANE_DEPARTURE_WARNING: ${settings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING}`);
                    }

                    if (settingsChanged.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION) {
                        customSettings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION = settings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_delay"]} / STOP_SIGN_VIOLATION: ${settings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION}`);
                    }

                    if (settingsChanged.advanced.adas.detectionDelay.PEDESTRIAN_COLLISION) {
                        customSettings.advanced.adas.detectionDelay.PEDESTRIAN_COLLISION = settings.advanced.adas.detectionDelay.PEDESTRIAN_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_delay"]} / PEDESTRIAN_COLLISION: ${settings.advanced.adas.detectionDelay.PEDESTRIAN_COLLISION}`);
                    }

                    if (settingsChanged.advanced.adas.detectionDelay.CYCLIST_COLLISION) {
                        customSettings.advanced.adas.detectionDelay.CYCLIST_COLLISION = settings.advanced.adas.detectionDelay.CYCLIST_COLLISION;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_detection_delay"]} / CYCLIST_COLLISION: ${settings.advanced.adas.detectionDelay.CYCLIST_COLLISION}`);
                    }
                }

                //ADAS level
                if (settingsChanged.advanced.adas.level) {
                    customSettings.advanced.adas.level = settings.advanced.adas.level;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ADAS / ${messages["vidfleet_configuration_level"]}: ${settings.advanced.adas.level}`);
                }
            }

            if (checkPropertyChange(settingsChanged.advanced.ignition)) {
                customSettings.advanced.ignition = {};
                if (settingsChanged.advanced.ignition.virtualMode) {
                    customSettings.advanced.ignition.virtualMode = settings.advanced.ignition.virtualMode;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_ignition"]} / ${messages["vidfleet_configuration_virtual_mode"]}: ${settings.advanced.ignition.virtualMode}`);
                }
                if (settingsChanged.advanced.ignition.autoMode) {
                    customSettings.advanced.ignition.autoMode = settings.advanced.ignition.autoMode;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_ignition"]} / ${messages["vidfleet_configuration_auto_mode"]}: ${settings.advanced.ignition.autoMode}`);
                }
            }

            if (settingsChanged.advanced.subtitle.speed) {
                customSettings.advanced.subtitle = {};
                customSettings.advanced.subtitle.speed = settings.advanced.subtitle.speed;
                optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages['vidfleet_configuration_subtitle']} / ${messages['vidfleet_configuration_speed']}: ${settings.advanced.subtitle.speed}`);
            }

            if (settingsChanged.advanced.subtitle.timezone) {
                customSettings.advanced.subtitle = {};
                customSettings.advanced.subtitle.timezone = settings.advanced.subtitle.timezone;
                optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages['vidfleet_configuration_subtitle']} / ${messages['vidfleet_configuration_timezone']}: ${settings.advanced.subtitle.timezone}`);
            }

            if (settingsChanged.advanced.subtitle.gps) {
                customSettings.advanced.subtitle = {};
                customSettings.advanced.subtitle.gps = settings.advanced.subtitle.gps;
                optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages['vidfleet_configuration_subtitle']} / ${messages['vidfleet_configuration_gps']}: ${settings.advanced.subtitle.gps}`);
            }

            if (settingsChanged?.advanced?.driverID && checkPropertyChange(settingsChanged.advanced.driverID)) {
                customSettings.advanced.driverID = settings.advanced.driverID;
                optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_driver_id"]} / ${messages["vidfleet_configuration_upload_face"]}: ${settings.advanced.driverID.uploadFace}`);
            }

            //privacyControl
            if (checkPropertyChange(settingsChanged.advanced.privacyControl)) {
                customSettings.advanced.privacyControl = settings.advanced.privacyControl;
                optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages['vidfleet_configuration_privacy_control']} / ${messages["vidfleet_configuration_level"]}: ${settings.advanced.privacyControl.level}`);
            }

            //livePhoto
            if (checkPropertyChange(settingsChanged.advanced.livePhoto)) {
                customSettings.advanced.livePhoto = settings.advanced.livePhoto;
                optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages['vidfleet_configuration_live_photo']} / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.livePhoto.duration}`);
                optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages['vidfleet_configuration_live_photo']} / ${messages["vidfleet_configuration_interval"]}: ${settings.advanced.livePhoto.interval}`);
            }

            //AUX
            if (checkPropertyChange(settingsChanged.advanced.aux)) {
                customSettings.advanced.aux = {};

                if (settingsChanged.advanced.aux.model) {
                    customSettings.advanced.aux.model = settings.advanced.aux.model;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / AUX / ${messages["vidfleet_configuration_model"]} : ${settings.advanced.aux.model} `);
                }

                if (settingsChanged.advanced.aux.angle) {
                    customSettings.advanced.aux.angle = settings.advanced.aux.angle;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / AUX / ${messages["vidfleet_configuration_angle"]} : ${settings.advanced.aux.angle} `);

                }

                if (settingsChanged.advanced.aux.model2 && devices?.length && devices[0].deviceModel.name.includes("AIR2")) {
                    customSettings.advanced.aux.model2 = settings.advanced.aux.model2;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / AUX / ${messages["vidfleet_configuration_model2"]} : ${settings.advanced.aux.model2} `);
                }

                if (settingsChanged.advanced.aux.angle2 && settingsChanged.advanced.aux.model2 && devices?.length && devices[0].deviceModel.name.includes("AIR2")) {
                    customSettings.advanced.aux.angle2 = settings.advanced.aux.angle2;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / AUX / ${messages["vidfleet_configuration_angle2"]} : ${settings.advanced.aux.angle2} `);

                }

            }

            //language
            if (settingsChanged.advanced.lang) {
                customSettings.advanced.lang = settings.advanced.lang;
                optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_lang"]}: ${settings.advanced.lang}`);
            }

            //safetyAssistance
            if (checkPropertyChange(settingsChanged.advanced.safetyAssistance)) {
                customSettings.advanced.safetyAssistance = { alarm: {} };

                if (settingsChanged.advanced.safetyAssistance.alarm.NO_SEATBELT) {
                    customSettings.advanced.safetyAssistance.alarm.NO_SEATBELT = settings.advanced.safetyAssistance.alarm.NO_SEATBELT;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / NO_SEATBELT / ${messages["common_enabled"]}: ${settings.advanced.safetyAssistance.alarm.NO_SEATBELT.enable}`);
                    if (settings.advanced.safetyAssistance.alarm.NO_SEATBELT.enable)
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / NO_SEATBELT / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.safetyAssistance.alarm.NO_SEATBELT.duration}`);
                }

                if (settingsChanged.advanced.safetyAssistance.alarm.USING_CELLPHONE) {
                    customSettings.advanced.safetyAssistance.alarm.USING_CELLPHONE = settings.advanced.safetyAssistance.alarm.USING_CELLPHONE;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / USING_CELLPHONE / ${messages["common_enabled"]}: ${settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.enable}`);
                    if (settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.enable)
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / USING_CELLPHONE / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.duration}`);
                }

                if (settingsChanged.advanced.safetyAssistance.alarm.DISTRACTED) {
                    customSettings.advanced.safetyAssistance.alarm.DISTRACTED = settings.advanced.safetyAssistance.alarm.DISTRACTED;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / DISTRACTED / ${messages["common_enabled"]}: ${settings.advanced.safetyAssistance.alarm.DISTRACTED.enable}`);
                    if (settings.advanced.safetyAssistance.alarm.DISTRACTED.enable)
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / DISTRACTED / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.safetyAssistance.alarm.DISTRACTED.duration}`);
                }

                if (settingsChanged.advanced.safetyAssistance.alarm.NO_DRIVER) {
                    customSettings.advanced.safetyAssistance.alarm.NO_DRIVER = settings.advanced.safetyAssistance.alarm.NO_DRIVER;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / NO_DRIVER / ${messages["common_enabled"]}: ${settings.advanced.safetyAssistance.alarm.NO_DRIVER.enable}`);
                    if (settings.advanced.safetyAssistance.alarm.NO_DRIVER.enable)
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / NO_DRIVER / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.safetyAssistance.alarm.NO_DRIVER.duration}`);
                }

                if (settingsChanged.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING) {
                    customSettings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING = settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / CLOSE_FOLLOWING / ${messages["common_enabled"]}: ${settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.enable}`);
                    if (settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.enable)
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / CLOSE_FOLLOWING / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.duration}`);
                }

                if (settingsChanged.advanced.safetyAssistance.alarm.TAILGATING) {
                    customSettings.advanced.safetyAssistance.alarm.TAILGATING = settings.advanced.safetyAssistance.alarm.TAILGATING;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / TAILGATING / ${messages["common_enabled"]}: ${settings.advanced.safetyAssistance.alarm.TAILGATING.enable}`);
                    if (settings.advanced.safetyAssistance.alarm.TAILGATING.enable)
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / TAILGATING / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.safetyAssistance.alarm.TAILGATING.duration}`);
                }

                if (settingsChanged.advanced.safetyAssistance.alarm.LANE_DEPARTURE) {
                    customSettings.advanced.safetyAssistance.alarm.LANE_DEPARTURE = settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / LANE_DEPARTURE / ${messages["common_enabled"]}: ${settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.enable}`);
                    if (settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.enable)
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / LANE_DEPARTURE / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.duration}`);
                }

                if (settingsChanged.advanced.safetyAssistance.alarm.SPEEDING) {
                    customSettings.advanced.safetyAssistance.alarm.SPEEDING = settings.advanced.safetyAssistance.alarm.SPEEDING;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / SPEEDING / ${messages["common_enabled"]}: ${settings.advanced.safetyAssistance.alarm.SPEEDING.enable}`);
                    if (settings.advanced.safetyAssistance.alarm.SPEEDING.enable)
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / SPEEDING / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.safetyAssistance.alarm.SPEEDING.duration}`);
                }

                if (settingsChanged.advanced.safetyAssistance.alarm.OVER_MAX_SPEED) {
                    customSettings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED = settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / OVER_MAX_SPEED / ${messages["common_enabled"]}: ${settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.enable}`);
                    if (settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.enable)
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / OVER_MAX_SPEED / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.duration}`);
                }

                if (settingsChanged.advanced.safetyAssistance.alarm.PEDESTRIAN) {
                    customSettings.advanced.safetyAssistance.alarm.PEDESTRIAN = settings.advanced.safetyAssistance.alarm.PEDESTRIAN;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / PEDESTRIAN / ${messages["common_enabled"]}: ${settings.advanced.safetyAssistance.alarm.PEDESTRIAN.enable}`);
                    if (settings.advanced.safetyAssistance.alarm.PEDESTRIAN.enable)
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / PEDESTRIAN / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.safetyAssistance.alarm.PEDESTRIAN.duration}`);
                }

                if (settingsChanged.advanced.safetyAssistance.alarm.CYCLIST) {
                    customSettings.advanced.safetyAssistance.alarm.CYCLIST = settings.advanced.safetyAssistance.alarm.CYCLIST;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / CYCLIST / ${messages["common_enabled"]}: ${settings.advanced.safetyAssistance.alarm.CYCLIST.enable}`);
                    if (settings.advanced.safetyAssistance.alarm.CYCLIST.enable)
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / CYCLIST / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.safetyAssistance.alarm.CYCLIST.duration}`);
                }

                if (settingsChanged.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT) {
                    customSettings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT = settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT;
                    optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / PASSENGERS_NO_SEATBELT / ${messages["common_enabled"]}: ${settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.enable}`);
                    if (settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.enable)
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_safety_assistance"]} / PASSENGERS_NO_SEATBELT / ${messages["vidfleet_configuration_duration"]}: ${settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.duration}`);
                }
            }

            //nav
            if (settings?.advanced?.nav && customSettings?.advanced && checkPropertyChange(settingsChanged?.advanced?.nav)) {
                customSettings.advanced.nav = {}
                if (settings?.advanced?.nav?.maxSpeed && customSettings?.advanced?.nav && checkPropertyChange(settingsChanged?.advanced?.nav?.maxSpeed)) {
                    customSettings.advanced.nav.maxSpeed = {};

                    if (settingsChanged?.advanced?.nav?.maxSpeed?.OVER_MAX_SPEED && settings?.advanced?.nav?.maxSpeed?.OVER_MAX_SPEED !== undefined) {
                        customSettings.advanced.nav.maxSpeed.OVER_MAX_SPEED = settings.advanced.nav.maxSpeed.OVER_MAX_SPEED;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / NAV / ${messages["vidfleet_configuration_max_speed"]} / OVER_MAX_SPEED: ${settings.advanced.nav.maxSpeed.OVER_MAX_SPEED}`);
                    }
                }

                if (settings?.advanced?.nav?.minSpeed && customSettings?.advanced?.nav && checkPropertyChange(settingsChanged?.advanced?.nav?.minSpeed)) {
                    customSettings.advanced.nav.minSpeed = {};

                    if (settingsChanged?.advanced?.nav?.minSpeed?.SPEEDING && settings?.advanced?.nav?.minSpeed?.SPEEDING !== undefined) {
                        customSettings.advanced.nav.minSpeed.SPEEDING = settings.advanced.nav.minSpeed.SPEEDING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / NAV / ${messages["vidfleet_configuration_min_speed"]} / SPEEDING: ${settings.advanced.nav.minSpeed.SPEEDING}`);
                    }

                    if (settingsChanged?.advanced?.nav?.minSpeed?.OVER_MAX_SPEED && settings?.advanced?.nav?.minSpeed?.OVER_MAX_SPEED !== undefined) {
                        customSettings.advanced.nav.minSpeed.OVER_MAX_SPEED = settings.advanced.nav.minSpeed.OVER_MAX_SPEED;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / NAV / ${messages["vidfleet_configuration_min_speed"]} / OVER_MAX_SPEED: ${settings.advanced.nav.minSpeed.OVER_MAX_SPEED}`);
                    }
                }

                if (settings?.advanced?.nav?.repeatInterval && customSettings?.advanced?.nav && checkPropertyChange(settingsChanged?.advanced?.nav?.repeatInterval) && HasPermission(user, "chngrptintrvl", "u")) {
                    customSettings.advanced.nav.repeatInterval = {};

                    if (settingsChanged?.advanced?.nav?.repeatInterval?.SPEEDING && settings?.advanced?.nav?.repeatInterval?.SPEEDING !== undefined) {
                        customSettings.advanced.nav.repeatInterval.SPEEDING = settings.advanced.nav.repeatInterval.SPEEDING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / NAV / ${messages["vidfleet_configuration_repeat_interval"]} / SPEEDING: ${settings.advanced.nav.repeatInterval.SPEEDING}`);
                    }

                    if (settingsChanged?.advanced?.nav?.repeatInterval?.OVER_MAX_SPEED && settings?.advanced?.nav?.repeatInterval?.OVER_MAX_SPEED !== undefined) {
                        customSettings.advanced.nav.repeatInterval.OVER_MAX_SPEED = settings.advanced.nav.repeatInterval.OVER_MAX_SPEED;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / NAV / ${messages["vidfleet_configuration_repeat_interval"]} / OVER_MAX_SPEED: ${settings.advanced.nav.repeatInterval.OVER_MAX_SPEED}`);
                    }
                }

                if (settings?.advanced?.nav?.detectionDelay && customSettings?.advanced?.nav && checkPropertyChange(settingsChanged?.advanced?.nav?.detectionDelay)) {
                    customSettings.advanced.nav.detectionDelay = {};

                    if (settingsChanged?.advanced?.nav?.detectionDelay?.SPEEDING && settings?.advanced?.nav?.detectionDelay?.SPEEDING !== undefined) {
                        customSettings.advanced.nav.detectionDelay.SPEEDING = settings.advanced.nav.detectionDelay.SPEEDING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / NAV / ${messages["vidfleet_configuration_detection_delay"]} / SPEEDING: ${settings.advanced.nav.detectionDelay.SPEEDING}`);
                    }

                    if (settingsChanged.advanced.nav.detectionDelay.OVER_MAX_SPEED) {
                        customSettings.advanced.nav.detectionDelay.OVER_MAX_SPEED = settings.advanced.nav.detectionDelay.OVER_MAX_SPEED;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / NAV / ${messages["vidfleet_configuration_detection_delay"]} / OVER_MAX_SPEED: ${settings.advanced.nav.detectionDelay.OVER_MAX_SPEED}`);
                    }
                }

                if (settings?.advanced?.nav?.detectionReset && customSettings?.advanced?.nav && checkPropertyChange(settingsChanged?.advanced?.nav?.detectionReset)) {
                    customSettings.advanced.nav.detectionReset = {};

                    if (settingsChanged?.advanced?.nav?.detectionReset?.SPEEDING && settings?.advanced?.nav?.detectionReset?.SPEEDING !== undefined) {
                        customSettings.advanced.nav.detectionReset.SPEEDING = settings.advanced.nav.detectionReset.SPEEDING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / NAV / ${messages["vidfleet_configuration_detection_reset"]} / SPEEDING: ${settings.advanced.nav.detectionReset.SPEEDING}`);
                    }

                    if (settingsChanged?.advanced?.nav?.detectionReset?.OVER_MAX_SPEED && settings?.advanced?.nav?.detectionReset?.OVER_MAX_SPEED !== undefined) {
                        customSettings.advanced.nav.detectionReset.OVER_MAX_SPEED = settings.advanced.nav.detectionReset.OVER_MAX_SPEED;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / NAV / ${messages["vidfleet_configuration_detection_reset"]} / OVER_MAX_SPEED: ${settings.advanced.nav.detectionReset.OVER_MAX_SPEED}`);
                    }
                }

                if (settings?.advanced?.nav?.overspeedLimit && customSettings?.advanced?.nav && checkPropertyChange(settingsChanged?.advanced?.nav?.overspeedLimit)) {
                    customSettings.advanced.nav.overspeedLimit = {};

                    if (settingsChanged?.advanced?.nav?.overspeedLimit?.SPEEDING && settings?.advanced?.nav?.overspeedLimit?.SPEEDING !== undefined) {
                        customSettings.advanced.nav.overspeedLimit.SPEEDING = settings.advanced.nav.overspeedLimit.SPEEDING;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / NAV / ${messages["vidfleet_configuration_over_speed_limit"]} / SPEEDING: ${settings.advanced.nav.overspeedLimit.SPEEDING}`);
                    }

                    if (settingsChanged?.advanced?.nav?.overspeedLimit?.OVER_MAX_SPEED && settings?.advanced?.nav?.overspeedLimit?.OVER_MAX_SPEED !== undefined) {
                        customSettings.advanced.nav.overspeedLimit.OVER_MAX_SPEED = settings.advanced.nav.overspeedLimit.OVER_MAX_SPEED;
                        optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / NAV / ${messages["vidfleet_configuration_over_speed_limit"]} / OVER_MAX_SPEED: ${settings.advanced.nav.overspeedLimit.OVER_MAX_SPEED}`);
                    }
                }
            }
        }

        //time zone
        if (settingsChanged.timezone) {
            customSettings.timezone = settings.timezone;

            if (customSettings.advanced) {
                if (customSettings.advanced.subtitle) {
                    customSettings.advanced.subtitle.timezone = settings.advanced.subtitle.timezone;
                } else {
                    customSettings.advanced.subtitle = settings.advanced.subtitle;
                }
            } else {
                customSettings.advanced = {
                    subtitle: {}
                };
            }
            let istimezone = settings.timezone.includes("/")
            if (istimezone)
                customSettings.advanced.subtitle.timezone = "name";
            else
                customSettings.advanced.subtitle.timezone = "GMT%z";

            optionLabels.push(`${messages["vidfleet_configuration_timezone"]}: ${customSettings.timezone}`);
            optionLabels.push(`${messages["vidfleet_configuration_advanced"]} / ${messages["vidfleet_configuration_timezone_subtitle"]} / ${messages["vidfleet_configuration_timezone"]}: ${customSettings.advanced.subtitle.timezone}`);

        }

        if (!_.isEmpty(customSettings)) {
            //to know if restrictions are needed to validate
            if (devices && devices[0]?.deviceModel?.name?.includes("360")) { } else {
                //if any of those option has been modified the other need to be checked and send as a group
                if (customSettings?.advanced?.adas?.level || customSettings?.advanced?.aux?.model || customSettings?.advanced?.aux?.model2 || customSettings?.dms?.level || customSettings?.recordConfig?.config) {
                    customSettings.recordConfig = settings.recordConfig;

                    if (customSettings.dms) {
                        customSettings.dms.level = settings.dms.level
                    } else {
                        customSettings.dms = { level: settings.dms.level }
                    }
                    if (customSettings.advanced) {
                        if (!customSettings.advanced.aux)
                            customSettings.advanced.aux = {}

                        customSettings.advanced.aux.model = settings.advanced.aux.model
                        customSettings.advanced.aux.angle = settings.advanced.aux.angle

                        if (devices?.length && devices[0].deviceModel.name.includes("AIR2")) {
                            if (settings.advanced.aux.model2) {
                                customSettings.advanced.aux.model2 = settings.advanced.aux.model2
                                customSettings.advanced.aux.angle2 = settings.advanced.aux.angle2
                            }
                        }
                        //Privacy Control
                        customSettings.advanced.privacyControl = settings.advanced.privacyControl;
                        if (customSettings.advanced.adas) {
                            customSettings.advanced.adas.level = settings.advanced.adas.level
                        } else {
                            customSettings.advanced.adas = { level: settings.advanced.adas.level }
                        }
                        //Privacy Control
                        if (customSettings.advanced.privacyControl) {
                            customSettings.advanced.privacyControl.level = settings.advanced.privacyControl.level
                        } else {
                            customSettings.advanced.privacyControl = { level: settings.advanced.privacyControl.level }
                        }
                    } else {
                        customSettings.advanced = {
                            aux: settings.advanced.aux,
                            adas: { level: settings.advanced.adas.level },
                            privacyControl: { level: settings.advanced.privacyControl.level }
                        }
                    }
                }
            }
        }

        //if user does not have permissions remove repeat Interval
        if (!HasPermission(user, "chngrptintrvl", "u")) {
            if (customSettings?.dms?.repeatInterval)
                delete customSettings.dms.repeatInterval;
            if (customSettings?.advanced?.adas?.repeatInterval)
                delete customSettings.advanced.adas.repeatInterval
            if (customSettings?.advanced?.nav?.repeatInterval)
                delete customSettings.advanced.nav.repeatInterval;
        }

        let data = {
            customSettings: customSettings,
            optionLabels: optionLabels
        }
        return data;
    }

    //to expande or collapse all
    const collapseAll = (expand) => {


        optionCollapsed.collapsedSound = expand;
        optionCollapsed.colapsedEventLevelThreshold = expand;
        optionCollapsed.colapsedVideoStream = expand;
        optionCollapsed.colapsedVideoResolution = expand;
        optionCollapsed.colapsedDelayedPowerOff = expand;
        optionCollapsed.colapsedLiveStreamDuration = expand;
        optionCollapsed.colapsedAutoSnapshot = expand;
        optionCollapsed.colapsedMic = expand;
        optionCollapsed.colapsedPrivacyMask = expand;
        optionCollapsed.colapsedRotate = expand;
        optionCollapsed.colapsedMarkedClipDuration = expand;
        optionCollapsed.colapsedMinSpeed = expand;
        optionCollapsed.colapsedDetectionDelay = expand;
        optionCollapsed.colapsedRepeatInterval = expand;
        optionCollapsed.colapsedDetectionReset = expand;
        optionCollapsed.colapsedTrustAcc = expand;
        optionCollapsed.colapsedNightVision = expand;
        optionCollapsed.colapsedVolume = expand;
        optionCollapsed.colapsedObdII = expand;
        optionCollapsed.colapsedDMSEvents = expand;
        optionCollapsed.colapsedRunningInfo = expand;
        optionCollapsed.colapsedAdvanced = expand;
        optionCollapsed.collapsedAll = expand;
        optionCollapsed.colapsedTimezone = expand;
        optionCollapsed.colapsedCev = expand;

        setOptionCollapsed({ ...optionCollapsed })

    }

    //To restore settings to default
    const restoreToDefault = () => {

        let mergedSettingsFleet = null
        if (_.isEmpty(settingsFromFleetAPIRaw))
            mergedSettingsFleet = defaultManufacturer;
        else
            mergedSettingsFleet = { settings: mergeSettings(defaultManufacturer.settings, settingsFromFleetAPIRaw?.settings) }

        //setSettingsData(JSON.parse(JSON.stringify(defaultManufacturer)))
        setSettingsData(JSON.parse(JSON.stringify(mergedSettingsFleet)))
        const updatedChanges = JSON.parse(JSON.stringify(replaceObjectvalues(settingsDataChanged, true)));
        setSettingsDataChanged(updatedChanges);
        if (props.setSettingsDataChanged) {
            props.setSettingsDataChanged(updatedChanges);
        }
        setVehicleClass(null);
        setSaveSettingsAvailable(true);
        collapseAll(true);
        setOptionDefaults({
            ...{
                soundsDefault: true,
                eventLevelThresholdDefault: true,
                videoStreamDefault: true,
                videoResolutionDefault: true,
                delayedPowerOffDefault: true,
                liveStreamDurationDefault: true,
                autoSnapshotDefault: true,
                runningInfoDefault: true,
                advancedDefault: true,
                micDefault: true,
                privacyMaskDefault: true,
                rotateDefault: true,
                markedClipDurationDefault: true,
                minSpeedDefault: true,
                detectionDelayDefault: true,
                repeatIntervalDefault: true,
                detectionResetDefault: true,
                trustAccDefault: true,
                nightVisionDefault: true,
                volumeDefault: true,
                obdIIDefault: true,
                DMSEventsDefault: true,
                timezoneDefault: true,
                cevDefault: true
            }
        })

    }

    //To cancel the chages and restore to the config to the point where before meke a change on any element
    const cancelChange = () => {
        if (bulk) {
            enableCustomOptionsOnLoad(settingsFromAPIRaw, null, settingsDefaultRaw);
        } else {
            if (devices.length === 0) {
                enableCustomOptionsOnLoad(settingsFromAPIRaw, null, settingsDefaultRaw);
            } else {
                enableCustomOptionsOnLoad(settingsFromAPIRaw, settingsFromFleetAPIRaw, settingsDefaultRaw);
            }
        }
        collapseAll(true);
        setSaveSettingsAvailable(false);
    }

    /**
     * 
     * @param {*} timeRatioPercentage 
     * @param {*} half 
     * @param {*} seconds 
     * @returns 
     * ADAS Sensibility to detect risk events on the road
     * Time to Collision (TTC)
     * Headway Monitoring Warning (HWM) 2.8s (default)
     * Headway Monitoring Emergency (HME) 1/2 of HWM
     * Forward Collision Warning (FCW) 2.8s (default)
     * 
     * The default ratio is 100, or 100%. to change the sensitivity, for example, to decrease TTC 2 seconds for HMW (and thereby 1s for HME),
     * you would set the headway monitoring ratio value to 71 (71% of 2.8s)
     * this function is the way to calculate to show and save the proper values for HWM, HME and FCW
     */
    const transformTimeRatio = (timeRatioPercentage, half, seconds) => {
        let time = 0;
        let percentage = 0;
        let baseTime = 2.8; // default base time
        let basePercentage = 100; // default base percentage
        if (!seconds) { // to show the seconds that represent the percentage selected
            time = (baseTime * timeRatioPercentage) / basePercentage;
            if (half)
                time = time / 2;
            time = Math.round((time + Number.EPSILON) * 100) / 100
            return time;
        } else { // to convert to percentage the seconds showed
            percentage = Math.round((basePercentage * seconds) / baseTime);
            return percentage;
        }

    }

    //merge config settings
    const mergeSettings = (defaultSettings, settingToMerge) => {
        let customSettings = JSON.parse(JSON.stringify(defaultSettings));
        let settings = JSON.parse(JSON.stringify(settingToMerge));

        // sounds
        {
            if (settings.sounds && !_.isEqual(settings.sounds, customSettings.sounds)) {

                if (settings.sounds.power && !_.isEqual(settings.sounds.power, customSettings.sounds.power)) {
                    customSettings.sounds.power = settings.sounds.power;
                }

                if (settings.sounds.eventTrigger && !_.isEqual(settings.sounds.eventTrigger, customSettings.sounds.eventTrigger)) {
                    customSettings.sounds.eventTrigger = settings.sounds.eventTrigger;
                }

                if (settings.sounds.recording && !_.isEqual(settings.sounds.recording, customSettings.sounds.recording)) {
                    customSettings.sounds.recording = settings.sounds.recording;
                }

                if (settings.sounds.dms && !_.isEqual(settings.sounds.dms, customSettings.sounds.dms)) {

                    if (settings.sounds.dms.NO_DRIVER && !_.isEqual(settings.sounds.dms.NO_DRIVER, customSettings.sounds.dms.NO_DRIVER)) {
                        customSettings.sounds.dms.NO_DRIVER = settings.sounds.dms.NO_DRIVER;
                    }
                    if (settings.sounds.dms.ASLEEP && !_.isEqual(settings.sounds.dms.ASLEEP, customSettings.sounds.dms.ASLEEP)) {
                        customSettings.sounds.dms.ASLEEP = settings.sounds.dms.ASLEEP
                    }
                    if (settings.sounds.dms.DROWSINESS && !_.isEqual(settings.sounds.dms.DROWSINESS, customSettings.sounds.dms.DROWSINESS)) {
                        customSettings.sounds.dms.DROWSINESS = settings.sounds.dms.DROWSINESS

                    }
                    if (settings.sounds.dms.DAYDREAMING && !_.isEqual(settings.sounds.dms.DAYDREAMING, customSettings.sounds.dms.DAYDREAMING)) {
                        customSettings.sounds.dms.DAYDREAMING = settings.sounds.dms.DAYDREAMING
                    }
                    if (settings.sounds.dms.YAWN && !_.isEqual(settings.sounds.dms.YAWN, customSettings.sounds.dms.YAWN)) {
                        customSettings.sounds.dms.YAWN = settings.sounds.dms.YAWN

                    }
                    if (settings.sounds.dms.USING_PHONE && !_.isEqual(settings.sounds.dms.USING_PHONE, customSettings.sounds.dms.USING_PHONE)) {
                        customSettings.sounds.dms.USING_PHONE = settings.sounds.dms.USING_PHONE

                    }
                    if (settings.sounds.dms.DISTRACTED && !_.isEqual(settings.sounds.dms.DISTRACTED, customSettings.sounds.dms.DISTRACTED)) {
                        customSettings.sounds.dms.DISTRACTED = settings.sounds.dms.DISTRACTED

                    }
                    if (settings.sounds.dms.SMOKING && !_.isEqual(settings.sounds.dms.SMOKING, customSettings.sounds.dms.SMOKING)) {
                        customSettings.sounds.dms.SMOKING = settings.sounds.dms.SMOKING

                    }
                    if (settings.sounds.dms.NO_SEATBELT && !_.isEqual(settings.sounds.dms.NO_SEATBELT, customSettings.sounds.dms.NO_SEATBELT)) {
                        customSettings.sounds.dms.NO_SEATBELT = settings.sounds.dms.NO_SEATBELT

                    }
                    if (settings.sounds.dms.ATTENTIVE && !_.isEqual(settings.sounds.dms.ATTENTIVE, customSettings.sounds.dms.ATTENTIVE)) {
                        customSettings.sounds.dms.ATTENTIVE = settings.sounds.dms.ATTENTIVE

                    }
                    if (settings.sounds.dms.IDLE_NO_DRIVER && !_.isEqual(settings.sounds.dms.IDLE_NO_DRIVER, customSettings.sounds.dms.IDLE_NO_DRIVER)) {
                        customSettings.sounds.dms.IDLE_NO_DRIVER = settings.sounds.dms.IDLE_NO_DRIVER
                    }

                    if (settings.sounds.dms.PASSENGERS_INSIDE && !_.isEqual(settings.sounds.dms.PASSENGERS_INSIDE, customSettings.sounds.dms.PASSENGERS_INSIDE)) {
                        customSettings.sounds.dms.PASSENGERS_INSIDE = settings.sounds.dms.PASSENGERS_INSIDE
                    }

                    if (settings.sounds.dms.PASSENGERS_NO_SEATBELT && !_.isEqual(settings.sounds.dms.PASSENGERS_NO_SEATBELT, customSettings.sounds.dms.PASSENGERS_NO_SEATBELT)) {
                        customSettings.sounds.dms.PASSENGERS_NO_SEATBELT = settings.sounds.dms.PASSENGERS_NO_SEATBELT
                    }
                }

                if (settings.sounds.adas && !_.isEqual(settings.sounds.adas, customSettings.sounds.adas)) {
                    if (settings.sounds.adas.FORWARD_COLLISION_WARNING && !_.isEqual(settings.sounds.adas.FORWARD_COLLISION_WARNING, customSettings.sounds.adas.FORWARD_COLLISION_WARNING)) {
                        customSettings.sounds.adas.FORWARD_COLLISION_WARNING = settings.sounds.adas.FORWARD_COLLISION_WARNING

                    }
                    if (settings.sounds.adas.HEADWAY_MONITORING_WARNING && !_.isEqual(settings.sounds.adas.HEADWAY_MONITORING_WARNING, customSettings.sounds.adas.HEADWAY_MONITORING_WARNING)) {
                        customSettings.sounds.adas.HEADWAY_MONITORING_WARNING = settings.sounds.adas.HEADWAY_MONITORING_WARNING

                    }
                    if (settings.sounds.adas.HEADWAY_MONITORING_EMERGENCY && !_.isEqual(settings.sounds.adas.HEADWAY_MONITORING_EMERGENCY, customSettings.sounds.adas.HEADWAY_MONITORING_EMERGENCY)) {
                        customSettings.sounds.adas.HEADWAY_MONITORING_EMERGENCY = settings.sounds.adas.HEADWAY_MONITORING_EMERGENCY

                    }
                    if (settings.sounds.adas.LANE_DEPARTURE_WARNING && !_.isEqual(settings.sounds.adas.LANE_DEPARTURE_WARNING, customSettings.sounds.adas.LANE_DEPARTURE_WARNING)) {
                        customSettings.sounds.adas.LANE_DEPARTURE_WARNING = settings.sounds.adas.LANE_DEPARTURE_WARNING
                    }

                    if (settings.sounds.adas.STOP_SIGN_VIOLATION && !_.isEqual(settings.sounds.adas.STOP_SIGN_VIOLATION, customSettings.sounds.adas.STOP_SIGN_VIOLATION)) {
                        customSettings.sounds.adas.STOP_SIGN_VIOLATION = settings.sounds.adas.STOP_SIGN_VIOLATION
                    }

                    if (settings.sounds.adas.PEDESTRIAN_COLLISION && !_.isEqual(settings.sounds.adas.PEDESTRIAN_COLLISION, customSettings.sounds.adas.PEDESTRIAN_COLLISION)) {
                        customSettings.sounds.adas.PEDESTRIAN_COLLISION = settings.sounds.adas.PEDESTRIAN_COLLISION
                    }

                    if (settings.sounds.adas.CYCLIST_COLLISION && !_.isEqual(settings.sounds.adas.CYCLIST_COLLISION, customSettings.sounds.adas.CYCLIST_COLLISION)) {
                        customSettings.sounds.adas.CYCLIST_COLLISION = settings.sounds.adas.CYCLIST_COLLISION
                    }
                }

                if (settings.sounds.nav && !_.isEqual(settings.sounds.nav, customSettings.sounds.nav)) {
                    if (settings?.sounds?.nav?.SPEEDING && customSettings?.sounds?.nav?.SPEEDING && !_.isEqual(settings.sounds.nav.SPEEDING, customSettings.sounds.nav.SPEEDING)) {
                        customSettings.sounds.nav.SPEEDING = settings.sounds.nav.SPEEDING
                    }
                    if (settings?.sounds?.nav?.OVER_MAX_SPEED && customSettings?.sounds?.nav?.OVER_MAX_SPEED && !_.isEqual(settings.sounds.nav.OVER_MAX_SPEED, customSettings.sounds.nav.OVER_MAX_SPEED)) {
                        customSettings.sounds.nav.OVER_MAX_SPEED = settings.sounds.nav.OVER_MAX_SPEED
                    }
                }

                if (settings.sounds.acc && !_.isEqual(settings.sounds.acc, customSettings.sounds.acc)) {
                    if (!customSettings.sounds.acc)
                        customSettings.sounds.acc = {}
                    if (settings?.sounds?.acc?.HARD_ACCEL && !_.isEqual(settings?.sounds?.acc?.HARD_ACCEL, customSettings?.sounds?.acc?.HARD_ACCEL)) {
                        customSettings.sounds.acc.HARD_ACCEL = settings?.sounds?.acc?.HARD_ACCEL
                    }
                    if (settings?.sounds?.acc?.HARSH_ACCEL && !_.isEqual(settings?.sounds?.acc?.HARSH_ACCEL, customSettings?.sounds?.acc?.HARSH_ACCEL)) {
                        customSettings.sounds.acc.HARSH_ACCEL = settings?.sounds?.acc?.HARSH_ACCEL
                    }
                    if (settings?.sounds?.acc?.SEVERE_ACCEL && !_.isEqual(settings?.sounds?.acc?.SEVERE_ACCEL, customSettings?.sounds?.acc?.SEVERE_ACCEL)) {
                        customSettings.sounds.acc.SEVERE_ACCEL = settings?.sounds?.acc?.SEVERE_ACCEL
                    }

                    if (settings?.sounds?.acc?.HARD_BRAKE && !_.isEqual(settings?.sounds?.acc?.HARD_BRAKE, customSettings?.sounds?.acc?.HARD_BRAKE)) {
                        customSettings.sounds.acc.HARD_BRAKE = settings?.sounds?.acc?.HARD_BRAKE
                    }
                    if (settings?.sounds?.acc?.HARSH_BRAKE && !_.isEqual(settings?.sounds?.acc?.HARSH_BRAKE, customSettings?.sounds?.acc?.HARSH_BRAKE)) {
                        customSettings.sounds.acc.HARSH_BRAKE = settings?.sounds?.acc?.HARSH_BRAKE
                    }
                    if (settings?.sounds?.acc?.SEVERE_BRAKE && !_.isEqual(settings?.sounds?.acc?.SEVERE_BRAKE, customSettings?.sounds?.acc?.SEVERE_BRAKE)) {
                        customSettings.sounds.acc.SEVERE_BRAKE = settings?.sounds?.acc?.SEVERE_BRAKE
                    }

                    if (settings?.sounds?.acc?.SHARP_TURN && !_.isEqual(settings?.sounds?.acc?.SHARP_TURN, customSettings?.sounds?.acc?.SHARP_TURN)) {
                        customSettings.sounds.acc.SHARP_TURN = settings?.sounds?.acc?.SHARP_TURN
                    }
                    if (settings?.sounds?.acc?.HARSH_TURN && !_.isEqual(settings?.sounds?.acc?.HARSH_TURN, customSettings?.sounds?.acc?.HARSH_TURN)) {
                        customSettings.sounds.acc.HARSH_TURN = settings?.sounds?.acc?.HARSH_TURN
                    }
                    if (settings?.sounds?.acc?.SEVERE_TURN && !_.isEqual(settings?.sounds?.acc?.SEVERE_TURN, customSettings?.sounds?.acc?.SEVERE_TURN)) {
                        customSettings.sounds.acc.SEVERE_TURN = settings?.sounds?.acc?.SEVERE_TURN
                    }
                }
            }

        }
        // eventLevelThreshold
        {
            //event level
            if (settings.eventLevel && !_.isEqual(settings.eventLevel, customSettings.eventLevel)) {

                if (settings.eventLevel.HARD_ACCEL && !_.isEqual(settings.eventLevel.HARD_ACCEL, customSettings.eventLevel.HARD_ACCEL)) {
                    customSettings.eventLevel.HARD_ACCEL = settings.eventLevel.HARD_ACCEL;
                }
                if (settings.eventLevel.HARD_BRAKE && !_.isEqual(settings.eventLevel.HARD_BRAKE, customSettings.eventLevel.HARD_BRAKE)) {
                    customSettings.eventLevel.HARD_BRAKE = settings.eventLevel.HARD_BRAKE;
                }
                if (settings.eventLevel.SHARP_TURN && !_.isEqual(settings.eventLevel.SHARP_TURN, customSettings.eventLevel.SHARP_TURN)) {
                    customSettings.eventLevel.SHARP_TURN = settings.eventLevel.SHARP_TURN;
                }
                /* if (settings.eventLevel.DRIVING_HIT && !_.isEqual(settings.eventLevel.DRIVING_HIT, customSettings.eventLevel.DRIVING_HIT)) {
                    customSettings.eventLevel.DRIVING_HIT = settings.eventLevel.DRIVING_HIT;
                } */
                if (settings.eventLevel.POWER_LOST && !_.isEqual(settings.eventLevel.POWER_LOST, customSettings.eventLevel.POWER_LOST)) {
                    customSettings.eventLevel.POWER_LOST = settings.eventLevel.POWER_LOST;
                }

                if (settings.eventLevel.POTENTIAL_INCIDENT && !_.isEqual(settings.eventLevel.POTENTIAL_INCIDENT, customSettings.eventLevel.POTENTIAL_INCIDENT)) {
                    customSettings.eventLevel.POTENTIAL_INCIDENT = settings.eventLevel.POTENTIAL_INCIDENT;
                }

                if (settings.eventLevel.PARKING_HIT && !_.isEqual(settings.eventLevel.PARKING_HIT, customSettings.eventLevel.PARKING_HIT)) {
                    customSettings.eventLevel.PARKING_HIT = settings.eventLevel.PARKING_HIT;
                }

                if (settings.eventLevel.DMS && !_.isEqual(settings.eventLevel.DMS, customSettings.eventLevel.DMS)) {

                    if (settings.eventLevel.DMS.NO_DRIVER && !_.isEqual(settings.eventLevel.DMS.NO_DRIVER, customSettings.eventLevel.DMS.NO_DRIVER)) {
                        customSettings.eventLevel.DMS.NO_DRIVER = settings.eventLevel.DMS.NO_DRIVER;
                    }
                    if (settings.eventLevel.DMS.ASLEEP && !_.isEqual(settings.eventLevel.DMS.ASLEEP, customSettings.eventLevel.DMS.ASLEEP)) {
                        customSettings.eventLevel.DMS.ASLEEP = settings.eventLevel.DMS.ASLEEP;
                    }
                    if (settings.eventLevel.DMS.DROWSINESS && !_.isEqual(settings.eventLevel.DMS.DROWSINESS, customSettings.eventLevel.DMS.DROWSINESS)) {
                        customSettings.eventLevel.DMS.DROWSINESS = settings.eventLevel.DMS.DROWSINESS;
                    }
                    if (settings.eventLevel.DMS.DAYDREAMING && !_.isEqual(settings.eventLevel.DMS.DAYDREAMING, customSettings.eventLevel.DMS.DAYDREAMING)) {
                        customSettings.eventLevel.DMS.DAYDREAMING = settings.eventLevel.DMS.DAYDREAMING;
                    }
                    if (settings.eventLevel.DMS.YAWN && !_.isEqual(settings.eventLevel.DMS.YAWN, customSettings.eventLevel.DMS.YAWN)) {
                        customSettings.eventLevel.DMS.YAWN = settings.eventLevel.DMS.YAWN;
                    }
                    if (settings.eventLevel.DMS.USING_PHONE && !_.isEqual(settings.eventLevel.DMS.USING_PHONE, customSettings.eventLevel.DMS.USING_PHONE)) {
                        customSettings.eventLevel.DMS.USING_PHONE = settings.eventLevel.DMS.USING_PHONE;
                    }
                    if (settings.eventLevel.DMS.DISTRACTED && !_.isEqual(settings.eventLevel.DMS.DISTRACTED, customSettings.eventLevel.DMS.DISTRACTED)) {
                        customSettings.eventLevel.DMS.DISTRACTED = settings.eventLevel.DMS.DISTRACTED;
                    }
                    if (settings.eventLevel.DMS.SMOKING && !_.isEqual(settings.eventLevel.DMS.SMOKING, customSettings.eventLevel.DMS.SMOKING)) {
                        customSettings.eventLevel.DMS.SMOKING = settings.eventLevel.DMS.SMOKING;
                    }
                    if (settings.eventLevel.DMS.NO_SEATBELT && !_.isEqual(settings.eventLevel.DMS.NO_SEATBELT, customSettings.eventLevel.DMS.NO_SEATBELT)) {
                        customSettings.eventLevel.DMS.NO_SEATBELT = settings.eventLevel.DMS.NO_SEATBELT;
                    }
                    if (settings.eventLevel.DMS.IDLE_NO_DRIVER && !_.isEqual(settings.eventLevel.DMS.IDLE_NO_DRIVER, customSettings.eventLevel.DMS.IDLE_NO_DRIVER)) {
                        customSettings.eventLevel.DMS.IDLE_NO_DRIVER = settings.eventLevel.DMS.IDLE_NO_DRIVER;
                    }
                    if (settings.eventLevel.DMS.PASSENGERS_INSIDE && !_.isEqual(settings.eventLevel.DMS.PASSENGERS_INSIDE, customSettings.eventLevel.DMS.PASSENGERS_INSIDE)) {
                        customSettings.eventLevel.DMS.PASSENGERS_INSIDE = settings.eventLevel.DMS.PASSENGERS_INSIDE;
                    }
                    if (settings.eventLevel.DMS.PASSENGERS_NO_SEATBELT && !_.isEqual(settings.eventLevel.DMS.PASSENGERS_NO_SEATBELT, customSettings.eventLevel.DMS.PASSENGERS_NO_SEATBELT)) {
                        customSettings.eventLevel.DMS.PASSENGERS_NO_SEATBELT = settings.eventLevel.DMS.PASSENGERS_NO_SEATBELT;
                    }


                }

                if (settings.eventLevel.ADAS && !_.isEqual(settings.eventLevel.ADAS, customSettings.eventLevel.ADAS)) {

                    if (settings.eventLevel.ADAS.FORWARD_COLLISION && !_.isEqual(settings.eventLevel.ADAS.FORWARD_COLLISION, customSettings.eventLevel.ADAS.FORWARD_COLLISION)) {
                        customSettings.eventLevel.ADAS.FORWARD_COLLISION = settings.eventLevel.ADAS.FORWARD_COLLISION;
                    }
                    if (settings.eventLevel.ADAS.HEADWAY_MONITORING && !_.isEqual(settings.eventLevel.ADAS.HEADWAY_MONITORING, customSettings.eventLevel.ADAS.HEADWAY_MONITORING)) {
                        customSettings.eventLevel.ADAS.HEADWAY_MONITORING = settings.eventLevel.ADAS.HEADWAY_MONITORING;
                    }
                    if (settings.eventLevel.ADAS.LANE_DEPARTURE && !_.isEqual(settings.eventLevel.ADAS.LANE_DEPARTURE, customSettings.eventLevel.ADAS.LANE_DEPARTURE)) {
                        customSettings.eventLevel.ADAS.LANE_DEPARTURE = settings.eventLevel.ADAS.LANE_DEPARTURE;
                    }
                    if (settings.eventLevel.ADAS.STOP_SIGN_VIOLATION && !_.isEqual(settings.eventLevel.ADAS.STOP_SIGN_VIOLATION, customSettings.eventLevel.ADAS.STOP_SIGN_VIOLATION)) {
                        customSettings.eventLevel.ADAS.STOP_SIGN_VIOLATION = settings.eventLevel.ADAS.STOP_SIGN_VIOLATION;
                    }
                    if (settings.eventLevel.ADAS.PEDESTRIAN_COLLISION && !_.isEqual(settings.eventLevel.ADAS.PEDESTRIAN_COLLISION, customSettings.eventLevel.ADAS.PEDESTRIAN_COLLISION)) {
                        customSettings.eventLevel.ADAS.PEDESTRIAN_COLLISION = settings.eventLevel.ADAS.PEDESTRIAN_COLLISION;
                    }
                    if (settings.eventLevel.ADAS.CYCLIST_COLLISION && !_.isEqual(settings.eventLevel.ADAS.CYCLIST_COLLISION, customSettings.eventLevel.ADAS.CYCLIST_COLLISION)) {
                        customSettings.eventLevel.ADAS.CYCLIST_COLLISION = settings.eventLevel.ADAS.CYCLIST_COLLISION;
                    }

                }

                if (settings.eventLevel.NAV && !_.isEqual(settings.eventLevel.NAV, customSettings.eventLevel.NAV)) {

                    if (settings?.eventLevel?.NAV?.SPEEDING && customSettings?.eventLevel?.NAV?.SPEEDING && !_.isEqual(settings.eventLevel.NAV.SPEEDING, customSettings.eventLevel.NAV.SPEEDING)) {
                        customSettings.eventLevel.NAV.SPEEDING = settings.eventLevel.NAV.SPEEDING;
                    }
                    if (settings?.eventLevel?.NAV?.OVER_MAX_SPEED && customSettings?.eventLevel?.NAV?.OVER_MAX_SPEED && !_.isEqual(settings.eventLevel.NAV.OVER_MAX_SPEED, customSettings.eventLevel.NAV.OVER_MAX_SPEED)) {
                        customSettings.eventLevel.NAV.OVER_MAX_SPEED = settings.eventLevel.NAV.OVER_MAX_SPEED;
                    }
                }
            }

            //event Threshold
            if (settings.eventThreshold && !_.isEqual(settings.eventThreshold, customSettings.eventThreshold)) {

                if ((settings.eventThreshold.accValue || settings.eventThreshold.accValue === 0) && !_.isEqual(settings.eventThreshold.accValue, customSettings.eventThreshold.accValue)) {
                    customSettings.eventThreshold.accValue = settings.eventThreshold.accValue;
                }
                if ((settings.eventThreshold.brakeValue || settings.eventThreshold.brakeValue === 0) && !_.isEqual(settings.eventThreshold.brakeValue, customSettings.eventThreshold.brakeValue)) {
                    customSettings.eventThreshold.brakeValue = settings.eventThreshold.brakeValue;
                }
                if ((settings.eventThreshold.turnValue || settings.eventThreshold.turnValue === 0) && !_.isEqual(settings.eventThreshold.turnValue, customSettings.eventThreshold.turnValue)) {
                    customSettings.eventThreshold.turnValue = settings.eventThreshold.turnValue;
                }
                if ((settings.eventThreshold.accDuration || settings.eventThreshold.accDuration === 0) && !_.isEqual(settings.eventThreshold.accDuration, customSettings.eventThreshold.accDuration)) {
                    customSettings.eventThreshold.accDuration = settings.eventThreshold.accDuration;
                }
                if ((settings.eventThreshold.brakeDuration || settings.eventThreshold.brakeDuration === 0) && !_.isEqual(settings.eventThreshold.brakeDuration, customSettings.eventThreshold.brakeDuration)) {
                    customSettings.eventThreshold.brakeDuration = settings.eventThreshold.brakeDuration;
                }
                if ((settings.eventThreshold.turnDuration || settings.eventThreshold.turnDuration === 0) && !_.isEqual(settings.eventThreshold.turnDuration, customSettings.eventThreshold.turnDuration)) {
                    customSettings.eventThreshold.turnDuration = settings.eventThreshold.turnDuration;
                }
                //if (!_.isEqual(settings.eventThreshold.steps, customSettings.eventThreshold.steps)) {
                //  customSettings.eventThreshold.steps = settings.eventThreshold.steps;
                // }
            }
        }

        // videoStreamDefault: true,
        {
            if (settings.videoStreams && !_.isEqual(settings.videoStreams, customSettings.videoStreams)) {
                customSettings.videoStreams = settings.videoStreams;
            }
        }

        // videoResolutionDefault: true,
        {
            if (settings.recordConfig && !_.isEqual(settings.recordConfig, customSettings.recordConfig)) {
                customSettings.recordConfig = settings.recordConfig;
            }

        }

        // delayedPowerOffDefault: true,
        {
            if ((settings.delayedPowerOff || settings.delayedPowerOff === 0) && !_.isEqual(settings.delayedPowerOff, customSettings.delayedPowerOff)) {
                customSettings.delayedPowerOff = settings.delayedPowerOff;
            }
        }
        // liveStreamDurationDefault: true,
        {
            if ((settings.liveStreamDuration || settings.liveStreamDuration === 0) && !_.isEqual(settings.liveStreamDuration, customSettings.liveStreamDuration)) {
                customSettings.liveStreamDuration = settings.liveStreamDuration;
            }

        }
        // autoSnapshotDefault: true,
        {
            if (settings.autoSnapshot && !_.isEqual(settings.autoSnapshot, customSettings.autoSnapshot)) {
                customSettings.autoSnapshot = settings.autoSnapshot;
            }
        }
        // runningInfoDefault: true,
        {
            if (settings.runningInfo && !_.isEqual(settings.runningInfo, customSettings.runningInfo)) {
                customSettings.runningInfo = settings.runningInfo;
            }
        }
        // micDefault: true,
        {
            if (settings.mic && !_.isEqual(settings.mic, customSettings.mic)) {
                customSettings.mic = settings.mic;
            }
        }
        // privacyMaskDefault: true,
        {
            if (settings.privacyMask && !_.isEqual(settings.privacyMask, customSettings.privacyMask)) {
                customSettings.privacyMask = settings.privacyMask;
            }
        }
        // rotateDefault: true,
        {
            if (settings.rotate && !_.isEqual(settings.rotate, customSettings.rotate)) {
                customSettings.rotate = settings.rotate;
            } else {
                customSettings.rotate = "upsidedown";
            }
        }

        // markedClipDurationDefault: true,
        {
            if (settings.markedClipDuration && !_.isEqual(settings.markedClipDuration, customSettings.markedClipDuration)) {


                if ((settings.markedClipDuration.accel || settings.markedClipDuration.accel === 0) && !_.isEqual(settings.markedClipDuration.accel, customSettings.markedClipDuration.accel)) {
                    customSettings.markedClipDuration.accel = settings.markedClipDuration.accel;
                }

                if ((settings.markedClipDuration.manual || settings.markedClipDuration.manual === 0) && !_.isEqual(settings.markedClipDuration.manual, customSettings.markedClipDuration.manual)) {
                    customSettings.markedClipDuration.manual = settings.markedClipDuration.manual;
                }

                if ((settings.markedClipDuration.dms || settings.markedClipDuration.dms === 0) && !_.isEqual(settings.markedClipDuration.dms, customSettings.markedClipDuration.dms)) {
                    customSettings.markedClipDuration.dms = settings.markedClipDuration.dms;
                }

                if ((settings.markedClipDuration.adas || settings.markedClipDuration.adas === 0) && !_.isEqual(settings.markedClipDuration.adas, customSettings.markedClipDuration.adas)) {
                    customSettings.markedClipDuration.adas = settings.markedClipDuration.adas;
                }

                if ((settings.markedClipDuration.nav || settings.markedClipDuration.nav === 0) && !_.isEqual(settings.markedClipDuration.nav, customSettings.markedClipDuration.nav)) {
                    customSettings.markedClipDuration.nav = settings.markedClipDuration.nav;
                }

                if (settings.markedClipDuration.enableEventThumbnail && !_.isEqual(settings.markedClipDuration.enableEventThumbnail, customSettings.markedClipDuration.enableEventThumbnail)) {
                    customSettings.markedClipDuration.enableEventThumbnail = settings.markedClipDuration.enableEventThumbnail;
                }
            }

        }
        // trustAccDefault: true,
        {
            if (!_.isEqual(settings.trustAcc, customSettings.trustAcc)) {
                if (settings.trustAcc != null)
                    customSettings.trustAcc = settings.trustAcc;
                else
                    customSettings.trustAcc = true;//default value from waylens
            }

        }
        // nightVisionDefault: true,
        {
            if (settings.nightVision && !_.isEqual(settings.nightVision, customSettings.nightVision)) {
                customSettings.nightVision = settings.nightVision;
            }
        }
        // volumeDefault: true,
        {
            if ((settings.volume || settings.volume === 0) && !_.isEqual(settings.volume, customSettings.volume)) {
                customSettings.volume = settings.volume;
            }
        }
        // obdIIDefault: true,
        {
            if (settings.obdII && !_.isEqual(settings.obdII, customSettings.obdII)) {
                if (settings.obdII.mode !== "Auto") {
                    customSettings.obdII = settings.obdII;
                } else {
                    customSettings.obdII.mode = "Auto"
                    customSettings.obdII.dataUpload = settings.obdII.dataUpload;
                    customSettings.obdII.uploadInterval = settings.obdII.uploadInterval;
                    customSettings.obdII.uploadMode = settings.obdII.uploadMode;
                }
            }
        }

        // DMS Events,
        {
            if (settings.dms && !_.isEqual(settings.dms, customSettings.dms)) {

                // dms level
                if (settings.dms.level && !_.isEqual(settings.dms.level, customSettings.dms.level)) {
                    customSettings.dms.level = settings.dms.level;
                }
                //minSpeed
                if (settings.dms.minSpeed && !_.isEqual(settings.dms.minSpeed, customSettings.dms.minSpeed)) {

                    if (customSettings.dms.minSpeed) {
                        if ((settings.dms.minSpeed.NO_DRIVER || settings.dms.minSpeed.NO_DRIVER === 0) && !_.isEqual(settings.dms.minSpeed.NO_DRIVER, customSettings.dms.minSpeed.NO_DRIVER)) {
                            customSettings.dms.minSpeed.NO_DRIVER = settings.dms.minSpeed.NO_DRIVER;

                        }
                        if ((settings.dms.minSpeed.ASLEEP || settings.dms.minSpeed.ASLEEP === 0) && !_.isEqual(settings.dms.minSpeed.ASLEEP, customSettings.dms.minSpeed.ASLEEP)) {
                            customSettings.dms.minSpeed.ASLEEP = settings.dms.minSpeed.ASLEEP;

                        }
                        if ((settings.dms.minSpeed.DROWSINESS || settings.dms.minSpeed.DROWSINESS === 0) && !_.isEqual(settings.dms.minSpeed.DROWSINESS, customSettings.dms.minSpeed.DROWSINESS)) {
                            customSettings.dms.minSpeed.DROWSINESS = settings.dms.minSpeed.DROWSINESS;

                        }
                        if ((settings.dms.minSpeed.DAYDREAMING || settings.dms.minSpeed.DAYDREAMING === 0) && !_.isEqual(settings.dms.minSpeed.DAYDREAMING, customSettings.dms.minSpeed.DAYDREAMING)) {
                            customSettings.dms.minSpeed.DAYDREAMING = settings.dms.minSpeed.DAYDREAMING;

                        }
                        if ((settings.dms.minSpeed.YAWN || settings.dms.minSpeed.YAWN === 0) && !_.isEqual(settings.dms.minSpeed.YAWN, customSettings.dms.minSpeed.YAWN)) {
                            customSettings.dms.minSpeed.YAWN = settings.dms.minSpeed.YAWN;

                        }
                        if ((settings.dms.minSpeed.USING_PHONE || settings.dms.minSpeed.USING_PHONE === 0) && !_.isEqual(settings.dms.minSpeed.USING_PHONE, customSettings.dms.minSpeed.USING_PHONE)) {
                            customSettings.dms.minSpeed.USING_PHONE = settings.dms.minSpeed.USING_PHONE;

                        }
                        if ((settings.dms.minSpeed.DISTRACTED || settings.dms.minSpeed.DISTRACTED === 0) && !_.isEqual(settings.dms.minSpeed.DISTRACTED, customSettings.dms.minSpeed.DISTRACTED)) {
                            customSettings.dms.minSpeed.DISTRACTED = settings.dms.minSpeed.DISTRACTED;

                        }
                        if ((settings.dms.minSpeed.SMOKING || settings.dms.minSpeed.SMOKING === 0) && !_.isEqual(settings.dms.minSpeed.SMOKING, customSettings.dms.minSpeed.SMOKING)) {
                            customSettings.dms.minSpeed.SMOKING = settings.dms.minSpeed.SMOKING;

                        }
                        if ((settings.dms.minSpeed.NO_SEATBELT || settings.dms.minSpeed.NO_SEATBELT === 0) && !_.isEqual(settings.dms.minSpeed.NO_SEATBELT, customSettings.dms.minSpeed.NO_SEATBELT)) {
                            customSettings.dms.minSpeed.NO_SEATBELT = settings.dms.minSpeed.NO_SEATBELT;

                        }
                        if ((settings.dms.minSpeed.ATTENTIVE || settings.dms.minSpeed.ATTENTIVE === 0) && !_.isEqual(settings.dms.minSpeed.ATTENTIVE, customSettings.dms.minSpeed.ATTENTIVE)) {
                            customSettings.dms.minSpeed.ATTENTIVE = settings.dms.minSpeed.ATTENTIVE;

                        }
                        if ((settings.dms.minSpeed.IDLE_NO_DRIVER || settings.dms.minSpeed.IDLE_NO_DRIVER === 0) && !_.isEqual(settings.dms.minSpeed.IDLE_NO_DRIVER, customSettings.dms.minSpeed.IDLE_NO_DRIVER)) {
                            customSettings.dms.minSpeed.IDLE_NO_DRIVER = settings.dms.minSpeed.IDLE_NO_DRIVER;

                        }
                        if ((settings.dms.minSpeed.PASSENGERS_INSIDE || settings.dms.minSpeed.PASSENGERS_INSIDE === 0) && !_.isEqual(settings.dms.minSpeed.PASSENGERS_INSIDE, customSettings.dms.minSpeed.PASSENGERS_INSIDE)) {
                            customSettings.dms.minSpeed.PASSENGERS_INSIDE = settings.dms.minSpeed.PASSENGERS_INSIDE;

                        }
                        if ((settings.dms.minSpeed.PASSENGERS_NO_SEATBELT || settings.dms.minSpeed.PASSENGERS_NO_SEATBELT === 0) && !_.isEqual(settings.dms.minSpeed.PASSENGERS_NO_SEATBELT, customSettings.dms.minSpeed.PASSENGERS_NO_SEATBELT)) {
                            customSettings.dms.minSpeed.PASSENGERS_NO_SEATBELT = settings.dms.minSpeed.PASSENGERS_NO_SEATBELT;

                        }

                    } else
                        customSettings.dms.minSpeed = settings.dms.minSpeed
                }

                //detectionDelay
                if (settings.dms.detectionDelay && !_.isEqual(settings.dms.detectionDelay, customSettings.dms.detectionDelay)) {

                    if (customSettings.dms.detectionDelay) {
                        if ((settings.dms.detectionDelay.NO_DRIVER || settings.dms.detectionDelay.NO_DRIVER === 0) && !_.isEqual(settings.dms.detectionDelay.NO_DRIVER, customSettings.dms.detectionDelay.NO_DRIVER)) {
                            customSettings.dms.detectionDelay.NO_DRIVER = settings.dms.detectionDelay.NO_DRIVER;

                        }
                        if ((settings.dms.detectionDelay.ASLEEP || settings.dms.detectionDelay.ASLEEP === 0) && !_.isEqual(settings.dms.detectionDelay.ASLEEP, customSettings.dms.detectionDelay.ASLEEP)) {
                            customSettings.dms.detectionDelay.ASLEEP = settings.dms.detectionDelay.ASLEEP;

                        }
                        if ((settings.dms.detectionDelay.DROWSINESS || settings.dms.detectionDelay.DROWSINESS === 0) && !_.isEqual(settings.dms.detectionDelay.DROWSINESS, customSettings.dms.detectionDelay.DROWSINESS)) {
                            customSettings.dms.detectionDelay.DROWSINESS = settings.dms.detectionDelay.DROWSINESS;

                        }
                        if ((settings.dms.detectionDelay.DAYDREAMING || settings.dms.detectionDelay.DAYDREAMING === 0) && !_.isEqual(settings.dms.detectionDelay.DAYDREAMING, customSettings.dms.detectionDelay.DAYDREAMING)) {
                            customSettings.dms.detectionDelay.DAYDREAMING = settings.dms.detectionDelay.DAYDREAMING;

                        }
                        if ((settings.dms.detectionDelay.YAWN || settings.dms.detectionDelay.YAWN === 0) && !_.isEqual(settings.dms.detectionDelay.YAWN, customSettings.dms.detectionDelay.YAWN)) {
                            customSettings.dms.detectionDelay.YAWN = settings.dms.detectionDelay.YAWN;

                        }
                        if ((settings.dms.detectionDelay.USING_PHONE || settings.dms.detectionDelay.USING_PHONE === 0) && !_.isEqual(settings.dms.detectionDelay.USING_PHONE, customSettings.dms.detectionDelay.USING_PHONE)) {
                            customSettings.dms.detectionDelay.USING_PHONE = settings.dms.detectionDelay.USING_PHONE;

                        }
                        if ((settings.dms.detectionDelay.DISTRACTED || settings.dms.detectionDelay.DISTRACTED === 0) && !_.isEqual(settings.dms.detectionDelay.DISTRACTED, customSettings.dms.detectionDelay.DISTRACTED)) {
                            customSettings.dms.detectionDelay.DISTRACTED = settings.dms.detectionDelay.DISTRACTED;

                        }
                        if ((settings.dms.detectionDelay.SMOKING || settings.dms.detectionDelay.SMOKING === 0) && !_.isEqual(settings.dms.detectionDelay.SMOKING, customSettings.dms.detectionDelay.SMOKING)) {
                            customSettings.dms.detectionDelay.SMOKING = settings.dms.detectionDelay.SMOKING;

                        }
                        if ((settings.dms.detectionDelay.NO_SEATBELT || settings.dms.detectionDelay.NO_SEATBELT === 0) && !_.isEqual(settings.dms.detectionDelay.NO_SEATBELT, customSettings.dms.detectionDelay.NO_SEATBELT)) {
                            customSettings.dms.detectionDelay.NO_SEATBELT = settings.dms.detectionDelay.NO_SEATBELT;

                        }
                        if ((settings.dms.detectionDelay.ATTENTIVE || settings.dms.detectionDelay.ATTENTIVE === 0) && !_.isEqual(settings.dms.detectionDelay.ATTENTIVE, customSettings.dms.detectionDelay.ATTENTIVE)) {
                            customSettings.dms.detectionDelay.ATTENTIVE = settings.dms.detectionDelay.ATTENTIVE === 0 ? 5 : settings.dms.detectionDelay.ATTENTIVE;

                        }
                        if ((settings.dms.detectionDelay.IDLE_NO_DRIVER || settings.dms.detectionDelay.IDLE_NO_DRIVER === 0) && !_.isEqual(settings.dms.detectionDelay.IDLE_NO_DRIVER, customSettings.dms.detectionDelay.IDLE_NO_DRIVER)) {
                            customSettings.dms.detectionDelay.IDLE_NO_DRIVER = settings.dms.detectionDelay.IDLE_NO_DRIVER;

                        }
                        if ((settings.dms.detectionDelay.PASSENGERS_INSIDE || settings.dms.detectionDelay.PASSENGERS_INSIDE === 0) && !_.isEqual(settings.dms.detectionDelay.PASSENGERS_INSIDE, customSettings.dms.detectionDelay.PASSENGERS_INSIDE)) {
                            customSettings.dms.detectionDelay.PASSENGERS_INSIDE = settings.dms.detectionDelay.PASSENGERS_INSIDE;

                        }
                        if ((settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT || settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT === 0) && !_.isEqual(settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT, customSettings.dms.detectionDelay.PASSENGERS_NO_SEATBELT)) {
                            customSettings.dms.detectionDelay.PASSENGERS_NO_SEATBELT = settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT;

                        }

                    } else
                        customSettings.dms.detectionDelay = settings.dms.detectionDelay
                }

                //repeatInterval
                if (settings.dms.repeatInterval && !_.isEqual(settings.dms.repeatInterval, customSettings.dms.repeatInterval)) {

                    if (customSettings.dms.repeatInterval) {
                        if ((settings.dms.repeatInterval.NO_DRIVER || settings.dms.repeatInterval.NO_DRIVER === 0) && !_.isEqual(settings.dms.repeatInterval.NO_DRIVER, customSettings.dms.repeatInterval.NO_DRIVER)) {
                            customSettings.dms.repeatInterval.NO_DRIVER = settings.dms.repeatInterval.NO_DRIVER;

                        }
                        if ((settings.dms.repeatInterval.ASLEEP || settings.dms.repeatInterval.ASLEEP === 0) && !_.isEqual(settings.dms.repeatInterval.ASLEEP, customSettings.dms.repeatInterval.ASLEEP)) {
                            customSettings.dms.repeatInterval.ASLEEP = settings.dms.repeatInterval.ASLEEP;

                        }
                        if ((settings.dms.repeatInterval.DROWSINESS || settings.dms.repeatInterval.DROWSINESS === 0) && !_.isEqual(settings.dms.repeatInterval.DROWSINESS, customSettings.dms.repeatInterval.DROWSINESS)) {
                            customSettings.dms.repeatInterval.DROWSINESS = settings.dms.repeatInterval.DROWSINESS;

                        }
                        if ((settings.dms.repeatInterval.DAYDREAMING || settings.dms.repeatInterval.DAYDREAMING === 0) && !_.isEqual(settings.dms.repeatInterval.DAYDREAMING, customSettings.dms.repeatInterval.DAYDREAMING)) {
                            customSettings.dms.repeatInterval.DAYDREAMING = settings.dms.repeatInterval.DAYDREAMING;

                        }
                        if ((settings.dms.repeatInterval.YAWN || settings.dms.repeatInterval.YAWN === 0) && !_.isEqual(settings.dms.repeatInterval.YAWN, customSettings.dms.repeatInterval.YAWN)) {
                            customSettings.dms.repeatInterval.YAWN = settings.dms.repeatInterval.YAWN;

                        }
                        if ((settings.dms.repeatInterval.USING_PHONE || settings.dms.repeatInterval.USING_PHONE === 0) && !_.isEqual(settings.dms.repeatInterval.USING_PHONE, customSettings.dms.repeatInterval.USING_PHONE)) {
                            customSettings.dms.repeatInterval.USING_PHONE = settings.dms.repeatInterval.USING_PHONE;

                        }
                        if ((settings.dms.repeatInterval.DISTRACTED || settings.dms.repeatInterval.DISTRACTED === 0) && !_.isEqual(settings.dms.repeatInterval.DISTRACTED, customSettings.dms.repeatInterval.DISTRACTED)) {
                            customSettings.dms.repeatInterval.DISTRACTED = settings.dms.repeatInterval.DISTRACTED;

                        }
                        if ((settings.dms.repeatInterval.SMOKING || settings.dms.repeatInterval.SMOKING === 0) && !_.isEqual(settings.dms.repeatInterval.SMOKING, customSettings.dms.repeatInterval.SMOKING)) {
                            customSettings.dms.repeatInterval.SMOKING = settings.dms.repeatInterval.SMOKING;

                        }
                        if ((settings.dms.repeatInterval.NO_SEATBELT || settings.dms.repeatInterval.NO_SEATBELT === 0) && !_.isEqual(settings.dms.repeatInterval.NO_SEATBELT, customSettings.dms.repeatInterval.NO_SEATBELT)) {
                            customSettings.dms.repeatInterval.NO_SEATBELT = settings.dms.repeatInterval.NO_SEATBELT;

                        }
                        if ((settings.dms.repeatInterval.ATTENTIVE || settings.dms.repeatInterval.ATTENTIVE === 0) && !_.isEqual(settings.dms.repeatInterval.ATTENTIVE, customSettings.dms.repeatInterval.ATTENTIVE)) {
                            customSettings.dms.repeatInterval.ATTENTIVE = settings.dms.repeatInterval.ATTENTIVE;

                        }
                        if ((settings.dms.repeatInterval.IDLE_NO_DRIVER || settings.dms.repeatInterval.IDLE_NO_DRIVER === 0) && !_.isEqual(settings.dms.repeatInterval.IDLE_NO_DRIVER, customSettings.dms.repeatInterval.IDLE_NO_DRIVER)) {
                            customSettings.dms.repeatInterval.IDLE_NO_DRIVER = settings.dms.repeatInterval.IDLE_NO_DRIVER;

                        }
                        if ((settings.dms.repeatInterval.PASSENGERS_INSIDE || settings.dms.repeatInterval.PASSENGERS_INSIDE === 0) && !_.isEqual(settings.dms.repeatInterval.PASSENGERS_INSIDE, customSettings.dms.repeatInterval.PASSENGERS_INSIDE)) {
                            customSettings.dms.repeatInterval.PASSENGERS_INSIDE = settings.dms.repeatInterval.PASSENGERS_INSIDE;

                        }
                        if ((settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT || settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT === 0) && !_.isEqual(settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT, customSettings.dms.repeatInterval.PASSENGERS_NO_SEATBELT)) {
                            customSettings.dms.repeatInterval.PASSENGERS_NO_SEATBELT = settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT;

                        }
                    } else
                        customSettings.dms.repeatInterval = settings.dms.repeatInterval
                }

                //detectionReset
                if (settings.dms.detectionReset && !_.isEqual(settings.dms.detectionReset, customSettings.dms.detectionReset)) {

                    if (customSettings.dms.detectionReset) {
                        if ((settings.dms.detectionReset.NO_DRIVER || settings.dms.detectionReset.NO_DRIVER === 0) && !_.isEqual(settings.dms.detectionReset.NO_DRIVER, customSettings.dms.detectionReset.NO_DRIVER)) {
                            customSettings.dms.detectionReset.NO_DRIVER = settings.dms.detectionReset.NO_DRIVER;
                        }
                        if ((settings.dms.detectionReset.ASLEEP || settings.dms.detectionReset.ASLEEP === 0) && !_.isEqual(settings.dms.detectionReset.ASLEEP, customSettings.dms.detectionReset.ASLEEP)) {
                            customSettings.dms.detectionReset.ASLEEP = settings.dms.detectionReset.ASLEEP;

                        }
                        if ((settings.dms.detectionReset.DROWSINESS || settings.dms.detectionReset.DROWSINESS === 0) && !_.isEqual(settings.dms.detectionReset.DROWSINESS, customSettings.dms.detectionReset.DROWSINESS)) {
                            customSettings.dms.detectionReset.DROWSINESS = settings.dms.detectionReset.DROWSINESS;

                        }
                        if ((settings.dms.detectionReset.DAYDREAMING || settings.dms.detectionReset.DAYDREAMING === 0) && !_.isEqual(settings.dms.detectionReset.DAYDREAMING, customSettings.dms.detectionReset.DAYDREAMING)) {
                            customSettings.dms.detectionReset.DAYDREAMING = settings.dms.detectionReset.DAYDREAMING;

                        }
                        if ((settings.dms.detectionReset.YAWN || settings.dms.detectionReset.YAWN === 0) && !_.isEqual(settings.dms.detectionReset.YAWN, customSettings.dms.detectionReset.YAWN)) {
                            customSettings.dms.detectionReset.YAWN = settings.dms.detectionReset.YAWN;

                        }
                        if ((settings.dms.detectionReset.USING_PHONE || settings.dms.detectionReset.USING_PHONE === 0) && !_.isEqual(settings.dms.detectionReset.USING_PHONE, customSettings.dms.detectionReset.USING_PHONE)) {
                            customSettings.dms.detectionReset.USING_PHONE = settings.dms.detectionReset.USING_PHONE;

                        }
                        if ((settings.dms.detectionReset.DISTRACTED || settings.dms.detectionReset.DISTRACTED === 0) && !_.isEqual(settings.dms.detectionReset.DISTRACTED, customSettings.dms.detectionReset.DISTRACTED)) {
                            customSettings.dms.detectionReset.DISTRACTED = settings.dms.detectionReset.DISTRACTED;

                        }
                        if ((settings.dms.detectionReset.SMOKING || settings.dms.detectionReset.SMOKING === 0) && !_.isEqual(settings.dms.detectionReset.SMOKING, customSettings.dms.detectionReset.SMOKING)) {
                            customSettings.dms.detectionReset.SMOKING = settings.dms.detectionReset.SMOKING;

                        }
                        if ((settings.dms.detectionReset.NO_SEATBELT || settings.dms.detectionReset.NO_SEATBELT === 0) && !_.isEqual(settings.dms.detectionReset.NO_SEATBELT, customSettings.dms.detectionReset.NO_SEATBELT)) {
                            customSettings.dms.detectionReset.NO_SEATBELT = settings.dms.detectionReset.NO_SEATBELT;

                        }
                        if ((settings.dms.detectionReset.ATTENTIVE || settings.dms.detectionReset.ATTENTIVE === 0) && !_.isEqual(settings.dms.detectionReset.ATTENTIVE, customSettings.dms.detectionReset.ATTENTIVE)) {
                            customSettings.dms.detectionReset.ATTENTIVE = settings.dms.detectionReset.ATTENTIVE;

                        }
                        if ((settings.dms.detectionReset.IDLE_NO_DRIVER || settings.dms.detectionReset.IDLE_NO_DRIVER === 0) && !_.isEqual(settings.dms.detectionReset.IDLE_NO_DRIVER, customSettings.dms.detectionReset.IDLE_NO_DRIVER)) {
                            customSettings.dms.detectionReset.IDLE_NO_DRIVER = settings.dms.detectionReset.IDLE_NO_DRIVER;

                        }
                        if ((settings.dms.detectionReset.PASSENGERS_INSIDE || settings.dms.detectionReset.PASSENGERS_INSIDE === 0) && !_.isEqual(settings.dms.detectionReset.PASSENGERS_INSIDE, customSettings.dms.detectionReset.PASSENGERS_INSIDE)) {
                            customSettings.dms.detectionReset.PASSENGERS_INSIDE = settings.dms.detectionReset.PASSENGERS_INSIDE;

                        }
                        if ((settings.dms.detectionReset.PASSENGERS_NO_SEATBELT || settings.dms.detectionReset.PASSENGERS_NO_SEATBELT === 0) && !_.isEqual(settings.dms.detectionReset.PASSENGERS_NO_SEATBELT, customSettings.dms.detectionReset.PASSENGERS_NO_SEATBELT)) {
                            customSettings.dms.detectionReset.PASSENGERS_NO_SEATBELT = settings.dms.detectionReset.PASSENGERS_NO_SEATBELT;

                        }
                    } else
                        customSettings.dms.detectionReset = settings.dms.detectionReset
                }
            }
        }

        // advancedDefault: true,
        {

            if (settings.advanced && !_.isEqual(settings.advanced, customSettings.advanced)) {

                if (settings.advanced.sdCardEventSpace && !_.isEqual(settings.advanced.sdCardEventSpace, customSettings.advanced.sdCardEventSpace)) {
                    customSettings.advanced.sdCardEventSpace = settings.advanced.sdCardEventSpace;
                }

                if (settings.advanced.adas && !_.isEqual(settings.advanced.adas, customSettings.advanced.adas)) {

                    // adas level
                    if (settings.advanced.adas.level && !_.isEqual(settings.advanced.adas, customSettings.advanced.adas.level)) {
                        customSettings.advanced.adas.level = settings.advanced.adas.level;
                    }

                    if (settings.advanced.adas.timeRatio && !_.isEqual(settings.advanced.adas.timeRatio, customSettings.advanced.adas.timeRatio)) {

                        if (settings.advanced.adas.timeRatio.FORWARD_COLLISION && !_.isEqual(settings.advanced.adas.timeRatio.FORWARD_COLLISION, customSettings.advanced.adas.timeRatio.FORWARD_COLLISION)) {
                            customSettings.advanced.adas.timeRatio.FORWARD_COLLISION = settings.advanced.adas.timeRatio.FORWARD_COLLISION < 72 ? 72 : settings.advanced.adas.timeRatio.FORWARD_COLLISION;
                        }
                        if (settings.advanced.adas.timeRatio.HEADWAY_MONITORING && !_.isEqual(settings.advanced.adas.timeRatio.HEADWAY_MONITORING, customSettings.advanced.adas.timeRatio.HEADWAY_MONITORING)) {
                            customSettings.advanced.adas.timeRatio.HEADWAY_MONITORING = settings.advanced.adas.timeRatio.HEADWAY_MONITORING;
                        }
                        if (settings.advanced.adas.timeRatio.PEDESTRIAN_COLLISION && !_.isEqual(settings.advanced.adas.timeRatio.PEDESTRIAN_COLLISION, customSettings.advanced.adas.timeRatio.PEDESTRIAN_COLLISION)) {
                            customSettings.advanced.adas.timeRatio.PEDESTRIAN_COLLISION = settings.advanced.adas.timeRatio.PEDESTRIAN_COLLISION;
                        }
                        if (settings.advanced.adas.timeRatio.CYCLIST_COLLISION && !_.isEqual(settings.advanced.adas.timeRatio.CYCLIST_COLLISION, customSettings.advanced.adas.timeRatio.CYCLIST_COLLISION)) {
                            customSettings.advanced.adas.timeRatio.CYCLIST_COLLISION = settings.advanced.adas.timeRatio.CYCLIST_COLLISION;
                        }
                    }
                    if ((settings.advanced.adas.cameraHeight || settings.advanced.adas.cameraHeight === 0) && !_.isEqual(settings.advanced.adas.cameraHeight, customSettings.advanced.adas.cameraHeight)) {
                        customSettings.advanced.adas.cameraHeight = settings.advanced.adas.cameraHeight;
                    }
                    if ((settings.advanced.adas.vehicleWidth || settings.advanced.adas.vehicleWidth === 0) && !_.isEqual(settings.advanced.adas.vehicleWidth, customSettings.advanced.adas.vehicleWidth)) {
                        customSettings.advanced.adas.vehicleWidth = settings.advanced.adas.vehicleWidth;
                    }
                    if ((settings.advanced.adas.cameraPositionToCenter || settings.advanced.adas.cameraPositionToCenter === 0) && !_.isEqual(settings.advanced.adas.cameraPositionToCenter, customSettings.advanced.adas.cameraPositionToCenter)) {
                        customSettings.advanced.adas.cameraPositionToCenter = settings.advanced.adas.cameraPositionToCenter;
                    }
                    if (settings.advanced.adas.minSpeed && !_.isEqual(settings.advanced.adas.minSpeed, customSettings.advanced.adas.minSpeed)) {

                        if (customSettings.advanced.adas.minSpeed) {

                            if (typeof settings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING !== "undefined" && !_.isEqual(settings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING, customSettings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING)) {
                                customSettings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING = settings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING;
                            }
                            if (typeof settings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING !== "undefined" && !_.isEqual(settings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING, customSettings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING)) {
                                customSettings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING = settings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING;
                            }

                            if (typeof settings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY !== "undefined" && !_.isEqual(settings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY, customSettings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY)) {
                                customSettings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY = settings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY;
                            }

                            if (typeof settings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING !== "undefined" && !_.isEqual(settings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING, customSettings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING)) {
                                customSettings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING = settings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING;
                            }

                            if (typeof settings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION !== "undefined" && !_.isEqual(settings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION, customSettings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION)) {
                                customSettings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION = settings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION;
                            }
                        } else
                            customSettings.advanced.adas.minSpeed = settings.advanced.adas.minSpeed
                    }

                    if (settings.advanced.adas.repeatInterval && !_.isEqual(settings.advanced.adas.repeatInterval, customSettings.advanced.adas.repeatInterval)) {
                        if (customSettings.advanced.adas.repeatInterval) {
                            if (typeof settings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING !== "undefined" && !_.isEqual(settings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING, customSettings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING)) {
                                customSettings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING = settings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING;
                            }
                            if (typeof settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING !== "undefined" && !_.isEqual(settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING, customSettings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING)) {
                                customSettings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING = settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING;
                            }
                            if (typeof settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY !== "undefined" && !_.isEqual(settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY, customSettings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY)) {
                                customSettings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY = settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY;
                            }
                            if (typeof settings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING !== "undefined" && !_.isEqual(settings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING, customSettings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING)) {
                                customSettings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING = settings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING;
                            }
                            if (typeof settings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION !== "undefined" && !_.isEqual(settings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION, customSettings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION)) {
                                customSettings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION = settings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION;
                            }
                        } else
                            customSettings.advanced.adas.repeatInterval = settings.advanced.adas.repeatInterval
                    }

                    if (settings.advanced.adas.detectionDelay && !_.isEqual(settings.advanced.adas.detectionDelay, customSettings.advanced.adas.detectionDelay)) {

                        if (customSettings.advanced.adas.detectionDelay) {
                            if (typeof settings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING !== "undefined" && !_.isEqual(settings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING, customSettings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING)) {
                                customSettings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING = settings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING;
                            }
                            if (typeof settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING !== "undefined" && !_.isEqual(settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING, customSettings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING)) {
                                customSettings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING = settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING;
                            }
                            if (typeof settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY !== "undefined" && !_.isEqual(settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY, customSettings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY)) {
                                customSettings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY = settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY;
                            }
                            if (typeof settings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING !== "undefined" && !_.isEqual(settings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING, customSettings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING)) {
                                customSettings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING = settings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING;
                            }
                            if (typeof settings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION !== "undefined" && !_.isEqual(settings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION, customSettings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION)) {
                                customSettings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION = settings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION;
                            }
                        } else
                            customSettings.advanced.adas.detectionDelay = settings.advanced.adas.detectionDelay
                    }

                    if (settings.advanced.adas.detectionReset && !_.isEqual(settings.advanced.adas.detectionReset, customSettings.advanced.adas.detectionReset)) {
                        if (customSettings.advanced.adas.detectionReset) {
                            if (typeof settings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING !== "undefined" && !_.isEqual(settings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING, customSettings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING)) {
                                customSettings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING = settings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING;
                            }
                            if (typeof settings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING !== "undefined" && !_.isEqual(settings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING, customSettings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING)) {
                                customSettings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING = settings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING;
                            }
                            if (typeof settings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY !== "undefined" && !_.isEqual(settings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY, customSettings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY)) {
                                customSettings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY = settings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY;
                            }
                            if (typeof settings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING !== "undefined" && !_.isEqual(settings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING, customSettings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING)) {
                                customSettings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING = settings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING;
                            }
                            if (typeof settings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION !== "undefined" && !_.isEqual(settings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION, customSettings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION)) {
                                customSettings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION = settings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION;
                            }
                        } else
                            customSettings.advanced.adas.detectionReset = settings.advanced.adas.detectionReset
                    }
                }

                if (settings?.advanced?.nav && customSettings?.advanced?.nav && !_.isEqual(settings.advanced.nav, customSettings.advanced.nav)) {

                    if (settings?.advanced?.nav?.maxSpeed && customSettings?.advanced?.nav?.maxSpeed && !_.isEqual(settings?.advanced?.nav?.maxSpeed, customSettings?.advanced?.nav?.maxSpeed)) {
                        if (customSettings?.advanced?.nav?.maxSpeed) {
                            if (typeof settings?.advanced?.nav?.maxSpeed?.OVER_MAX_SPEED !== "undefined" && !_.isEqual(settings?.advanced?.nav?.maxSpeed?.OVER_MAX_SPEED, customSettings?.advanced?.nav?.maxSpeed?.OVER_MAX_SPEED)) {
                                customSettings.advanced.nav.maxSpeed.OVER_MAX_SPEED = settings?.advanced?.nav?.maxSpeed?.OVER_MAX_SPEED;
                            }
                        } else {
                            customSettings.advanced.nav.maxSpeed = settings?.advanced?.nav?.maxSpeed
                        }
                    }

                    if (settings?.advanced?.nav?.minSpeed && customSettings?.advanced?.nav?.minSpeed && !_.isEqual(settings?.advanced?.nav?.minSpeed, customSettings?.advanced?.nav?.minSpeed)) {
                        if (settings?.advanced?.nav?.minSpeed && customSettings?.advanced?.nav?.minSpeed && !_.isEqual(settings?.advanced?.nav?.minSpeed, customSettings?.advanced?.nav?.minSpeed)) {
                            if (customSettings?.advanced?.nav?.minSpeed) {
                                if (typeof settings?.advanced?.nav?.minSpeed?.SPEEDING !== "undefined" && !_.isEqual(settings?.advanced?.nav?.minSpeed?.SPEEDING, customSettings?.advanced?.nav?.minSpeed?.SPEEDING)) {
                                    customSettings.advanced.nav.minSpeed.SPEEDING = settings?.advanced?.nav?.minSpeed?.SPEEDING;
                                }
                                if (typeof settings?.advanced?.nav?.minSpeed?.OVER_MAX_SPEED !== "undefined" && !_.isEqual(settings?.advanced?.nav?.minSpeed?.OVER_MAX_SPEED, customSettings?.advanced?.nav?.minSpeed?.OVER_MAX_SPEED)) {
                                    customSettings.advanced.nav.minSpeed.OVER_MAX_SPEED = settings?.advanced?.nav?.minSpeed?.OVER_MAX_SPEED;
                                }
                            } else {
                                customSettings.advanced.nav.minSpeed = settings?.advanced?.nav?.minSpeed
                            }
                        }
                    }

                    if (settings?.advanced?.nav?.repeatInterval && customSettings?.advanced?.nav?.repeatInterval && !_.isEqual(settings?.advanced?.nav?.repeatInterval, customSettings?.advanced?.nav?.repeatInterval)) {
                        if (settings?.advanced?.nav?.repeatInterval && customSettings?.advanced?.nav?.repeatInterval && !_.isEqual(settings?.advanced?.nav?.repeatInterval, customSettings?.advanced?.nav?.repeatInterval)) {
                            if (customSettings?.advanced?.nav?.repeatInterval) {
                                if (typeof settings?.advanced?.nav?.repeatInterval?.SPEEDING !== "undefined" && !_.isEqual(settings?.advanced?.nav?.repeatInterval?.SPEEDING, customSettings?.advanced?.nav?.repeatInterval?.SPEEDING)) {
                                    customSettings.advanced.nav.repeatInterval.SPEEDING = settings?.advanced?.nav?.repeatInterval?.SPEEDING;
                                }
                                if (typeof settings?.advanced?.nav?.repeatInterval?.OVER_MAX_SPEED !== "undefined" && !_.isEqual(settings?.advanced?.nav?.repeatInterval?.OVER_MAX_SPEED, customSettings?.advanced?.nav?.repeatInterval?.OVER_MAX_SPEED)) {
                                    customSettings.advanced.nav.repeatInterval.OVER_MAX_SPEED = settings?.advanced?.nav?.repeatInterval?.OVER_MAX_SPEED;
                                }
                            } else {
                                customSettings.advanced.nav.repeatInterval = settings?.advanced?.nav?.repeatInterval
                            }
                        }
                    }

                    if (settings?.advanced?.nav?.detectionDelay && customSettings?.advanced?.nav?.detectionDelay && !_.isEqual(settings?.advanced?.nav?.detectionDelay, customSettings?.advanced?.nav?.detectionDelay)) {
                        if (settings?.advanced?.nav?.detectionDelay && customSettings?.advanced?.nav?.detectionDelay && !_.isEqual(settings?.advanced?.nav?.detectionDelay, customSettings?.advanced?.nav?.detectionDelay)) {
                            if (customSettings?.advanced?.nav?.detectionDelay) {
                                if (typeof settings?.advanced?.nav?.detectionDelay?.SPEEDING !== "undefined" && !_.isEqual(settings?.advanced?.nav?.detectionDelay?.SPEEDING, customSettings?.advanced?.nav?.detectionDelay?.SPEEDING)) {
                                    customSettings.advanced.nav.detectionDelay.SPEEDING = settings?.advanced?.nav?.detectionDelay?.SPEEDING;
                                }
                                if (typeof settings?.advanced?.nav?.detectionDelay?.OVER_MAX_SPEED !== "undefined" && !_.isEqual(settings?.advanced?.nav?.detectionDelay?.OVER_MAX_SPEED, customSettings?.advanced?.nav?.detectionDelay?.OVER_MAX_SPEED)) {
                                    customSettings.advanced.nav.detectionDelay.OVER_MAX_SPEED = settings?.advanced?.nav?.detectionDelay?.OVER_MAX_SPEED;
                                }
                            } else {
                                customSettings.advanced.nav.detectionDelay = settings.advanced.nav.detectionDelay
                            }
                        }
                    }

                    if (settings.advanced.nav.detectionReset && !_.isEqual(settings.advanced.nav.detectionReset, customSettings.advanced.nav.detectionReset)) {
                        if (settings.advanced.nav.detectionReset && !_.isEqual(settings.advanced.nav.detectionReset, customSettings.advanced.nav.detectionReset)) {
                            if (customSettings.advanced.nav.detectionReset) {
                                if (typeof settings.advanced.nav.detectionReset.SPEEDING !== "undefined" && !_.isEqual(settings.advanced.nav.detectionReset.SPEEDING, customSettings.advanced.nav.detectionReset.SPEEDING)) {
                                    customSettings.advanced.nav.detectionReset.SPEEDING = settings.advanced.nav.detectionReset.SPEEDING;
                                }
                                if (typeof settings.advanced.nav.detectionReset.OVER_MAX_SPEED !== "undefined" && !_.isEqual(settings.advanced.nav.detectionReset.OVER_MAX_SPEED, customSettings.advanced.nav.detectionReset.OVER_MAX_SPEED)) {
                                    customSettings.advanced.nav.detectionReset.OVER_MAX_SPEED = settings.advanced.nav.detectionReset.OVER_MAX_SPEED;
                                }
                            } else {
                                customSettings.advanced.nav.detectionReset = settings.advanced.nav.detectionReset
                            }
                        }
                    }

                    if (settings.advanced.nav.overspeedLimit && !_.isEqual(settings.advanced.nav.overspeedLimit, customSettings.advanced.nav.overspeedLimit)) {
                        if (settings.advanced.nav.overspeedLimit && !_.isEqual(settings.advanced.nav.overspeedLimit, customSettings.advanced.nav.overspeedLimit)) {
                            if (customSettings.advanced.nav.overspeedLimit) {
                                if (typeof settings.advanced.nav.overspeedLimit.SPEEDING !== "undefined" && !_.isEqual(settings.advanced.nav.overspeedLimit.SPEEDING, customSettings.advanced.nav.overspeedLimit.SPEEDING)) {
                                    customSettings.advanced.nav.overspeedLimit.SPEEDING = settings.advanced.nav.overspeedLimit.SPEEDING;
                                }
                                if (typeof settings.advanced.nav.overspeedLimit.OVER_MAX_SPEED !== "undefined" && !_.isEqual(settings.advanced.nav.overspeedLimit.OVER_MAX_SPEED, customSettings.advanced.nav.overspeedLimit.OVER_MAX_SPEED)) {
                                    customSettings.advanced.nav.overspeedLimit.OVER_MAX_SPEED = settings.advanced.nav.overspeedLimit.OVER_MAX_SPEED;
                                }
                            } else {
                                customSettings.advanced.nav.overspeedLimit = settings.advanced.nav.overspeedLimit
                            }
                        }
                    }
                }

                if (settings.advanced.ignition && !_.isEqual(settings.advanced.ignition, customSettings.advanced.ignition)) {
                    customSettings.advanced.ignition = settings.advanced.ignition;
                }
                if (settings.advanced.driverID && !_.isEqual(settings.advanced.driverID, customSettings.advanced.driverID)) {
                    customSettings.advanced.driverID = settings.advanced.driverID;
                }

                //Privacy Control
                if (settings.advanced.privacyControl && !_.isEqual(settings.advanced.privacyControl, customSettings.advanced.privacyControl)) {
                    customSettings.advanced.privacyControl = settings.advanced.privacyControl;
                }

                if (settings.advanced.lang && !_.isEqual(settings.advanced.lang, customSettings.advanced.lang)) {
                    customSettings.advanced.lang = settings.advanced.lang;
                }

                if (settings.advanced.aux && !_.isEqual(settings.advanced.aux, customSettings.advanced.aux)) {
                    customSettings.advanced.aux = settings.advanced.aux;
                }

                if (settings.advanced.parkingHeartbeat && !_.isEqual(settings.advanced.aux, customSettings.advanced.parkingHeartbeat)) {
                    customSettings.advanced.parkingHeartbeat = settings.advanced.parkingHeartbeat;
                }

                if (settings.advanced.subtitle && !_.isEqual(settings.advanced.subtitle, customSettings.advanced.subtitle)) {
                    if (settings.advanced.subtitle.speed && !_.isEqual(settings.advanced.subtitle.speed, customSettings.advanced.subtitle.speed))
                        customSettings.advanced.subtitle.speed = settings.advanced.subtitle.speed;

                    if (settings.advanced.subtitle.timezone && !_.isEqual(settings.advanced.subtitle.timezone, customSettings.advanced.subtitle.timezone))
                        customSettings.advanced.subtitle.timezone = settings.advanced.subtitle.timezone;

                    if (settings.advanced.subtitle.gps && !_.isEqual(settings.advanced.subtitle.gps, customSettings.advanced.subtitle.gps))
                        customSettings.advanced.subtitle.gps = settings.advanced.subtitle.gps;
                }

                //safetyAssistance
                if (settings.advanced.safetyAssistance && !_.isEqual(settings.advanced.safetyAssistance, customSettings.advanced.safetyAssistance)) {

                    if (customSettings.advanced.safetyAssistance && settings.advanced.safetyAssistance.alarm && !_.isEqual(settings.advanced.safetyAssistance.alarm, customSettings.advanced.safetyAssistance.alarm)) {
                        if (typeof settings.advanced.safetyAssistance.alarm.NO_SEATBELT !== "undefined" && !_.isEqual(settings.advanced.safetyAssistance.alarm.NO_SEATBELT, customSettings.advanced.safetyAssistance.alarm.NO_SEATBELT)) {
                            customSettings.advanced.safetyAssistance.alarm.NO_SEATBELT = settings.advanced.safetyAssistance.alarm.NO_SEATBELT;
                        }

                        if (typeof settings.advanced.safetyAssistance.alarm.USING_CELLPHONE !== "undefined" && !_.isEqual(settings.advanced.safetyAssistance.alarm.USING_CELLPHONE, customSettings.advanced.safetyAssistance.alarm.USING_CELLPHONE)) {
                            customSettings.advanced.safetyAssistance.alarm.USING_CELLPHONE = settings.advanced.safetyAssistance.alarm.USING_CELLPHONE;
                        }

                        if (typeof settings.advanced.safetyAssistance.alarm.DISTRACTED !== "undefined" && !_.isEqual(settings.advanced.safetyAssistance.alarm.DISTRACTED, customSettings.advanced.safetyAssistance.alarm.DISTRACTED)) {
                            customSettings.advanced.safetyAssistance.alarm.DISTRACTED = settings.advanced.safetyAssistance.alarm.DISTRACTED;
                        }

                        if (typeof settings.advanced.safetyAssistance.alarm.NO_DRIVER !== "undefined" && !_.isEqual(settings.advanced.safetyAssistance.alarm.NO_DRIVER, customSettings.advanced.safetyAssistance.alarm.NO_DRIVER)) {
                            customSettings.advanced.safetyAssistance.alarm.NO_DRIVER = settings.advanced.safetyAssistance.alarm.NO_DRIVER;
                        }

                        if (typeof settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING !== "undefined" && !_.isEqual(settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING, customSettings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING)) {
                            customSettings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING = settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING;
                        }

                        if (typeof settings.advanced.safetyAssistance.alarm.TAILGATING !== "undefined" && !_.isEqual(settings.advanced.safetyAssistance.alarm.TAILGATING, customSettings.advanced.safetyAssistance.alarm.TAILGATING)) {
                            customSettings.advanced.safetyAssistance.alarm.TAILGATING = settings.advanced.safetyAssistance.alarm.TAILGATING;
                        }

                        if (typeof settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE !== "undefined" && !_.isEqual(settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE, customSettings.advanced.safetyAssistance.alarm.LANE_DEPARTURE)) {
                            customSettings.advanced.safetyAssistance.alarm.LANE_DEPARTURE = settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE;
                        }

                        if (typeof settings.advanced.safetyAssistance.alarm.SPEEDING !== "undefined" && !_.isEqual(settings.advanced.safetyAssistance.alarm.SPEEDING, customSettings.advanced.safetyAssistance.alarm.SPEEDING)) {
                            customSettings.advanced.safetyAssistance.alarm.SPEEDING = settings.advanced.safetyAssistance.alarm.SPEEDING;
                        }

                        if (typeof settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED !== "undefined" && !_.isEqual(settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED, customSettings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED)) {
                            customSettings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED = settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED;
                        }

                        if (typeof settings.advanced.safetyAssistance.alarm.PEDESTRIAN !== "undefined" && !_.isEqual(settings.advanced.safetyAssistance.alarm.PEDESTRIAN, customSettings.advanced.safetyAssistance.alarm.PEDESTRIAN)) {
                            customSettings.advanced.safetyAssistance.alarm.PEDESTRIAN = settings.advanced.safetyAssistance.alarm.PEDESTRIAN;
                        }

                        if (typeof settings.advanced.safetyAssistance.alarm.CYCLIST !== "undefined" && !_.isEqual(settings.advanced.safetyAssistance.alarm.CYCLIST, customSettings.advanced.safetyAssistance.alarm.CYCLIST)) {
                            customSettings.advanced.safetyAssistance.alarm.CYCLIST = settings.advanced.safetyAssistance.alarm.CYCLIST;
                        }

                        if (typeof settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT !== "undefined" && !_.isEqual(settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT, customSettings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT)) {
                            customSettings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT = settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT;
                        }
                    } else
                        customSettings.advanced.safetyAssistance = settings.advanced.safetyAssistance
                }

                //livePhoto
                if (settings.advanced.livePhoto && !_.isEqual(settings.advanced.livePhoto, customSettings.advanced.livePhoto)) {
                    customSettings.advanced.livePhoto = settings.advanced.livePhoto;
                }

            }
        }

        //timezone
        {
            if (settings.timezone && !_.isEqual(settings.timezone, customSettings.timezone)) {
                customSettings.timezone = settings.timezone;
            }
        }

        //cev
        {
            if (settings.cev && settings.cev?.enabledEvents?.length > 0 && !_.isEqual(settings.cev, customSettings.cev)) {
                customSettings.cev = settings.cev;
            }
        }

        return customSettings;

    }

    const checkRestrictions = (settings) => {
        let valid = true;
        if ((settings?.advanced?.adas?.level || settings?.advanced?.aux?.model || settings?.dms?.level || settings?.recordConfig?.config) && settings?.advanced && !settings?.advanced?.aux) {
            settings.advanced.aux = {
                model: "NA"
            }
        }

        if (settings?.advanced?.adas?.level && settings?.advanced?.aux?.model && settings?.dms?.level && settings?.recordConfig?.config && settings?.advanced?.privacyControl?.level) {
            let restriction = {
                adasLevel: settings.advanced.adas.level,
                auxModel: settings.advanced.aux.model,
                dmsLevel: settings.dms.level,
                recordProfile: settings.recordConfig.config,
                privacyControl: settings.advanced.privacyControl.level
            }

            let found = restrictions.restrictedPropertiesList.find(x => x.adasLevel === restriction.adasLevel && x.auxModel === restriction.auxModel && x.dmsLevel === restriction.dmsLevel && x.recordProfile === restriction.recordProfile && x.privacyControl === restriction.privacyControl)
            valid = found ? true : false;
        } else if (settings?.advanced?.adas?.level || settings?.advanced?.aux?.model || settings?.dms?.level || settings?.recordConfig?.config || settings?.advanced?.privacyControl?.level) {
            valid = false;
        }

        return valid;
    }

    const getDevicesForSelect = (input, callback) => {
        if (!input) {
            callback([])
            return
        }
        getDevices(input).then((response) => {
            if (response.result) {
                const { items } = response?.result
                callback(items)
            }
        })
    }

    const validateProperties = () => {
        let properties = restrictions?.restrictedPropertiesList ? restrictions.restrictedPropertiesList : []
        return (buttonState) => {
            let validate = properties.filter(item => {
                return (
                    item.dmsLevel === buttonState?.dmsLevel &&
                    item.adasLevel === buttonState?.adasLevel &&
                    item.recordProfile === buttonState.recordProfile &&
                    item.auxModel === buttonState.auxModel &&
                    item.privacyControl === buttonState.privacyControl
                );
            })
            return !(validate.length > 0)
        };

    };

    // modules start
    const sounds = () => {
        return (
            !optionCollapsed.collapsedSound &&
            <Grid container>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div style={{ marginLeft: 44 }}>
                        <h6>
                            {messages['vidfleet_configuration_sounds']}
                        </h6>
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={2}>
                            <ToggleSwitch
                                id="soundPowerss"
                                blueToggle={true}
                                large={true}
                                label={messages["vidfleet_configuration_power"]}
                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                checked={settingsData.settings.sounds.power === "on" ? true : false}
                                onChange={(checked) => {

                                    settingsData.settings.sounds.power = checked ? "on" : "off";
                                    settingsDataChanged.settings.sounds.power = true;
                                    optionDefaults.soundsDefault = false;
                                    setSettingsData({ ...settingsData });
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults });
                                }}
                                width={100}
                            />
                        </Grid>
                        {/* <Grid item xs={3}>
                            <div style={{ display: "flex" }}>
                                <ToggleSwitch
                                    id="soundEventTriggers"
                                    blueToggle={true}
                                    large={true}
                                    label={messages["vidfleet_configuration_event_trigger"]}
                                    optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                    checked={settingsData.settings.sounds.eventTrigger === "on" ? true : false}
                                    onChange={(checked) => {

                                        settingsData.settings.sounds.eventTrigger = checked ? "on" : "off";
                                        settingsDataChanged.settings.sounds.eventTrigger = true;
                                        optionDefaults.soundsDefault = false;
                                        setSettingsData({ ...settingsData });
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults });
                                    }}
                                    width={100}
                                />
                                <div className="d-sm-inline-block mb-1">
                                    <Tooltip title={messages["vidfleet_configuration_event_trigger_msg"]} tooltipplacement="left">
                                        <a className="btn-link align-middle">
                                            <Icon style={{ marginTop: 5 }}>info</Icon>
                                        </a>
                                    </Tooltip>
                                </div>
                            </div>
                        </Grid> */}
                        <Grid item xs={2}>
                            <ToggleSwitch
                                id="soundRecordings"
                                blueToggle={true}
                                large={true}
                                label={messages["vidfleet_configuration_recording"]}
                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                checked={settingsData.settings.sounds.recording === "on" ? true : false}
                                onChange={(checked) => {

                                    settingsData.settings.sounds.recording = checked ? "on" : "off";
                                    settingsDataChanged.settings.sounds.recording = true;
                                    optionDefaults.soundsDefault = false;
                                    setSettingsData({ ...settingsData });
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults });
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                {
                    //dms sounds settings, only for devices 4K
                    (!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name === "4K" || devices[0].deviceModel.name === "AIR2")) &&
                    <Grid container>
                        <Grid item xs={12}>
                            <div style={{ marginLeft: 44, marginTop: 30 }}>
                                <h6>
                                    {messages['vidfleet_configuration_dms']}
                                </h6>
                            </div>
                        </Grid>
                        <Grid item xs={12} style={{ marginLeft: 44 }}>
                            <Grid container>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="USING_PHONE"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_using_phone"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.sounds.dms && settingsData.settings.sounds.dms.USING_PHONE === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.dms)
                                                settingsData.settings.sounds.dms = {}

                                            settingsData.settings.sounds.dms.USING_PHONE = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.dms.USING_PHONE = true;
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="SMOKING"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_smoking"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.sounds.dms && settingsData.settings.sounds.dms.SMOKING === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.dms)
                                                settingsData.settings.sounds.dms = {}

                                            settingsData.settings.sounds.dms.SMOKING = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.dms.SMOKING = true;
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="DROWSINESS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_drowsiness"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.sounds.dms && settingsData.settings.sounds.dms.DROWSINESS === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.dms)
                                                settingsData.settings.sounds.dms = {}

                                            settingsData.settings.sounds.dms.DROWSINESS = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.dms.DROWSINESS = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="NO_SEATBELT"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_no_seatbelt"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.sounds.dms && settingsData.settings.sounds.dms.NO_SEATBELT === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.dms)
                                                settingsData.settings.sounds.dms = {}

                                            settingsData.settings.sounds.dms.NO_SEATBELT = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.dms.NO_SEATBELT = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="DAYDREAMING"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_daydreaming"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.sounds.dms && settingsData.settings.sounds.dms.DAYDREAMING === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.dms)
                                                settingsData.settings.sounds.dms = {}

                                            settingsData.settings.sounds.dms.DAYDREAMING = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.dms.DAYDREAMING = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="YAWN"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_yawn"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.sounds.dms && settingsData.settings.sounds.dms.YAWN === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.dms)
                                                settingsData.settings.sounds.dms = {}

                                            settingsData.settings.sounds.dms.YAWN = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.dms.YAWN = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="ASLEEP"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_asleep"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.sounds.dms && settingsData.settings.sounds.dms.ASLEEP === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.dms)
                                                settingsData.settings.sounds.dms = {}

                                            settingsData.settings.sounds.dms.ASLEEP = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.dms.ASLEEP = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="DISTRACTED"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_distracted"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.sounds.dms && settingsData.settings.sounds.dms.DISTRACTED === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.dms)
                                                settingsData.settings.sounds.dms = {}

                                            settingsData.settings.sounds.dms.DISTRACTED = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.dms.DISTRACTED = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="NO_DRIVER"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_no_driver"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.sounds.dms && settingsData.settings.sounds.dms.NO_DRIVER === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.dms)
                                                settingsData.settings.sounds.dms = {}

                                            settingsData.settings.sounds.dms.NO_DRIVER = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.dms.NO_DRIVER = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="PASSENGERS_INSIDE"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_passengers_inside"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.sounds.dms && settingsData.settings.sounds.dms.PASSENGERS_INSIDE === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.dms)
                                                settingsData.settings.sounds.dms = {}

                                            settingsData.settings.sounds.dms.PASSENGERS_INSIDE = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.dms.PASSENGERS_INSIDE = true;
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="PASSENGERS_NO_SEATBELT"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_passengers_no_seatbelt"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.sounds.dms && settingsData.settings.sounds.dms.PASSENGERS_NO_SEATBELT === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.dms)
                                                settingsData.settings.sounds.dms = {}

                                            settingsData.settings.sounds.dms.PASSENGERS_NO_SEATBELT = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.dms.PASSENGERS_NO_SEATBELT = true;
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>
                }
                {
                    //adas sounds settings, only for devices 4K
                    (!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name === "4K" || devices[0].deviceModel.name.includes("AIR"))) &&
                    <Grid item xs={12}>
                        <Grid item xs={12}>
                            <div style={{ marginLeft: 44, marginTop: 30 }}>
                                <h6>
                                    {messages["vidfleet_configuration_adas"]}
                                </h6>
                            </div>
                        </Grid>
                        <Grid item xs={12} style={{ marginLeft: 44 }}>
                            <Grid container>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="FORWARD_COLLISION_WARNING_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_forward_collision_warning']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.adas && settingsData.settings.sounds.adas.FORWARD_COLLISION_WARNING === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.adas)
                                                settingsData.settings.sounds.adas = {}

                                            settingsData.settings.sounds.adas.FORWARD_COLLISION_WARNING = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.adas.FORWARD_COLLISION_WARNING = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="HEADWAY_MONITORING_WARNING_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_headway_monitoring_warning']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.adas && settingsData.settings.sounds.adas.HEADWAY_MONITORING_WARNING === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.adas)
                                                settingsData.settings.sounds.adas = {}

                                            settingsData.settings.sounds.adas.HEADWAY_MONITORING_WARNING = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.adas.HEADWAY_MONITORING_WARNING = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="HEADWAY_MONITORING_EMERGENCY_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_headway_monitoring_emergency']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.adas && settingsData.settings.sounds.adas.HEADWAY_MONITORING_EMERGENCY === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.adas)
                                                settingsData.settings.sounds.adas = {}

                                            settingsData.settings.sounds.adas.HEADWAY_MONITORING_EMERGENCY = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.adas.HEADWAY_MONITORING_EMERGENCY = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="LANE_DEPARTURE_WARNING_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_lane_departure_warning']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.adas && settingsData.settings.sounds.adas.LANE_DEPARTURE_WARNING === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.adas)
                                                settingsData.settings.sounds.adas = {}

                                            settingsData.settings.sounds.adas.LANE_DEPARTURE_WARNING = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.adas.LANE_DEPARTURE_WARNING = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                        disabled={!HasPermission(user, "vftclndw", "u")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="SOUNDS_ADAS_STOP_SIGN_VIOLATION_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_stop_sign_violation']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.adas && settingsData.settings.sounds.adas.STOP_SIGN_VIOLATION === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.adas)
                                                settingsData.settings.sounds.adas = {}

                                            settingsData.settings.sounds.adas.STOP_SIGN_VIOLATION = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.adas.STOP_SIGN_VIOLATION = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                        disabled={!HasPermission(user, "vftclndw", "u")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="PEDESTRIAN_COLLISION_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_pedestrian_collision']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.adas && settingsData.settings.sounds.adas.PEDESTRIAN_COLLISION === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.adas)
                                                settingsData.settings.sounds.adas = {}

                                            settingsData.settings.sounds.adas.PEDESTRIAN_COLLISION = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.adas.PEDESTRIAN_COLLISION = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                        disabled={!HasPermission(user, "vftclndw", "u")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="CYCLIST_COLLISION_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_cyclist_collision']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.adas && settingsData.settings.sounds.adas.CYCLIST_COLLISION === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.adas)
                                                settingsData.settings.sounds.adas = {}

                                            settingsData.settings.sounds.adas.CYCLIST_COLLISION = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.adas.CYCLIST_COLLISION = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                        disabled={!HasPermission(user, "vftclndw", "u")}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
                {
                    //nav sounds settings, only for devices 4K
                    (!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name === "4K" || devices[0].deviceModel.name.includes("AIR"))) &&
                    <Grid item xs={12}>
                        <Grid item xs={12}>
                            <div style={{ marginLeft: 44, marginTop: 30 }}>
                                <h6>
                                    {messages["vidfleet_configuration_nav"]}
                                </h6>
                            </div>
                        </Grid>
                        <Grid item xs={12} style={{ marginLeft: 44 }}>
                            <Grid container>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="SPEEDING_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_speeding']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.nav && settingsData.settings.sounds.nav.SPEEDING === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.nav)
                                                settingsData.settings.sounds.nav = {}

                                            settingsData.settings.sounds.nav.SPEEDING = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.nav.SPEEDING = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="OVER_MAX_SPEED_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_over_max_speeding']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.nav && settingsData.settings.sounds.nav.OVER_MAX_SPEED === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.nav)
                                                settingsData.settings.sounds.nav = {}

                                            settingsData.settings.sounds.nav.OVER_MAX_SPEED = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.nav.OVER_MAX_SPEED = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
                {
                    //acc sounds settings
                    !settingsDataChanged?.settings?.sounds?.eventTrigger &&
                    <Grid item xs={12}>
                        <Grid item xs={12}>
                            <div style={{ marginLeft: 44, marginTop: 30 }}>
                                <h6>
                                    {messages["vidfleet_configuration_acceleration"]}
                                </h6>
                            </div>
                        </Grid>
                        <Grid item xs={12} style={{ marginLeft: 44 }}>
                            <Grid container>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="HARD_ACCEL_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_hard_accel']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.acc && settingsData.settings.sounds.acc.HARD_ACCEL === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.acc)
                                                settingsData.settings.sounds.acc = {}

                                            if (!settingsDataChanged.settings.sounds.acc)
                                                settingsDataChanged.settings.sounds.acc = {}

                                            settingsData.settings.sounds.acc.HARD_ACCEL = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.acc.HARD_ACCEL = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="HARSH_ACCEL_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_harsh_accel']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.acc && settingsData.settings.sounds.acc.HARSH_ACCEL === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.acc)
                                                settingsData.settings.sounds.acc = {}

                                            if (!settingsDataChanged.settings.sounds.acc)
                                                settingsDataChanged.settings.sounds.acc = {}

                                            settingsData.settings.sounds.acc.HARSH_ACCEL = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.acc.HARSH_ACCEL = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="SEVERE_ACCEL_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_severe_accel']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.acc && settingsData.settings.sounds.acc.SEVERE_ACCEL === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.acc)
                                                settingsData.settings.sounds.acc = {}

                                            if (!settingsDataChanged.settings.sounds.acc)
                                                settingsDataChanged.settings.sounds.acc = {}

                                            settingsData.settings.sounds.acc.SEVERE_ACCEL = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.acc.SEVERE_ACCEL = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="HARD_BRAKE_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_hard_brake']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.acc && settingsData.settings.sounds.acc.HARD_BRAKE === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.acc)
                                                settingsData.settings.sounds.acc = {}

                                            if (!settingsDataChanged.settings.sounds.acc)
                                                settingsDataChanged.settings.sounds.acc = {}

                                            settingsData.settings.sounds.acc.HARD_BRAKE = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.acc.HARD_BRAKE = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="HARSH_BRAKE_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_harsh_brake']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.acc && settingsData.settings.sounds.acc.HARSH_BRAKE === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.acc)
                                                settingsData.settings.sounds.acc = {}

                                            if (!settingsDataChanged.settings.sounds.acc)
                                                settingsDataChanged.settings.sounds.acc = {}

                                            settingsData.settings.sounds.acc.HARSH_BRAKE = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.acc.HARSH_BRAKE = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="SEVERE_BRAKE_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_severe_brake']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.acc && settingsData.settings.sounds.acc.SEVERE_BRAKE === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.acc)
                                                settingsData.settings.sounds.acc = {}

                                            if (!settingsDataChanged.settings.sounds.acc)
                                                settingsDataChanged.settings.sounds.acc = {}

                                            settingsData.settings.sounds.acc.SEVERE_BRAKE = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.acc.SEVERE_BRAKE = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="SHARP_TURN_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_sharp_turn']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.acc && settingsData.settings.sounds.acc.SHARP_TURN === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.acc)
                                                settingsData.settings.sounds.acc = {}

                                            if (!settingsDataChanged.settings.sounds.acc)
                                                settingsDataChanged.settings.sounds.acc = {}

                                            settingsData.settings.sounds.acc.SHARP_TURN = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.acc.SHARP_TURN = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="HARSH_TURN_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_harsh_turn']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.acc && settingsData.settings.sounds.acc.HARSH_TURN === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.acc)
                                                settingsData.settings.sounds.acc = {}

                                            if (!settingsDataChanged.settings.sounds.acc)
                                                settingsDataChanged.settings.sounds.acc = {}

                                            settingsData.settings.sounds.acc.HARSH_TURN = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.acc.HARSH_TURN = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <ToggleSwitch
                                        id="SEVERE_TURN_SOUNDS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_severe_turn']}
                                        optionLabels={[messages["common_on"], messages["common_off"]]}
                                        checked={settingsData.settings.sounds.acc && settingsData.settings.sounds.acc.SEVERE_TURN === "on" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.sounds.acc)
                                                settingsData.settings.sounds.acc = {}

                                            if (!settingsDataChanged.settings.sounds.acc)
                                                settingsDataChanged.settings.sounds.acc = {}

                                            settingsData.settings.sounds.acc.SEVERE_TURN = checked ? "on" : "off";
                                            settingsDataChanged.settings.sounds.acc.SEVERE_TURN = true
                                            optionDefaults.soundsDefault = false;
                                            setSettingsData({ ...settingsData });
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults });
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Grid>
        );
    }

    const eventLevelThreshold = () => {
        return (
            !optionCollapsed.colapsedEventLevelThreshold &&
            <Grid container>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div style={{ marginLeft: 44 }}>
                        <h6>
                            {messages["vidfleet_configuration_vehicle_class_present"]}
                        </h6>
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid item xs={props.bulk ? 4 : 3}>
                        <Select
                            id="selectVehicleClass"
                            name="selectVehicleClass"
                            placeholder={messages["vidfleet_configuration_vehicle_class_present"]}
                            value={vehicleClass}
                            options={vehicleClassOptions}
                            textField="name"
                            valueField="id"
                            onChange={(item) => {
                                vehicleClass = item;
                                settingsData.settings.eventThreshold = item.eventThreshold;
                                settingsDataChanged.settings.eventThreshold = {
                                    accValue: true, brakeValue: true, turnValue: true, accDuration: true, brakeDuration: true, turnDuration: true,
                                    steps: [true, true, true, true, true, true]
                                }
                                optionDefaults.eventLevelThresholdDefault = false;
                                setVehicleClass({ ...item });
                                setSettingsData({ ...settingsData })
                                saveChangedSettings({ ...settingsDataChanged })
                                setSaveSettingsAvailable(true);
                                setOptionDefaults({ ...optionDefaults })
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44, marginTop: 20 }}>
                    <div >
                        <h6>
                            {messages["vidfleet_configuration_thresholds"]}
                        </h6>
                    </div>
                </Grid>
                {
                    !HasPermission(user, "vfets", "u") &&
                    <Grid item xs={12} style={{ marginLeft: 44 }}>
                        <Alert severity="warning" style={{ marginBottom: 10 }}>
                            {messages['vidfleet_configuration_permission_message']}
                        </Alert>
                    </Grid>
                }
                <Grid item xs={12} style={{ marginLeft: 50 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={5}>
                            {messages["vidfleet_configuration_acc_value"]}
                            <Slider
                                min={0}
                                max={500}
                                value={settingsData.settings.eventThreshold.accValue || 0}
                                onChange={(event) => {
                                    settingsData.settings.eventThreshold.accValue = event;
                                    settingsDataChanged.settings.eventThreshold.accValue = true;
                                    optionDefaults.eventLevelThresholdDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure={messages["vidfleet_configuration_milli_g"]}
                                disabled={!HasPermission(user, "vfets", "u")}
                            />
                        </Grid>

                        <Grid item xs={5} style={{ marginLeft: 44 }}>
                            {messages["vidfleet_configuration_brake_value"]}
                            <Slider
                                min={-500}
                                max={500}
                                value={settingsData.settings.eventThreshold.brakeValue || 0}
                                onChange={(event) => {

                                    settingsData.settings.eventThreshold.brakeValue = event;
                                    settingsDataChanged.settings.eventThreshold.brakeValue = true

                                    optionDefaults.eventLevelThresholdDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure={messages["vidfleet_configuration_milli_g"]}
                                disabled={!HasPermission(user, "vfets", "u")}
                            />
                        </Grid>

                        <Grid item xs={5}>
                            {messages["vidfleet_configuration_turn_value"]}
                            <Slider
                                min={0}
                                max={500}
                                value={settingsData.settings.eventThreshold.turnValue || 0}
                                onChange={(event) => {

                                    settingsData.settings.eventThreshold.turnValue = event;
                                    settingsDataChanged.settings.eventThreshold.turnValue = true

                                    optionDefaults.eventLevelThresholdDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure={messages["vidfleet_configuration_milli_g"]}
                                disabled={!HasPermission(user, "vfets", "u")}
                            />
                        </Grid>

                        <Grid item xs={5} style={{ marginLeft: 44 }}>
                            {messages["vidfleet_configuration_acc_duration"]}
                            <Slider
                                min={0}
                                max={30}
                                value={settingsData.settings.eventThreshold.accDuration || 0}
                                onChange={(event) => {

                                    settingsData.settings.eventThreshold.accDuration = event;
                                    settingsDataChanged.settings.eventThreshold.accDuration = true

                                    optionDefaults.eventLevelThresholdDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure={messages["vidfleet_configuration_10_s"]}
                                disabled={!HasPermission(user, "vfets", "u")}
                            />
                        </Grid>

                        <Grid item xs={5}>
                            {messages["vidfleet_configuration_brake_duration"]}
                            <Slider
                                min={0}
                                max={30}
                                value={settingsData.settings.eventThreshold.brakeDuration || 0}
                                onChange={(event) => {

                                    settingsData.settings.eventThreshold.brakeDuration = event;
                                    settingsDataChanged.settings.eventThreshold.brakeDuration = true

                                    optionDefaults.eventLevelThresholdDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure={messages["vidfleet_configuration_10_s"]}
                                disabled={!HasPermission(user, "vfets", "u")}
                            />
                        </Grid>

                        <Grid item xs={5} style={{ marginLeft: 44 }}>
                            {messages["vidfleet_configuration_turn_duration"]}
                            <Slider
                                min={0}
                                max={30}
                                value={settingsData.settings.eventThreshold.turnDuration || 0}
                                onChange={(event) => {

                                    settingsData.settings.eventThreshold.turnDuration = event;
                                    settingsDataChanged.settings.eventThreshold.turnDuration = true

                                    optionDefaults.eventLevelThresholdDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure={messages["vidfleet_configuration_10_s"]}
                                disabled={!HasPermission(user, "vfets", "u")}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44, marginTop: 20 }}>
                    <div >
                        <h6>
                            {messages["vidfleet_configuration_events"]}
                        </h6>
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={props.bulk ? 4 : 3}>
                            <Grid item xs={12}>
                                {messages["vidfleet_configuration_hard_accel"]}
                            </Grid>
                            <Grid item xs={12}>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="hard-accel-high"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.HARD_ACCEL = "HIGH";
                                        settingsDataChanged.settings.eventLevel.HARD_ACCEL = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_high"], settingsData.settings.eventLevel.HARD_ACCEL === "HIGH")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="hard-accel-medium"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.HARD_ACCEL = "MEDIUM";
                                        settingsDataChanged.settings.eventLevel.HARD_ACCEL = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_medium"], settingsData.settings.eventLevel.HARD_ACCEL === "MEDIUM")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="hard-accel-low"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.HARD_ACCEL = "LOW";
                                        settingsDataChanged.settings.eventLevel.HARD_ACCEL = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_low"], settingsData.settings.eventLevel.HARD_ACCEL === "LOW")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="hard-accel-off"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.HARD_ACCEL = "OFF";
                                        settingsDataChanged.settings.eventLevel.HARD_ACCEL = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_off"], settingsData.settings.eventLevel.HARD_ACCEL === "OFF")
                                    }
                                </div>
                            </Grid>
                        </Grid>

                        <Grid item xs={props.bulk ? 4 : 3}>
                            <Grid item xs={12}>
                                {messages["vidfleet_configuration_hard_break"]}
                            </Grid>
                            <Grid item xs={12}>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="hardbreak-high"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.HARD_BRAKE = "HIGH";
                                        settingsDataChanged.settings.eventLevel.HARD_BRAKE = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_high"], settingsData.settings.eventLevel.HARD_BRAKE === "HIGH")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="hardbreak-medium"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.HARD_BRAKE = "MEDIUM";
                                        settingsDataChanged.settings.eventLevel.HARD_BRAKE = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_medium"], settingsData.settings.eventLevel.HARD_BRAKE === "MEDIUM")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="hardbreak-low"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.HARD_BRAKE = "LOW";
                                        settingsDataChanged.settings.eventLevel.HARD_BRAKE = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_low"], settingsData.settings.eventLevel.HARD_BRAKE === "LOW")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="hardbreak-off"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.HARD_BRAKE = "OFF";
                                        settingsDataChanged.settings.eventLevel.HARD_BRAKE = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_off"], settingsData.settings.eventLevel.HARD_BRAKE === "OFF")
                                    }
                                </div>
                            </Grid>
                        </Grid>

                        <Grid item xs={props.bulk ? 4 : 3}>
                            <Grid item xs={12}>
                                {messages["vidfleet_configuration_sharp_turn"]}
                            </Grid>
                            <Grid item xs={12}>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="sharpturn-high"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.SHARP_TURN = "HIGH";
                                        settingsDataChanged.settings.eventLevel.SHARP_TURN = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_high"], settingsData.settings.eventLevel.SHARP_TURN === "HIGH")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="sharpturn-medium"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.SHARP_TURN = "MEDIUM";
                                        settingsDataChanged.settings.eventLevel.SHARP_TURN = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_medium"], settingsData.settings.eventLevel.SHARP_TURN === "MEDIUM")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="sharpturn-low"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.SHARP_TURN = "LOW";
                                        settingsDataChanged.settings.eventLevel.SHARP_TURN = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_low"], settingsData.settings.eventLevel.SHARP_TURN === "LOW")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="sharpturn-off"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.SHARP_TURN = "OFF";
                                        settingsDataChanged.settings.eventLevel.SHARP_TURN = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_off"], settingsData.settings.eventLevel.SHARP_TURN === "OFF")
                                    }
                                </div>
                            </Grid>
                        </Grid>
                        {
                            HasPermission(user, "vftpi", "u") &&
                            <Grid item xs={props.bulk ? 4 : 3}>
                                <Grid item xs={12}>
                                    {messages["vidfleet_configuration_potential_incident"]}
                                </Grid>
                                <Grid item xs={12}>
                                    <div
                                        className="d-sm-inline-block mb-1 vidfleet-pills"
                                        key="potencial-incident-high"
                                        onClick={e => {
                                            settingsData.settings.eventLevel.POTENTIAL_INCIDENT = "HIGH";
                                            settingsDataChanged.settings.eventLevel.POTENTIAL_INCIDENT = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    >
                                        {
                                            props.getBadget(messages["vidfleet_configuration_high"], settingsData.settings.eventLevel.POTENTIAL_INCIDENT === "HIGH")
                                        }
                                    </div>
                                    <div
                                        className="d-sm-inline-block mb-1 vidfleet-pills"
                                        key="potencial-incident-medium"
                                        onClick={e => {
                                            settingsData.settings.eventLevel.POTENTIAL_INCIDENT = "MEDIUM";
                                            settingsDataChanged.settings.eventLevel.POTENTIAL_INCIDENT = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    >
                                        {
                                            props.getBadget(messages["vidfleet_configuration_medium"], settingsData.settings.eventLevel.POTENTIAL_INCIDENT === "MEDIUM")
                                        }
                                    </div>
                                    <div
                                        className="d-sm-inline-block mb-1 vidfleet-pills"
                                        key="potencial-incident-low"
                                        onClick={e => {
                                            settingsData.settings.eventLevel.POTENTIAL_INCIDENT = "LOW";
                                            settingsDataChanged.settings.eventLevel.POTENTIAL_INCIDENT = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    >
                                        {
                                            props.getBadget(messages["vidfleet_configuration_low"], settingsData.settings.eventLevel.POTENTIAL_INCIDENT === "LOW")
                                        }
                                    </div>
                                    <div
                                        className="d-sm-inline-block mb-1 vidfleet-pills"
                                        key="potencial-incident-off"
                                        onClick={e => {
                                            settingsData.settings.eventLevel.POTENTIAL_INCIDENT = "OFF";
                                            settingsDataChanged.settings.eventLevel.POTENTIAL_INCIDENT = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    >
                                        {
                                            props.getBadget(messages["vidfleet_configuration_off"], settingsData.settings.eventLevel.POTENTIAL_INCIDENT === "OFF")
                                        }
                                    </div>
                                </Grid>
                            </Grid>
                        }

                        <Grid item xs={props.bulk ? 4 : 3}>
                            <Grid item xs={12}>
                                {messages["vidfleet_configuration_parking_hit"]}
                            </Grid>
                            <Grid item xs={12}>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="parking_hit-high"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.PARKING_HIT = "HIGH";
                                        settingsDataChanged.settings.eventLevel.PARKING_HIT = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_high"], settingsData.settings.eventLevel.PARKING_HIT === "HIGH")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="parking_hit-low"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.PARKING_HIT = "LOW";
                                        settingsDataChanged.settings.eventLevel.PARKING_HIT = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_low"], settingsData.settings.eventLevel.PARKING_HIT === "LOW")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="parking_hit-off"
                                    onClick={e => {
                                        settingsData.settings.eventLevel.PARKING_HIT = "OFF";
                                        settingsDataChanged.settings.eventLevel.PARKING_HIT = true

                                        optionDefaults.eventLevelThresholdDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_off"], settingsData.settings.eventLevel.PARKING_HIT === "OFF")
                                    }
                                </div>
                            </Grid>
                        </Grid>

                        <Grid item xs={props.bulk ? 3 : 2}>
                            <ToggleSwitch
                                id="power_lost"
                                blueToggle={true}
                                large={true}
                                label={messages["vidfleet_configuration_power_lost"]}
                                optionLabels={[messages["common_on"], messages["common_off"]]}
                                checked={settingsData.settings.eventLevel.POWER_LOST === "DEFAULT" ? true : false}
                                onChange={(checked) => {

                                    settingsData.settings.eventLevel.POWER_LOST = checked ? "DEFAULT" : "OFF";
                                    settingsDataChanged.settings.eventLevel.POWER_LOST = true

                                    optionDefaults.eventLevelThresholdDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                width={100}
                            />
                        </Grid>

                    </Grid>
                </Grid>
                {
                    //dms, only for devices 4K
                    (!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name === "4K" || devices[0].deviceModel.name === "AIR2")) &&
                    <Grid item xs={12}>
                        <Grid item xs={12}>
                            <div style={{ marginLeft: 44, marginTop: 30 }}>
                                <h6>
                                    {messages["vidfleet_configuration_dms"]}
                                </h6>
                            </div>
                        </Grid>
                        <Grid item xs={12} style={{ marginLeft: 44 }}>
                            <Grid container>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="USING_PHONE_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_using_phone"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.USING_PHONE === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.USING_PHONE = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.USING_PHONE = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="SMOKING_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_smoking"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.SMOKING === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.SMOKING = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.SMOKING = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="DROWSINESS_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_drowsiness"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.DROWSINESS === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.DROWSINESS = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.DROWSINESS = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="NO_SEATBELT_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_no_seatbelt"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.NO_SEATBELT === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.NO_SEATBELT = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.NO_SEATBELT = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="DAYDREAMING_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_daydreaming"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.DAYDREAMING === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.DAYDREAMING = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.DAYDREAMING = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="YAWN_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_yawn"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.YAWN === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.YAWN = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.YAWN = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="ASLEEP_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_asleep"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.ASLEEP === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.ASLEEP = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.ASLEEP = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="DISTRACTED_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_distracted"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.DISTRACTED === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.DISTRACTED = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.DISTRACTED = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="NO_DRIVER_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_no_driver"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.NO_DRIVER === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.NO_DRIVER = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.NO_DRIVER = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="IDLE_NO_DRIVER_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_idle_no_driver"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.IDLE_NO_DRIVER === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.IDLE_NO_DRIVER = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.IDLE_NO_DRIVER = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="PASSENGERS_INSIDE_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_passengers_inside"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.PASSENGERS_INSIDE === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.PASSENGERS_INSIDE = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.PASSENGERS_INSIDE = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="PASSENGERS_NO_SEATBELT_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_passengers_no_seatbelt"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.PASSENGERS_NO_SEATBELT === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.PASSENGERS_NO_SEATBELT = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.PASSENGERS_NO_SEATBELT = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="ATTENTIVE_LEVELS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages["vidfleet_configuration_attentive"]}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.eventLevel.DMS && settingsData.settings.eventLevel.DMS.ATTENTIVE === "DEFAULT" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {}

                                            settingsData.settings.eventLevel.DMS.ATTENTIVE = checked ? "DEFAULT" : "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.ATTENTIVE = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
                {
                    //adas, only for devices 4K
                    (!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name === "4K" || devices[0].deviceModel.name.includes("AIR"))) &&
                    <Grid item xs={12}>
                        <Grid item xs={12}>
                            <div style={{ marginLeft: 44, marginTop: 30 }}>
                                <h6>
                                    {messages["vidfleet_configuration_adas"]}
                                </h6>
                            </div>
                        </Grid>
                        <Grid item xs={12} style={{ marginLeft: 44 }}>
                            <Grid container>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="FORWARD_COLLISION_EVENTS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_forward_collision']}
                                        optionLabels={[messages["common_warning"], messages["common_off"]]}
                                        checked={settingsData.settings.eventLevel.ADAS && settingsData.settings.eventLevel.ADAS.FORWARD_COLLISION === "WARNING" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.ADAS)
                                                settingsData.settings.eventLevel.ADAS = {}

                                            settingsData.settings.eventLevel.ADAS.FORWARD_COLLISION = checked ? "WARNING" : "OFF";
                                            settingsDataChanged.settings.eventLevel.ADAS.FORWARD_COLLISION = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2} >
                                    <Grid item xs={12}>
                                        {messages["vidfleet_configuration_headway_monitoring"]}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <div
                                            className="d-sm-inline-block mb-1 vidfleet-pills"
                                            key="high_bit_rate-super-warning"
                                            onClick={e => {

                                                if (!settingsData.settings.eventLevel.ADAS)
                                                    settingsData.settings.eventLevel.ADAS = {};

                                                settingsData.settings.eventLevel.ADAS.HEADWAY_MONITORING = "WARNING";
                                                settingsDataChanged.settings.eventLevel.ADAS.HEADWAY_MONITORING = true

                                                optionDefaults.eventLevelThresholdDefault = false;
                                                setSettingsData({ ...settingsData })
                                                saveChangedSettings({ ...settingsDataChanged })
                                                setSaveSettingsAvailable(true);
                                                setOptionDefaults({ ...optionDefaults })
                                            }}
                                        >
                                            {
                                                props.getBadget(messages["common_warning"], settingsData.settings.eventLevel.ADAS.HEADWAY_MONITORING === "WARNING")
                                            }
                                        </div>
                                        <div
                                            className="d-sm-inline-block mb-1 vidfleet-pills"
                                            key="high_bit_rate-super-emergency"
                                            onClick={e => {

                                                if (!settingsData.settings.eventLevel.ADAS)
                                                    settingsData.settings.eventLevel.ADAS = {};

                                                settingsData.settings.eventLevel.ADAS.HEADWAY_MONITORING = "EMERGENCY";
                                                settingsDataChanged.settings.eventLevel.ADAS.HEADWAY_MONITORING = true

                                                optionDefaults.eventLevelThresholdDefault = false;
                                                setSettingsData({ ...settingsData })
                                                saveChangedSettings({ ...settingsDataChanged })
                                                setSaveSettingsAvailable(true);
                                                setOptionDefaults({ ...optionDefaults })
                                            }}
                                        >
                                            {
                                                props.getBadget(messages["common_emergency"], settingsData.settings.eventLevel.ADAS.HEADWAY_MONITORING === "EMERGENCY")
                                            }
                                        </div>
                                        <div
                                            className="d-sm-inline-block mb-1 vidfleet-pills"
                                            key="high_bit_rate-super-off"
                                            onClick={e => {

                                                if (!settingsData.settings.eventLevel.ADAS)
                                                    settingsData.settings.eventLevel.ADAS = {};

                                                settingsData.settings.eventLevel.ADAS.HEADWAY_MONITORING = "OFF";
                                                settingsDataChanged.settings.eventLevel.ADAS.HEADWAY_MONITORING = true

                                                optionDefaults.eventLevelThresholdDefault = false;
                                                setSettingsData({ ...settingsData })
                                                saveChangedSettings({ ...settingsDataChanged })
                                                setSaveSettingsAvailable(true);
                                                setOptionDefaults({ ...optionDefaults })
                                            }}
                                        >
                                            {
                                                props.getBadget(messages["common_off"], settingsData.settings.eventLevel.ADAS.HEADWAY_MONITORING === "OFF")
                                            }
                                        </div>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <div style={{ fontSize: "12px" }}>
                                            {
                                                settingsData.settings.eventLevel.ADAS.HEADWAY_MONITORING === "WARNING" &&
                                                messages["vidfleet_configuration_headway_monitoring_warning_label"]
                                            }
                                            {
                                                settingsData.settings.eventLevel.ADAS.HEADWAY_MONITORING === "EMERGENCY" &&
                                                messages["vidfleet_configuration_headway_monitoring_emergency_label"]

                                            }
                                        </div>
                                    </Grid>
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="LANE_DEPARTURE_EVENTS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_lane_departure']}
                                        optionLabels={[messages["common_warning"], messages["common_off"]]}
                                        checked={settingsData.settings.eventLevel.ADAS && settingsData.settings.eventLevel.ADAS.LANE_DEPARTURE === "WARNING" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.ADAS)
                                                settingsData.settings.eventLevel.ADAS = {}

                                            settingsData.settings.eventLevel.ADAS.LANE_DEPARTURE = checked ? "WARNING" : "OFF";
                                            settingsDataChanged.settings.eventLevel.ADAS.LANE_DEPARTURE = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                        disabled={!HasPermission(user, "vftclnd", "u")}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="STOP_SIGN_VIOLATION_EVENTS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_stop_sign_violation']}
                                        optionLabels={[messages["common_warning"], messages["common_off"]]}
                                        checked={settingsData.settings.eventLevel.ADAS && settingsData.settings.eventLevel.ADAS.STOP_SIGN_VIOLATION === "WARNING" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.ADAS)
                                                settingsData.settings.eventLevel.ADAS = {}

                                            settingsData.settings.eventLevel.ADAS.STOP_SIGN_VIOLATION = checked ? "WARNING" : "OFF";
                                            settingsDataChanged.settings.eventLevel.ADAS.STOP_SIGN_VIOLATION = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="PEDESTRIAN_COLLISION_EVENTS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_pedestrian_collision']}
                                        optionLabels={[messages["common_warning"], messages["common_off"]]}
                                        checked={settingsData.settings.eventLevel.ADAS && settingsData.settings.eventLevel.ADAS.PEDESTRIAN_COLLISION === "WARNING" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.ADAS)
                                                settingsData.settings.eventLevel.ADAS = {}

                                            settingsData.settings.eventLevel.ADAS.PEDESTRIAN_COLLISION = checked ? "WARNING" : "OFF";
                                            settingsDataChanged.settings.eventLevel.ADAS.PEDESTRIAN_COLLISION = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="CYCLIST_COLLISION_EVENTS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_cyclist_collision']}
                                        optionLabels={[messages["common_warning"], messages["common_off"]]}
                                        checked={settingsData.settings.eventLevel.ADAS && settingsData.settings.eventLevel.ADAS.CYCLIST_COLLISION === "WARNING" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.ADAS)
                                                settingsData.settings.eventLevel.ADAS = {}

                                            settingsData.settings.eventLevel.ADAS.CYCLIST_COLLISION = checked ? "WARNING" : "OFF";
                                            settingsDataChanged.settings.eventLevel.ADAS.CYCLIST_COLLISION = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
                {
                    //nav, only for devices 4K
                    (!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name === "4K" || devices[0].deviceModel.name.includes("AIR"))) &&
                    <Grid item xs={12}>
                        <Grid item xs={12}>
                            <div style={{ marginLeft: 44, marginTop: 30 }}>
                                <h6>
                                    {messages["vidfleet_configuration_nav"]}
                                </h6>
                            </div>
                        </Grid>
                        <Grid item xs={12} style={{ marginLeft: 44 }}>
                            <Grid container>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="SPEEDING_EVENTS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_speeding']}
                                        optionLabels={[messages["common_warning"], messages["common_off"]]}
                                        checked={settingsData.settings.eventLevel.NAV && settingsData.settings.eventLevel.NAV.SPEEDING === "WARNING" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.NAV)
                                                settingsData.settings.eventLevel.NAV = {}

                                            settingsData.settings.eventLevel.NAV.SPEEDING = checked ? "WARNING" : "OFF";
                                            settingsDataChanged.settings.eventLevel.NAV.SPEEDING = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={props.bulk ? 3 : 2}>
                                    <ToggleSwitch
                                        id="OVER_MAX_SPEED_EVENTS"
                                        blueToggle={true}
                                        large={true}
                                        label={messages['vidfleet_configuration_over_max_speeding']}
                                        optionLabels={[messages["common_warning"], messages["common_off"]]}
                                        checked={settingsData.settings.eventLevel.NAV && settingsData.settings.eventLevel.NAV.OVER_MAX_SPEED === "WARNING" ? true : false}
                                        onChange={(checked) => {

                                            if (!settingsData.settings.eventLevel.NAV)
                                                settingsData.settings.eventLevel.NAV = {}

                                            settingsData.settings.eventLevel.NAV.OVER_MAX_SPEED = checked ? "WARNING" : "OFF";
                                            settingsDataChanged.settings.eventLevel.NAV.OVER_MAX_SPEED = true

                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Grid>
        );
    }

    const dmsEvents = () => {

        const isButtonDisabled = validateProperties();
        let buttonStates = settingsData.settings

        let curretStates = {
            adasLevel: buttonStates?.advanced?.adas?.level,
            recordProfile: buttonStates?.recordConfig?.config,
            auxModel: buttonStates?.advanced?.aux?.model,
            privacyControl: buttonStates?.advanced?.privacyControl?.level
        }
        let indexCount = 0;
        let indexKey = 0;
        return (
            !optionCollapsed.colapsedDMSEvents &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={12}>
                            {messages["vidfleet_configuration_level"]}
                            <div style={{ marginLeft: 0 }}>
                                <div
                                    key="level-dms-off"
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                >
                                    <div
                                        onClick={e => {
                                            setTab(0)

                                            if (isButtonDisabled({ ...curretStates, dmsLevel: "OFF" })) {
                                                NotificationManager.warning(
                                                    <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                    messages["vidfleet_configuration_combination_not_allowed"],
                                                    6000,
                                                    null,
                                                    null,
                                                    'error'
                                                );
                                            }
                                            settingsData.settings.dms.level = "OFF";
                                            settingsDataChanged.settings.dms.level = true;
                                            //is necessary to disble this events
                                            if (!settingsData.settings.eventLevel)
                                                settingsData.settings.eventLevel = { DMS: {} };
                                            else if (!settingsData.settings.eventLevel.DMS)
                                                settingsData.settings.eventLevel.DMS = {};

                                            settingsData.settings.eventLevel.DMS.NO_DRIVER = "OFF";
                                            settingsDataChanged.settings.eventLevel.DMS.NO_DRIVER = true;

                                            settingsData.settings.eventLevel.DMS.ASLEEP = "OFF"
                                            settingsDataChanged.settings.eventLevel.DMS.ASLEEP = true;

                                            settingsData.settings.eventLevel.DMS.DROWSINESS = "OFF"
                                            settingsDataChanged.settings.eventLevel.DMS.DROWSINESS = true;

                                            settingsData.settings.eventLevel.DMS.DAYDREAMING = "OFF"
                                            settingsDataChanged.settings.eventLevel.DMS.DAYDREAMING = true;

                                            settingsData.settings.eventLevel.DMS.YAWN = "OFF"
                                            settingsDataChanged.settings.eventLevel.DMS.YAWN = true;

                                            settingsData.settings.eventLevel.DMS.USING_PHONE = "OFF"
                                            settingsDataChanged.settings.eventLevel.DMS.USING_PHONE = true;

                                            settingsData.settings.eventLevel.DMS.DISTRACTED = "OFF"
                                            settingsDataChanged.settings.eventLevel.DMS.DISTRACTED = true;

                                            settingsData.settings.eventLevel.DMS.SMOKING = "OFF"
                                            settingsDataChanged.settings.eventLevel.DMS.SMOKING = true;

                                            settingsData.settings.eventLevel.DMS.NO_SEATBELT = "OFF"
                                            settingsDataChanged.settings.eventLevel.DMS.NO_SEATBELT = true;

                                            settingsData.settings.eventLevel.DMS.IDLE_NO_DRIVER = "OFF"
                                            settingsDataChanged.settings.eventLevel.DMS.IDLE_NO_DRIVER = true;

                                            settingsData.settings.eventLevel.DMS.PASSENGERS_INSIDE = "OFF"
                                            settingsDataChanged.settings.eventLevel.DMS.PASSENGERS_INSIDE = true;

                                            settingsData.settings.eventLevel.DMS.PASSENGERS_NO_SEATBELT = "OFF"
                                            settingsDataChanged.settings.eventLevel.DMS.PASSENGERS_NO_SEATBELT = true;

                                            optionDefaults.DMSEventsDefault = false;
                                            optionDefaults.eventLevelThresholdDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                        style={{ cursor: isButtonDisabled({ ...curretStates, dmsLevel: "OFF" }) ? 'not-allowed' : 'pointer' }}
                                    >
                                        {
                                            props.getBadget(messages["vidfleet_configuration_off"], settingsData.settings.dms.level === "OFF")
                                        }
                                    </div>
                                </div>
                                <div className="d-sm-inline-block mb-1 vidfleet-pills" key="level-dms-standard">
                                    <div
                                        onClick={e => {
                                            setTab(0)

                                            if (isButtonDisabled({ ...curretStates, dmsLevel: "STANDARD" })) {
                                                NotificationManager.warning(
                                                    <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                    messages["vidfleet_configuration_combination_not_allowed"],
                                                    6000,
                                                    null,
                                                    null,
                                                    'error'
                                                );
                                            }
                                            settingsData.settings.dms.level = "STANDARD";
                                            settingsDataChanged.settings.dms.level = true
                                            optionDefaults.DMSEventsDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })

                                        }}
                                        style={{ cursor: isButtonDisabled({ ...curretStates, dmsLevel: "STANDARD" }) ? 'not-allowed' : 'pointer' }}
                                    >
                                        {
                                            props.getBadget(messages["vidfleet_configuration_standard"], settingsData.settings.dms.level === "STANDARD")
                                        }
                                    </div>
                                </div>
                                <div className="d-sm-inline-block mb-1 vidfleet-pills" key="level-dms-lite">
                                    <div
                                        onClick={e => {
                                            setTab(0)

                                            if (isButtonDisabled({ ...curretStates, dmsLevel: "LITE" })) {
                                                NotificationManager.warning(
                                                    <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                    messages["vidfleet_configuration_combination_not_allowed"],
                                                    6000,
                                                    null,
                                                    null,
                                                    'error'
                                                );
                                            }
                                            settingsData.settings.dms.level = "LITE";
                                            optionDefaults.DMSEventsDefault = false;
                                            optionDefaults.eventLevelThresholdDefault = false;
                                            //settingsData.settings.dms.minSpeed.DISTRACTED = 0;
                                            //settingsData.settings.dms.detectionDelay.DISTRACTED = 0;
                                            //settingsData.settings.dms.repeatInterval.DISTRACTED = 0;
                                            //settingsData.settings.dms.detectionReset.DISTRACTED = 0;
                                            settingsData.settings.eventLevel.DMS.DISTRACTED = "OFF"

                                            settingsDataChanged.settings.dms.level = true;
                                            settingsDataChanged.eventLevelThresholdDefault = true;
                                            //settingsDataChanged.settings.dms.minSpeed.DISTRACTED = true;
                                            //settingsDataChanged.settings.dms.detectionDelay.DISTRACTED = true;
                                            //settingsDataChanged.settings.dms.repeatInterval.DISTRACTED = true;
                                            //settingsDataChanged.settings.dms.detectionReset.DISTRACTED = true;
                                            settingsDataChanged.settings.eventLevel.DMS.DISTRACTED = true

                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })

                                        }}
                                        style={{ cursor: isButtonDisabled({ ...curretStates, dmsLevel: "LITE" }) ? 'not-allowed' : 'pointer' }}
                                    >
                                        {
                                            props.getBadget(messages["vidfleet_configuration_lite"], settingsData.settings.dms.level === "LITE")
                                        }
                                    </div>
                                </div>
                                <div className="d-sm-inline-block mb-1">
                                    <Tooltip title={messages["vidfleet_configuration_message_available_combinations"]} tooltipplacement="left">
                                        <a className="btn-link align-middle" onClick={() => { dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings)) }}>
                                            <Icon style={{ marginTop: 5 }}>info</Icon>
                                        </a>
                                    </Tooltip>
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            {
                                settingsData.settings.dms.level === "OFF" &&
                                <span>{messages["vidfleet_configuration_dms_level_off"]}</span>
                            }
                            {
                                settingsData.settings.dms.level === "STANDARD" &&
                                <span>{messages["vidfleet_configuration_dms_level_standard"]}</span>
                            }
                            {
                                settingsData.settings.dms.level === "LITE" &&
                                <span>{messages["vidfleet_configuration_dms_level_lite"]}</span>
                            }
                        </Grid>
                    </Grid>
                </Grid>
                {
                    settingsData.settings.dms.level !== "OFF" &&
                    <Grid item xs={12} style={{ marginLeft: 44 }}>
                        <Tabs tab={tab} onChange={(numberTab) => { setTab(numberTab) }} scrollButtons>
                            {
                                (settingsData.settings.dms.level === "STANDARD" || settingsData.settings.dms.level === "LITE") && settingsData.settings.eventLevel.DMS.USING_PHONE === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_using_phone"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div >
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        {
                                            !HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="usin-phone-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.USING_PHONE === defaultSettingsData?.settings?.dms?.minSpeed?.USING_PHONE}
                                                            onChange={(checked) => {
                                                                if (checked) {
                                                                    settingsData.settings.dms.minSpeed.USING_PHONE = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.USING_PHONE));
                                                                    settingsDataChanged.settings.dms.minSpeed.USING_PHONE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    settingsDataChanged.settings.dms.minSpeed.USING_PHONE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: 15 }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.minSpeed.USING_PHONE.min}
                                                        max={vidFleetConfigLimitDefault.dms.minSpeed.USING_PHONE.max}
                                                        value={settingsData.settings.dms.minSpeed.USING_PHONE || vidFleetConfigLimitDefault.dms.minSpeed.USING_PHONE.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.USING_PHONE = event;
                                                            settingsDataChanged.settings.dms.minSpeed.USING_PHONE = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })

                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} /* style={{ marginLeft: 20 }} */>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="usin-phone-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.USING_PHONE === defaultSettingsData?.settings?.dms?.detectionDelay?.USING_PHONE}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.USING_PHONE = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.USING_PHONE));
                                                                    settingsDataChanged.settings.dms.detectionDelay.USING_PHONE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    settingsDataChanged.settings.dms.detectionDelay.USING_PHONE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionDelay.USING_PHONE.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionDelay.USING_PHONE.max}
                                                        value={settingsData.settings.dms.detectionDelay.USING_PHONE || vidFleetConfigLimitDefault.dms.detectionDelay.USING_PHONE.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.USING_PHONE = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.USING_PHONE = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="usin-phone-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.USING_PHONE === defaultSettingsData?.settings?.dms?.repeatInterval?.USING_PHONE}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.USING_PHONE = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.USING_PHONE));
                                                                    settingsDataChanged.settings.dms.repeatInterval.USING_PHONE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    settingsDataChanged.settings.dms.repeatInterval.USING_PHONE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }
                                                            }}
                                                            disabled={!HasPermission(user, "chngrptintrvl", "u")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.repeatInterval.USING_PHONE.min}
                                                        max={vidFleetConfigLimitDefault.dms.repeatInterval.USING_PHONE.max}
                                                        value={settingsData.settings.dms.repeatInterval.USING_PHONE || vidFleetConfigLimitDefault.dms.repeatInterval.USING_PHONE.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.USING_PHONE = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.USING_PHONE = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ /* marginLeft: 20, */ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="usin-phone-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.USING_PHONE === defaultSettingsData?.settings?.dms?.detectionReset?.USING_PHONE}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.USING_PHONE = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.USING_PHONE));
                                                                    settingsDataChanged.settings.dms.detectionReset.USING_PHONE = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.USING_PHONE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("USING_PHONE")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionReset.USING_PHONE.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionReset.USING_PHONE.max}
                                                        value={settingsData.settings.dms.detectionReset.USING_PHONE || vidFleetConfigLimitDefault.dms.detectionReset.USING_PHONE.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.USING_PHONE = event;
                                                            settingsDataChanged.settings.dms.detectionReset.USING_PHONE = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </TabContainer>
                            }
                            {
                                (settingsData.settings.dms.level === "STANDARD" || settingsData.settings.dms.level === "LITE") && settingsData.settings.eventLevel.DMS.DROWSINESS === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_drowsiness"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div>
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>

                                    <Grid container spacing={2}>
                                        {
                                            !HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="drowsiness-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.DROWSINESS === defaultSettingsData?.settings?.dms?.minSpeed?.DROWSINESS}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.minSpeed.DROWSINESS = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.DROWSINESS));
                                                                    settingsDataChanged.settings.dms.minSpeed.DROWSINESS = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    settingsDataChanged.settings.dms.minSpeed.DROWSINESS = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.minSpeed.DROWSINESS.min}
                                                        max={vidFleetConfigLimitDefault.dms.minSpeed.DROWSINESS.max}
                                                        value={settingsData.settings.dms.minSpeed.DROWSINESS || vidFleetConfigLimitDefault.dms.minSpeed.DROWSINESS.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.DROWSINESS = event;
                                                            settingsDataChanged.settings.dms.minSpeed.DROWSINESS = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="drowsiness-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.DROWSINESS === defaultSettingsData?.settings?.dms?.detectionDelay?.DROWSINESS}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.DROWSINESS = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.DROWSINESS));
                                                                    settingsDataChanged.settings.dms.detectionDelay.DROWSINESS = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionDelay.DROWSINESS = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("DROWSINESS")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionDelay.DROWSINESS.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionDelay.DROWSINESS.max}
                                                        value={settingsData.settings.dms.detectionDelay.DROWSINESS || vidFleetConfigLimitDefault.dms.detectionDelay.DROWSINESS.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.DROWSINESS = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.DROWSINESS = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="drowsiness-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.DROWSINESS === defaultSettingsData?.settings?.dms?.repeatInterval?.DROWSINESS}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.DROWSINESS = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.DROWSINESS));
                                                                    settingsDataChanged.settings.dms.repeatInterval.DROWSINESS = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.repeatInterval.DROWSINESS = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.repeatInterval.DROWSINESS.min}
                                                        max={vidFleetConfigLimitDefault.dms.repeatInterval.DROWSINESS.max}
                                                        value={settingsData.settings.dms.repeatInterval.DROWSINESS || vidFleetConfigLimitDefault.dms.repeatInterval.DROWSINESS.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.DROWSINESS = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.DROWSINESS = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="drowsiness-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.DROWSINESS === defaultSettingsData?.settings?.dms?.detectionReset?.DROWSINESS}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.DROWSINESS = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.DROWSINESS));
                                                                    settingsDataChanged.settings.dms.detectionReset.DROWSINESS = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.DROWSINESS = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("DROWSINESS")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionReset.DROWSINESS.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionReset.DROWSINESS.max}
                                                        value={settingsData.settings.dms.detectionReset.DROWSINESS || vidFleetConfigLimitDefault.dms.detectionReset.DROWSINESS.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.DROWSINESS = event;
                                                            settingsDataChanged.settings.dms.detectionReset.DROWSINESS = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </TabContainer>
                            }
                            {
                                settingsData.settings.dms.level === "STANDARD" && settingsData.settings.eventLevel.DMS.DAYDREAMING === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_daydreaming"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div >
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid container>
                                        {
                                            !HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="daydreaming-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.DAYDREAMING === defaultSettingsData?.settings?.dms?.minSpeed?.DAYDREAMING}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.minSpeed.DAYDREAMING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.DAYDREAMING));
                                                                    settingsDataChanged.settings.dms.minSpeed.DAYDREAMING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.minSpeed.DAYDREAMING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.minSpeed.DAYDREAMING.min}
                                                        max={vidFleetConfigLimitDefault.dms.minSpeed.DAYDREAMING.max}
                                                        value={settingsData.settings.dms.minSpeed.DAYDREAMING || vidFleetConfigLimitDefault.dms.minSpeed.DAYDREAMING.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.DAYDREAMING = event;
                                                            settingsDataChanged.settings.dms.minSpeed.DAYDREAMING = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: 25 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="daydreaming-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.DAYDREAMING === defaultSettingsData?.settings?.dms?.detectionDelay?.DAYDREAMING}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.DAYDREAMING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.DAYDREAMING));
                                                                    settingsDataChanged.settings.dms.detectionDelay.DAYDREAMING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionDelay.DAYDREAMING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("DAYDREAMING")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionDelay.DAYDREAMING.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionDelay.DAYDREAMING.max}
                                                        value={settingsData.settings.dms.detectionDelay.DAYDREAMING || vidFleetConfigLimitDefault.dms.detectionDelay.DAYDREAMING.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.DAYDREAMING = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.DAYDREAMING =

                                                                optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="daydreaming-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.DAYDREAMING === defaultSettingsData?.settings?.dms?.repeatInterval?.DAYDREAMING}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.DAYDREAMING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.DAYDREAMING));
                                                                    settingsDataChanged.settings.dms.repeatInterval.DAYDREAMING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.repeatInterval.DAYDREAMING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.repeatInterval.DAYDREAMING.min}
                                                        max={vidFleetConfigLimitDefault.dms.repeatInterval.DAYDREAMING.max}
                                                        value={settingsData.settings.dms.repeatInterval.DAYDREAMING || vidFleetConfigLimitDefault.dms.repeatInterval.DAYDREAMING.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.DAYDREAMING = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.DAYDREAMING = true
                                                            settingsDataChanged.settings.dms.level = true;

                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10, marginLeft: 25 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="daydreaming-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.DAYDREAMING === defaultSettingsData?.settings?.dms?.detectionReset?.DAYDREAMING}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.DAYDREAMING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.DAYDREAMING));
                                                                    settingsDataChanged.settings.dms.detectionReset.DAYDREAMING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.DAYDREAMING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("DAYDREAMING")}
                                                        />
                                                    </div>

                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionReset.DAYDREAMING.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionReset.DAYDREAMING.max}
                                                        value={settingsData.settings.dms.detectionReset.DAYDREAMING || vidFleetConfigLimitDefault.dms.detectionReset.DAYDREAMING.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.DAYDREAMING = event;
                                                            settingsDataChanged.settings.dms.detectionReset.DAYDREAMING = true
                                                            settingsDataChanged.settings.dms.level = true;

                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </TabContainer>
                            }
                            {
                                (settingsData.settings.dms.level === "STANDARD" || settingsData.settings.dms.level === "LITE") && settingsData.settings.eventLevel.DMS.DISTRACTED === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_distracted"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div >
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        {
                                            !HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="distracted-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.DISTRACTED === defaultSettingsData?.settings?.dms?.minSpeed?.DISTRACTED}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.minSpeed.DISTRACTED = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.DISTRACTED));
                                                                    settingsDataChanged.settings.dms.minSpeed.DISTRACTED = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.minSpeed.DISTRACTED = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.minSpeed.DISTRACTED.min}
                                                        max={vidFleetConfigLimitDefault.dms.minSpeed.DISTRACTED.max}
                                                        value={settingsData.settings.dms.minSpeed.DISTRACTED || vidFleetConfigLimitDefault.dms.minSpeed.DISTRACTED.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.DISTRACTED = event;
                                                            settingsDataChanged.settings.dms.minSpeed.DISTRACTED = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="distracted-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.DISTRACTED === defaultSettingsData?.settings?.dms?.detectionDelay?.DISTRACTED}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.DISTRACTED = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.DISTRACTED));
                                                                    settingsDataChanged.settings.dms.detectionDelay.DISTRACTED = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionDelay.DISTRACTED = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("DISTRACTED")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionDelay.DISTRACTED.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionDelay.DISTRACTED.max}
                                                        value={settingsData.settings.dms.detectionDelay.DISTRACTED || vidFleetConfigLimitDefault.dms.detectionDelay.DISTRACTED.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.DISTRACTED = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.DISTRACTED = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="distracted-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.DISTRACTED === defaultSettingsData?.settings?.dms?.repeatInterval?.DISTRACTED}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.DISTRACTED = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.DISTRACTED));
                                                                    settingsDataChanged.settings.dms.repeatInterval.DISTRACTED = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.repeatInterval.DISTRACTED = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.repeatInterval.DISTRACTED.min}
                                                        max={vidFleetConfigLimitDefault.dms.repeatInterval.DISTRACTED.max}
                                                        value={settingsData.settings.dms.repeatInterval.DISTRACTED || vidFleetConfigLimitDefault.dms.repeatInterval.DISTRACTED.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.DISTRACTED = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.DISTRACTED = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%", alignItems: "left" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="distracted-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.DISTRACTED === defaultSettingsData?.settings?.dms?.detectionReset?.DISTRACTED}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.DISTRACTED = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.DISTRACTED));
                                                                    settingsDataChanged.settings.dms.detectionReset.DISTRACTED = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.DISTRACTED = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("DISTRACTED")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionReset.DISTRACTED.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionReset.DISTRACTED.max}
                                                        value={settingsData.settings.dms.detectionReset.DISTRACTED || vidFleetConfigLimitDefault.dms.detectionReset.DISTRACTED.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.DISTRACTED = event;
                                                            settingsDataChanged.settings.dms.detectionReset.DISTRACTED = true
                                                            settingsDataChanged.settings.dms.level = true;

                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </TabContainer>
                            }
                            {
                                (settingsData.settings.dms.level === "STANDARD" || settingsData.settings.dms.level === "LITE") && settingsData.settings.eventLevel.DMS.ATTENTIVE === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_attentive"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div >
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        {
                                            !HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="attentive-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.ATTENTIVE === defaultSettingsData?.settings?.dms?.minSpeed?.ATTENTIVE}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.minSpeed.ATTENTIVE = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.ATTENTIVE));
                                                                    settingsDataChanged.settings.dms.minSpeed.ATTENTIVE = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.minSpeed.ATTENTIVE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.minSpeed.ATTENTIVE.min}
                                                        max={vidFleetConfigLimitDefault.dms.minSpeed.ATTENTIVE.max}
                                                        value={settingsData.settings.dms.minSpeed.ATTENTIVE || vidFleetConfigLimitDefault.dms.minSpeed.ATTENTIVE.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.ATTENTIVE = event;
                                                            settingsDataChanged.settings.dms.minSpeed.ATTENTIVE = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="attentive-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.ATTENTIVE === defaultSettingsData?.settings?.dms?.detectionDelay?.ATTENTIVE}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.ATTENTIVE = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.ATTENTIVE));
                                                                    settingsDataChanged.settings.dms.detectionDelay.ATTENTIVE = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionDelay.ATTENTIVE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("ATTENTIVE")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionDelay.ATTENTIVE.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionDelay.ATTENTIVE.max}
                                                        value={settingsData.settings.dms.detectionDelay.ATTENTIVE || vidFleetConfigLimitDefault.dms.detectionDelay.ATTENTIVE.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.ATTENTIVE = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.ATTENTIVE = true
                                                            settingsDataChanged.settings.dms.level = true;

                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="attentive-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.ATTENTIVE === defaultSettingsData?.settings?.dms?.repeatInterval?.ATTENTIVE}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.ATTENTIVE = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.ATTENTIVE));
                                                                    settingsDataChanged.settings.dms.repeatInterval.ATTENTIVE = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.repeatInterval.ATTENTIVE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.repeatInterval.ATTENTIVE.min}
                                                        max={vidFleetConfigLimitDefault.dms.repeatInterval.ATTENTIVE.max}
                                                        value={settingsData.settings.dms.repeatInterval.ATTENTIVE || vidFleetConfigLimitDefault.dms.repeatInterval.ATTENTIVE.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.ATTENTIVE = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.ATTENTIVE = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="attentive-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.ATTENTIVE === defaultSettingsData?.settings?.dms?.detectionReset?.ATTENTIVE}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.ATTENTIVE = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.ATTENTIVE));
                                                                    settingsDataChanged.settings.dms.detectionReset.ATTENTIVE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.ATTENTIVE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("ATTENTIVE")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionReset.ATTENTIVE.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionReset.ATTENTIVE.max}
                                                        value={settingsData.settings.dms.detectionReset.ATTENTIVE || vidFleetConfigLimitDefault.dms.detectionReset.ATTENTIVE.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.ATTENTIVE = event;
                                                            settingsDataChanged.settings.dms.detectionReset.ATTENTIVE = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>

                                    </Grid>
                                </TabContainer>
                            }
                            {
                                (settingsData.settings.dms.level === "STANDARD" || settingsData.settings.dms.level === "LITE") && settingsData.settings.eventLevel.DMS.NO_SEATBELT === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_no_seatbelt"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div >
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        {
                                            !HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <div>
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="no_seatbelt-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.NO_SEATBELT === defaultSettingsData?.settings?.dms?.minSpeed?.NO_SEATBELT}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.minSpeed.NO_SEATBELT = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.NO_SEATBELT));
                                                                    settingsDataChanged.settings.dms.minSpeed.NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.minSpeed.NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.minSpeed.NO_SEATBELT.min}
                                                        max={vidFleetConfigLimitDefault.dms.minSpeed.NO_SEATBELT.max}
                                                        value={settingsData.settings.dms.minSpeed.NO_SEATBELT || vidFleetConfigLimitDefault.dms.minSpeed.NO_SEATBELT.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.NO_SEATBELT = event;
                                                            settingsDataChanged.settings.dms.minSpeed.NO_SEATBELT = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </div>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <div>
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="no_seatbelt-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.NO_SEATBELT === defaultSettingsData?.settings?.dms?.detectionDelay?.NO_SEATBELT}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.NO_SEATBELT = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.NO_SEATBELT));
                                                                    settingsDataChanged.settings.dms.detectionDelay.NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionDelay.NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("NO_SEATBELT")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionDelay.NO_SEATBELT.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionDelay.NO_SEATBELT.max}
                                                        value={settingsData.settings.dms.detectionDelay.NO_SEATBELT || vidFleetConfigLimitDefault.dms.detectionDelay.NO_SEATBELT.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.NO_SEATBELT = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.NO_SEATBELT = true
                                                            settingsDataChanged.settings.dms.level = true;

                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </div>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <div>
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="no_seatbelt-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.NO_SEATBELT === defaultSettingsData?.settings?.dms?.repeatInterval?.NO_SEATBELT}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.NO_SEATBELT = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.NO_SEATBELT));
                                                                    settingsDataChanged.settings.dms.repeatInterval.NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.repeatInterval.NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.repeatInterval.NO_SEATBELT.min}
                                                        max={vidFleetConfigLimitDefault.dms.repeatInterval.NO_SEATBELT.max}
                                                        value={settingsData.settings.dms.repeatInterval.NO_SEATBELT || vidFleetConfigLimitDefault.dms.repeatInterval.NO_SEATBELT.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.NO_SEATBELT = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.NO_SEATBELT = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </div>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <div>
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="no_seatbelt-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.NO_SEATBELT === defaultSettingsData?.settings?.dms?.detectionReset?.NO_SEATBELT}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.NO_SEATBELT = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.NO_SEATBELT));
                                                                    settingsDataChanged.settings.dms.detectionReset.NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("NO_SEATBELT")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionReset.NO_SEATBELT.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionReset.NO_SEATBELT.max}
                                                        value={settingsData.settings.dms.detectionReset.NO_SEATBELT || vidFleetConfigLimitDefault.dms.detectionReset.NO_SEATBELT.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.NO_SEATBELT = event;
                                                            settingsDataChanged.settings.dms.detectionReset.NO_SEATBELT = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </div>
                                        </Grid>
                                    </Grid>
                                </TabContainer>
                            }
                            {
                                (settingsData.settings.dms.level === "STANDARD" || settingsData.settings.dms.level === "LITE") && settingsData.settings.eventLevel.DMS.YAWN === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_yawn"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div >
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        {
                                            !HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="yawn-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.YAWN === defaultSettingsData?.settings?.dms?.minSpeed?.YAWN}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.minSpeed.YAWN = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.YAWN));
                                                                    settingsDataChanged.settings.dms.minSpeed.YAWN = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.minSpeed.YAWN = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.minSpeed.YAWN.min}
                                                        max={vidFleetConfigLimitDefault.dms.minSpeed.YAWN.max}
                                                        value={settingsData.settings.dms.minSpeed.YAWN || vidFleetConfigLimitDefault.dms.minSpeed.YAWN.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.YAWN = event;
                                                            settingsDataChanged.settings.dms.minSpeed.YAWN = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} /* style={{ marginLeft: 20 }} */>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="yawn-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.YAWN === defaultSettingsData?.settings?.dms?.detectionDelay?.YAWN}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.YAWN = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.YAWN));
                                                                    settingsDataChanged.settings.dms.detectionDelay.YAWN = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionDelay.YAWN = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })

                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("YAWN")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionDelay.YAWN.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionDelay.YAWN.max}
                                                        value={settingsData.settings.dms.detectionDelay.YAWN || vidFleetConfigLimitDefault.dms.detectionDelay.YAWN.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.YAWN = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.YAWN = true
                                                            settingsDataChanged.settings.dms.level = true;

                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="yawn-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.YAWN === defaultSettingsData?.settings?.dms?.repeatInterval?.YAWN}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.YAWN = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.YAWN));
                                                                    settingsDataChanged.settings.dms.repeatInterval.YAWN = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.repeatInterval.YAWN = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.repeatInterval.YAWN.min}
                                                        max={vidFleetConfigLimitDefault.dms.repeatInterval.YAWN.max}
                                                        value={settingsData.settings.dms.repeatInterval.YAWN || vidFleetConfigLimitDefault.dms.repeatInterval.YAWN.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.YAWN = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.YAWN = true
                                                            settingsDataChanged.settings.dms.level = true;

                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ /* marginLeft: 20, */ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="yawn-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.YAWN === defaultSettingsData?.settings?.dms?.detectionReset?.YAWN}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.YAWN = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.YAWN));
                                                                    settingsDataChanged.settings.dms.detectionReset.YAWN = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.YAWN = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("YAWN")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionReset.YAWN.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionReset.YAWN.max}
                                                        value={settingsData.settings.dms.detectionReset.YAWN || vidFleetConfigLimitDefault.dms.detectionReset.YAWN.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.YAWN = event;
                                                            settingsDataChanged.settings.dms.detectionReset.YAWN = true
                                                            settingsDataChanged.settings.dms.level = true;

                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </TabContainer>
                            }
                            {
                                (settingsData.settings.dms.level === "STANDARD" || settingsData.settings.dms.level === "LITE") && settingsData.settings.eventLevel.DMS.SMOKING === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_smoking"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div >
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        {
                                            !HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="smoking-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.SMOKING === defaultSettingsData?.settings?.dms?.minSpeed?.SMOKING}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.minSpeed.SMOKING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.SMOKING));
                                                                    settingsDataChanged.settings.dms.minSpeed.SMOKING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.minSpeed.SMOKING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.minSpeed.SMOKING.min}
                                                        max={vidFleetConfigLimitDefault.dms.minSpeed.SMOKING.max}
                                                        value={settingsData.settings.dms.minSpeed.SMOKING || vidFleetConfigLimitDefault.dms.minSpeed.SMOKING.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.SMOKING = event;
                                                            settingsDataChanged.settings.dms.minSpeed.SMOKING = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="smoking-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.SMOKING === defaultSettingsData?.settings?.dms?.detectionDelay?.SMOKING}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.SMOKING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.SMOKING));
                                                                    settingsDataChanged.settings.dms.detectionDelay.SMOKING = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionDelay.SMOKING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("SMOKING")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionDelay.SMOKING.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionDelay.SMOKING.max}
                                                        value={settingsData.settings.dms.detectionDelay.SMOKING || vidFleetConfigLimitDefault.dms.detectionDelay.SMOKING.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.SMOKING = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.SMOKING = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="smoking-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.SMOKING === defaultSettingsData?.settings?.dms?.repeatInterval?.SMOKING}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.SMOKING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.SMOKING));
                                                                    settingsDataChanged.settings.dms.repeatInterval.SMOKING = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.repeatInterval.SMOKING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.repeatInterval.SMOKING.min}
                                                        max={vidFleetConfigLimitDefault.dms.repeatInterval.SMOKING.max}
                                                        value={settingsData.settings.dms.repeatInterval.SMOKING || vidFleetConfigLimitDefault.dms.repeatInterval.SMOKING.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.SMOKING = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.SMOKING = true
                                                            settingsDataChanged.settings.dms.level = true;

                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="smoking-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.SMOKING === defaultSettingsData?.settings?.dms?.detectionReset?.SMOKING}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.SMOKING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.SMOKING));
                                                                    settingsDataChanged.settings.dms.detectionReset.SMOKING = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.SMOKING = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("SMOKING")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionReset.SMOKING.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionReset.SMOKING.max}
                                                        value={settingsData.settings.dms.detectionReset.SMOKING || vidFleetConfigLimitDefault.dms.detectionReset.SMOKING.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.SMOKING = event;
                                                            settingsDataChanged.settings.dms.detectionReset.SMOKING = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </TabContainer>
                            }
                            {
                                settingsData.settings.dms.level === "STANDARD" && settingsData.settings.eventLevel.DMS.ASLEEP === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_asleep"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div >
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        {
                                            !HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="asleep-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.ASLEEP === defaultSettingsData?.settings?.dms?.minSpeed?.ASLEEP}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.minSpeed.ASLEEP = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.ASLEEP));
                                                                    settingsDataChanged.settings.dms.minSpeed.ASLEEP = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.minSpeed.ASLEEP = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.minSpeed.ASLEEP.min}
                                                        max={vidFleetConfigLimitDefault.dms.minSpeed.ASLEEP.max}
                                                        value={settingsData.settings.dms.minSpeed.ASLEEP || vidFleetConfigLimitDefault.dms.minSpeed.ASLEEP.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.ASLEEP = event;
                                                            settingsDataChanged.settings.dms.minSpeed.ASLEEP = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="asleep-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.ASLEEP === defaultSettingsData?.settings?.dms?.detectionDelay?.ASLEEP}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.ASLEEP = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.ASLEEP));
                                                                    settingsDataChanged.settings.dms.detectionDelay.ASLEEP = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionDelay.ASLEEP = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("ASLEEP")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionDelay.ASLEEP.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionDelay.ASLEEP.max}
                                                        value={settingsData.settings.dms.detectionDelay.ASLEEP || vidFleetConfigLimitDefault.dms.detectionDelay.ASLEEP.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.ASLEEP = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.ASLEEP = true
                                                            settingsDataChanged.settings.dms.level = true;

                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="asleep-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.ASLEEP === defaultSettingsData?.settings?.dms?.repeatInterval?.ASLEEP}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.ASLEEP = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.ASLEEP));
                                                                    settingsDataChanged.settings.dms.repeatInterval.ASLEEP = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.repeatInterval.ASLEEP = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.repeatInterval.ASLEEP.min}
                                                        max={vidFleetConfigLimitDefault.dms.repeatInterval.ASLEEP.max}
                                                        value={settingsData.settings.dms.repeatInterval.ASLEEP || vidFleetConfigLimitDefault.dms.repeatInterval.ASLEEP.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.ASLEEP = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.ASLEEP = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="asleep-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.ASLEEP === defaultSettingsData?.settings?.dms?.detectionReset?.ASLEEP}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.ASLEEP = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.ASLEEP));
                                                                    settingsDataChanged.settings.dms.detectionReset.ASLEEP = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.ASLEEP = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("ASLEEP")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionReset.ASLEEP.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionReset.ASLEEP.max}
                                                        value={settingsData.settings.dms.detectionReset.ASLEEP || vidFleetConfigLimitDefault.dms.detectionReset.ASLEEP.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.ASLEEP = event;
                                                            settingsDataChanged.settings.dms.detectionReset.ASLEEP = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>

                                    </Grid>
                                </TabContainer>
                            }
                            {
                                (settingsData.settings.dms.level === "STANDARD" || settingsData.settings.dms.level === "LITE") && settingsData.settings.eventLevel.DMS.NO_DRIVER === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_no_driver"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div >
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        {
                                            !HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="no-driver-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.NO_DRIVER === defaultSettingsData?.settings?.dms?.minSpeed?.NO_DRIVER}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.minSpeed.NO_DRIVER = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.NO_DRIVER));
                                                                    settingsDataChanged.settings.dms.minSpeed.NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.minSpeed.NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.minSpeed.NO_DRIVER.min}
                                                        max={vidFleetConfigLimitDefault.dms.minSpeed.NO_DRIVER.max}
                                                        value={settingsData.settings.dms.minSpeed.NO_DRIVER || vidFleetConfigLimitDefault.dms.minSpeed.NO_DRIVER.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.NO_DRIVER = event;
                                                            settingsDataChanged.settings.dms.minSpeed.NO_DRIVER = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} /* style={{ marginLeft: 20 }} */>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="no-driver-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.NO_DRIVER === defaultSettingsData?.settings?.dms?.detectionDelay?.NO_DRIVER}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.NO_DRIVER = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.NO_DRIVER));
                                                                    settingsDataChanged.settings.dms.detectionDelay.NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionDelay.NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("NO_DRIVER")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionDelay.NO_DRIVER.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionDelay.NO_DRIVER.max}
                                                        value={settingsData.settings.dms.detectionDelay.NO_DRIVER || vidFleetConfigLimitDefault.dms.detectionDelay.NO_DRIVER.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.NO_DRIVER = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.NO_DRIVER = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="no-driver-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.NO_DRIVER === defaultSettingsData?.settings?.dms?.repeatInterval?.NO_DRIVER}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.NO_DRIVER = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.NO_DRIVER));
                                                                    settingsDataChanged.settings.dms.repeatInterval.NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.repeatInterval.NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.repeatInterval.NO_DRIVER.min}
                                                        max={vidFleetConfigLimitDefault.dms.repeatInterval.NO_DRIVER.max}
                                                        value={settingsData.settings.dms.repeatInterval.NO_DRIVER || vidFleetConfigLimitDefault.dms.repeatInterval.NO_DRIVER.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.NO_DRIVER = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.NO_DRIVER = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ /* marginLeft: 20, */ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="no-driver-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.NO_DRIVER === defaultSettingsData?.settings?.dms?.detectionReset?.NO_DRIVER}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.NO_DRIVER = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.NO_DRIVER));
                                                                    settingsDataChanged.settings.dms.detectionReset.NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("NO_DRIVER")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionReset.NO_DRIVER.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionReset.NO_DRIVER.max}
                                                        value={settingsData.settings.dms.detectionReset.NO_DRIVER || vidFleetConfigLimitDefault.dms.detectionReset.NO_DRIVER.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.NO_DRIVER = event;
                                                            settingsDataChanged.settings.dms.detectionReset.NO_DRIVER = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </TabContainer>
                            }
                            {
                                (settingsData.settings.dms.level === "STANDARD" || settingsData.settings.dms.level === "LITE") && settingsData.settings.eventLevel.DMS.IDLE_NO_DRIVER === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_idle_no_driver"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div >
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        {
                                            !HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="idle-no-driver-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.IDLE_NO_DRIVER === defaultSettingsData?.settings?.dms?.minSpeed?.IDLE_NO_DRIVER}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.minSpeed.IDLE_NO_DRIVER = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.IDLE_NO_DRIVER));
                                                                    settingsDataChanged.settings.dms.minSpeed.IDLE_NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.minSpeed.IDLE_NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.minSpeed.IDLE_NO_DRIVER.min}
                                                        max={vidFleetConfigLimitDefault.dms.minSpeed.IDLE_NO_DRIVER.max}
                                                        value={settingsData.settings.dms.minSpeed.IDLE_NO_DRIVER || vidFleetConfigLimitDefault.dms.minSpeed.IDLE_NO_DRIVER.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.IDLE_NO_DRIVER = event;
                                                            settingsDataChanged.settings.dms.minSpeed.IDLE_NO_DRIVER = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="idle-no-driver-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.IDLE_NO_DRIVER === defaultSettingsData?.settings?.dms?.detectionDelay?.IDLE_NO_DRIVER}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.IDLE_NO_DRIVER = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.IDLE_NO_DRIVER));
                                                                    settingsDataChanged.settings.dms.detectionDelay.IDLE_NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionDelay.IDLE_NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("IDLE_NO_DRIVER")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionDelay.IDLE_NO_DRIVER.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionDelay.IDLE_NO_DRIVER.max}
                                                        value={settingsData.settings.dms.detectionDelay.IDLE_NO_DRIVER || vidFleetConfigLimitDefault.dms.detectionDelay.IDLE_NO_DRIVER.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.IDLE_NO_DRIVER = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.IDLE_NO_DRIVER = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="idle-no-driver-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.IDLE_NO_DRIVER === defaultSettingsData?.settings?.dms?.repeatInterval?.IDLE_NO_DRIVER}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.IDLE_NO_DRIVER = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.IDLE_NO_DRIVER));
                                                                    settingsDataChanged.settings.dms.repeatInterval.IDLE_NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.repeatInterval.IDLE_NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.repeatInterval.IDLE_NO_DRIVER.min}
                                                        max={vidFleetConfigLimitDefault.dms.repeatInterval.IDLE_NO_DRIVER.max}
                                                        value={settingsData.settings.dms.repeatInterval.IDLE_NO_DRIVER || vidFleetConfigLimitDefault.dms.repeatInterval.IDLE_NO_DRIVER.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.IDLE_NO_DRIVER = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.IDLE_NO_DRIVER = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="idle-no-driver-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.IDLE_NO_DRIVER === defaultSettingsData?.settings?.dms?.detectionReset?.IDLE_NO_DRIVER}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.IDLE_NO_DRIVER = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.IDLE_NO_DRIVER));
                                                                    settingsDataChanged.settings.dms.detectionReset.IDLE_NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.IDLE_NO_DRIVER = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                        //disabled={!checkDMSSettingEnabled("IDLE_NO_DRIVER")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={vidFleetConfigLimitDefault.dms.detectionReset.IDLE_NO_DRIVER.min}
                                                        max={vidFleetConfigLimitDefault.dms.detectionReset.IDLE_NO_DRIVER.max}
                                                        value={settingsData.settings.dms.detectionReset.IDLE_NO_DRIVER || vidFleetConfigLimitDefault.dms.detectionReset.IDLE_NO_DRIVER.default}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.IDLE_NO_DRIVER = event;
                                                            settingsDataChanged.settings.dms.detectionReset.IDLE_NO_DRIVER = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </TabContainer>
                            }
                            {
                                (settingsData.settings.dms.level === "STANDARD" || settingsData.settings.dms.level === "LITE") && settingsData.settings.eventLevel.DMS.PASSENGERS_INSIDE === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_passengers_inside"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div >
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        {
                                            /*!HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>*/
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="passengers-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.PASSENGERS_INSIDE === defaultSettingsData?.settings?.dms?.minSpeed?.PASSENGERS_INSIDE}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.minSpeed.PASSENGERS_INSIDE = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.PASSENGERS_INSIDE));
                                                                    settingsDataChanged.settings.dms.minSpeed.PASSENGERS_INSIDE = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.minSpeed.PASSENGERS_INSIDE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={!HasPermission(user, "vfdmst", "u")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={0}
                                                        max={193}
                                                        value={settingsData.settings.dms.minSpeed.PASSENGERS_INSIDE || 0}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.PASSENGERS_INSIDE = event;
                                                            settingsDataChanged.settings.dms.minSpeed.PASSENGERS_INSIDE = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="passengers-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.PASSENGERS_INSIDE === defaultSettingsData?.settings?.dms?.detectionDelay?.PASSENGERS_INSIDE}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.PASSENGERS_INSIDE = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.PASSENGERS_INSIDE));
                                                                    settingsDataChanged.settings.dms.detectionDelay.PASSENGERS_INSIDE = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionDelay.PASSENGERS_INSIDE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={!HasPermission(user, "vfdmst", "u")}

                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={0}
                                                        max={4000}
                                                        value={settingsData.settings.dms.detectionDelay.PASSENGERS_INSIDE || 0}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.PASSENGERS_INSIDE = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.PASSENGERS_INSIDE = true
                                                            settingsDataChanged.settings.dms.level = true;

                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="passengers-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.PASSENGERS_INSIDE === defaultSettingsData?.settings?.dms?.repeatInterval?.PASSENGERS_INSIDE}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.PASSENGERS_INSIDE = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.PASSENGERS_INSIDE));
                                                                    settingsDataChanged.settings.dms.repeatInterval.PASSENGERS_INSIDE = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.repeatInterval.PASSENGERS_INSIDE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={0}
                                                        max={40000}
                                                        value={settingsData.settings.dms.repeatInterval.PASSENGERS_INSIDE || 0}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.PASSENGERS_INSIDE = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.PASSENGERS_INSIDE = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="passengers-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.PASSENGERS_INSIDE === defaultSettingsData?.settings?.dms?.detectionReset?.PASSENGERS_INSIDE}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.PASSENGERS_INSIDE = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.PASSENGERS_INSIDE));
                                                                    settingsDataChanged.settings.dms.detectionReset.PASSENGERS_INSIDE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.PASSENGERS_INSIDE = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={!HasPermission(user, "vfdmst", "u")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={0}
                                                        max={4000}
                                                        value={settingsData.settings.dms.detectionReset.PASSENGERS_INSIDE || 0}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.PASSENGERS_INSIDE = event;
                                                            settingsDataChanged.settings.dms.detectionReset.PASSENGERS_INSIDE = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>

                                    </Grid>
                                </TabContainer>
                            }
                            {
                                (settingsData.settings.dms.level === "STANDARD" || settingsData.settings.dms.level === "LITE") && settingsData.settings.eventLevel.DMS.PASSENGERS_NO_SEATBELT === "DEFAULT" &&
                                <TabContainer key={`menu-${indexKey++}`} label={messages["vidfleet_configuration_passengers_no_seatbelt"]} index={indexCount++}>
                                    <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                        <div >
                                            <h6>
                                                {messages["vidfleet_configuration_thresholds"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        {
                                            /*!HasPermission(user, "vfdmst", "u") &&
                                            <Grid item xs={12}>
                                                <Alert severity="warning" style={{ marginBottom: 10 }}>
                                                    {messages['vidfleet_configuration_permission_message']}
                                                </Alert>
                                            </Grid>*/
                                        }
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_min_speed"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="passengers-no-seatbelt-min_speed"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.minSpeed.PASSENGERS_NO_SEATBELT === defaultSettingsData?.settings?.dms?.minSpeed?.PASSENGERS_NO_SEATBELT}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.minSpeed.PASSENGERS_NO_SEATBELT = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.minSpeed?.PASSENGERS_NO_SEATBELT));
                                                                    settingsDataChanged.settings.dms.minSpeed.PASSENGERS_NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.minSpeed.PASSENGERS_NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={!HasPermission(user, "vfdmst", "u")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={0}
                                                        max={193}
                                                        value={settingsData.settings.dms.minSpeed.PASSENGERS_NO_SEATBELT || 0}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.minSpeed)
                                                                settingsData.settings.dms.minSpeed = {}

                                                            settingsData.settings.dms.minSpeed.PASSENGERS_NO_SEATBELT = event;
                                                            settingsDataChanged.settings.dms.minSpeed.PASSENGERS_NO_SEATBELT = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_km_h"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_delay"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="passengers-no-seatbelt-detection_delay"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT === defaultSettingsData?.settings?.dms?.detectionDelay?.PASSENGERS_NO_SEATBELT}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionDelay?.PASSENGERS_NO_SEATBELT));
                                                                    settingsDataChanged.settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={!HasPermission(user, "vfdmst", "u")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={0}
                                                        max={4000}
                                                        value={settingsData.settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT || 0}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionDelay)
                                                                settingsData.settings.dms.detectionDelay = {}

                                                            settingsData.settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT = event;
                                                            settingsDataChanged.settings.dms.detectionDelay.PASSENGERS_NO_SEATBELT = true
                                                            settingsDataChanged.settings.dms.level = true;

                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_repeat_interval"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="passengers-no-seatbelt-repeat_interval"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT === defaultSettingsData?.settings?.dms?.repeatInterval?.PASSENGERS_NO_SEATBELT}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.repeatInterval?.PASSENGERS_NO_SEATBELT));
                                                                    settingsDataChanged.settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;

                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={0}
                                                        max={40000}
                                                        value={settingsData.settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT || 0}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.repeatInterval)
                                                                settingsData.settings.dms.repeatInterval = {}

                                                            settingsData.settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT = event;
                                                            settingsDataChanged.settings.dms.repeatInterval.PASSENGERS_NO_SEATBELT = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                            <Grid item xs="auto">
                                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                    <div style={{ width: "50%" }}>
                                                        {messages["vidfleet_configuration_detection_reset"]}
                                                    </div>
                                                    <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                        <ToggleSwitch
                                                            id="passengers-no-seatbelt-detection_reset"
                                                            darkBlueToggle={true}
                                                            extraLarge={true}
                                                            optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                            checked={settingsData.settings.dms.detectionReset.PASSENGERS_NO_SEATBELT === defaultSettingsData?.settings?.dms?.detectionReset?.PASSENGERS_NO_SEATBELT}
                                                            onChange={(checked) => {
                                                                if (checked) {

                                                                    settingsData.settings.dms.detectionReset.PASSENGERS_NO_SEATBELT = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.dms?.detectionReset?.PASSENGERS_NO_SEATBELT));
                                                                    settingsDataChanged.settings.dms.detectionReset.PASSENGERS_NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                } else {
                                                                    optionDefaults.DMSEventsDefault = false;
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                    settingsDataChanged.settings.dms.detectionReset.PASSENGERS_NO_SEATBELT = true
                                                                    settingsDataChanged.settings.dms.level = true;
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                }
                                                            }}
                                                            disabled={!HasPermission(user, "vfdmst", "u")}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: "15px" }}>
                                                    <Slider
                                                        min={0}
                                                        max={4000}
                                                        value={settingsData.settings.dms.detectionReset.PASSENGERS_NO_SEATBELT || 0}
                                                        onChange={(event) => {

                                                            if (!settingsData.settings.dms)
                                                                settingsData.settings.dms = {}
                                                            if (!settingsData.settings.dms.detectionReset)
                                                                settingsData.settings.dms.detectionReset = {}

                                                            settingsData.settings.dms.detectionReset.PASSENGERS_NO_SEATBELT = event;
                                                            settingsDataChanged.settings.dms.detectionReset.PASSENGERS_NO_SEATBELT = true
                                                            settingsDataChanged.settings.dms.level = true;
                                                            optionDefaults.DMSEventsDefault = false;
                                                            setSettingsData({ ...settingsData })
                                                            saveChangedSettings({ ...settingsDataChanged })
                                                            setSaveSettingsAvailable(true);
                                                            setOptionDefaults({ ...optionDefaults })
                                                        }}
                                                        sliderOneColor
                                                        showValue
                                                        valueLabelDisplay="off"
                                                        measure={messages["vidfleet_configuration_10_s"]}
                                                        disabled={!HasPermission(user, "vfdmst", "u")}
                                                    />
                                                </div>
                                            </Grid>
                                        </Grid>

                                    </Grid>
                                </TabContainer>
                            }

                        </Tabs>
                    </Grid>
                }
            </Grid>
        );
    }

    const cev = () => {

        const toggleCevEvent = (eventType) => {
            if (!HasPermission(user, "vfcev", "u")) {
                return;
            }
            if (!settingsData.settings.cev) {
                settingsData.settings.cev = { enabledEvents: [] };
            }
            let enabledEvents = settingsData.settings.cev.enabledEvents || [];
            if (enabledEvents.includes(eventType)) {
                enabledEvents = enabledEvents.filter(event => event !== eventType);
            } else {
                enabledEvents = [...enabledEvents, eventType];
            }
            settingsData.settings.cev.enabledEvents = enabledEvents;

            if (!settingsDataChanged.settings.cev) {
                settingsDataChanged.settings.cev = {};
            }
            settingsDataChanged.settings.cev.enabledEvents = true;

            optionDefaults.cevDefault = false;
            setSettingsData({ ...settingsData })
            saveChangedSettings({ ...settingsDataChanged })
            setSaveSettingsAvailable(true);
            setOptionDefaults({ ...optionDefaults })
        }

        const cevEvents = [
            { value: "NO_SEATBELT", label: "vidfleet_configuration_cev_no_seatbelt" },
            { value: "USING_PHONE", label: "vidfleet_configuration_cev_using_phone" },
            { value: "NO_DRIVER", label: "vidfleet_configuration_cev_no_driver" },
            { value: "PASSENGERS_NO_SEATBELT", label: "vidfleet_configuration_cev_passengers_no_seatbelt" },
            { value: "SMOKING", label: "vidfleet_configuration_cev_smoking" },
            { value: "POTENTIAL_INCIDENT", label: "vidfleet_configuration_cev_potential_incident" }
        ];

        let enabledEvents = settingsData?.settings?.cev?.enabledEvents || [];

        let showUsingPhoneWarning = enabledEvents.includes("USING_PHONE") &&
            (settingsData?.settings?.advanced?.privacyControl?.level !== "NONE" ||
                settingsData?.settings?.recordConfig?.config === "FRONT_ONLY");

        let hasCevPermission = HasPermission(user, "vfcev", "u");

        return (
            !optionCollapsed.colapsedCev &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                {
                    !hasCevPermission &&
                    <Grid item xs={12} style={{ marginLeft: 44 }}>
                        <Alert severity="warning" style={{ marginBottom: 10 }}>
                            {messages['vidfleet_configuration_permission_message']}
                        </Alert>
                    </Grid>
                }
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    {messages["vidfleet_configuration_cev_events"]}
                    <div style={{ marginLeft: 0, marginTop: 10 }}>
                        {
                            cevEvents.map(event => (
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key={`cev-${event.value}`}
                                    onClick={() => toggleCevEvent(event.value)}
                                    style={{ cursor: hasCevPermission ? 'pointer' : 'not-allowed' }}
                                >
                                    {
                                        props.getBadget(messages[event.label], enabledEvents.includes(event.value))
                                    }
                                </div>
                            ))
                        }
                    </div>
                    {
                        showUsingPhoneWarning &&
                        <Alert severity="info" style={{ marginBottom: 10, marginTop: 10 }}>
                            {messages["vidfleet_configuration_cev_using_phone_warning"]}
                        </Alert>
                    }
                </Grid>
            </Grid>
        );
    }

    const videoStreams = () => {
        return (
            !optionCollapsed.colapsedVideoStream &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={2} style={{ marginTop: -5 }}>
                            <ToggleSwitch
                                id="stream0Enable"
                                blueToggle={true}
                                large={true}
                                label={messages["vidfleet_configuration_stream0"]}
                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                checked={settingsData.settings.videoStreams[0].enable ? true : false}
                                onChange={(checked) => {
                                    settingsData.settings.videoStreams[0].enable = checked;
                                    settingsDataChanged.settings.videoStreams[0].enable = true
                                    optionDefaults.videoStreamDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                width={100}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            {messages["vidfleet_configuration_high_bit_rate"]}
                            <div style={{ marginLeft: 0 }}>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="high_bit_rate_super_low"
                                    onClick={e => {

                                        if (settingsData.settings.videoStreams[0].enable) {
                                            settingsData.settings.videoStreams[0].quality = "SUPERLOW";
                                            settingsDataChanged.settings.videoStreams[0].quality = true
                                        }
                                        optionDefaults.videoStreamDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_super_low"], settingsData.settings.videoStreams[0].enable ? settingsData.settings.videoStreams[0].quality === "SUPERLOW" : false)
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="high_bit_rate-low"
                                    onClick={e => {
                                        if (settingsData.settings.videoStreams[0].enable) {
                                            settingsData.settings.videoStreams[0].quality = "LOW";
                                            settingsDataChanged.settings.videoStreams[0].quality = true
                                        }
                                        optionDefaults.videoStreamDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_low"], settingsData.settings.videoStreams[0].enable ? settingsData.settings.videoStreams[0].quality === "LOW" : false)
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="high_bit_rate-normal"
                                    onClick={e => {
                                        settingsData.settings.videoStreams[0].quality = "NORMAL";
                                        settingsDataChanged.settings.videoStreams[0].quality = true
                                        optionDefaults.videoStreamDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_normal"], settingsData.settings.videoStreams[0].enable ? settingsData.settings.videoStreams[0].quality === "NORMAL" : false)
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="high_bit_rate-high"
                                    onClick={e => {
                                        if (settingsData.settings.videoStreams[0].enable) {
                                            settingsData.settings.videoStreams[0].quality = "HIGH";
                                            settingsDataChanged.settings.videoStreams[0].quality = true;
                                        }
                                        optionDefaults.videoStreamDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_high"], settingsData.settings.videoStreams[0].enable ? settingsData.settings.videoStreams[0].quality === "HIGH" : false)
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="high_bit_rate-super"
                                    onClick={e => {
                                        if (settingsData.settings.videoStreams[0].enable) {
                                            settingsData.settings.videoStreams[0].quality = "SUPER";
                                            settingsDataChanged.settings.videoStreams[0].quality = true
                                        }
                                        optionDefaults.videoStreamDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_super"], settingsData.settings.videoStreams[0].enable ? settingsData.settings.videoStreams[0].quality === "SUPER" : false)
                                    }
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={2} style={{ marginTop: -5 }}>
                            <ToggleSwitch
                                id="stream1Enable"
                                blueToggle={true}
                                large={true}
                                label={messages["vidfleet_configuration_stream1"]}
                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                checked={settingsData.settings.videoStreams[1].enable ? true : false}
                                onChange={(checked) => {
                                    settingsData.settings.videoStreams[1].enable = checked;
                                    settingsDataChanged.settings.videoStreams[1].enable = true

                                    optionDefaults.videoStreamDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                width={100}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            {messages["vidfleet_configuration_low_bit_rate"]}
                            <div style={{ marginLeft: 0 }}>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="low-bit-rate-low"
                                    onClick={e => {
                                        if (settingsData.settings.videoStreams[1].enable) {
                                            settingsData.settings.videoStreams[1].quality = "LOW";
                                            settingsDataChanged.settings.videoStreams[1].quality = true
                                        }
                                        optionDefaults.videoStreamDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_low"], settingsData.settings.videoStreams[1].enable ? settingsData.settings.videoStreams[1].quality === "LOW" : false)
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="low-bit-rate-normal"
                                    onClick={e => {

                                        if (settingsData.settings.videoStreams[1].enable) {
                                            settingsData.settings.videoStreams[1].quality = "NORMAL";
                                            settingsDataChanged.settings.videoStreams[1].quality = true
                                        }
                                        optionDefaults.videoStreamDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_normal"], settingsData.settings.videoStreams[1].enable ? settingsData.settings.videoStreams[1].quality === "NORMAL" : false)
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="low-bit-rate-high"
                                    onClick={e => {
                                        if (settingsData.settings.videoStreams[1].enable) {
                                            settingsData.settings.videoStreams[1].quality = "HIGH";
                                            settingsDataChanged.settings.videoStreams[1].quality = true
                                        }
                                        optionDefaults.videoStreamDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_high"], settingsData.settings.videoStreams[1].enable ? settingsData.settings.videoStreams[1].quality === "HIGH" : false)
                                    }
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    const recordConfig = () => {

        const isButtonDisabled = validateProperties();
        let buttonStates = settingsData.settings

        let curretStates = {
            adasLevel: buttonStates?.advanced?.adas?.level,
            auxModel: buttonStates?.advanced?.aux?.model,
            privacyControl: buttonStates?.advanced?.privacyControl?.level,
            dmsLevel: buttonStates?.dms?.level
        }

        return (
            !optionCollapsed.colapsedVideoResolution &&
            <Grid container>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={12}>
                            {messages["vidfleet_configuration_video_resolution"]}
                            <div style={{ marginLeft: 0 }}>
                                {deviceInfo?.recordConfigList?.includes("MAIN") &&
                                    <div className="d-sm-inline-block mb-1 vidfleet-pills" key="video_resolution_main">
                                        <div
                                            onClick={e => {
                                                if (isButtonDisabled({ ...curretStates, recordProfile: "MAIN" })) {
                                                    NotificationManager.warning(
                                                        <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                        messages["vidfleet_configuration_combination_not_allowed"],
                                                        6000,
                                                        null,
                                                        null,
                                                        'error'
                                                    );
                                                }
                                                settingsData.settings.recordConfig.config = "MAIN";
                                                settingsDataChanged.settings.recordConfig.config = true

                                                optionDefaults.videoResolutionDefault = false;
                                                setSettingsData({ ...settingsData })
                                                saveChangedSettings({ ...settingsDataChanged })
                                                setSaveSettingsAvailable(true);
                                                setOptionDefaults({ ...optionDefaults })
                                            }}
                                            style={{ cursor: isButtonDisabled({ ...curretStates, recordProfile: "MAIN" }) ? 'not-allowed' : 'pointer' }}
                                        >
                                            {
                                                props.getBadget(messages["vidfleet_configuration_video_resolution_main"], settingsData.settings.recordConfig.config === "MAIN")
                                            }
                                        </div>
                                    </div>
                                }
                                {deviceInfo?.recordConfigList?.includes("HIGH") && devices?.length &&
                                    !devices[0].deviceModel.name.includes("AIR1") &&
                                    !devices[0].deviceModel.name.includes("AIR2") &&
                                    <div className="d-sm-inline-block mb-1 vidfleet-pills" key="video_resolution_high">
                                        <div
                                            onClick={e => {
                                                if (isButtonDisabled({ ...curretStates, recordProfile: "HIGH" })) {
                                                    NotificationManager.warning(
                                                        <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                        messages["vidfleet_configuration_combination_not_allowed"],
                                                        6000,
                                                        null,
                                                        null,
                                                        'error'
                                                    );
                                                }
                                                settingsData.settings.recordConfig.config = "HIGH";
                                                settingsDataChanged.settings.recordConfig.config = true
                                                optionDefaults.videoResolutionDefault = false;
                                                setSettingsData({ ...settingsData })
                                                saveChangedSettings({ ...settingsDataChanged })
                                                setSaveSettingsAvailable(true);
                                                setOptionDefaults({ ...optionDefaults })
                                            }}
                                            style={{ cursor: isButtonDisabled({ ...curretStates, recordProfile: "HIGH" }) ? 'not-allowed' : 'pointer' }}
                                        >
                                            {
                                                props.getBadget(messages["vidfleet_configuration_video_resolution_high"], settingsData.settings.recordConfig.config === "HIGH")
                                            }
                                        </div>
                                    </div>
                                }
                                {deviceInfo?.recordConfigList?.includes("LOW") &&
                                    <div className="d-sm-inline-block mb-1 vidfleet-pills" key="video_resolution_low">
                                        <div
                                            onClick={e => {
                                                if (isButtonDisabled({ ...curretStates, recordProfile: "LOW" })) {
                                                    NotificationManager.warning(
                                                        <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                        messages["vidfleet_configuration_combination_not_allowed"],
                                                        6000,
                                                        null,
                                                        null,
                                                        'error'
                                                    );
                                                }
                                                settingsData.settings.recordConfig.config = "LOW";
                                                settingsDataChanged.settings.recordConfig.config = true
                                                optionDefaults.videoResolutionDefault = false;
                                                setSettingsData({ ...settingsData })
                                                saveChangedSettings({ ...settingsDataChanged })
                                                setSaveSettingsAvailable(true);
                                                setOptionDefaults({ ...optionDefaults })
                                            }}
                                            style={{ cursor: isButtonDisabled({ ...curretStates, recordProfile: "LOW" }) ? 'not-allowed' : 'pointer' }}
                                        >
                                            {
                                                props.getBadget(messages["vidfleet_configuration_video_resolution_low"], settingsData.settings.recordConfig.config === "LOW")
                                            }
                                        </div>
                                    </div>
                                }
                                {deviceInfo?.recordConfigList?.includes("LOW_PRIV") &&
                                    !devices[0].deviceModel.name.includes("AIR1") &&
                                    <div className="d-sm-inline-block mb-1 vidfleet-pills" key="video_resolution_low_priv">
                                        <div
                                            onClick={e => {
                                                if (isButtonDisabled({ ...curretStates, recordProfile: "LOW_PRIV" })) {
                                                    NotificationManager.warning(
                                                        <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                        messages["vidfleet_configuration_combination_not_allowed"],
                                                        6000,
                                                        null,
                                                        null,
                                                        'error'
                                                    );
                                                }
                                                settingsData.settings.recordConfig.config = "LOW_PRIV";
                                                settingsDataChanged.settings.recordConfig.config = true
                                                optionDefaults.videoResolutionDefault = false;
                                                setSettingsData({ ...settingsData })
                                                saveChangedSettings({ ...settingsDataChanged })
                                                setSaveSettingsAvailable(true);
                                                setOptionDefaults({ ...optionDefaults })
                                            }}
                                            style={{ cursor: isButtonDisabled({ ...curretStates, recordProfile: "LOW_PRIV" }) ? 'not-allowed' : 'pointer' }}
                                        >
                                            {
                                                props.getBadget(messages["vidfleet_configuration_video_resolution_low_priv"], settingsData.settings.recordConfig.config === "LOW_PRIV")
                                            }
                                        </div>
                                    </div>
                                }
                                {deviceInfo?.recordConfigList?.includes("FRONT_ONLY") &&
                                    !devices[0].deviceModel.name.includes("AIR1") &&
                                    <div className="d-sm-inline-block mb-1 vidfleet-pills" key="video_resolution_front_only">
                                        <div
                                            onClick={e => {
                                                if (isButtonDisabled({ ...curretStates, recordProfile: "FRONT_ONLY" })) {
                                                    NotificationManager.warning(
                                                        <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                        messages["vidfleet_configuration_combination_not_allowed"],
                                                        6000,
                                                        null,
                                                        null,
                                                        'error'
                                                    );
                                                }
                                                settingsData.settings.recordConfig.config = "FRONT_ONLY";
                                                settingsDataChanged.settings.recordConfig.config = true
                                                optionDefaults.videoResolutionDefault = false;
                                                setSettingsData({ ...settingsData })
                                                saveChangedSettings({ ...settingsDataChanged })
                                                setSaveSettingsAvailable(true);
                                                setOptionDefaults({ ...optionDefaults })
                                            }}
                                            style={{ cursor: isButtonDisabled({ ...curretStates, recordProfile: "FRONT_ONLY" }) ? 'not-allowed' : 'pointer' }}
                                        >
                                            {
                                                props.getBadget(messages["vidfleet_configuration_video_resolution_front_only"], settingsData.settings.recordConfig.config === "FRONT_ONLY")
                                            }
                                        </div>
                                    </div>
                                }
                                { //shows only when FRONT ONLY is selected
                                    deviceInfo?.recordConfigList?.includes("FRONT_ONLY") && settingsData.settings.recordConfig.config === "FRONT_ONLY" &&
                                    <div className="d-sm-inline-block mb-1 vidfleet-pills" key={"video_resolution_front-only-tooltip"}>
                                        <div
                                            onClick={e => {
                                                settingsData.settings.recordConfig.config = "FRONT_ONLY";
                                                settingsDataChanged.settings.recordConfig.config = true

                                                optionDefaults.videoResolutionDefault = false;
                                                setSettingsData({ ...settingsData })
                                                saveChangedSettings({ ...settingsDataChanged })
                                                setSaveSettingsAvailable(true);
                                                setOptionDefaults({ ...optionDefaults })
                                            }}
                                        >
                                            <div className="pr-2 align-items-center" style={{ marginLeft: "10px" }}>
                                                <Tooltip title={messages["vidfleet_configuration_video_resolution_front_only_message"]} tooltipplacement="top">
                                                    <a className="btn-link align-middle" onClick={() => { }}>
                                                        <Icon style={{ marginTop: 5 }}>
                                                            info
                                                        </Icon>
                                                    </a>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                }
                                <div className="d-sm-inline-block mb-1">
                                    <Tooltip title={messages["vidfleet_configuration_message_available_combinations"]} tooltipplacement="left">
                                        <a className="btn-link align-middle" onClick={() => { dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings)) }}>
                                            <Icon style={{ marginTop: 5 }}>info</Icon>
                                        </a>
                                    </Tooltip>
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    {messages["vidfleet_configuration_select_aux_camera"]}
                </Grid>
            </Grid>
        );
    }

    const delayedPowerOff = () => {
        return (
            !optionCollapsed.colapsedDelayedPowerOff &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid item xs={props.bulk ? 12 : 5}>
                        {messages["vidfleet_configuration_interval"]}
                        <Slider
                            min={15}
                            max={devices?.length && devices[0]?.deviceModel?.name?.includes("AIR2") && !isBiggerFirmware("3.35.910", camerainfo?.firmware || "") ? 43200 : 3600}
                            value={settingsData.settings.delayedPowerOff || 0}
                            onChange={(event) => {

                                settingsData.settings.delayedPowerOff = event;
                                settingsDataChanged.settings.delayedPowerOff = true

                                optionDefaults.delayedPowerOffDefault = false;
                                setSettingsData({ ...settingsData })
                                saveChangedSettings({ ...settingsDataChanged })
                                setSaveSettingsAvailable(true);
                                setOptionDefaults({ ...optionDefaults })
                            }}
                            sliderOneColor
                            showValue
                            valueLabelDisplay="off"
                            measure={messages["vidfleet_configuration_seconds"]}
                        />
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    const nightVision = () => {
        return (
            !optionCollapsed.colapsedNightVision &&
            <Grid container>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div style={{ marginLeft: 44 }}>
                        <h6>
                            {messages["vidfleet_configuration_driving_night_vision"]}
                        </h6>
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <div className="d-sm-inline-block mb-1 vidfleet-pills" key="driving-night_vision_on">
                        <div
                            onClick={e => {

                                settingsData.settings.nightVision.drivingNightVision = "on";
                                settingsDataChanged.settings.nightVision.drivingNightVision = true

                                optionDefaults.nightVisionDefault = false;
                                setSettingsData({ ...settingsData })
                                saveChangedSettings({ ...settingsDataChanged })
                                setSaveSettingsAvailable(true);
                                setOptionDefaults({ ...optionDefaults })
                            }}
                        >
                            {
                                props.getBadget(messages["vidfleet_configuration_driving_night_vision_on"], settingsData?.settings?.nightVision?.drivingNightVision === "on")
                            }
                        </div>
                    </div>
                    <div className="d-sm-inline-block mb-1 vidfleet-pills" key="hard_accel-med">
                        <div
                            onClick={e => {

                                settingsData.settings.nightVision.drivingNightVision = "auto";
                                settingsDataChanged.settings.nightVision.drivingNightVision = true

                                optionDefaults.nightVisionDefault = false;
                                setSettingsData({ ...settingsData })
                                saveChangedSettings({ ...settingsDataChanged })
                                setSaveSettingsAvailable(true);
                                setOptionDefaults({ ...optionDefaults })
                            }}
                        >
                            {
                                props.getBadget(messages["vidfleet_configuration_driving_night_vision_auto"], settingsData?.settings?.nightVision?.drivingNightVision === "auto")
                            }
                        </div>
                    </div>
                    <div className="d-sm-inline-block mb-1 vidfleet-pills" key="hard_accel-high">
                        <div
                            onClick={e => {

                                settingsData.settings.nightVision.drivingNightVision = "off";
                                settingsDataChanged.settings.nightVision.drivingNightVision = true

                                optionDefaults.nightVisionDefault = false;
                                setSettingsData({ ...settingsData })
                                saveChangedSettings({ ...settingsDataChanged })
                                setSaveSettingsAvailable(true);
                                setOptionDefaults({ ...optionDefaults })
                            }}
                        >
                            {
                                props.getBadget(messages["vidfleet_configuration_driving_night_vision_off"], settingsData?.settings?.nightVision?.drivingNightVision === "off")
                            }
                        </div>
                    </div>
                </Grid>
                {
                    settingsData.settings.nightVision.drivingNightVision === "on" &&
                    <Grid item xs={12} style={{ marginTop: 10, marginLeft: 54 }}>
                        <Grid container>
                            <Grid item xs={props.bulk ? 4 : 3}>
                                {messages["vidfleet_configuration_from"]}
                                <InputDate
                                    id="input-date-from"
                                    type="time"
                                    timeIntervals="60"
                                    //iconType="unfold_more"
                                    onChange={(event) => {
                                        let date = moment(event);
                                        if (!settingsData.settings.nightVision)
                                            settingsData.settings.nightVision = {}
                                        if (!settingsData.settings.nightVision.drivingNightVisionTime)
                                            settingsData.settings.nightVision.drivingNightVisionTime = {}

                                        let start = moment().hours(0).minutes(0);
                                        var duration = moment.duration(date.diff(start));
                                        var minutes = duration.asMinutes();

                                        settingsData.settings.nightVision.drivingNightVisionTime.from = Math.round(minutes);
                                        settingsDataChanged.settings.nightVision.drivingNightVisionTime.from = true

                                        optionDefaults.nightVisionDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}

                                    value={getTimeFromMins(settingsData?.settings?.nightVision?.drivingNightVisionTime?.from || 0).toDate()}
                                />
                            </Grid>
                            <Grid item xs={2} style={{ marginLeft: 14 }}>
                                {messages["vidfleet_configuration_to"]}
                                <InputDate
                                    id="input-date-to"
                                    type="time"
                                    timeIntervals="60"
                                    //iconType="unfold_more"
                                    onChange={(event) => {
                                        let date = moment(event);
                                        if (!settingsData.settings.nightVision)
                                            settingsData.settings.nightVision = {}
                                        if (!settingsData.settings.nightVision.drivingNightVisionTime)
                                            settingsData.settings.nightVision.drivingNightVisionTime = {}

                                        let start = moment().hours(0).minutes(0);
                                        var duration = moment.duration(date.diff(start));
                                        var minutes = duration.asMinutes();

                                        settingsData.settings.nightVision.drivingNightVisionTime.to = Math.round(minutes);
                                        settingsDataChanged.settings.nightVision.drivingNightVisionTime.to = true

                                        optionDefaults.nightVisionDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    value={getTimeFromMins(settingsData.settings.nightVision.drivingNightVisionTime && settingsData.settings.nightVision.drivingNightVisionTime.to || 0).toDate()}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Grid>
        );
    }

    const liveStreamDuration = () => {
        return (
            !optionCollapsed.colapsedLiveStreamDuration &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                {
                    !HasPermission(user, "vftls", "u") &&
                    <Grid item xs={12} style={{ marginLeft: 44 }}>
                        <Alert severity="warning" style={{ marginBottom: 10 }}>
                            {messages['vidfleet_configuration_permission_message']}
                        </Alert>
                    </Grid>
                }
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={props.bulk ? 12 : 5}>
                            {messages["vidfleet_configuration_interval"]}
                            <Slider
                                min={30}
                                max={120} //maximum allowed 
                                value={settingsData.settings.liveStreamDuration || 0}
                                onChange={(event) => {

                                    settingsData.settings.liveStreamDuration = event;
                                    settingsDataChanged.settings.liveStreamDuration = true

                                    optionDefaults.liveStreamDurationDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure={messages["vidfleet_configuration_seconds"]}
                                disabled={!HasPermission(user, "vftls", "u")}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    const trustAcc = () => {
        return (
            !optionCollapsed.colapsedTrustAcc &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={1} style={{ marginTop: 15 }}>
                            <ToggleSwitch
                                id="trustAcc"
                                blueToggle={true}
                                large={true}
                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                checked={settingsData.settings.trustAcc}
                                onChange={(checked) => {

                                    settingsData.settings.trustAcc = checked
                                    settingsDataChanged.settings.trustAcc = true

                                    optionDefaults.trustAccDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                width={100}
                            />
                        </Grid>
                        <Grid item xs={1} style={{ marginTop: 15 }}>
                            <Tooltip title={<img src="/assets/images/trust_acc.png" alt="tooltip" width={800} />} tooltipplacement="left">
                                <Icon style={{ cursor: 'pointer', color: '#57595b' }}>info</Icon>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    const autoSnapshot = () => {
        return (
            !optionCollapsed.colapsedAutoSnapshot &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={2} style={{ marginTop: 15 }}>
                            <ToggleSwitch
                                id="autoSnapshot"
                                blueToggle={true}
                                large={true}
                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                checked={settingsData.settings.autoSnapshot.enable ? true : false}
                                onChange={(checked) => {

                                    settingsData.settings.autoSnapshot.enable = checked;
                                    settingsDataChanged.settings.autoSnapshot.enable = true
                                    if (checked) {
                                        settingsData.settings.autoSnapshot.interval = 30;
                                        settingsDataChanged.settings.autoSnapshot.interval = true
                                    }

                                    optionDefaults.autoSnapshotDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                width={100}
                            />
                        </Grid>
                        <Grid item xs={props.bulk ? 12 : 5}>
                            {messages["vidfleet_configuration_interval"]}
                            <Slider
                                min={30}
                                max={600}
                                value={settingsData.settings.autoSnapshot.interval || 30}
                                onChange={(event) => {

                                    settingsData.settings.autoSnapshot.interval = event;
                                    settingsDataChanged.settings.autoSnapshot.interval = true

                                    optionDefaults.autoSnapshotDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                disabled={!settingsData.settings.autoSnapshot.enable}
                                measure={messages["vidfleet_configuration_seconds"]}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    const mic = () => {
        return (
            !optionCollapsed.colapsedMic &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={12} style={{ marginTop: 15 }}>
                            <ToggleSwitch
                                id="mic"
                                blueToggle={true}
                                large={true}
                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                checked={settingsData.settings.mic === "on" ? true : false ? true : false}
                                onChange={(checked) => {

                                    settingsData.settings.mic = checked ? "on" : "off";
                                    settingsDataChanged.settings.mic = true

                                    optionDefaults.micDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                width={100}
                                disabled={!HasPermission(user, "vftcm", "u")}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    const privacyMask = () => {
        return (
            !optionCollapsed.colapsedPrivacyMask &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={1} style={{ marginTop: 15 }}>
                            <ToggleSwitch
                                id="privacyMask"
                                blueToggle={true}
                                large={true}
                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                checked={settingsData.settings.privacyMask.enable}
                                onChange={(checked) => {

                                    settingsData.settings.privacyMask.enable = checked;
                                    settingsDataChanged.settings.privacyMask.enable = true

                                    optionDefaults.privacyMaskDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                width={100}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    const rotate = () => {
        return (
            !optionCollapsed.colapsedRotate &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={props.bulk ? 12 : 5}>
                            {messages["vidfleet_configuration_rotate"]}
                            <div style={{ marginLeft: 0 }}>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="rotate-normal"
                                    onClick={e => {

                                        settingsData.settings.rotate = "normal";
                                        settingsDataChanged.settings.rotate = true

                                        optionDefaults.rotateDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_normal"], settingsData.settings.rotate === "normal")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="rotate-upside-down"
                                    onClick={e => {

                                        settingsData.settings.rotate = "upsidedown";
                                        settingsDataChanged.settings.rotate = true

                                        optionDefaults.rotateDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_upside_down"], settingsData.settings.rotate === "upsidedown")
                                    }
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    const volume = () => {
        return (
            !optionCollapsed.colapsedVolume &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 15 }}>
                            {messages["vidfleet_configuration_volume_level"]}
                            <Slider
                                min={0}
                                max={10}
                                value={settingsData.settings.volume && settingsData.settings.volume.speaker || 0}
                                onChange={(event) => {

                                    if (!settingsData.settings.volume)
                                        settingsData.settings.volume = {}
                                    settingsData.settings.volume.speaker = event;
                                    settingsDataChanged.settings.volume.speaker = true

                                    optionDefaults.volumeDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    const markedClipDuration = () => {
        return (
            !optionCollapsed.colapsedMarkedClipDuration &&
            <Grid item xs={12}>
                <Grid container>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={3}>
                            <Grid item xs={12}>
                                {messages["vidfleet_configuration_acceleration"]}
                            </Grid>
                            {messages["vidfleet_configuration_before"]}
                            <Slider
                                min={1}
                                max={30}
                                value={settingsData.settings.markedClipDuration.accel.before || 0}
                                onChange={(event) => {

                                    settingsData.settings.markedClipDuration.accel.before = event;
                                    settingsDataChanged.settings.markedClipDuration.accel.before = true

                                    optionDefaults.markedClipDurationDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure={messages["vidfleet_configuration_seconds"]}
                            />
                        </Grid>
                        <Grid item xs={1} />
                        <Grid item xs={3}>
                            <Grid item xs={12}>
                                {messages["vidfleet_configuration_manual"]}
                            </Grid>
                            {messages["vidfleet_configuration_before"]}
                            <Slider
                                min={1}
                                max={30}
                                value={settingsData.settings.markedClipDuration.manual.before || 0}
                                onChange={(event) => {

                                    settingsData.settings.markedClipDuration.manual.before = event;
                                    settingsDataChanged.settings.markedClipDuration.manual.before = true

                                    optionDefaults.markedClipDurationDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure={messages["vidfleet_configuration_seconds"]}
                            />
                        </Grid>
                        <Grid item xs={1} />
                        {
                            (!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name === "4K" || devices[0].deviceModel.name === "AIR2")) &&
                            <Grid item xs={3}>
                                <Grid item xs={12}>
                                    {messages["vidfleet_configuration_dms"]}
                                </Grid>
                                {messages["vidfleet_configuration_before"]}
                                <Slider
                                    min={1}
                                    max={30}
                                    value={settingsData.settings.markedClipDuration.dms.before || 0}
                                    onChange={(event) => {

                                        settingsData.settings.markedClipDuration.dms.before = event;
                                        settingsDataChanged.settings.markedClipDuration.dms.before = true

                                        optionDefaults.markedClipDurationDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    sliderOneColor
                                    showValue
                                    valueLabelDisplay="off"
                                    measure={messages["vidfleet_configuration_seconds"]}
                                />
                            </Grid>
                        }

                    </Grid>
                    <Grid container>
                        <Grid item xs={3}>
                            {messages["vidfleet_configuration_after"]}
                            <Slider
                                min={1}
                                max={30}
                                value={settingsData.settings.markedClipDuration.accel.after || 0}
                                onChange={(event) => {
                                    settingsData.settings.markedClipDuration.accel.after = event;
                                    settingsDataChanged.settings.markedClipDuration.accel.after = true

                                    optionDefaults.markedClipDurationDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure={messages["vidfleet_configuration_seconds"]}
                            />
                        </Grid>
                        <Grid item xs={1} />
                        <Grid item xs={3}>
                            {messages["vidfleet_configuration_after"]}
                            <Slider
                                min={1}
                                max={30}
                                value={settingsData.settings.markedClipDuration.manual.after || 0}
                                onChange={(event) => {
                                    settingsData.settings.markedClipDuration.manual.after = event;
                                    settingsDataChanged.settings.markedClipDuration.manual.after = true

                                    optionDefaults.markedClipDurationDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure={messages["vidfleet_configuration_seconds"]}
                            />
                        </Grid>
                        <Grid item xs={1} />
                        {
                            (!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name === "4K" || devices[0].deviceModel.name === "AIR2")) &&
                            <Grid item xs={3}>
                                {messages["vidfleet_configuration_after"]}
                                <Slider
                                    min={1}
                                    max={30}
                                    value={settingsData.settings.markedClipDuration.dms.after || 0}
                                    onChange={(event) => {
                                        settingsData.settings.markedClipDuration.dms.after = event;
                                        settingsDataChanged.settings.markedClipDuration.dms.after = true

                                        optionDefaults.markedClipDurationDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    sliderOneColor
                                    showValue
                                    valueLabelDisplay="off"
                                    measure={messages["vidfleet_configuration_seconds"]}
                                />
                            </Grid>
                        }
                    </Grid>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <div style={{ marginTop: 20 }}>
                            <Divider style={{ backgroundColor: '#999999' }} />
                        </div>
                    </Grid>
                </Grid>
                {
                    (!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name === "4K" || devices[0].deviceModel.name.includes("AIR"))) &&
                    <Grid item xs={12} style={{ marginLeft: 44 }}>
                        <Grid container>
                            <Grid item xs={3}>
                                <Grid item xs={12}>
                                    {messages["vidfleet_configuration_adas"]}
                                </Grid>
                                {messages["vidfleet_configuration_before"]}
                                <Slider
                                    min={1}
                                    max={30}
                                    value={settingsData.settings.markedClipDuration.adas.before || 0}
                                    onChange={(event) => {

                                        settingsData.settings.markedClipDuration.adas.before = event;
                                        settingsDataChanged.settings.markedClipDuration.adas.before = true

                                        optionDefaults.markedClipDurationDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    sliderOneColor
                                    showValue
                                    valueLabelDisplay="off"
                                    measure={messages["vidfleet_configuration_seconds"]}
                                />
                            </Grid>
                            <Grid item xs={1} />
                            <Grid item xs={3}>
                                <Grid item xs={12}>
                                    {messages["vidfleet_configuration_nav"]}
                                </Grid>
                                {messages["vidfleet_configuration_before"]}
                                <Slider
                                    min={1}
                                    max={30}
                                    value={settingsData.settings.markedClipDuration.nav.before || 0}
                                    onChange={(event) => {

                                        settingsData.settings.markedClipDuration.nav.before = event;
                                        settingsDataChanged.settings.markedClipDuration.nav.before = true

                                        optionDefaults.markedClipDurationDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    sliderOneColor
                                    showValue
                                    valueLabelDisplay="off"
                                    measure={messages["vidfleet_configuration_seconds"]}
                                />
                            </Grid>


                        </Grid>
                        <Grid container>
                            <Grid item xs={3}>
                                {messages["vidfleet_configuration_after"]}
                                <Slider
                                    min={1}
                                    max={30}
                                    value={settingsData.settings.markedClipDuration.adas.after || 0}
                                    onChange={(event) => {
                                        settingsData.settings.markedClipDuration.adas.after = event;
                                        settingsDataChanged.settings.markedClipDuration.adas.after = true

                                        optionDefaults.markedClipDurationDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    sliderOneColor
                                    showValue
                                    valueLabelDisplay="off"
                                    measure={messages["vidfleet_configuration_seconds"]}
                                />
                            </Grid>
                            <Grid item xs={1} />
                            <Grid item xs={3}>
                                {messages["vidfleet_configuration_after"]}
                                <Slider
                                    min={1}
                                    max={30}
                                    value={settingsData.settings.markedClipDuration.nav.after || 0}
                                    onChange={(event) => {
                                        settingsData.settings.markedClipDuration.nav.after = event;
                                        settingsDataChanged.settings.markedClipDuration.nav.after = true

                                        optionDefaults.markedClipDurationDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    sliderOneColor
                                    showValue
                                    valueLabelDisplay="off"
                                    measure={messages["vidfleet_configuration_seconds"]}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                }
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <div style={{ marginTop: 20 }}>
                            <Divider style={{ backgroundColor: '#999999' }} />
                        </div>
                    </Grid>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={3}>
                            <ToggleSwitch
                                id="enableEventThumbnail"
                                blueToggle={true}
                                large={true}
                                label={messages["vidfleet_configuration_enable_event_thumbnail"]}
                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                checked={settingsData.settings.markedClipDuration.enableEventThumbnail}
                                onChange={(checked) => {

                                    settingsData.settings.markedClipDuration.enableEventThumbnail = checked;
                                    settingsDataChanged.settings.markedClipDuration.enableEventThumbnail = true

                                    optionDefaults.markedClipDurationDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                width={100}
                            />
                        </Grid >
                    </Grid >
                </Grid >

            </Grid>
        );
    }

    const obdII = () => {
        return (
            !optionCollapsed.colapsedObdII &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 10 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div style={{ marginLeft: 44 }}>
                        <h6>
                            {messages["vidfleet_configuration_obdii_mode"]}
                        </h6>
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid item xs={5}>
                        <Select
                            id="selectObdMode"
                            name="selectObdMode"
                            placeholder={messages["vidfleet_configuration_obdii_mode"]}
                            value={obdIIOptions.find(x => x.id === settingsData.settings.obdII.mode)}
                            options={obdIIOptions}
                            textField="name"
                            valueField="id"
                            onChange={(value) => {

                                settingsData.settings.obdII.mode = value.id;
                                settingsDataChanged.settings.obdII.mode = true

                                optionDefaults.obdIIDefault = false;
                                setSettingsData({ ...settingsData })
                                saveChangedSettings({ ...settingsDataChanged })
                                setSaveSettingsAvailable(true);
                                setOptionDefaults({ ...optionDefaults })
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44, marginTop: 15 }}>
                    {
                        settingsData.settings.obdII.mode && settingsData.settings.obdII.mode !== "Auto" &&
                        <Grid container spacing={1}>
                            <Grid item xs={5}>
                                {messages["vidfleet_configuration_voltage_on"]}
                                <Slider
                                    min={0}
                                    max={15000}
                                    value={settingsData.settings.obdII && settingsData.settings.obdII.voltage && settingsData.settings.obdII.voltage.on || 0}
                                    onChange={(event) => {

                                        if (!settingsData.settings.obdII)
                                            settingsData.settings.obdII = {}
                                        if (!settingsData.settings.obdII.voltage)
                                            settingsData.settings.obdII.voltage = {}

                                        settingsData.settings.obdII.voltage.on = event;
                                        settingsDataChanged.settings.obdII.voltage.on = true

                                        optionDefaults.obdIIDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    sliderOneColor
                                    showValue
                                    valueLabelDisplay="off"
                                    measure={messages["vidfleet_configuration_milli_volts"]}
                                />
                            </Grid>
                            <Grid item xs={5}>
                                {messages["vidfleet_configuration_voltage_off"]}
                                <Slider
                                    min={1}
                                    max={15000}
                                    value={settingsData.settings.obdII && settingsData.settings.obdII.voltage && settingsData.settings.obdII.voltage.off || 0}
                                    onChange={(event) => {

                                        if (!settingsData.settings.obdII)
                                            settingsData.settings.obdII = {}
                                        if (!settingsData.settings.obdII.voltage)
                                            settingsData.settings.obdII.voltage = {}

                                        settingsData.settings.obdII.voltage.off = event;
                                        settingsDataChanged.settings.obdII.voltage.off = true

                                        optionDefaults.obdIIDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    sliderOneColor
                                    showValue
                                    valueLabelDisplay="off"
                                    measure={messages["vidfleet_configuration_milli_volts"]}
                                />
                            </Grid>
                            <Grid item xs={5}>
                                {messages["vidfleet_configuration_voltage_check"]}
                                <Slider
                                    min={1}
                                    max={15000}
                                    value={settingsData.settings.obdII && settingsData.settings.obdII.voltage && settingsData.settings.obdII.voltage.check || 0}
                                    onChange={(event) => {

                                        if (!settingsData.settings.obdII)
                                            settingsData.settings.obdII = {}
                                        if (!settingsData.settings.obdII.voltage)
                                            settingsData.settings.obdII.voltage = {}

                                        settingsData.settings.obdII.voltage.check = event;
                                        settingsDataChanged.settings.obdII.voltage.check = true;

                                        optionDefaults.obdIIDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    sliderOneColor
                                    showValue
                                    valueLabelDisplay="off"
                                    measure={messages["vidfleet_configuration_milli_volts"]}
                                />
                            </Grid>
                        </Grid>
                    }
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <ToggleSwitch
                        id="dataUpload"
                        blueToggle={true}
                        large={true}
                        label={messages["vidfleet_configuration_data_upload"]}
                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                        checked={settingsData.settings.obdII?.dataUpload ? true : false}
                        onChange={(checked) => {

                            settingsData.settings.obdII.dataUpload = checked;
                            settingsDataChanged.settings.obdII.dataUpload = true

                            optionDefaults.obdIIDefault = false;
                            setSettingsData({ ...settingsData })
                            saveChangedSettings({ ...settingsDataChanged })
                            setSaveSettingsAvailable(true);
                            setOptionDefaults({ ...optionDefaults })
                        }}
                        width={100}
                    />
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44, marginTop: 15 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={5}>
                            {messages["vidfleet_configuration_upload_interval"]}
                            <Slider
                                min={1}
                                max={600}
                                value={settingsData.settings.obdII.uploadInterval ? (settingsData.settings.obdII.uploadInterval / 1000) : 1}
                                onChange={(event) => {

                                    settingsData.settings.obdII.uploadInterval = event * 1000;
                                    settingsDataChanged.settings.obdII.uploadInterval = true

                                    optionDefaults.obdIIDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure="s"
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid item xs={props.bulk ? 12 : 5}>
                        {messages["vidfleet_configuration_upload_mode"]}
                        <div style={{ marginLeft: 0 }}>
                            <div
                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                key="upload-mode-hybrid"
                                onClick={e => {

                                    settingsData.settings.obdII.uploadMode = "hybrid";
                                    settingsDataChanged.settings.obdII.uploadMode = true

                                    optionDefaults.obdIIDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                            >
                                {
                                    props.getBadget(messages["vidfleet_configuration_upload_mode_hybrid"], settingsData?.settings?.obdII?.uploadMode === "hybrid")
                                }
                            </div>
                            <div
                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                key="upload-mode-default"
                                onClick={e => {

                                    settingsData.settings.obdII.uploadMode = "default";
                                    settingsDataChanged.settings.obdII.uploadMode = true

                                    optionDefaults.obdIIDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                            >
                                {
                                    props.getBadget(messages["vidfleet_configuration_upload_mode_default"], settingsData?.settings?.obdII?.uploadMode ? settingsData?.settings?.obdII?.uploadMode === "default" : true)
                                }
                            </div>
                        </div>
                    </Grid>
                    {
                        settingsData?.settings?.obdII?.uploadMode === "hybrid" &&
                        <Grid item xs={12} >
                            <div>
                                {messages["vidfleet_configuration_upload_mode_hybrid_msg"]}
                            </div>
                        </Grid>
                    }
                </Grid>
            </Grid>
        );
    }

    const runningInfo = () => {
        return (
            !optionCollapsed.colapsedRunningInfo &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <Grid container style={{ marginLeft: 44 }}>
                        <Grid item xs={2} style={{ marginTop: 15 }}>
                            <ToggleSwitch
                                id="runningInfo"
                                blueToggle={true}
                                large={true}
                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                checked={settingsData.settings.runningInfo.enable ? true : false}
                                onChange={(checked) => {

                                    settingsData.settings.runningInfo.enable = checked;
                                    settingsDataChanged.settings.runningInfo.enable = true

                                    optionDefaults.runningInfoDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                width={100}
                            />
                        </Grid>
                        <Grid item xs={props.bulk ? 12 : 5}>
                            {messages["vidfleet_configuration_interval_in_min"]}
                            <Slider
                                min={1}
                                max={60}
                                value={settingsData.settings.runningInfo.intervalInMin || 1}
                                onChange={(event) => {

                                    settingsData.settings.runningInfo.intervalInMin = event;
                                    settingsDataChanged.settings.runningInfo.intervalInMin = true

                                    optionDefaults.runningInfoDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                disabled={!settingsData.settings.runningInfo.enable}
                                measure="m"
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    const advanced = () => {

        const isButtonDisabled = validateProperties();
        let buttonStates = settingsData.settings
        let curretStates = {
            recordProfile: buttonStates?.recordConfig?.config,
            auxModel: buttonStates?.advanced?.aux?.model,
            privacyControl: buttonStates?.advanced?.privacyControl?.level,
            dmsLevel: buttonStates?.dms?.level
        }
        let indexCount = 0;
        return (
            !optionCollapsed.colapsedAdvanced &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid container>
                        <Grid item xs={3}>
                            <div>
                                {messages["vidfleet_configuration_sd_card_event"]}
                            </div>
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid item xs={6}>
                            <div style={{ marginLeft: 0 }}>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="low-bit-rate-large"
                                    onClick={e => {
                                        settingsData.settings.advanced.sdCardEventSpace = "large"
                                        settingsDataChanged.settings.advanced.sdCardEventSpace = true

                                        optionDefaults.advancedDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_large"], settingsData.settings.advanced.sdCardEventSpace === "large")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="low-bit-rate-medium"
                                    onClick={e => {
                                        settingsData.settings.advanced.sdCardEventSpace = "medium"
                                        settingsDataChanged.settings.advanced.sdCardEventSpace = true

                                        optionDefaults.advancedDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_medium"], settingsData.settings.advanced.sdCardEventSpace === "medium")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="low-bit-rate-small"
                                    onClick={e => {
                                        settingsData.settings.advanced.sdCardEventSpace = "small"
                                        settingsDataChanged.settings.advanced.sdCardEventSpace = true

                                        optionDefaults.advancedDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_small"], settingsData.settings.advanced.sdCardEventSpace === "small")
                                    }
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                    {(!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name.includes("4K") || devices[0].deviceModel.name.includes("AIR"))) &&
                        <Grid item xs={12}>
                            <Grid container>
                                {
                                    <Grid item xs={12}>
                                        <Grid item xs={3} style={{ marginTop: 10 }}>
                                            <div>
                                                {messages["vidfleet_configuration_aux"]}
                                            </div>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container>
                                                <Grid item xs={12}>
                                                    {messages["vidfleet_configuration_model"]}
                                                    <div style={{ marginLeft: 0, marginBottom: 10 }}>
                                                        {
                                                            <div
                                                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                                                key="advanced-aux-model-na"
                                                            >
                                                                <div
                                                                    onClick={e => {
                                                                        if (isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, auxModel: "NA" })) {
                                                                            NotificationManager.warning(
                                                                                <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                                                messages["vidfleet_configuration_combination_not_allowed"],
                                                                                6000,
                                                                                null,
                                                                                null,
                                                                                'error'
                                                                            );
                                                                        }
                                                                        if (!settingsData.settings.advanced.aux)
                                                                            settingsData.settings.advanced.aux = {}

                                                                        settingsData.settings.advanced.aux.model = "NA";
                                                                        settingsDataChanged.settings.advanced.aux.model = true

                                                                        if (devices?.length && devices[0].deviceModel.name.includes("AIR2") && settingsData?.settings?.advanced?.aux?.model2 !== "NA") {
                                                                            settingsData.settings.advanced.aux.model2 = "NA";
                                                                            settingsDataChanged.settings.advanced.aux.model2 = true
                                                                        }

                                                                        optionDefaults.advancedDefault = false;
                                                                        setSettingsData({ ...settingsData })
                                                                        saveChangedSettings({ ...settingsDataChanged })
                                                                        setSaveSettingsAvailable(true);
                                                                        setOptionDefaults({ ...optionDefaults })

                                                                    }}
                                                                    style={{ cursor: isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, auxModel: "NA" }) ? 'not-allowed' : 'pointer' }}
                                                                >
                                                                    {
                                                                        props.getBadget(messages["vidfleet_configuration_na"], settingsData.settings?.advanced?.aux?.model === "NA")
                                                                    }
                                                                </div>
                                                            </div>
                                                        }
                                                        {
                                                            <div
                                                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                                                key="advanced-aux-model-ecm01"
                                                            >
                                                                <div
                                                                    onClick={e => {
                                                                        if (isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, auxModel: "ECM01" })) {
                                                                            NotificationManager.warning(
                                                                                <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                                                messages["vidfleet_configuration_combination_not_allowed"],
                                                                                6000,
                                                                                null,
                                                                                null,
                                                                                'error'
                                                                            );
                                                                        }
                                                                        if (!settingsData.settings.advanced.aux)
                                                                            settingsData.settings.advanced.aux = {}

                                                                        settingsData.settings.advanced.aux.model = "ECM01";
                                                                        settingsDataChanged.settings.advanced.aux.model = true
                                                                        if (devices?.length && devices[0].deviceModel.name.includes("AIR2") && settingsData?.settings?.advanced?.aux?.model2 !== "NA") {
                                                                            settingsData.settings.advanced.aux.model2 = "NA";
                                                                            settingsDataChanged.settings.advanced.aux.model2 = true
                                                                        }

                                                                        optionDefaults.advancedDefault = false;
                                                                        setSettingsData({ ...settingsData })
                                                                        saveChangedSettings({ ...settingsDataChanged })
                                                                        setSaveSettingsAvailable(true);
                                                                        setOptionDefaults({ ...optionDefaults })

                                                                    }}
                                                                    style={{ cursor: isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, auxModel: "ECM01" }) ? 'not-allowed' : 'pointer' }}
                                                                >
                                                                    {
                                                                        props.getBadget(messages["vidfleet_configuration_ecm01"], settingsData.settings?.advanced?.aux?.model === "ECM01")
                                                                    }
                                                                </div>
                                                            </div>
                                                        }
                                                        {
                                                            <div
                                                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                                                key="advanced-adas-ecm02"
                                                            >
                                                                <div
                                                                    onClick={e => {
                                                                        if (isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, auxModel: "ECM02" })) {
                                                                            NotificationManager.warning(
                                                                                <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                                                messages["vidfleet_configuration_combination_not_allowed"],
                                                                                6000,
                                                                                null,
                                                                                null,
                                                                                'error'
                                                                            );
                                                                        }

                                                                        if (!settingsData.settings.advanced.aux)
                                                                            settingsData.settings.advanced.aux = {}

                                                                        settingsData.settings.advanced.aux.model = "ECM02";
                                                                        settingsDataChanged.settings.advanced.aux.model = true
                                                                        if (devices?.length && devices[0].deviceModel.name.includes("AIR2") && settingsData?.settings?.advanced?.aux?.model2 !== "NA") {
                                                                            settingsData.settings.advanced.aux.model2 = "NA";
                                                                            settingsDataChanged.settings.advanced.aux.model2 = true
                                                                        }

                                                                        optionDefaults.advancedDefault = false;
                                                                        setSettingsData({ ...settingsData })
                                                                        saveChangedSettings({ ...settingsDataChanged })
                                                                        setSaveSettingsAvailable(true);
                                                                        setOptionDefaults({ ...optionDefaults })

                                                                    }}
                                                                    style={{ cursor: isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, auxModel: "ECM02" }) ? 'not-allowed' : 'pointer' }}
                                                                >
                                                                    {
                                                                        props.getBadget(messages["vidfleet_configuration_ecm02"], settingsData.settings?.advanced?.aux?.model === "ECM02")
                                                                    }
                                                                </div>
                                                            </div>
                                                        }
                                                        {
                                                            <div
                                                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                                                key="advanced-aux-model-ecm03"
                                                            >
                                                                <div
                                                                    onClick={e => {
                                                                        if (isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, auxModel: "ECM03" })) {
                                                                            NotificationManager.warning(
                                                                                <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                                                messages["vidfleet_configuration_combination_not_allowed"],
                                                                                6000,
                                                                                null,
                                                                                null,
                                                                                'error'
                                                                            );
                                                                        }

                                                                        if (!settingsData.settings.advanced.aux)
                                                                            settingsData.settings.advanced.aux = {}

                                                                        settingsData.settings.advanced.aux.model = "ECM03";
                                                                        settingsDataChanged.settings.advanced.aux.model = true

                                                                        optionDefaults.advancedDefault = false;
                                                                        setSettingsData({ ...settingsData })
                                                                        saveChangedSettings({ ...settingsDataChanged })
                                                                        setSaveSettingsAvailable(true);
                                                                        setOptionDefaults({ ...optionDefaults })

                                                                    }}
                                                                    style={{ cursor: isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, auxModel: "ECM03" }) ? 'not-allowed' : 'pointer' }}
                                                                >
                                                                    {
                                                                        props.getBadget(messages["vidfleet_configuration_ecm03"], settingsData.settings?.advanced?.aux?.model === "ECM03")
                                                                    }
                                                                </div>
                                                            </div>
                                                        }
                                                        <div className="d-sm-inline-block mb-1">
                                                            <Tooltip title={messages["vidfleet_configuration_message_available_combinations"]} tooltipplacement="left">
                                                                <a className="btn-link align-middle" onClick={() => { dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings)) }}>
                                                                    <Icon style={{ marginTop: 5 }}>info</Icon>
                                                                </a>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                </Grid>
                                            </Grid>
                                            <Grid container>
                                                <Grid item xs={12}>
                                                    {messages["vidfleet_configuration_angle"]}
                                                    <div style={{ marginLeft: 0, marginBottom: 10 }}>
                                                        <div
                                                            className="d-sm-inline-block mb-1 vidfleet-pills"
                                                            style={{ cursor: `${"pointer"}` }}
                                                            key="advanced-aux-angle-normal"
                                                            onClick={e => {

                                                                if (!settingsData.settings.advanced.aux)
                                                                    settingsData.settings.advanced.aux = {}

                                                                settingsData.settings.advanced.aux.angle = "normal";
                                                                settingsDataChanged.settings.advanced.aux.angle = true

                                                                if (devices?.length && devices[0].deviceModel.name.includes("AIR2") && settingsData?.settings?.advanced?.aux?.model2 !== "NA") {
                                                                    settingsData.settings.advanced.aux.angle2 = "normal";
                                                                    settingsDataChanged.settings.advanced.aux.angle2 = true
                                                                    settingsDataChanged.settings.advanced.aux.model = true
                                                                    settingsDataChanged.settings.advanced.aux.model2 = true
                                                                }

                                                                optionDefaults.advancedDefault = false;
                                                                setSettingsData({ ...settingsData })
                                                                saveChangedSettings({ ...settingsDataChanged })
                                                                setSaveSettingsAvailable(true);
                                                                setOptionDefaults({ ...optionDefaults })

                                                            }}
                                                        >
                                                            {
                                                                props.getBadget(messages["vidfleet_configuration_angle_normal"], settingsData.settings?.advanced?.aux?.angle === "normal")
                                                            }
                                                        </div>
                                                        <div
                                                            className="d-sm-inline-block mb-1 vidfleet-pills"
                                                            style={{ cursor: `${"pointer"}` }}
                                                            key="advanced-aux-angle-upsidedown"
                                                            onClick={e => {

                                                                if (!settingsData.settings.advanced.aux)
                                                                    settingsData.settings.advanced.aux = {}

                                                                settingsData.settings.advanced.aux.angle = "upsidedown";
                                                                settingsDataChanged.settings.advanced.aux.angle = true

                                                                if (devices?.length && devices[0].deviceModel.name.includes("AIR2") && settingsData?.settings?.advanced?.aux?.model2 !== "NA") {
                                                                    settingsData.settings.advanced.aux.angle2 = "upsidedown";
                                                                    settingsDataChanged.settings.advanced.aux.angle2 = true
                                                                    settingsDataChanged.settings.advanced.aux.model = true
                                                                    settingsDataChanged.settings.advanced.aux.model2 = true
                                                                }

                                                                optionDefaults.advancedDefault = false;
                                                                setSettingsData({ ...settingsData })
                                                                saveChangedSettings({ ...settingsDataChanged })
                                                                setSaveSettingsAvailable(true);
                                                                setOptionDefaults({ ...optionDefaults })

                                                            }}
                                                        >
                                                            {
                                                                props.getBadget(messages["vidfleet_configuration_angle_upsidedown"], settingsData.settings?.advanced?.aux?.angle === "upsidedown")
                                                            }
                                                        </div>
                                                        {
                                                            <div
                                                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                                                style={{ cursor: `${(settingsData.settings.recordConfig.config !== "MAIN" || settingsData.settings.recordConfig.config === "HIGH") ? "pointer" : "not-allowed"}` }}
                                                                key="advanced-angle-rotate_left"
                                                                onClick={e => {
                                                                    if ((settingsData.settings.recordConfig.config !== "MAIN" || settingsData.settings.recordConfig.config === "HIGH")) {

                                                                        if (!settingsData.settings.advanced.aux)
                                                                            settingsData.settings.advanced.aux = {}

                                                                        settingsData.settings.advanced.aux.angle = "rotate_left";
                                                                        settingsDataChanged.settings.advanced.aux.angle = true

                                                                        if (devices?.length && devices[0].deviceModel.name.includes("AIR2") && settingsData?.settings?.advanced?.aux?.model2 !== "NA") {
                                                                            settingsData.settings.advanced.aux.angle2 = "rotate_left";
                                                                            settingsDataChanged.settings.advanced.aux.angle2 = true
                                                                            settingsDataChanged.settings.advanced.aux.model = true
                                                                            settingsDataChanged.settings.advanced.aux.model2 = true
                                                                        }

                                                                        optionDefaults.advancedDefault = false;
                                                                        setSettingsData({ ...settingsData })
                                                                        saveChangedSettings({ ...settingsDataChanged })
                                                                        setSaveSettingsAvailable(true);
                                                                        setOptionDefaults({ ...optionDefaults })
                                                                    }
                                                                }}
                                                            >
                                                                {
                                                                    props.getBadget(messages["vidfleet_configuration_angle_rotate_left"], settingsData.settings?.advanced?.aux?.angle === "rotate_left")
                                                                }
                                                            </div>
                                                        }
                                                        {
                                                            <div
                                                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                                                style={{ cursor: `${(settingsData.settings.recordConfig.config !== "MAIN" || settingsData.settings.recordConfig.config === "HIGH") ? "pointer" : "not-allowed"}` }}
                                                                key="advanced-angle-rotate_right"
                                                                onClick={e => {
                                                                    if ((settingsData.settings.recordConfig.config !== "MAIN" || settingsData.settings.recordConfig.config === "HIGH")) {

                                                                        if (!settingsData.settings.advanced.aux)
                                                                            settingsData.settings.advanced.aux = {}

                                                                        settingsData.settings.advanced.aux.angle = "rotate_right";
                                                                        settingsDataChanged.settings.advanced.aux.angle = true

                                                                        if (devices?.length && devices[0].deviceModel.name.includes("AIR2") && settingsData?.settings?.advanced?.aux?.model2 !== "NA") {
                                                                            settingsData.settings.advanced.aux.angle2 = "rotate_right";
                                                                            settingsDataChanged.settings.advanced.aux.angle2 = true
                                                                            settingsDataChanged.settings.advanced.aux.model = true
                                                                            settingsDataChanged.settings.advanced.aux.model2 = true
                                                                        }

                                                                        optionDefaults.advancedDefault = false;
                                                                        setSettingsData({ ...settingsData })
                                                                        saveChangedSettings({ ...settingsDataChanged })
                                                                        setSaveSettingsAvailable(true);
                                                                        setOptionDefaults({ ...optionDefaults })
                                                                    }
                                                                }}
                                                            >
                                                                {
                                                                    props.getBadget(messages["vidfleet_configuration_angle_rotate_right"], settingsData.settings?.advanced?.aux?.angle === "rotate_right")
                                                                }
                                                            </div>
                                                        }
                                                    </div>
                                                </Grid>
                                            </Grid>
                                            {
                                                devices?.length && devices[0].deviceModel.name.includes("AIR2") &&
                                                <Grid container>
                                                    <Grid item xs={12}>
                                                        {messages["vidfleet_configuration_model2"]}
                                                        <div style={{ marginLeft: 0, marginBottom: 10 }}>
                                                            {
                                                                <div
                                                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                                                    key="advanced-aux-model-na"
                                                                >
                                                                    <div
                                                                        onClick={e => {
                                                                            if (isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, auxModel: "NA" })) {
                                                                                NotificationManager.warning(
                                                                                    <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings, true))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                                                    messages["vidfleet_configuration_combination_not_allowed"],
                                                                                    6000,
                                                                                    null,
                                                                                    null,
                                                                                    'error'
                                                                                );
                                                                            }

                                                                            settingsData.settings.advanced.aux.model2 = "NA";
                                                                            settingsDataChanged.settings.advanced.aux.model2 = true
                                                                            settingsDataChanged.settings.advanced.aux.model = true
                                                                            settingsData.settings.advanced.aux.angle2 = "normal"
                                                                            settingsDataChanged.settings.advanced.aux.angle2 = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })

                                                                        }}
                                                                        style={{ cursor: isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, auxModel: "NA" }) ? 'not-allowed' : 'pointer' }}
                                                                    >
                                                                        {
                                                                            props.getBadget(messages["vidfleet_configuration_na"], settingsData.settings.advanced.aux.model2 === "NA")
                                                                        }
                                                                    </div>
                                                                </div>
                                                            }
                                                            {
                                                                <div
                                                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                                                    key="advanced-aux-model-ecm03"
                                                                >
                                                                    <div
                                                                        onClick={e => {
                                                                            if (isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, auxModel: "ECM03" })) {
                                                                                NotificationManager.warning(
                                                                                    <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings, true))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                                                    messages["vidfleet_configuration_combination_not_allowed"],
                                                                                    6000,
                                                                                    null,
                                                                                    null,
                                                                                    'error'
                                                                                );
                                                                            }

                                                                            settingsData.settings.advanced.aux.model2 = "ECM03";
                                                                            settingsDataChanged.settings.advanced.aux.model2 = true
                                                                            settingsData.settings.advanced.aux.model = "ECM03";
                                                                            settingsDataChanged.settings.advanced.aux.model = true

                                                                            settingsData.settings.advanced.aux.angle2 = settingsData.settings.advanced.aux.angle;
                                                                            settingsDataChanged.settings.advanced.aux.angle2 = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })

                                                                        }}
                                                                        style={{ cursor: isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, auxModel: "ECM03" }) ? 'not-allowed' : 'pointer' }}
                                                                    >
                                                                        {
                                                                            props.getBadget(messages["vidfleet_configuration_ecm03"], settingsData.settings.advanced.aux.model2 === "ECM03")
                                                                        }
                                                                    </div>
                                                                </div>
                                                            }
                                                            <div className="d-sm-inline-block mb-1">
                                                                <Tooltip title={messages["vidfleet_configuration_message_available_combinations"]} tooltipplacement="left">
                                                                    <a className="btn-link align-middle" onClick={() => { dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings, true)) }}>
                                                                        <Icon style={{ marginTop: 5 }}>info</Icon>
                                                                    </a>
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            }
                                            {
                                                devices?.length && devices[0].deviceModel.name.includes("AIR2") &&
                                                <Grid container>
                                                    <Grid item xs={12}>
                                                        {messages["vidfleet_configuration_angle2"]}
                                                        <div style={{ marginLeft: 0, marginBottom: 10 }}>
                                                            <div
                                                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                                                style={{ cursor: `${(settingsData.settings.advanced.aux.angle === "normal" || settingsData.settings.advanced.aux.angle === "upsidedown") ? "pointer" : "not-allowed"}` }}
                                                                key="advanced-aux-angle-normal"
                                                                onClick={e => {
                                                                    if ((settingsData.settings.advanced.aux.angle === "normal" || settingsData.settings.advanced.aux.angle === "upsidedown")) {
                                                                        settingsData.settings.advanced.aux.angle2 = "normal";
                                                                        settingsDataChanged.settings.advanced.aux.angle2 = true

                                                                        optionDefaults.advancedDefault = false;
                                                                        setSettingsData({ ...settingsData })
                                                                        saveChangedSettings({ ...settingsDataChanged })
                                                                        setSaveSettingsAvailable(true);
                                                                        setOptionDefaults({ ...optionDefaults })
                                                                    }
                                                                }}
                                                            >
                                                                {
                                                                    props.getBadget(messages["vidfleet_configuration_angle_normal"], settingsData.settings.advanced.aux.angle2 === "normal")
                                                                }
                                                            </div>
                                                            <div
                                                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                                                style={{ cursor: `${(settingsData.settings.advanced.aux.angle === "normal" || settingsData.settings.advanced.aux.angle === "upsidedown") ? "pointer" : "not-allowed"}` }}
                                                                key="advanced-aux-angle-upsidedown"
                                                                onClick={e => {
                                                                    if ((settingsData.settings.advanced.aux.angle === "normal" || settingsData.settings.advanced.aux.angle === "upsidedown")) {
                                                                        settingsData.settings.advanced.aux.angle2 = "upsidedown";
                                                                        settingsDataChanged.settings.advanced.aux.angle2 = true

                                                                        optionDefaults.advancedDefault = false;
                                                                        setSettingsData({ ...settingsData })
                                                                        saveChangedSettings({ ...settingsDataChanged })
                                                                        setSaveSettingsAvailable(true);
                                                                        setOptionDefaults({ ...optionDefaults })
                                                                    }
                                                                }}
                                                            >
                                                                {
                                                                    props.getBadget(messages["vidfleet_configuration_angle_upsidedown"], settingsData.settings.advanced.aux.angle2 === "upsidedown")
                                                                }
                                                            </div>
                                                            {
                                                                <div
                                                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                                                    style={{ cursor: `${(settingsData.settings.recordConfig.config !== "MAIN" || settingsData.settings.recordConfig.config === "HIGH") && (settingsData.settings.advanced.aux.angle === "rotate_left" || settingsData.settings.advanced.aux.angle === "rotate_right") ? "pointer" : "not-allowed"}` }}
                                                                    key="advanced-angle-rotate_left"
                                                                    onClick={e => {
                                                                        if ((settingsData.settings.recordConfig.config !== "MAIN" || settingsData.settings.recordConfig.config === "HIGH") && (settingsData.settings.advanced.aux.angle === "rotate_left" || settingsData.settings.advanced.aux.angle === "rotate_right")) {

                                                                            settingsData.settings.advanced.aux.angle2 = "rotate_left";
                                                                            settingsDataChanged.settings.advanced.aux.angle2 = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        }
                                                                    }}
                                                                >
                                                                    {
                                                                        props.getBadget(messages["vidfleet_configuration_angle_rotate_left"], settingsData.settings.advanced.aux.angle2 === "rotate_left")
                                                                    }
                                                                </div>
                                                            }
                                                            {
                                                                <div
                                                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                                                    style={{ cursor: `${(settingsData.settings.recordConfig.config !== "MAIN" || settingsData.settings.recordConfig.config === "HIGH") && (settingsData.settings.advanced.aux.angle === "rotate_left" || settingsData.settings.advanced.aux.angle === "rotate_right") ? "pointer" : "not-allowed"}` }}
                                                                    key="advanced-angle-rotate_right"
                                                                    onClick={e => {
                                                                        if ((settingsData.settings.recordConfig.config !== "MAIN" || settingsData.settings.recordConfig.config === "HIGH") && (settingsData.settings.advanced.aux.angle === "rotate_left" || settingsData.settings.advanced.aux.angle === "rotate_right")) {

                                                                            settingsData.settings.advanced.aux.angle2 = "rotate_right";
                                                                            settingsDataChanged.settings.advanced.aux.angle2 = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        }
                                                                    }}
                                                                >
                                                                    {
                                                                        props.getBadget(messages["vidfleet_configuration_angle_rotate_right"], settingsData.settings.advanced.aux.angle2 === "rotate_right")
                                                                    }
                                                                </div>
                                                            }
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            }
                                        </Grid>
                                    </Grid>
                                }
                                <Grid item xs={12}>
                                    <Grid item xs={12} style={{ marginTop: 20 }}>
                                        <div>
                                            {messages["vidfleet_configuration_adas"]}
                                        </div>
                                    </Grid>
                                    <Grid item xs={12}>
                                        {messages["vidfleet_configuration_level"]}
                                        <div style={{ marginLeft: 0 }}>
                                            <div className="d-sm-inline-block mb-1 vidfleet-pills" key="level-advanced-adas-off">
                                                <div
                                                    onClick={e => {
                                                        if (isButtonDisabled({ ...curretStates, adasLevel: "OFF" })) {
                                                            NotificationManager.warning(
                                                                <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                                messages["vidfleet_configuration_combination_not_allowed"],
                                                                6000,
                                                                null,
                                                                null,
                                                                'error'
                                                            );
                                                        }
                                                        settingsData.settings.advanced.adas.level = "OFF";
                                                        settingsDataChanged.settings.advanced.adas.level = true


                                                        if (!settingsData.settings.eventLevel)
                                                            settingsData.settings.eventLevel = { ADAS: {} }
                                                        else if (!settingsData.settings.eventLevel.ADAS)
                                                            settingsData.settings.eventLevel.ADAS = {}

                                                        settingsData.settings.eventLevel.ADAS.FORWARD_COLLISION = "OFF";
                                                        settingsDataChanged.settings.eventLevel.ADAS.FORWARD_COLLISION = true

                                                        settingsData.settings.eventLevel.ADAS.HEADWAY_MONITORING = "OFF";
                                                        settingsDataChanged.settings.eventLevel.ADAS.HEADWAY_MONITORING = true

                                                        settingsData.settings.eventLevel.ADAS.LANE_DEPARTURE = "OFF";
                                                        settingsDataChanged.settings.eventLevel.ADAS.LANE_DEPARTURE = true

                                                        settingsData.settings.eventLevel.ADAS.STOP_SIGN_VIOLATION = "OFF";
                                                        settingsDataChanged.settings.eventLevel.ADAS.STOP_SIGN_VIOLATION = true

                                                        optionDefaults.advancedDefault = false;
                                                        optionDefaults.eventLevelThresholdDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    style={{ cursor: isButtonDisabled({ ...curretStates, adasLevel: "OFF" }) ? 'not-allowed' : 'pointer' }}
                                                >
                                                    {
                                                        props.getBadget(messages["vidfleet_configuration_off"], settingsData.settings.advanced.adas.level === "OFF")
                                                    }
                                                </div>
                                            </div>
                                            <div className="d-sm-inline-block mb-1 vidfleet-pills" key="level-advanced-adas-standard">
                                                <div
                                                    onClick={e => {
                                                        if (isButtonDisabled({ ...curretStates, adasLevel: "STANDARD" })) {
                                                            NotificationManager.warning(
                                                                <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                                messages["vidfleet_configuration_combination_not_allowed"],
                                                                6000,
                                                                null,
                                                                null,
                                                                'error'
                                                            );
                                                        }
                                                        settingsData.settings.advanced.adas.level = "STANDARD";
                                                        settingsDataChanged.settings.advanced.adas.level = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    style={{ cursor: isButtonDisabled({ ...curretStates, adasLevel: "STANDARD" }) ? 'not-allowed' : 'pointer' }}
                                                >
                                                    {
                                                        props.getBadget(messages["vidfleet_configuration_standard"], settingsData.settings.advanced.adas.level === "STANDARD")
                                                    }
                                                </div>
                                            </div>
                                            <div className="d-sm-inline-block mb-1">
                                                <Tooltip title={messages["vidfleet_configuration_message_available_combinations"]} tooltipplacement="left">
                                                    <a className="btn-link align-middle" onClick={() => { dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings)) }}>
                                                        <Icon style={{ marginTop: 5 }}>info</Icon>
                                                    </a>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </Grid>
                                    {
                                        settingsData.settings.advanced.adas.level === "OFF" &&
                                        <Grid item xs={12} >
                                            <div>
                                                {messages["vidfleet_configuration_adas_level_off"]}
                                            </div>
                                        </Grid>
                                    }
                                    {
                                        settingsData.settings.advanced.adas.level === "STANDARD" &&
                                        <Grid item xs={12} >
                                            <div>
                                                {messages["vidfleet_configuration_adas_level_standard"]}
                                            </div>
                                        </Grid>
                                    }
                                    <Grid item xs={12}>
                                        <div style={{ marginTop: 10 }}>
                                            <h6>
                                                {messages["vidfleet_configuration_vehicle_class_present"]}
                                            </h6>
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} style={{ marginTop: 5 }}>
                                        <Grid item xs={props.bulk ? 5 : 3}>
                                            <Select
                                                id="selectVehicleClassAdas"
                                                name="selectVehicleClassAdas"
                                                placeholder={messages["vidfleet_configuration_vehicle_class_present"]}
                                                value={vehicleClassAdas}
                                                options={vehicleClassOptions}
                                                textField="name"
                                                valueField="id"
                                                onChange={(value) => {
                                                    vehicleClassAdas = value;
                                                    settingsData.settings.advanced.adas.cameraHeight = JSON.parse(JSON.stringify(value.advanced.adas.cameraHeight));
                                                    settingsData.settings.advanced.adas.vehicleWidth = JSON.parse(JSON.stringify(value.advanced.adas.vehicleWidth));
                                                    settingsData.settings.advanced.adas.timeRatio = JSON.parse(JSON.stringify(value.advanced.adas.timeRatio));

                                                    settingsDataChanged.settings.advanced.adas.cameraHeight = true
                                                    settingsDataChanged.settings.advanced.adas.vehicleWidth = true
                                                    settingsDataChanged.settings.advanced.adas.timeRatio = {}
                                                    settingsDataChanged.settings.advanced.adas.timeRatio.FORWARD_COLLISION = true
                                                    settingsDataChanged.settings.advanced.adas.timeRatio.HEADWAY_MONITORING = true

                                                    setVehicleClassAdas({ ...value })
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {/* Time Ratio */}
                            <Grid container>
                                <Grid item xs={12}>
                                    <div style={{ marginTop: 15 }}>
                                        {messages["vidfleet_configuration_time_ratio"]}
                                    </div>
                                </Grid>
                                {
                                    !HasPermission(user, "vfath", "u") &&
                                    <Grid item xs={12}>
                                        <Alert severity="warning" style={{ marginBottom: 10, marginTop: 5 }}>
                                            {messages['vidfleet_configuration_permission_message']}
                                        </Alert>
                                    </Grid>
                                }
                            </Grid>

                            <Grid container>
                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 20 }} >
                                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                        <div style={{ width: "70%", alignItems: "left" }}>
                                            {messages["vidfleet_configuration_following_too_closely_adas"]}
                                        </div>
                                        <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                            {messages["vidfleet_configuration_message_time_ratio"]}
                                        </div>
                                    </div>
                                    <div>
                                        <Slider
                                            min={0.56}
                                            max={5.60}
                                            value={transformTimeRatio(settingsData?.settings?.advanced?.adas?.timeRatio?.HEADWAY_MONITORING || 100, false, null)}
                                            onChange={(event) => {

                                                if (settingsData?.settings?.advanced?.adas?.timeRatio?.HEADWAY_MONITORING) {
                                                    settingsData.settings.advanced.adas.timeRatio.HEADWAY_MONITORING = transformTimeRatio(null, false, event);
                                                } else if (settingsData?.settings?.advanced?.adas?.timeRatio) {
                                                    settingsData.settings.advanced.adas.timeRatio.HEADWAY_MONITORING = transformTimeRatio(null, false, event);
                                                } else {
                                                    let adas = {
                                                        timeRatio: {
                                                            HEADWAY_MONITORING: transformTimeRatio(null, false, event)
                                                        }
                                                    }
                                                    settingsData.settings.advanced.adas = adas
                                                }
                                                settingsDataChanged.settings.advanced.adas.timeRatio.HEADWAY_MONITORING = true

                                                optionDefaults.advancedDefault = false;
                                                setSettingsData({ ...settingsData })
                                                saveChangedSettings({ ...settingsDataChanged })
                                                setSaveSettingsAvailable(true);
                                                setOptionDefaults({ ...optionDefaults })
                                            }}
                                            sliderOneColor
                                            showValue
                                            valueLabelDisplay="off"
                                            measure={messages["vidfleet_configuration_seconds"]}
                                            sliderStep={0.01}
                                            disabled={!HasPermission(user, "vfath", "u")}
                                        />
                                    </div>
                                </Grid>
                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 15, marginLeft: !props.bulk && 20 }} >
                                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%", fontSize: "10px", }}>
                                        <div style={{ width: "70%", textAlign: "center" }}>
                                            {messages["vidfleet_configuration_message_time_ratio_tailgating"]}
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                        <div style={{ width: "70%", textAlign: "center" }}>
                                            {messages["vidfleet_configuration_tailgating_adas"]}
                                        </div>
                                        <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                            {messages["vidfleet_configuration_message_time_ratio"]}
                                        </div>
                                    </div>
                                    <div>
                                        <Slider
                                            min={0.28}
                                            max={2.80}
                                            value={transformTimeRatio(settingsData?.settings?.advanced?.adas?.timeRatio?.HEADWAY_MONITORING || 100, true, null)}
                                            onChange={(event) => {

                                                if (settingsData?.settings?.advanced?.adas?.timeRatio?.HEADWAY_MONITORING) {
                                                    settingsData.settings.advanced.adas.timeRatio.HEADWAY_MONITORING = transformTimeRatio(null, false, event) * 2;
                                                } else if (settingsData?.settings?.advanced?.adas?.timeRatio) {
                                                    settingsData.settings.advanced.adas.timeRatio.HEADWAY_MONITORING = transformTimeRatio(null, false, event) * 2;
                                                } else {
                                                    let adas = {
                                                        timeRatio: {
                                                            HEADWAY_MONITORING: transformTimeRatio(null, false, event) * 2
                                                        }
                                                    }
                                                    settingsData.settings.advanced.adas = adas
                                                }
                                                settingsDataChanged.settings.advanced.adas.timeRatio.HEADWAY_MONITORING = true

                                                optionDefaults.advancedDefault = false;
                                                setSettingsData({ ...settingsData })
                                                saveChangedSettings({ ...settingsDataChanged })
                                                setSaveSettingsAvailable(true);
                                                setOptionDefaults({ ...optionDefaults })
                                            }}
                                            sliderOneColor
                                            showValue
                                            valueLabelDisplay="off"
                                            measure={messages["vidfleet_configuration_seconds"]}
                                            sliderStep={0.01}
                                            disabled={!HasPermission(user, "vfath", "u")}
                                        />
                                    </div>
                                </Grid>
                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 30 }}>
                                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                        <div style={{ width: "70%", alignItems: "left" }}>
                                            {messages["vidfleet_configuration_forward_collision_adas"]}
                                        </div>
                                        <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                            {messages["vidfleet_configuration_message_time_ratio"]}
                                        </div>
                                    </div>

                                    <Slider
                                        min={2.02}
                                        max={5.60}
                                        value={transformTimeRatio(settingsData?.settings?.advanced?.adas?.timeRatio?.FORWARD_COLLISION || 100, false, null)}
                                        onChange={(event) => {


                                            if (settingsData?.settings?.advanced?.adas?.timeRatio?.FORWARD_COLLISION) {
                                                settingsData.settings.advanced.adas.timeRatio.FORWARD_COLLISION = transformTimeRatio(null, false, event);
                                            } else if (settingsData?.settings?.advanced?.adas?.timeRatio) {
                                                settingsData.settings.advanced.adas.timeRatio.FORWARD_COLLISION = transformTimeRatio(null, false, event);
                                            } else {
                                                let adas = {
                                                    timeRatio: {
                                                        FORWARD_COLLISION: transformTimeRatio(null, false, event)
                                                    }
                                                }
                                                settingsData.settings.advanced.adas = adas
                                            }
                                            settingsDataChanged.settings.advanced.adas.timeRatio.FORWARD_COLLISION = true

                                            optionDefaults.advancedDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                        sliderOneColor
                                        showValue
                                        valueLabelDisplay="off"
                                        measure={messages["vidfleet_configuration_seconds"]}
                                        sliderStep={0.01}
                                        disabled={!HasPermission(user, "vfath", "u")}
                                    />

                                </Grid>
                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 30, marginLeft: !props.bulk && 20 }}>
                                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                        <div style={{ width: "70%", alignItems: "left" }}>
                                            {messages["vidfleet_configuration_pedestrian_collision"]}
                                        </div>
                                        <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                            {messages["vidfleet_configuration_message_time_ratio"]}
                                        </div>
                                    </div>

                                    <Slider
                                        min={0.56}
                                        max={5.60}
                                        value={transformTimeRatio(settingsData?.settings?.advanced?.adas?.timeRatio?.PEDESTRIAN_COLLISION || 100, false, null)}
                                        onChange={(event) => {


                                            if (settingsData?.settings?.advanced?.adas?.timeRatio?.PEDESTRIAN_COLLISION) {
                                                settingsData.settings.advanced.adas.timeRatio.PEDESTRIAN_COLLISION = transformTimeRatio(null, false, event);
                                            } else if (settingsData?.settings?.advanced?.adas?.timeRatio) {
                                                settingsData.settings.advanced.adas.timeRatio.PEDESTRIAN_COLLISION = transformTimeRatio(null, false, event);
                                            } else {
                                                let adas = {
                                                    timeRatio: {
                                                        PEDESTRIAN_COLLISION: transformTimeRatio(null, false, event)
                                                    }
                                                }
                                                settingsData.settings.advanced.adas = adas
                                            }
                                            settingsDataChanged.settings.advanced.adas.timeRatio.PEDESTRIAN_COLLISION = true

                                            optionDefaults.advancedDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                        sliderOneColor
                                        showValue
                                        valueLabelDisplay="off"
                                        measure={messages["vidfleet_configuration_seconds"]}
                                        sliderStep={0.01}
                                        disabled={!HasPermission(user, "vfath", "u")}
                                    />

                                </Grid>
                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 30 }}>
                                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                        <div style={{ width: "70%", alignItems: "left" }}>
                                            {messages["vidfleet_configuration_cyclist_collision"]}
                                        </div>
                                        <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                            {messages["vidfleet_configuration_message_time_ratio"]}
                                        </div>
                                    </div>

                                    <Slider
                                        min={0.56}
                                        max={5.60}
                                        value={transformTimeRatio(settingsData?.settings?.advanced?.adas?.timeRatio?.CYCLIST_COLLISION || 100, false, null)}
                                        onChange={(event) => {


                                            if (settingsData?.settings?.advanced?.adas?.timeRatio?.CYCLIST_COLLISION) {
                                                settingsData.settings.advanced.adas.timeRatio.CYCLIST_COLLISION = transformTimeRatio(null, false, event);
                                            } else if (settingsData?.settings?.advanced?.adas?.timeRatio) {
                                                settingsData.settings.advanced.adas.timeRatio.CYCLIST_COLLISION = transformTimeRatio(null, false, event);
                                            } else {
                                                let adas = {
                                                    timeRatio: {
                                                        CYCLIST_COLLISION: transformTimeRatio(null, false, event)
                                                    }
                                                }
                                                settingsData.settings.advanced.adas = adas
                                            }
                                            settingsDataChanged.settings.advanced.adas.timeRatio.CYCLIST_COLLISION = true

                                            optionDefaults.advancedDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                        sliderOneColor
                                        showValue
                                        valueLabelDisplay="off"
                                        measure={messages["vidfleet_configuration_seconds"]}
                                        sliderStep={0.01}
                                        disabled={!HasPermission(user, "vfath", "u")}
                                    />

                                </Grid>
                            </Grid>
                            {/* Time Ration */}
                            <Grid item xs={12}>
                                <div style={{ marginTop: 20 }}>
                                    <Divider className='mb-3' />
                                </div>
                            </Grid>
                            <Grid container>
                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                    {messages["vidfleet_configuration_camera_height"]}
                                    <Slider
                                        min={0.0001}
                                        max={9.9999}
                                        value={settingsData?.settings?.advanced?.adas?.cameraHeight || 0.0001}
                                        onChange={(event) => {

                                            if (settingsData?.settings?.advanced?.adas?.cameraHeight) {
                                                settingsData.settings.advanced.adas.cameraHeight = event;
                                            } else if (settingsData?.settings?.advanced?.adas) {
                                                settingsData.settings.advanced.adas.cameraHeight = event;
                                            } else {
                                                let adas = {
                                                    cameraHeight: event
                                                }
                                                settingsData.settings.advanced.adas = adas
                                            }
                                            settingsDataChanged.settings.advanced.adas.cameraHeight = true

                                            optionDefaults.advancedDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                        sliderOneColor
                                        showValue
                                        valueLabelDisplay="off"
                                        sliderStep={0.0001}
                                        measure={messages["vidfleet_configuration_meter"]}
                                        disabled={!HasPermission(user, "vfath", "u")}
                                    />
                                </Grid>

                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10, marginLeft: !props.bulk && 20 }}>
                                    {messages["vidfleet_configuration_vehicle_width"]}
                                    <Slider
                                        min={0.0001}
                                        max={9.9999}
                                        value={settingsData?.settings?.advanced?.adas?.vehicleWidth || 0.0001}
                                        onChange={(event) => {

                                            if (settingsData?.settings?.advanced?.adas?.vehicleWidth) {
                                                settingsData.settings.advanced.adas.vehicleWidth = event;
                                            } else if (settingsData?.settings?.advanced?.adas) {
                                                settingsData.settings.advanced.adas.vehicleWidth = event;
                                            } else {
                                                let adas = {
                                                    vehicleWidth: event
                                                }
                                                settingsData.settings.advanced.adas = adas
                                            }
                                            settingsDataChanged.settings.advanced.adas.vehicleWidth = true

                                            optionDefaults.advancedDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                        sliderOneColor
                                        showValue
                                        valueLabelDisplay="off"
                                        sliderStep={0.0001}
                                        measure={messages["vidfleet_configuration_meter"]}
                                        disabled={!HasPermission(user, "vfath", "u")}
                                    />
                                </Grid>

                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                    {messages["vidfleet_configuration_camera_position_to_center"]}
                                    <Slider
                                        min={-1.0000}
                                        max={1.0000}
                                        value={settingsData?.settings?.advanced?.adas?.cameraPositionToCenter || 0}
                                        onChange={(event) => {

                                            if (settingsData?.settings?.advanced?.adas?.cameraPositionToCenter) {
                                                settingsData.settings.advanced.adas.cameraPositionToCenter = event;
                                            } else if (settingsData?.settings?.advanced?.adas) {
                                                settingsData.settings.advanced.adas.cameraPositionToCenter = event;
                                            } else {
                                                let adas = {
                                                    cameraPositionToCenter: event
                                                }
                                                settingsData.settings.advanced.adas = adas
                                            }
                                            settingsDataChanged.settings.advanced.adas.cameraPositionToCenter = true

                                            optionDefaults.advancedDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                        sliderOneColor
                                        showValue
                                        valueLabelDisplay="off"
                                        sliderStep={0.0001}
                                        measure={messages["vidfleet_configuration_meter"]}
                                        disabled={!HasPermission(user, "vfath", "u")}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <div style={{ marginTop: 20 }}>
                                    <Divider className='mb-3' />
                                </div>
                            </Grid>
                            <Grid item xs={12}>
                                <Tabs
                                    onChange={(numberTab) => { setTabAdas(numberTab) }}
                                >
                                    {
                                        settingsData.settings.eventLevel.ADAS.FORWARD_COLLISION === "WARNING" &&
                                        <TabContainer key={`menu-${0}`} label={messages['vidfleet_configuration_forward_collision_warning_adas']} index={indexCount++}>
                                            <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                                <div >
                                                    <h6>
                                                        {messages["vidfleet_configuration_thresholds"]}
                                                    </h6>
                                                </div>
                                            </Grid>
                                            <Grid container>
                                                {
                                                    !HasPermission(user, "vfath", "u") &&
                                                    <Grid item xs={12}>
                                                        <Alert severity="warning" style={{ marginBottom: 10, marginTop: 5 }}>
                                                            {messages['vidfleet_configuration_permission_message']}
                                                        </Alert>
                                                    </Grid>
                                                }
                                                <Grid item xs={props.bulk ? 12 : 5}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_min_speed"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="min_speed-forward_collision_warning"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.minSpeed?.FORWARD_COLLISION_WARNING === defaultSettingsData?.settings?.advanced?.adas?.minSpeed?.FORWARD_COLLISION_WARNING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.adas?.minSpeed?.FORWARD_COLLISION_WARNING));
                                                                            settingsDataChanged.settings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("FORWARD_COLLISION_WARNING")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING.max}
                                                                value={settingsData?.settings?.advanced?.adas?.minSpeed?.FORWARD_COLLISION_WARNING || vidFleetConfigLimitDefault.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.minSpeed)
                                                                        settingsData.settings.advanced.adas.minSpeed = {}

                                                                    settingsData.settings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING = event;
                                                                    settingsDataChanged.settings.advanced.adas.minSpeed.FORWARD_COLLISION_WARNING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_km_h"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_repeat_interval"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="repeat_interval-forward_collision_warning"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.repeatInterval?.FORWARD_COLLISION_WARNING === defaultSettingsData?.settings?.advanced?.adas?.repeatInterval.FORWARD_COLLISION_WARNING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.adas?.repeatInterval.FORWARD_COLLISION_WARNING));
                                                                            settingsDataChanged.settings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING = true
                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                    disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING.max}
                                                                value={settingsData?.settings?.advanced?.adas?.repeatInterval?.FORWARD_COLLISION_WARNING || vidFleetConfigLimitDefault.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.repeatInterval)
                                                                        settingsData.settings.advanced.adas.repeatInterval = {}

                                                                    settingsData.settings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING = event;
                                                                    settingsDataChanged.settings.advanced.adas.repeatInterval.FORWARD_COLLISION_WARNING = true
                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_delay"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_delay-forward_collision_warning_adas"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.detectionDelay?.FORWARD_COLLISION_WARNING === defaultSettingsData?.settings?.advanced?.adas?.detectionDelay?.FORWARD_COLLISION_WARNING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING));
                                                                            settingsDataChanged.settings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("FORWARD_COLLISION_WARNING")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING.max}
                                                                value={settingsData?.settings?.advanced?.adas?.detectionDelay?.FORWARD_COLLISION_WARNING || vidFleetConfigLimitDefault.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.detectionDelay)
                                                                        settingsData.settings.advanced.adas.detectionDelay = {}

                                                                    settingsData.settings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING = event;
                                                                    settingsDataChanged.settings.advanced.adas.detectionDelay.FORWARD_COLLISION_WARNING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20, marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_reset"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_reset-forward_collision_warning_adas"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.detectionReset?.FORWARD_COLLISION_WARNING === defaultSettingsData?.settings?.advanced?.adas?.detectionReset?.FORWARD_COLLISION_WARNING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING));
                                                                            settingsDataChanged.settings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING = true
                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("FORWARD_COLLISION_WARNING")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING.max}
                                                                value={settingsData?.settings?.advanced?.adas?.detectionReset?.FORWARD_COLLISION_WARNING || vidFleetConfigLimitDefault.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.detectionReset)
                                                                        settingsData.settings.advanced.adas.detectionReset = {}

                                                                    settingsData.settings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING = event;
                                                                    settingsDataChanged.settings.advanced.adas.detectionReset.FORWARD_COLLISION_WARNING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </TabContainer>
                                    }
                                    {
                                        settingsData.settings.eventLevel.ADAS.HEADWAY_MONITORING === "WARNING" &&
                                        <TabContainer key={`menu-${1}`} label={messages['vidfleet_configuration_headway_monitoring_warning_adas']} index={indexCount++}>
                                            <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                                <div >
                                                    <h6>
                                                        {messages["vidfleet_configuration_thresholds"]}
                                                    </h6>
                                                </div>
                                            </Grid>

                                            <Grid container>
                                                {
                                                    !HasPermission(user, "vfath", "u") &&
                                                    <Grid item xs={12}>
                                                        <Alert severity="warning" style={{ marginBottom: 10, marginTop: 5 }}>
                                                            {messages['vidfleet_configuration_permission_message']}
                                                        </Alert>
                                                    </Grid>
                                                }
                                                <Grid item xs={props.bulk ? 12 : 5}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_min_speed"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="min_speed-headway_monitoring_warning"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.minSpeed?.HEADWAY_MONITORING_WARNING === defaultSettingsData?.settings?.advanced?.adas?.minSpeed?.HEADWAY_MONITORING_WARNING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.adas?.minSpeed?.HEADWAY_MONITORING_WARNING));
                                                                            settingsDataChanged.settings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("HEADWAY_MONITORING_WARNING")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING.max}
                                                                value={settingsData?.settings?.advanced?.adas?.minSpeed?.HEADWAY_MONITORING_WARNING || vidFleetConfigLimitDefault.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.minSpeed)
                                                                        settingsData.settings.advanced.adas.minSpeed = {}

                                                                    settingsData.settings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING = event;
                                                                    settingsDataChanged.settings.advanced.adas.minSpeed.HEADWAY_MONITORING_WARNING = false

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_km_h"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_repeat_interval"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="repeat_interval-headway_monitoring_warning"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.repeatInterval?.HEADWAY_MONITORING_WARNING === defaultSettingsData?.settings?.advanced?.adas?.repeatInterval.HEADWAY_MONITORING_WARNING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.adas?.repeatInterval.HEADWAY_MONITORING_WARNING));
                                                                            settingsDataChanged.settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                    disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING.max}
                                                                value={settingsData?.settings?.advanced?.adas?.repeatInterval?.HEADWAY_MONITORING_WARNING || vidFleetConfigLimitDefault.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.repeatInterval)
                                                                        settingsData.settings.advanced.adas.repeatInterval = {}

                                                                    settingsData.settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING = event;
                                                                    settingsDataChanged.settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_WARNING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_delay"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_delay-headway_monitoring_warning_adas"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.detectionDelay?.HEADWAY_MONITORING_WARNING === defaultSettingsData?.settings?.advanced?.adas?.detectionDelay?.HEADWAY_MONITORING_WARNING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING));
                                                                            settingsDataChanged.settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("HEADWAY_MONITORING_WARNING")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING.max}
                                                                value={settingsData?.settings?.advanced?.adas?.detectionDelay?.HEADWAY_MONITORING_WARNING || vidFleetConfigLimitDefault.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.detectionDelay)
                                                                        settingsData.settings.advanced.adas.detectionDelay = {}

                                                                    settingsData.settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_WARNING = event;
                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20, marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_reset"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_reset-headway_monitoring_warning_adas"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.detectionReset?.HEADWAY_MONITORING_WARNING === defaultSettingsData?.settings?.advanced?.adas?.detectionReset?.HEADWAY_MONITORING_WARNING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING));
                                                                            settingsDataChanged.settings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("HEADWAY_MONITORING_WARNING")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING.max}
                                                                value={settingsData?.settings?.advanced?.adas?.detectionReset?.HEADWAY_MONITORING_WARNING || vidFleetConfigLimitDefault.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.detectionReset)
                                                                        settingsData.settings.advanced.adas.detectionReset = {}

                                                                    settingsData.settings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING = event;
                                                                    settingsDataChanged.settings.advanced.adas.detectionReset.HEADWAY_MONITORING_WARNING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </TabContainer>
                                    }
                                    {
                                        (settingsData.settings.eventLevel.ADAS.HEADWAY_MONITORING === "WARNING" || settingsData.settings.eventLevel.ADAS.HEADWAY_MONITORING === "EMERGENCY") &&
                                        <TabContainer key={`menu-${2}`} label={messages['vidfleet_configuration_headway_monitoring_emergency']} index={indexCount++}>
                                            <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                                <div >
                                                    <h6>
                                                        {messages["vidfleet_configuration_thresholds"]}
                                                    </h6>
                                                </div>
                                            </Grid>
                                            <Grid container>
                                                {
                                                    !HasPermission(user, "vfath", "u") &&
                                                    <Grid item xs={12}>
                                                        <Alert severity="warning" style={{ marginBottom: 10, marginTop: 5 }}>
                                                            {messages['vidfleet_configuration_permission_message']}
                                                        </Alert>
                                                    </Grid>
                                                }
                                                <Grid item xs={props.bulk ? 12 : 5}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_min_speed"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="min_speed-headway_monitoring_emergency"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.minSpeed?.HEADWAY_MONITORING_EMERGENCY === defaultSettingsData?.settings?.advanced?.adas?.minSpeed?.HEADWAY_MONITORING_EMERGENCY}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.adas?.minSpeed?.HEADWAY_MONITORING_EMERGENCY));
                                                                            settingsDataChanged.settings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("HEADWAY_MONITORING_EMERGENCY")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY.max}
                                                                value={settingsData?.settings?.advanced?.adas?.minSpeed?.HEADWAY_MONITORING_EMERGENCY || vidFleetConfigLimitDefault.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.minSpeed)
                                                                        settingsData.settings.advanced.adas.minSpeed = {}

                                                                    settingsData.settings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY = event;
                                                                    settingsDataChanged.settings.advanced.adas.minSpeed.HEADWAY_MONITORING_EMERGENCY = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_km_h"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_repeat_interval"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="repeat_interval-headway_monitoring_emergency"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.repeatInterval?.HEADWAY_MONITORING_EMERGENCY === defaultSettingsData?.settings?.advanced?.adas?.repeatInterval.HEADWAY_MONITORING_EMERGENCY}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.adas?.repeatInterval.HEADWAY_MONITORING_EMERGENCY));
                                                                            settingsDataChanged.settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                    disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY.max}
                                                                value={settingsData?.settings?.advanced?.adas?.repeatInterval?.HEADWAY_MONITORING_EMERGENCY || vidFleetConfigLimitDefault.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.repeatInterval)
                                                                        settingsData.settings.advanced.adas.repeatInterval = {}

                                                                    settingsData.settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY = event;
                                                                    settingsDataChanged.settings.advanced.adas.repeatInterval.HEADWAY_MONITORING_EMERGENCY = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_delay"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_delay-headway_monitoring_emergency"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.detectionDelay?.HEADWAY_MONITORING_EMERGENCY === defaultSettingsData?.settings?.advanced?.adas?.detectionDelay?.HEADWAY_MONITORING_EMERGENCY}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY));
                                                                            settingsDataChanged.settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("HEADWAY_MONITORING_EMERGENCY")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY.max}
                                                                value={settingsData?.settings?.advanced?.adas?.detectionDelay?.HEADWAY_MONITORING_EMERGENCY || vidFleetConfigLimitDefault.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.detectionDelay)
                                                                        settingsData.settings.advanced.adas.detectionDelay = {}

                                                                    settingsData.settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY = event;
                                                                    settingsDataChanged.settings.advanced.adas.detectionDelay.HEADWAY_MONITORING_EMERGENCY = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20, marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_reset"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_reset-headway_monitoring_emergency"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.detectionReset?.HEADWAY_MONITORING_EMERGENCY === defaultSettingsData?.settings?.advanced?.adas?.detectionReset?.HEADWAY_MONITORING_EMERGENCY}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY));
                                                                            settingsDataChanged.settings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("HEADWAY_MONITORING_EMERGENCY")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY.max}
                                                                value={settingsData?.settings?.advanced?.adas?.detectionReset?.HEADWAY_MONITORING_EMERGENCY || vidFleetConfigLimitDefault.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.detectionReset)
                                                                        settingsData.settings.advanced.adas.detectionReset = {}

                                                                    settingsData.settings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY = event;
                                                                    settingsDataChanged.settings.advanced.adas.detectionReset.HEADWAY_MONITORING_EMERGENCY = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </TabContainer>
                                    }
                                    {
                                        settingsData.settings.eventLevel.ADAS.LANE_DEPARTURE === "WARNING" &&
                                        <TabContainer key={`menu-${3}`} label={messages['vidfleet_configuration_lane_departure_warning']} index={indexCount++}>
                                            <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                                <div >
                                                    <h6>
                                                        {messages["vidfleet_configuration_thresholds"]}
                                                    </h6>
                                                </div>
                                            </Grid>
                                            <Grid container>
                                                {
                                                    !HasPermission(user, "vfath", "u") &&
                                                    <Grid item xs={12}>
                                                        <Alert severity="warning" style={{ marginBottom: 10, marginTop: 5 }}>
                                                            {messages['vidfleet_configuration_permission_message']}
                                                        </Alert>
                                                    </Grid>
                                                }
                                                <Grid item xs={props.bulk ? 12 : 5}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_min_speed"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="min_speed-lane_departure_warning"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.minSpeed?.LANE_DEPARTURE_WARNING === defaultSettingsData?.settings?.advanced?.adas?.minSpeed?.LANE_DEPARTURE_WARNING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.adas?.minSpeed?.LANE_DEPARTURE_WARNING));
                                                                            settingsDataChanged.settings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("LANE_DEPARTURE_WARNING")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING.max}
                                                                value={settingsData?.settings?.advanced?.adas?.minSpeed?.LANE_DEPARTURE_WARNING || vidFleetConfigLimitDefault.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.minSpeed)
                                                                        settingsData.settings.advanced.adas.minSpeed = {}

                                                                    settingsData.settings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING = event;
                                                                    settingsDataChanged.settings.advanced.adas.minSpeed.LANE_DEPARTURE_WARNING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_km_h"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_repeat_interval"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="repeat_interval-lane_departure_warning"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.repeatInterval?.LANE_DEPARTURE_WARNING === defaultSettingsData?.settings?.advanced?.adas?.repeatInterval.LANE_DEPARTURE_WARNING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.adas?.repeatInterval.LANE_DEPARTURE_WARNING));
                                                                            settingsDataChanged.settings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })

                                                                        }
                                                                    }}
                                                                    disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING.max}
                                                                value={settingsData?.settings?.advanced?.adas?.repeatInterval?.LANE_DEPARTURE_WARNING || vidFleetConfigLimitDefault.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.repeatInterval)
                                                                        settingsData.settings.advanced.adas.repeatInterval = {}

                                                                    settingsData.settings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING = event;
                                                                    settingsDataChanged.settings.advanced.adas.repeatInterval.LANE_DEPARTURE_WARNING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_delay"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_delay-lane_departure_warning"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.detectionDelay?.LANE_DEPARTURE_WARNING === defaultSettingsData?.settings?.advanced?.adas?.detectionDelay?.LANE_DEPARTURE_WARNING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING));
                                                                            settingsDataChanged.settings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING = true
                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("LANE_DEPARTURE_WARNING")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING.max}
                                                                value={settingsData?.settings?.advanced?.adas?.detectionDelay?.LANE_DEPARTURE_WARNING || vidFleetConfigLimitDefault.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.detectionDelay)
                                                                        settingsData.settings.advanced.adas.detectionDelay = {}

                                                                    settingsData.settings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING = event;
                                                                    settingsDataChanged.settings.advanced.adas.detectionDelay.LANE_DEPARTURE_WARNING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20, marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_reset"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_reset-lane_departure_warning"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.detectionReset?.LANE_DEPARTURE_WARNING === defaultSettingsData?.settings?.advanced?.adas?.detectionReset?.LANE_DEPARTURE_WARNING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING));
                                                                            settingsDataChanged.settings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING = true
                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("LANE_DEPARTURE_WARNING")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING.max}
                                                                value={settingsData?.settings?.advanced?.adas?.detectionReset?.LANE_DEPARTURE_WARNING || vidFleetConfigLimitDefault.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.detectionReset)
                                                                        settingsData.settings.advanced.adas.detectionReset = {}

                                                                    settingsData.settings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING = event;
                                                                    settingsDataChanged.settings.advanced.adas.detectionReset.LANE_DEPARTURE_WARNING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </TabContainer>
                                    }
                                    {
                                        settingsData.settings.eventLevel.ADAS.STOP_SIGN_VIOLATION === "WARNING" &&
                                        <TabContainer key={`menu-${4}`} label={messages['vidfleet_configuration_stop_sign_violation']} index={indexCount++}>
                                            <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                                <div >
                                                    <h6>
                                                        {messages["vidfleet_configuration_thresholds"]}
                                                    </h6>
                                                </div>
                                            </Grid>
                                            <Grid container>
                                                {
                                                    !HasPermission(user, "vfath", "u") &&
                                                    <Grid item xs={12}>
                                                        <Alert severity="warning" style={{ marginBottom: 10, marginTop: 5 }}>
                                                            {messages['vidfleet_configuration_permission_message']}
                                                        </Alert>
                                                    </Grid>
                                                }
                                                <Grid item xs={props.bulk ? 12 : 5}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_min_speed"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="min_speed-stop_sign_vilation"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.minSpeed?.STOP_SIGN_VIOLATION === defaultSettingsData?.settings?.advanced?.adas?.minSpeed?.STOP_SIGN_VIOLATION}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.adas?.minSpeed?.STOP_SIGN_VIOLATION));
                                                                            settingsDataChanged.settings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("STOP_SIGN_VIOLATION")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.minSpeed.STOP_SIGN_VIOLATION.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.minSpeed.STOP_SIGN_VIOLATION.max}
                                                                value={settingsData?.settings?.advanced?.adas?.minSpeed?.STOP_SIGN_VIOLATION || vidFleetConfigLimitDefault.advanced.adas.minSpeed.STOP_SIGN_VIOLATION.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.minSpeed)
                                                                        settingsData.settings.advanced.adas.minSpeed = {}

                                                                    settingsData.settings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION = event;
                                                                    settingsDataChanged.settings.advanced.adas.minSpeed.STOP_SIGN_VIOLATION = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_km_h"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_repeat_interval"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="repeat_interval-stop_sign_vilation"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.repeatInterval?.STOP_SIGN_VIOLATION === defaultSettingsData?.settings?.advanced?.adas?.repeatInterval.STOP_SIGN_VIOLATION}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.adas?.repeatInterval.STOP_SIGN_VIOLATION));
                                                                            settingsDataChanged.settings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                    disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION.max}
                                                                value={settingsData?.settings?.advanced?.adas?.repeatInterval?.STOP_SIGN_VIOLATION || vidFleetConfigLimitDefault.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.repeatInterval)
                                                                        settingsData.settings.advanced.adas.repeatInterval = {}

                                                                    settingsData.settings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION = event;
                                                                    settingsDataChanged.settings.advanced.adas.repeatInterval.STOP_SIGN_VIOLATION = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_delay"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_delay-stop_sign_vilation"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.detectionDelay?.STOP_SIGN_VIOLATION === defaultSettingsData?.settings?.advanced?.adas?.detectionDelay?.STOP_SIGN_VIOLATION}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION));
                                                                            settingsDataChanged.settings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("STOP_SIGN_VIOLATION")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION.max}
                                                                value={settingsData?.settings?.advanced?.adas?.detectionDelay?.STOP_SIGN_VIOLATION || vidFleetConfigLimitDefault.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.detectionDelay)
                                                                        settingsData.settings.advanced.adas.detectionDelay = {}

                                                                    settingsData.settings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION = event;
                                                                    settingsDataChanged.settings.advanced.adas.detectionDelay.STOP_SIGN_VIOLATION = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20, marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_reset"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_reset-stop_sign_vilation"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.adas?.detectionReset?.STOP_SIGN_VIOLATION === defaultSettingsData?.settings?.advanced?.adas?.detectionReset?.STOP_SIGN_VIOLATION}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION));
                                                                            settingsDataChanged.settings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("STOP_SIGN_VIOLATION")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.adas.detectionReset.STOP_SIGN_VIOLATION.min}
                                                                max={vidFleetConfigLimitDefault.advanced.adas.detectionReset.STOP_SIGN_VIOLATION.max}
                                                                value={settingsData?.settings?.advanced?.adas?.detectionReset?.STOP_SIGN_VIOLATION || vidFleetConfigLimitDefault.advanced.adas.detectionReset.STOP_SIGN_VIOLATION.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.adas)
                                                                        settingsData.settings.advanced.adas = {}
                                                                    if (!settingsData.settings.advanced.adas.detectionReset)
                                                                        settingsData.settings.advanced.adas.detectionReset = {}

                                                                    settingsData.settings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION = event;
                                                                    settingsDataChanged.settings.advanced.adas.detectionReset.STOP_SIGN_VIOLATION = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </TabContainer>
                                    }
                                </Tabs>
                            </Grid>
                            <Grid item xs={12}>
                                <div style={{ marginTop: 20 }}>
                                    <Divider className='mb-3' />
                                </div>
                            </Grid>
                            {/* safety assistance */}
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <div style={{ marginTop: 15 }}>
                                        {messages["vidfleet_configuration_safety_assistance"]}
                                    </div>
                                </Grid>
                                <Grid item xs={bulk ? 12 : 6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2} style={{ marginTop: 15 }}>
                                            <ToggleSwitch
                                                id="safetyAssistance-alarm-NO_SEATBELT"
                                                blueToggle={true}
                                                mediumLarge={true}
                                                label={messages["vidfleet_configuration_no_seatbelt"]}
                                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                                checked={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.NO_SEATBELT?.enable ? true : false}
                                                onChange={(checked) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.enable = checked;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.enable = true
                                                    if (checked) {
                                                        settingsData.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.duration = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.NO_SEATBELT.default * 10;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.duration = true

                                                        settingsData.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.cnt = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.cnt = true
                                                    } else {
                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.duration;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.duration = false

                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.cnt;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.cnt = false
                                                    }
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                width={130}
                                            />
                                        </Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            {messages["vidfleet_configuration_duration"]}
                                            <Slider
                                                min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.NO_SEATBELT.min}
                                                max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.NO_SEATBELT.max}
                                                value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.NO_SEATBELT.duration / 10 || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.NO_SEATBELT.default / 10}
                                                onChange={(event) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.duration = event * 10;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.duration = true

                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                sliderOneColor
                                                showValue
                                                valueLabelDisplay="off"
                                                disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.NO_SEATBELT?.enable}
                                                measure={messages["vidfleet_configuration_seconds"]}
                                            />
                                        </Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                <div style={{ width: "70%", alignItems: "left" }}>
                                                    {messages["vidfleet_configuration_count"]}
                                                </div>
                                                <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                                    {messages["vidfleet_configuration_count_msg"]}
                                                </div>
                                            </div>
                                            <div>
                                                <Slider
                                                    min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.min}
                                                    max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.max}
                                                    value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.NO_SEATBELT.cnt || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default}
                                                    onChange={(event) => {

                                                        settingsData.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.cnt = event;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_SEATBELT.cnt = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    sliderOneColor
                                                    showValue
                                                    valueLabelDisplay="off"
                                                    disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.NO_SEATBELT?.enable}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={bulk ? 12 : 6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2} style={{ marginTop: 15 }}>
                                            <ToggleSwitch
                                                id="safetyAssistance-alarm-USING_CELLPHONE"
                                                blueToggle={true}
                                                mediumLarge={true}
                                                label={messages["vidfleet_configuration_using_phone"]}
                                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                                checked={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.USING_CELLPHONE?.enable ? true : false}
                                                onChange={(checked) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.enable = checked;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.enable = true
                                                    if (checked) {
                                                        settingsData.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.duration = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.USING_CELLPHONE.default * 10;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.duration = true

                                                        settingsData.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.cnt = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.cnt = true
                                                    } else {
                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.duration;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.duration = false

                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.cnt;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.cnt = false
                                                    }
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                width={100}
                                            />
                                        </Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            {messages["vidfleet_configuration_duration"]}
                                            <Slider
                                                min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.USING_CELLPHONE.min}
                                                max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.USING_CELLPHONE.max}
                                                value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.USING_CELLPHONE.duration / 10 || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.USING_CELLPHONE.default / 10}
                                                onChange={(event) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.duration = event * 10;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.duration = true

                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                sliderOneColor
                                                showValue
                                                valueLabelDisplay="off"
                                                disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.USING_CELLPHONE?.enable}
                                                measure={messages["vidfleet_configuration_seconds"]}
                                            />
                                        </Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                <div style={{ width: "70%", alignItems: "left" }}>
                                                    {messages["vidfleet_configuration_count"]}
                                                </div>
                                                <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                                    {messages["vidfleet_configuration_count_msg"]}
                                                </div>
                                            </div>
                                            <div>
                                                <Slider
                                                    min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.min}
                                                    max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.max}
                                                    value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.USING_CELLPHONE.cnt || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default}
                                                    onChange={(event) => {

                                                        settingsData.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.cnt = event;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.USING_CELLPHONE.cnt = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    sliderOneColor
                                                    showValue
                                                    valueLabelDisplay="off"
                                                    disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.USING_CELLPHONE?.enable}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={bulk ? 12 : 6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2} style={{ marginTop: 15 }}>
                                            <ToggleSwitch
                                                id="safetyAssistance-alarm-DISTRACTED"
                                                blueToggle={true}
                                                mediumLarge={true}
                                                label={messages["vidfleet_configuration_distracted"]}
                                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                                checked={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.DISTRACTED?.enable ? true : false}
                                                onChange={(checked) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.DISTRACTED.enable = checked;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.DISTRACTED.enable = true
                                                    if (checked) {
                                                        settingsData.settings.advanced.safetyAssistance.alarm.DISTRACTED.duration = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.DISTRACTED.default * 10;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.DISTRACTED.duration = true

                                                        settingsData.settings.advanced.safetyAssistance.alarm.DISTRACTED.cnt = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.DISTRACTED.cnt = true
                                                    } else {
                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.DISTRACTED.duration;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.DISTRACTED.duration = false

                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.DISTRACTED.cnt;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.DISTRACTED.cnt = false
                                                    }
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                width={100}
                                            />
                                        </Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            {messages["vidfleet_configuration_duration"]}
                                            <Slider
                                                min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.DISTRACTED.min}
                                                max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.DISTRACTED.max}
                                                value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.DISTRACTED.duration / 10 || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.DISTRACTED.default / 10}
                                                onChange={(event) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.DISTRACTED.duration = event * 10;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.DISTRACTED.duration = true

                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                sliderOneColor
                                                showValue
                                                valueLabelDisplay="off"
                                                disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.DISTRACTED?.enable}
                                                measure={messages["vidfleet_configuration_seconds"]}
                                            />
                                        </Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                <div style={{ width: "70%", alignItems: "left" }}>
                                                    {messages["vidfleet_configuration_count"]}
                                                </div>
                                                <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                                    {messages["vidfleet_configuration_count_msg"]}
                                                </div>
                                            </div>
                                            <div>
                                                <Slider
                                                    min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.min}
                                                    max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.max}
                                                    value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.DISTRACTED.cnt || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default}
                                                    onChange={(event) => {

                                                        settingsData.settings.advanced.safetyAssistance.alarm.DISTRACTED.cnt = event;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.DISTRACTED.cnt = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    sliderOneColor
                                                    showValue
                                                    valueLabelDisplay="off"
                                                    disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.DISTRACTED?.enable}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={bulk ? 12 : 6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2} style={{ marginTop: 15 }}>
                                            <ToggleSwitch
                                                id="safetyAssistance-alarm-NO_DRIVER"
                                                blueToggle={true}
                                                mediumLarge={true}
                                                label={messages["vidfleet_configuration_no_driver"]}
                                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                                checked={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.NO_DRIVER?.enable ? true : false}
                                                onChange={(checked) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.NO_DRIVER.enable = checked;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_DRIVER.enable = true
                                                    if (checked) {
                                                        settingsData.settings.advanced.safetyAssistance.alarm.NO_DRIVER.duration = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.NO_DRIVER.default * 10;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_DRIVER.duration = true

                                                        settingsData.settings.advanced.safetyAssistance.alarm.NO_DRIVER.cnt = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_DRIVER.cnt = true
                                                    } else {
                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.NO_DRIVER.duration;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_DRIVER.duration = false

                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.NO_DRIVER.cnt;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_DRIVER.cnt = false
                                                    }
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                width={100}
                                            />
                                        </Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            {messages["vidfleet_configuration_duration"]}
                                            <Slider
                                                min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.NO_DRIVER.min}
                                                max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.NO_DRIVER.max}
                                                value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.NO_DRIVER.duration / 10 || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.NO_DRIVER.default / 10}
                                                onChange={(event) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.NO_DRIVER.duration = event * 10;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_DRIVER.duration = true

                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                sliderOneColor
                                                showValue
                                                valueLabelDisplay="off"
                                                disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.NO_DRIVER?.enable}
                                                measure={messages["vidfleet_configuration_seconds"]}
                                            />
                                        </Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                <div style={{ width: "70%", alignItems: "left" }}>
                                                    {messages["vidfleet_configuration_count"]}
                                                </div>
                                                <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                                    {messages["vidfleet_configuration_count_msg"]}
                                                </div>
                                            </div>
                                            <div>
                                                <Slider
                                                    min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.min}
                                                    max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.max}
                                                    value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.NO_DRIVER.cnt || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default}
                                                    onChange={(event) => {

                                                        settingsData.settings.advanced.safetyAssistance.alarm.NO_DRIVER.cnt = event;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.NO_DRIVER.cnt = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    sliderOneColor
                                                    showValue
                                                    valueLabelDisplay="off"
                                                    disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.NO_DRIVER?.enable}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={bulk ? 12 : 6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2} style={{ marginTop: 15 }}>
                                            <ToggleSwitch
                                                id="safetyAssistance-alarm-CLOSE_FOLLOWING"
                                                blueToggle={true}
                                                mediumLarge={true}
                                                label={messages["vidfleet_configuration_headway_monitoring_warning"]}
                                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                                checked={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.CLOSE_FOLLOWING?.enable ? true : false}
                                                onChange={(checked) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.enable = checked;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.enable = true
                                                    if (checked) {
                                                        settingsData.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.duration = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.default * 10;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.duration = true

                                                        settingsData.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.cnt = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.cnt = true
                                                    } else {
                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.duration;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.duration = false

                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.cnt;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.cnt = false
                                                    }
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                width={100}
                                            />
                                        </Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            {messages["vidfleet_configuration_duration"]}
                                            <Slider
                                                min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.min}
                                                max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.max}
                                                value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.CLOSE_FOLLOWING.duration / 10 || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.default / 10}
                                                onChange={(event) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.duration = event * 10;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.duration = true

                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                sliderOneColor
                                                showValue
                                                valueLabelDisplay="off"
                                                disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.CLOSE_FOLLOWING?.enable}
                                                measure={messages["vidfleet_configuration_seconds"]}
                                            />
                                        </Grid>

                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                <div style={{ width: "70%", alignItems: "left" }}>
                                                    {messages["vidfleet_configuration_count"]}
                                                </div>
                                                <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                                    {messages["vidfleet_configuration_count_msg"]}
                                                </div>
                                            </div>
                                            <div>
                                                <Slider
                                                    min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.min}
                                                    max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.max}
                                                    value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.CLOSE_FOLLOWING.cnt || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default}
                                                    onChange={(event) => {

                                                        settingsData.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.cnt = event;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.CLOSE_FOLLOWING.cnt = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    sliderOneColor
                                                    showValue
                                                    valueLabelDisplay="off"
                                                    disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.CLOSE_FOLLOWING?.enable}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={bulk ? 12 : 6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2} style={{ marginTop: 15 }}>
                                            <ToggleSwitch
                                                id="safetyAssistance-alarm-TAILGATING"
                                                blueToggle={true}
                                                mediumLarge={true}
                                                label={messages["vidfleet_configuration_headway_monitoring_emergency"]}
                                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                                checked={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.TAILGATING?.enable ? true : false}
                                                onChange={(checked) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.TAILGATING.enable = checked;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.TAILGATING.enable = true
                                                    if (checked) {
                                                        settingsData.settings.advanced.safetyAssistance.alarm.TAILGATING.duration = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.TAILGATING.default * 10;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.TAILGATING.duration = true

                                                        settingsData.settings.advanced.safetyAssistance.alarm.TAILGATING.cnt = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.TAILGATING.cnt = true
                                                    } else {
                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.TAILGATING.duration;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.TAILGATING.duration = false

                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.TAILGATING.cnt;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.TAILGATING.cnt = false
                                                    }
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                width={100}
                                            />
                                        </Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            {messages["vidfleet_configuration_duration"]}
                                            <Slider
                                                min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.TAILGATING.min}
                                                max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.TAILGATING.max}
                                                value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.TAILGATING.duration / 10 || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.TAILGATING.default / 10}
                                                onChange={(event) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.TAILGATING.duration = event * 10;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.TAILGATING.duration = true

                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                sliderOneColor
                                                showValue
                                                valueLabelDisplay="off"
                                                disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.TAILGATING?.enable}
                                                measure={messages["vidfleet_configuration_seconds"]}
                                            />
                                        </Grid>

                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                <div style={{ width: "70%", alignItems: "left" }}>
                                                    {messages["vidfleet_configuration_count"]}
                                                </div>
                                                <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                                    {messages["vidfleet_configuration_count_msg"]}
                                                </div>
                                            </div>
                                            <div>
                                                <Slider
                                                    min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.min}
                                                    max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.max}
                                                    value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.TAILGATING.cnt || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default}
                                                    onChange={(event) => {

                                                        settingsData.settings.advanced.safetyAssistance.alarm.TAILGATING.cnt = event;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.TAILGATING.cnt = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    sliderOneColor
                                                    showValue
                                                    valueLabelDisplay="off"
                                                    disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.TAILGATING?.enable}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={bulk ? 12 : 6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2} style={{ marginTop: 15 }}>
                                            <ToggleSwitch
                                                id="safetyAssistance-alarm-LANE_DEPARTURE"
                                                blueToggle={true}
                                                mediumLarge={true}
                                                label={messages["vidfleet_configuration_lane_departure"]}
                                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                                checked={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.LANE_DEPARTURE?.enable ? true : false}
                                                onChange={(checked) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.enable = checked;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.enable = true
                                                    if (checked) {
                                                        settingsData.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.duration = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.LANE_DEPARTURE.default * 10;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.duration = true

                                                        settingsData.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.cnt = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.cnt = true
                                                    } else {
                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.duration;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.duration = false

                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.cnt;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.cnt = false
                                                    }
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                width={100}
                                            />
                                        </Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            {messages["vidfleet_configuration_duration"]}
                                            <Slider
                                                min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.LANE_DEPARTURE.min}
                                                max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.LANE_DEPARTURE.max}
                                                value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.LANE_DEPARTURE.duration / 10 || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.LANE_DEPARTURE.default / 10}
                                                onChange={(event) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.duration = event * 10;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.duration = true

                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                sliderOneColor
                                                showValue
                                                valueLabelDisplay="off"
                                                disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.LANE_DEPARTURE?.enable}
                                                measure={messages["vidfleet_configuration_seconds"]}
                                            />
                                        </Grid>

                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                <div style={{ width: "70%", alignItems: "left" }}>
                                                    {messages["vidfleet_configuration_count"]}
                                                </div>
                                                <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                                    {messages["vidfleet_configuration_count_msg"]}
                                                </div>
                                            </div>
                                            <div>
                                                <Slider
                                                    min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.min}
                                                    max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.max}
                                                    value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.LANE_DEPARTURE.cnt || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default}
                                                    onChange={(event) => {

                                                        settingsData.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.cnt = event;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.LANE_DEPARTURE.cnt = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    sliderOneColor
                                                    showValue
                                                    valueLabelDisplay="off"
                                                    disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.LANE_DEPARTURE?.enable}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={bulk ? 12 : 6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2} style={{ marginTop: 15 }}>
                                            <ToggleSwitch
                                                id="safetyAssistance-alarm-SPEEDING"
                                                blueToggle={true}
                                                mediumLarge={true}
                                                label={messages["vidfleet_configuration_speeding"]}
                                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                                checked={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.SPEEDING?.enable ? true : false}
                                                onChange={(checked) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.SPEEDING.enable = checked;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.SPEEDING.enable = true
                                                    if (checked) {
                                                        settingsData.settings.advanced.safetyAssistance.alarm.SPEEDING.duration = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.SPEEDING.default * 10;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.SPEEDING.duration = true

                                                        settingsData.settings.advanced.safetyAssistance.alarm.SPEEDING.cnt = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.SPEEDING.cnt = true
                                                    } else {
                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.SPEEDING.duration;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.SPEEDING.duration = false

                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.SPEEDING.cnt;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.SPEEDING.cnt = false
                                                    }
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                width={100}
                                            />
                                        </Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            {messages["vidfleet_configuration_duration"]}
                                            <Slider
                                                min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.SPEEDING.min}
                                                max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.SPEEDING.max}
                                                value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.SPEEDING.duration / 10 || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.SPEEDING.default / 10}
                                                onChange={(event) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.SPEEDING.duration = event * 10;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.SPEEDING.duration = true

                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                sliderOneColor
                                                showValue
                                                valueLabelDisplay="off"
                                                disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.SPEEDING?.enable}
                                                measure={messages["vidfleet_configuration_seconds"]}
                                            />
                                        </Grid>

                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                <div style={{ width: "70%", alignItems: "left" }}>
                                                    {messages["vidfleet_configuration_count"]}
                                                </div>
                                                <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                                    {messages["vidfleet_configuration_count_msg"]}
                                                </div>
                                            </div>
                                            <div>
                                                <Slider
                                                    min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.min}
                                                    max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.max}
                                                    value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.SPEEDING.cnt || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default}
                                                    onChange={(event) => {

                                                        settingsData.settings.advanced.safetyAssistance.alarm.SPEEDING.cnt = event;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.SPEEDING.cnt = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    sliderOneColor
                                                    showValue
                                                    valueLabelDisplay="off"
                                                    disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.SPEEDING?.enable}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={bulk ? 12 : 6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2} style={{ marginTop: 15 }}>
                                            <ToggleSwitch
                                                id="safetyAssistance-alarm-OVER_MAX_SPEED"
                                                blueToggle={true}
                                                mediumLarge={true}
                                                label={messages["vidfleet_configuration_over_max_speed"]}
                                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                                checked={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.OVER_MAX_SPEED?.enable ? true : false}
                                                onChange={(checked) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.enable = checked;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.enable = true
                                                    if (checked) {
                                                        settingsData.settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.duration = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.default * 10;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.duration = true

                                                        settingsData.settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.cnt = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.cnt = true
                                                    } else {
                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.duration;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.duration = false

                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.cnt;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.cnt = false
                                                    }
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                width={100}
                                            />
                                        </Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            {messages["vidfleet_configuration_duration"]}
                                            <Slider
                                                min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.min}
                                                max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.max}
                                                value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.OVER_MAX_SPEED.duration / 10 || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.default / 10}
                                                onChange={(event) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.duration = event * 10;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.OVER_MAX_SPEED.duration = true

                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                sliderOneColor
                                                showValue
                                                valueLabelDisplay="off"
                                                disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.OVER_MAX_SPEED?.enable}
                                                measure={messages["vidfleet_configuration_seconds"]}
                                            />
                                        </Grid>

                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                <div style={{ width: "70%", alignItems: "left" }}>
                                                    {messages["vidfleet_configuration_count"]}
                                                </div>
                                                <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                                    {messages["vidfleet_configuration_count_msg"]}
                                                </div>
                                            </div>
                                            <div>
                                                <Slider
                                                    min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.min}
                                                    max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.max}
                                                    value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.PASSENGERS_NO_SEATBELT.cnt || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default}
                                                    onChange={(event) => {

                                                        settingsData.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.cnt = event;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.cnt = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    sliderOneColor
                                                    showValue
                                                    valueLabelDisplay="off"
                                                    disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.PASSENGERS_NO_SEATBELT?.enable}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={bulk ? 12 : 6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2} style={{ marginTop: 15 }}>
                                            <ToggleSwitch
                                                id="safetyAssistance-alarm-PEDESTRIAN"
                                                blueToggle={true}
                                                mediumLarge={true}
                                                label={messages["vidfleet_configuration_pedestrian"]}
                                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                                checked={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.PEDESTRIAN?.enable ? true : false}
                                                onChange={(checked) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.enable = checked;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.enable = true
                                                    if (checked) {
                                                        settingsData.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.duration = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.PEDESTRIAN.default * 10;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.duration = true

                                                        settingsData.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.cnt = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.cnt = true
                                                    } else {
                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.duration;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.duration = false

                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.cnt;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.cnt = false
                                                    }
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                width={100}
                                            />
                                        </Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            {messages["vidfleet_configuration_duration"]}
                                            <Slider
                                                min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.PEDESTRIAN.min}
                                                max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.PEDESTRIAN.max}
                                                value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.PEDESTRIAN.duration / 10 || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.PEDESTRIAN.default / 10}
                                                onChange={(event) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.duration = event * 10;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.duration = true

                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                sliderOneColor
                                                showValue
                                                valueLabelDisplay="off"
                                                disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.PEDESTRIAN?.enable}
                                                measure={messages["vidfleet_configuration_seconds"]}
                                            />
                                        </Grid>

                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                <div style={{ width: "70%", alignItems: "left" }}>
                                                    {messages["vidfleet_configuration_count"]}
                                                </div>
                                                <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                                    {messages["vidfleet_configuration_count_msg"]}
                                                </div>
                                            </div>
                                            <div>
                                                <Slider
                                                    min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.min}
                                                    max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.max}
                                                    value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.PEDESTRIAN.cnt || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default}
                                                    onChange={(event) => {

                                                        settingsData.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.cnt = event;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.PEDESTRIAN.cnt = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    sliderOneColor
                                                    showValue
                                                    valueLabelDisplay="off"
                                                    disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.PEDESTRIAN?.enable}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={bulk ? 12 : 6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2} style={{ marginTop: 15 }}>
                                            <ToggleSwitch
                                                id="safetyAssistance-alarm-CYCLIST"
                                                blueToggle={true}
                                                mediumLarge={true}
                                                label={messages["vidfleet_configuration_cyclist"]}
                                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                                checked={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.CYCLIST?.enable ? true : false}
                                                onChange={(checked) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.CYCLIST.enable = checked;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.CYCLIST.enable = true
                                                    if (checked) {
                                                        settingsData.settings.advanced.safetyAssistance.alarm.CYCLIST.duration = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.CYCLIST.default * 10;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.CYCLIST.duration = true

                                                        settingsData.settings.advanced.safetyAssistance.alarm.CYCLIST.cnt = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.CYCLIST.cnt = true
                                                    } else {
                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.CYCLIST.duration;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.CYCLIST.duration = false

                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.CYCLIST.cnt;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.CYCLIST.cnt = false
                                                    }
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                width={100}
                                            />
                                        </Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            {messages["vidfleet_configuration_duration"]}
                                            <Slider
                                                min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.CYCLIST.min}
                                                max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.CYCLIST.max}
                                                value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.CYCLIST.duration / 10 || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.CYCLIST.default / 10}
                                                onChange={(event) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.CYCLIST.duration = event * 10;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.CYCLIST.duration = true

                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                sliderOneColor
                                                showValue
                                                valueLabelDisplay="off"
                                                disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.CYCLIST?.enable}
                                                measure={messages["vidfleet_configuration_seconds"]}
                                            />
                                        </Grid>

                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                <div style={{ width: "70%", alignItems: "left" }}>
                                                    {messages["vidfleet_configuration_count"]}
                                                </div>
                                                <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                                    {messages["vidfleet_configuration_count_msg"]}
                                                </div>
                                            </div>
                                            <div>
                                                <Slider
                                                    min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.min}
                                                    max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.max}
                                                    value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.CYCLIST.cnt || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default}
                                                    onChange={(event) => {

                                                        settingsData.settings.advanced.safetyAssistance.alarm.CYCLIST.cnt = event;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.CYCLIST.cnt = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    sliderOneColor
                                                    showValue
                                                    valueLabelDisplay="off"
                                                    disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.CYCLIST?.enable}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={bulk ? 12 : 6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={2} style={{ marginTop: 15 }}>
                                            <ToggleSwitch
                                                id="safetyAssistance-alarm-PASSENGERS_NO_SEATBELT"
                                                blueToggle={true}
                                                mediumLarge={true}
                                                label={messages["vidfleet_configuration_passengers_no_seatbelt"]}
                                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                                checked={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.PASSENGERS_NO_SEATBELT?.enable ? true : false}
                                                onChange={(checked) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.enable = checked;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.enable = true
                                                    if (checked) {
                                                        settingsData.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.duration = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.default * 10;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.duration = true

                                                        settingsData.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.cnt = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.cnt = true
                                                    } else {
                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.duration;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.duration = false

                                                        delete settingsData.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.cnt;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.cnt = false
                                                    }
                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                width={100}
                                            />
                                        </Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            {messages["vidfleet_configuration_duration"]}
                                            <Slider
                                                min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.min}
                                                max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.max}
                                                value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.PASSENGERS_NO_SEATBELT.duration / 10 || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.default / 10}
                                                onChange={(event) => {

                                                    settingsData.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.duration = event * 10;
                                                    settingsDataChanged.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.duration = true

                                                    optionDefaults.advancedDefault = false;
                                                    setSettingsData({ ...settingsData })
                                                    saveChangedSettings({ ...settingsDataChanged })
                                                    setSaveSettingsAvailable(true);
                                                    setOptionDefaults({ ...optionDefaults })
                                                }}
                                                sliderOneColor
                                                showValue
                                                valueLabelDisplay="off"
                                                disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.PASSENGERS_NO_SEATBELT?.enable}
                                                measure={messages["vidfleet_configuration_seconds"]}
                                            />
                                        </Grid>

                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={2} style={{ marginTop: 15 }}></Grid>
                                        <Grid item xs={8} style={{ marginTop: 15 }}>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                <div style={{ width: "70%", alignItems: "left" }}>
                                                    {messages["vidfleet_configuration_count"]}
                                                </div>
                                                <div style={{ width: "30%", display: "flex", flexDirection: "row", justifyContent: "flex-end", fontSize: "10px", textAlign: "center" }}>
                                                    {messages["vidfleet_configuration_count_msg"]}
                                                </div>
                                            </div>
                                            <div>
                                                <Slider
                                                    min={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.min}
                                                    max={vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.max}
                                                    value={settingsData?.settings?.advanced?.safetyAssistance?.alarm?.PASSENGERS_NO_SEATBELT.cnt || vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default}
                                                    onChange={(event) => {

                                                        settingsData.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.cnt = event;
                                                        settingsDataChanged.settings.advanced.safetyAssistance.alarm.PASSENGERS_NO_SEATBELT.cnt = true

                                                        optionDefaults.advancedDefault = false;
                                                        setSettingsData({ ...settingsData })
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                        setSaveSettingsAvailable(true);
                                                        setOptionDefaults({ ...optionDefaults })
                                                    }}
                                                    sliderOneColor
                                                    showValue
                                                    valueLabelDisplay="off"
                                                    disabled={!settingsData?.settings?.advanced?.safetyAssistance?.alarm?.PASSENGERS_NO_SEATBELT?.enable}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <div style={{ marginTop: 20 }}>
                                    <Divider className='mb-3' />
                                </div>
                            </Grid>
                        </Grid>
                    }
                    {/* Navigation */}
                    {(!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name.includes("4K") || devices[0].deviceModel.name.includes("AIR"))) &&
                        <Grid item xs={12}>
                            {
                                (settingsData.settings.eventLevel.NAV.SPEEDING === "WARNING" || settingsData.settings.eventLevel.NAV.OVER_MAX_SPEED === "WARNING") &&
                                <Grid item xs={12}>
                                    <div style={{ marginTop: 20 }}>
                                        <h6>
                                            {messages["vidfleet_configuration_navigation"]}
                                        </h6>
                                    </div>
                                </Grid>
                            }
                            <Grid item xs={12}>
                                <Tabs
                                    tab={settingsData.settings.eventLevel.NAV.SPEEDING === "WARNING" && settingsData.settings.eventLevel.NAV.OVER_MAX_SPEED === "WARNING" ? 0 : settingsData.settings.eventLevel.NAV.OVER_MAX_SPEED === "WARNING" ? 1 : 0}
                                    onChange={(numberTab) => { setTabNav(numberTab) }}
                                >
                                    {
                                        settingsData.settings.eventLevel.NAV.SPEEDING === "WARNING" &&
                                        <TabContainer key={`menu-nav-${0}`} label={messages['vidfleet_configuration_speeding']} index={0}>
                                            <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                                <div >
                                                    <h6>
                                                        {messages["vidfleet_configuration_thresholds"]}
                                                    </h6>
                                                </div>
                                            </Grid>
                                            <Grid container>
                                                {
                                                    !HasPermission(user, "vfath", "u") &&
                                                    <Grid item xs={12}>
                                                        <Alert severity="warning" style={{ marginBottom: 10, marginTop: 5 }}>
                                                            {messages['vidfleet_configuration_permission_message']}
                                                        </Alert>
                                                    </Grid>
                                                }
                                                <Grid item xs={props.bulk ? 12 : 5}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_min_speed"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="min_speed-speeding"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.nav?.minSpeed?.SPEEDING === defaultSettingsData?.settings?.advanced?.nav?.minSpeed?.SPEEDING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.nav.minSpeed.SPEEDING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.nav?.minSpeed?.SPEEDING));
                                                                            settingsDataChanged.settings.advanced.nav.minSpeed.SPEEDING = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.nav.minSpeed.SPEEDING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("OVER_MAX_SPEED")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.nav.minSpeed.SPEEDING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.nav.minSpeed.SPEEDING.max}
                                                                value={settingsData?.settings?.advanced?.nav?.minSpeed?.SPEEDING || vidFleetConfigLimitDefault.advanced.nav.minSpeed.SPEEDING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.nav)
                                                                        settingsData.settings.advanced.nav = {}
                                                                    if (!settingsData.settings.advanced.nav.minSpeed)
                                                                        settingsData.settings.advanced.nav.minSpeed = {}

                                                                    settingsData.settings.advanced.nav.minSpeed.SPEEDING = event;
                                                                    settingsDataChanged.settings.advanced.nav.minSpeed.SPEEDING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_km_h"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_repeat_interval"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="repeat_interval-speeding"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.nav?.repeatInterval?.SPEEDING === defaultSettingsData?.settings?.advanced?.nav?.repeatInterval.SPEEDING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.nav.repeatInterval.SPEEDING = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.nav?.repeatInterval.SPEEDING));
                                                                            settingsDataChanged.settings.advanced.nav.repeatInterval.SPEEDING = true
                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.nav.repeatInterval.SPEEDING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                    disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.nav.repeatInterval.SPEEDING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.nav.repeatInterval.SPEEDING.max}
                                                                value={settingsData?.settings?.advanced?.nav?.repeatInterval?.SPEEDING || vidFleetConfigLimitDefault.advanced.nav.repeatInterval.SPEEDING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.nav)
                                                                        settingsData.settings.advanced.nav = {}
                                                                    if (!settingsData.settings.advanced.nav.repeatInterval)
                                                                        settingsData.settings.advanced.nav.repeatInterval = {}

                                                                    settingsData.settings.advanced.nav.repeatInterval.SPEEDING = event;
                                                                    settingsDataChanged.settings.advanced.nav.repeatInterval.SPEEDING = true
                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_delay"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_delay-speeding"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.nav?.detectionDelay?.SPEEDING === defaultSettingsData?.settings?.advanced?.nav?.detectionDelay?.SPEEDING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.nav.detectionDelay.SPEEDING = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.nav.detectionDelay.SPEEDING));
                                                                            settingsDataChanged.settings.advanced.nav.detectionDelay.SPEEDING = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.nav.detectionDelay.SPEEDING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("SPEEDING")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.nav.detectionDelay.SPEEDING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.nav.detectionDelay.SPEEDING.max}
                                                                value={settingsData?.settings?.advanced?.nav?.detectionDelay?.SPEEDING || vidFleetConfigLimitDefault.advanced.nav.detectionDelay.SPEEDING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.nav)
                                                                        settingsData.settings.advanced.nav = {}
                                                                    if (!settingsData.settings.advanced.nav.detectionDelay)
                                                                        settingsData.settings.advanced.nav.detectionDelay = {}

                                                                    settingsData.settings.advanced.nav.detectionDelay.SPEEDING = event;
                                                                    settingsDataChanged.settings.advanced.nav.detectionDelay.SPEEDING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20, marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_reset"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_reset-speeding"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.nav?.detectionReset?.SPEEDING === defaultSettingsData?.settings?.advanced?.nav?.detectionReset?.SPEEDING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.nav.detectionReset.SPEEDING = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.nav.detectionReset.SPEEDING));
                                                                            settingsDataChanged.settings.advanced.nav.detectionReset.SPEEDING = true
                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.nav.detectionReset.SPEEDING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("SPEEDING")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.nav.detectionReset.SPEEDING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.nav.detectionReset.SPEEDING.max}
                                                                value={settingsData?.settings?.advanced?.nav?.detectionReset?.SPEEDING || vidFleetConfigLimitDefault.advanced.nav.detectionReset.SPEEDING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.nav)
                                                                        settingsData.settings.advanced.nav = {}
                                                                    if (!settingsData.settings.advanced.nav.detectionReset)
                                                                        settingsData.settings.advanced.nav.detectionReset = {}

                                                                    settingsData.settings.advanced.nav.detectionReset.SPEEDING = event;
                                                                    settingsDataChanged.settings.advanced.nav.detectionReset.SPEEDING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_over_speed_limit"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="over_speed_limit-speeding"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.nav?.overspeedLimit?.SPEEDING === defaultSettingsData?.settings?.advanced?.nav?.overspeedLimit?.SPEEDING}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.nav.overspeedLimit.SPEEDING = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.nav.overspeedLimit.SPEEDING));
                                                                            settingsDataChanged.settings.advanced.nav.overspeedLimit.SPEEDING = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.nav.overspeedLimit.SPEEDING = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("SPEEDING")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.nav.overspeedLimit.SPEEDING.min}
                                                                max={vidFleetConfigLimitDefault.advanced.nav.overspeedLimit.SPEEDING.max}
                                                                value={settingsData?.settings?.advanced?.nav?.overspeedLimit?.SPEEDING || vidFleetConfigLimitDefault.advanced.nav.overspeedLimit.SPEEDING.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.nav)
                                                                        settingsData.settings.advanced.nav = {}
                                                                    if (!settingsData.settings.advanced.nav.overspeedLimit)
                                                                        settingsData.settings.advanced.nav.overspeedLimit = {}

                                                                    settingsData.settings.advanced.nav.overspeedLimit.SPEEDING = event;
                                                                    settingsDataChanged.settings.advanced.nav.overspeedLimit.SPEEDING = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_km_h"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </TabContainer>
                                    }
                                    {
                                        settingsData.settings.eventLevel.NAV.OVER_MAX_SPEED === "WARNING" &&
                                        <TabContainer key={`menu-nav-${1}`} label={messages['vidfleet_configuration_over_max_speeding']} index={1}>
                                            <Grid item xs={12} style={{ marginLeft: 2, marginBottom: 15, marginTop: 10 }}>
                                                <div >
                                                    <h6>
                                                        {messages["vidfleet_configuration_thresholds"]}
                                                    </h6>
                                                </div>
                                            </Grid>

                                            <Grid container>
                                                {
                                                    !HasPermission(user, "vfath", "u") &&
                                                    <Grid item xs={12}>
                                                        <Alert severity="warning" style={{ marginBottom: 10, marginTop: 5 }}>
                                                            {messages['vidfleet_configuration_permission_message']}
                                                        </Alert>
                                                    </Grid>
                                                }
                                                <Grid item xs={props.bulk ? 12 : 5}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_max_speed"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="max_speed-over-max-speed"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.nav?.maxSpeed?.OVER_MAX_SPEED === defaultSettingsData?.settings?.advanced?.nav?.maxSpeed?.OVER_MAX_SPEED}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.nav.maxSpeed.OVER_MAX_SPEED = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.nav?.maxSpeed?.OVER_MAX_SPEED));
                                                                            settingsDataChanged.settings.advanced.nav.maxSpeed.OVER_MAX_SPEED = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.nav.maxSpeed.OVER_MAX_SPEED = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("OVER_MAX_SPEED")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.nav.maxSpeed.OVER_MAX_SPEED.min}
                                                                max={vidFleetConfigLimitDefault.advanced.nav.maxSpeed.OVER_MAX_SPEED.max}
                                                                value={settingsData?.settings?.advanced?.nav?.maxSpeed?.OVER_MAX_SPEED || vidFleetConfigLimitDefault.advanced.nav.maxSpeed.OVER_MAX_SPEED.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.nav)
                                                                        settingsData.settings.advanced.nav = {}
                                                                    if (!settingsData.settings.advanced.nav.maxSpeed)
                                                                        settingsData.settings.advanced.nav.maxSpeed = {}

                                                                    settingsData.settings.advanced.nav.maxSpeed.OVER_MAX_SPEED = event;
                                                                    settingsDataChanged.settings.advanced.nav.maxSpeed.OVER_MAX_SPEED = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_km_h"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_min_speed"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="min_speed-over-max-speed"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.nav?.minSpeed?.OVER_MAX_SPEED === defaultSettingsData?.settings?.advanced?.nav?.minSpeed?.OVER_MAX_SPEED}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.nav.minSpeed.OVER_MAX_SPEED = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.nav?.minSpeed?.OVER_MAX_SPEED));
                                                                            settingsDataChanged.settings.advanced.nav.minSpeed.OVER_MAX_SPEED = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.nav.minSpeed.OVER_MAX_SPEED = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("OVER_MAX_SPEED")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.nav.minSpeed.OVER_MAX_SPEED.min}
                                                                max={vidFleetConfigLimitDefault.advanced.nav.minSpeed.OVER_MAX_SPEED.max}
                                                                value={settingsData?.settings?.advanced?.nav?.minSpeed?.OVER_MAX_SPEED || vidFleetConfigLimitDefault.advanced.nav.minSpeed.OVER_MAX_SPEED.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.nav)
                                                                        settingsData.settings.advanced.nav = {}
                                                                    if (!settingsData.settings.advanced.nav.minSpeed)
                                                                        settingsData.settings.advanced.nav.minSpeed = {}

                                                                    settingsData.settings.advanced.nav.minSpeed.OVER_MAX_SPEED = event;
                                                                    settingsDataChanged.settings.advanced.nav.minSpeed.OVER_MAX_SPEED = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_km_h"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_repeat_interval"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="repeat_interval-over-max-speed"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.nav?.repeatInterval?.OVER_MAX_SPEED === defaultSettingsData?.settings?.advanced?.nav?.repeatInterval.OVER_MAX_SPEED}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.nav.repeatInterval.OVER_MAX_SPEED = JSON.parse(JSON.stringify(defaultSettingsData?.settings?.advanced?.nav?.repeatInterval.OVER_MAX_SPEED));
                                                                            settingsDataChanged.settings.advanced.nav.repeatInterval.OVER_MAX_SPEED = true
                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.nav.repeatInterval.OVER_MAX_SPEED = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                    disabled={(!HasPermission(user, "chngrptintrvl", "u"))}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.nav.repeatInterval.OVER_MAX_SPEED.min}
                                                                max={vidFleetConfigLimitDefault.advanced.nav.repeatInterval.OVER_MAX_SPEED.max}
                                                                value={settingsData?.settings?.advanced?.nav?.repeatInterval?.OVER_MAX_SPEED || vidFleetConfigLimitDefault.advanced.nav.repeatInterval.OVER_MAX_SPEED.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.nav)
                                                                        settingsData.settings.advanced.nav = {}
                                                                    if (!settingsData.settings.advanced.nav.repeatInterval)
                                                                        settingsData.settings.advanced.nav.repeatInterval = {}

                                                                    settingsData.settings.advanced.nav.repeatInterval.OVER_MAX_SPEED = event;
                                                                    settingsDataChanged.settings.advanced.nav.repeatInterval.OVER_MAX_SPEED = true
                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u") || (!HasPermission(user, "chngrptintrvl", "u"))}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20, marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_delay"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_delay-over-max-speed"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.nav?.detectionDelay?.OVER_MAX_SPEED === defaultSettingsData?.settings?.advanced?.nav?.detectionDelay?.OVER_MAX_SPEED}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.nav.detectionDelay.OVER_MAX_SPEED = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.nav.detectionDelay.OVER_MAX_SPEED));
                                                                            settingsDataChanged.settings.advanced.nav.detectionDelay.OVER_MAX_SPEED = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.nav.detectionDelay.OVER_MAX_SPEED = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("OVER_MAX_SPEED")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.nav.detectionDelay.OVER_MAX_SPEED.min}
                                                                max={vidFleetConfigLimitDefault.advanced.nav.detectionDelay.OVER_MAX_SPEED.max}
                                                                value={settingsData?.settings?.advanced?.nav?.detectionDelay?.OVER_MAX_SPEED || vidFleetConfigLimitDefault.advanced.nav.detectionDelay.OVER_MAX_SPEED.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.nav)
                                                                        settingsData.settings.advanced.nav = {}
                                                                    if (!settingsData.settings.advanced.nav.detectionDelay)
                                                                        settingsData.settings.advanced.nav.detectionDelay = {}

                                                                    settingsData.settings.advanced.nav.detectionDelay.OVER_MAX_SPEED = event;
                                                                    settingsDataChanged.settings.advanced.nav.detectionDelay.OVER_MAX_SPEED = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_detection_reset"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="detection_reset-over-max-speed"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.nav?.detectionReset?.OVER_MAX_SPEED === defaultSettingsData?.settings?.advanced?.nav?.detectionReset?.OVER_MAX_SPEED}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.nav.detectionReset.OVER_MAX_SPEED = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.nav.detectionReset.OVER_MAX_SPEED));
                                                                            settingsDataChanged.settings.advanced.nav.detectionReset.OVER_MAX_SPEED = true
                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.nav.detectionReset.OVER_MAX_SPEED = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("OVER_MAX_SPEED")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.nav.detectionReset.OVER_MAX_SPEED.min}
                                                                max={vidFleetConfigLimitDefault.advanced.nav.detectionReset.OVER_MAX_SPEED.max}
                                                                value={settingsData?.settings?.advanced?.nav?.detectionReset?.OVER_MAX_SPEED || vidFleetConfigLimitDefault.advanced.nav.detectionReset.OVER_MAX_SPEED.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.nav)
                                                                        settingsData.settings.advanced.nav = {}
                                                                    if (!settingsData.settings.advanced.nav.detectionReset)
                                                                        settingsData.settings.advanced.nav.detectionReset = {}

                                                                    settingsData.settings.advanced.nav.detectionReset.OVER_MAX_SPEED = event;
                                                                    settingsDataChanged.settings.advanced.nav.detectionReset.OVER_MAX_SPEED = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_10_s"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={props.bulk ? 12 : 5} style={{ marginLeft: !props.bulk && 20, marginTop: 10 }}>
                                                    <Grid item xs="auto">
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                                            <div style={{ width: "50%", alignItems: "left" }}>
                                                                {messages["vidfleet_configuration_over_speed_limit"]}
                                                            </div>
                                                            <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                                                <ToggleSwitch
                                                                    id="over_speed_limit-over-max-speed"
                                                                    darkBlueToggle={true}
                                                                    extraLarge={true}
                                                                    optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                                    checked={settingsData?.settings?.advanced?.nav?.overspeedLimit?.OVER_MAX_SPEED === defaultSettingsData?.settings?.advanced?.nav?.overspeedLimit?.OVER_MAX_SPEED}
                                                                    onChange={(checked) => {
                                                                        if (checked) {

                                                                            settingsData.settings.advanced.nav.overspeedLimit.OVER_MAX_SPEED = JSON.parse(JSON.stringify(defaultSettingsData.settings.advanced.nav.overspeedLimit.OVER_MAX_SPEED));
                                                                            settingsDataChanged.settings.advanced.nav.overspeedLimit.OVER_MAX_SPEED = true

                                                                            optionDefaults.advancedDefault = false;
                                                                            setSettingsData({ ...settingsData })
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                            setSaveSettingsAvailable(true);
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                        } else {
                                                                            optionDefaults.advancedDefault = false;
                                                                            setOptionDefaults({ ...optionDefaults })
                                                                            settingsDataChanged.settings.advanced.nav.overspeedLimit.OVER_MAX_SPEED = false
                                                                            saveChangedSettings({ ...settingsDataChanged })
                                                                        }
                                                                    }}
                                                                //disabled={!checkDMSSettingEnabled("OVER_MAX_SPEED")}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: "15px" }}>
                                                            <Slider
                                                                min={vidFleetConfigLimitDefault.advanced.nav.overspeedLimit.OVER_MAX_SPEED.min}
                                                                max={vidFleetConfigLimitDefault.advanced.nav.overspeedLimit.OVER_MAX_SPEED.max}
                                                                value={settingsData?.settings?.advanced?.nav?.overspeedLimit?.OVER_MAX_SPEED || vidFleetConfigLimitDefault.advanced.nav.overspeedLimit.OVER_MAX_SPEED.default}
                                                                onChange={(event) => {

                                                                    if (!settingsData.settings.advanced.nav)
                                                                        settingsData.settings.advanced.nav = {}
                                                                    if (!settingsData.settings.advanced.nav.overspeedLimit)
                                                                        settingsData.settings.advanced.nav.overspeedLimit = {}

                                                                    settingsData.settings.advanced.nav.overspeedLimit.OVER_MAX_SPEED = event;
                                                                    settingsDataChanged.settings.advanced.nav.overspeedLimit.OVER_MAX_SPEED = true

                                                                    optionDefaults.advancedDefault = false;
                                                                    setSettingsData({ ...settingsData })
                                                                    saveChangedSettings({ ...settingsDataChanged })
                                                                    setSaveSettingsAvailable(true);
                                                                    setOptionDefaults({ ...optionDefaults })
                                                                }}
                                                                sliderOneColor
                                                                showValue
                                                                valueLabelDisplay="off"
                                                                measure={messages["vidfleet_configuration_km_h"]}
                                                                disabled={!HasPermission(user, "vfath", "u")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </TabContainer>
                                    }

                                </Tabs>
                            </Grid>
                        </Grid>
                    }
                    {/* Navigation */}

                    <Grid item xs={12} style={{ marginTop: 10 }}>
                        <div >
                            <h6>
                                {messages["vidfleet_configuration_ignition"]}
                            </h6>
                        </div>
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: 10 }}>
                        <ToggleSwitch
                            id="ignitionVirtualMode"
                            blueToggle={true}
                            large={true}
                            label={messages["vidfleet_configuration_virtual_mode"]}
                            optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                            checked={settingsData.settings.advanced.ignition.virtualMode}
                            onChange={(checked) => {
                                settingsData.settings.advanced.ignition.virtualMode = checked;
                                settingsDataChanged.settings.advanced.ignition.virtualMode = true

                                if (checked && (!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name.includes("AIR")))) {
                                    settingsData.settings.advanced.ignition.autoMode = false;
                                    settingsDataChanged.settings.advanced.ignition.autoMode = true
                                }

                                optionDefaults.advancedDefault = false;
                                setSettingsData({ ...settingsData })
                                saveChangedSettings({ ...settingsDataChanged })
                                setSaveSettingsAvailable(true);
                                setOptionDefaults({ ...optionDefaults })
                            }}
                            width={100}
                        />
                    </Grid>
                    {
                        (!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name.includes("AIR"))) &&
                        <Grid item xs={12} style={{ marginTop: 10 }}>
                            <ToggleSwitch
                                id="ignitionAutoMode"
                                blueToggle={true}
                                large={true}
                                label={messages["vidfleet_configuration_auto_mode"]}
                                optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                checked={settingsData.settings.advanced.ignition.autoMode}
                                onChange={(checked) => {
                                    settingsData.settings.advanced.ignition.autoMode = checked;
                                    settingsDataChanged.settings.advanced.ignition.autoMode = true

                                    if (checked) {
                                        settingsData.settings.advanced.ignition.virtualMode = false;
                                        settingsDataChanged.settings.advanced.ignition.virtualMode = true
                                    }

                                    optionDefaults.advancedDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                width={100}
                            />
                        </Grid>
                    }
                    {
                        HasPermission(user, "vftcl", "u") &&
                        <Grid item xs={12} style={{ marginTop: 10 }}>
                            <div>
                                {messages["vidfleet_configuration_lang"]}
                            </div>
                        </Grid>
                    }
                    {
                        HasPermission(user, "vftcl", "u") &&
                        <Grid container>
                            <Grid item xs={12} style={{ marginLeft: 0 }}>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="lang-english"
                                    onClick={e => {
                                        settingsData.settings.advanced.lang = "English"
                                        settingsDataChanged.settings.advanced.lang = true

                                        optionDefaults.advancedDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_english"], settingsData.settings.advanced.lang === "English")
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1 vidfleet-pills"
                                    key="lang-spanish"
                                    onClick={e => {
                                        settingsData.settings.advanced.lang = "Spanish"
                                        settingsDataChanged.settings.advanced.lang = true

                                        optionDefaults.advancedDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_spanish"], settingsData.settings.advanced.lang === "Spanish")
                                    }
                                </div>
                            </Grid>
                        </Grid>
                    }
                    <Grid item xs={12} style={{ marginTop: 10 }}>
                        <div >
                            <h6>
                                {messages['vidfleet_configuration_subtitle']}
                            </h6>
                        </div>
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: 10 }}>
                        <div>
                            {messages['vidfleet_configuration_speed']}
                        </div>
                    </Grid>
                    <Grid container>
                        <Grid item xs={12} style={{ marginLeft: 0 }}>
                            <div
                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                key="speed-disabled"
                                onClick={e => {
                                    settingsData.settings.advanced.subtitle.speed = "disable"
                                    settingsDataChanged.settings.advanced.subtitle.speed = true
                                    optionDefaults.advancedDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                            >
                                {
                                    props.getBadget(messages['vidfleet_configuration_disable'], settingsData.settings.advanced.subtitle.speed === "disable")
                                }
                            </div>
                            <div
                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                key="speed-kph"
                                onClick={e => {
                                    settingsData.settings.advanced.subtitle.speed = "kph"
                                    settingsDataChanged.settings.advanced.subtitle.speed = true

                                    optionDefaults.advancedDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                            >
                                {
                                    props.getBadget("Kph", settingsData.settings.advanced.subtitle.speed === "kph")
                                }
                            </div>
                            <div
                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                key="speed-mph"
                                onClick={e => {
                                    settingsData.settings.advanced.subtitle.speed = "mph"
                                    settingsDataChanged.settings.advanced.subtitle.speed = true

                                    optionDefaults.advancedDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                            >
                                {
                                    props.getBadget("Mph", settingsData.settings.advanced.subtitle.speed === "mph")
                                }
                            </div>
                        </Grid>
                    </Grid>
                    {/* Privacy Control */}
                    <Grid item xs={12} style={{ marginTop: 20 }}>
                        <div>
                            {messages["vidfleet_configuration_privacy_control"]}
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        {messages["vidfleet_configuration_level"]}
                        <div style={{ marginLeft: 0 }}>
                            <div className="d-sm-inline-block mb-1 vidfleet-pills" key="level-advanced-privacyControl-none">
                                <div
                                    onClick={e => {
                                        if (isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, privacyControl: "NONE" })) {
                                            NotificationManager.warning(
                                                <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                messages["vidfleet_configuration_combination_not_allowed"],
                                                6000,
                                                null,
                                                null,
                                                'error'
                                            );
                                        }
                                        settingsData.settings.advanced.privacyControl.level = "NONE";
                                        settingsDataChanged.settings.advanced.privacyControl.level = true
                                        optionDefaults.advancedDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    style={{ cursor: isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, privacyControl: "NONE" }) ? 'not-allowed' : 'pointer' }}
                                >
                                    {
                                        props.getBadget(messages['vidfleet_configuration_none'], settingsData.settings.advanced.privacyControl.level === "NONE")
                                    }
                                </div>
                            </div>
                            <div className="d-sm-inline-block mb-1 vidfleet-pills" key="level-advanced-privacyControl-incabin">
                                <div
                                    onClick={e => {
                                        if (isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, privacyControl: "INCABIN" })) {
                                            NotificationManager.warning(
                                                <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                messages["vidfleet_configuration_combination_not_allowed"],
                                                6000,
                                                null,
                                                null,
                                                'error'
                                            );
                                        }
                                        settingsData.settings.advanced.privacyControl.level = "INCABIN";
                                        settingsDataChanged.settings.advanced.privacyControl.level = true

                                        optionDefaults.advancedDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    style={{ cursor: isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, privacyControl: "INCABIN" }) ? 'not-allowed' : 'pointer' }}
                                >
                                    {
                                        props.getBadget(messages['vidfleet_configuration_in_cabin'], settingsData.settings.advanced.privacyControl.level === "INCABIN")
                                    }
                                </div>
                            </div>
                            <div className="d-sm-inline-block mb-1 vidfleet-pills" key="level-advanced-privacyControl-passenger">
                                <div
                                    onClick={e => {
                                        if (isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, privacyControl: "PASSENGER" })) {
                                            NotificationManager.warning(
                                                <a style={{ textDecoration: 'underline' }} onClick={() => dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings))}>{messages['vidfleet_configuration_view_restrictions']}</a>,
                                                messages["vidfleet_configuration_combination_not_allowed"],
                                                6000,
                                                null,
                                                null,
                                                'error'
                                            );
                                        }
                                        settingsData.settings.advanced.privacyControl.level = "PASSENGER";
                                        settingsDataChanged.settings.advanced.privacyControl.level = true

                                        optionDefaults.advancedDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    style={{ cursor: isButtonDisabled({ ...curretStates, adasLevel: buttonStates?.advanced?.adas?.level, privacyControl: "PASSENGER" }) ? 'not-allowed' : 'pointer' }}
                                >
                                    {
                                        props.getBadget(messages['vidfleet_configuration_passenger'], settingsData.settings.advanced.privacyControl.level === "PASSENGER")
                                    }
                                </div>
                            </div>
                            <div className="d-sm-inline-block mb-1">
                                <Tooltip title={messages["vidfleet_configuration_message_available_combinations"]} tooltipplacement="left">
                                    <a className="btn-link align-middle" onClick={() => { dispatch(props.showRestrictionsModal(restrictions?.restrictedPropertiesList || [], settingsData.settings)) }}>
                                        <Icon style={{ marginTop: 5 }}>info</Icon>
                                    </a>
                                </Tooltip>
                            </div>
                        </div>
                    </Grid>
                    {/* Privacy Control */}
                    {/* Live Photo */}
                    <Grid container>
                        <Grid item xs={12}>
                            <div style={{ marginTop: 15 }}>
                                {messages["vidfleet_configuration_live_photo"]}
                            </div>
                        </Grid>
                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10 }}>
                            {messages["vidfleet_configuration_duration"]}
                            <Slider
                                min={60}
                                max={120}
                                value={settingsData?.settings?.advanced?.livePhoto?.duration || 120}
                                onChange={(event) => {

                                    if (settingsData?.settings?.advanced?.livePhoto?.duration) {
                                        settingsData.settings.advanced.livePhoto.duration = event;
                                    } else if (settingsData?.settings?.advanced?.livePhoto) {
                                        settingsData.settings.advanced.livePhoto.duration = event;
                                    } else {
                                        let livePhoto = {
                                            duration: event
                                        }
                                        settingsData.settings.advanced.livePhoto = livePhoto
                                    }
                                    settingsDataChanged.settings.advanced.livePhoto = true

                                    optionDefaults.advancedDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                measure={messages["vidfleet_configuration_seconds"]}
                                sliderStep={1}
                            />
                        </Grid>
                        <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 10, marginLeft: !props.bulk && 20 }}>
                            {messages["vidfleet_configuration_interval"]}
                            <Slider
                                min={3}
                                max={5}
                                value={settingsData?.settings?.advanced?.livePhoto?.interval || 5}
                                onChange={(event) => {

                                    if (settingsData?.settings?.advanced?.livePhoto?.interval) {
                                        settingsData.settings.advanced.livePhoto.interval = event;
                                    } else if (settingsData?.settings?.advanced?.livePhoto) {
                                        settingsData.settings.advanced.livePhoto.interval = event;
                                    } else {
                                        let livePhoto = {
                                            interval: event
                                        }
                                        settingsData.settings.advanced.livePhoto = livePhoto
                                    }
                                    settingsDataChanged.settings.advanced.livePhoto = true

                                    optionDefaults.advancedDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                                sliderOneColor
                                showValue
                                valueLabelDisplay="off"
                                sliderStep={1}
                            />
                        </Grid>
                    </Grid>
                    {/* Live Photo */}
                    {/* Parking Heartbeat */}
                    <Grid item xs={props.bulk ? 12 : 5} style={{ marginTop: 30 }}>
                        <Grid item xs="auto">
                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                <div style={{ width: "50%", alignItems: "left" }}>
                                    {messages["vidfleet_configuration_advanced_parking_heart_beat"]}
                                </div>
                                <div style={{ width: "50%", display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                                    <ToggleSwitch
                                        id="parkingHeartbeat"
                                        blueToggle={true}
                                        large={true}
                                        optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                                        checked={settingsData.settings.advanced.parkingHeartbeat.enable ? true : false}
                                        onChange={(checked) => {

                                            settingsData.settings.advanced.parkingHeartbeat.enable = checked;
                                            settingsDataChanged.settings.advanced.parkingHeartbeat.enable = true

                                            optionDefaults.advancedDefault = false;
                                            setSettingsData({ ...settingsData })
                                            saveChangedSettings({ ...settingsDataChanged })
                                            setSaveSettingsAvailable(true);
                                            setOptionDefaults({ ...optionDefaults })
                                        }}
                                        width={100}
                                    />
                                </div>
                            </div>
                            <div style={{ marginTop: "15px" }}>
                                {messages["vidfleet_configuration_interval_in_min"]}
                                <Slider
                                    min={31}
                                    max={1440}
                                    value={settingsData.settings.advanced.parkingHeartbeat.intervalInMin || 30}
                                    onChange={(event) => {

                                        settingsData.settings.advanced.parkingHeartbeat.interval = event;
                                        settingsDataChanged.settings.advanced.parkingHeartbeat.interval = true

                                        optionDefaults.advancedDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                    sliderOneColor
                                    showValue
                                    valueLabelDisplay="off"
                                    disabled={!settingsData.settings.advanced.parkingHeartbeat.enable}
                                    measure={messages["vidfleet_configuration_minutes"]}
                                />
                            </div>
                        </Grid >
                    </Grid>
                    {/* Parking Heartbeat */}
                    <Grid item xs={12} style={{ marginTop: 10 }}>
                        <div>
                            {messages['vidfleet_configuration_timezone']}
                        </div>
                    </Grid>
                    <Grid container>
                        <Grid item xs={12} style={{ marginLeft: 0 }}>
                            <div
                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                key="timezone-GMTZ"
                                onClick={e => {
                                    settingsData.settings.advanced.subtitle.timezone = "GMT%z"
                                    settingsDataChanged.settings.advanced.subtitle.timezone = true
                                    optionDefaults.advancedDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                            >
                                {
                                    props.getBadget(messages['vidfleet_configuration_subtitle_timezone_gtmz'], settingsData.settings.advanced.subtitle.timezone === "GMT%z")
                                }
                            </div>
                            <div
                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                key="timezone-name"
                                onClick={e => {
                                    settingsData.settings.advanced.subtitle.timezone = "name"
                                    settingsDataChanged.settings.advanced.subtitle.timezone = true

                                    optionDefaults.advancedDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                            >
                                {
                                    props.getBadget(messages['vidfleet_configuration_subtitle_timezone_name'], settingsData.settings.advanced.subtitle.timezone === "name")
                                }
                            </div>

                        </Grid>
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: 10 }}>
                        <div>
                            {messages['vidfleet_configuration_gps']}
                        </div>
                    </Grid>
                    <Grid container>
                        <Grid item xs={12} style={{ marginLeft: 0 }}>
                            <div
                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                key="gps-disabled"
                                onClick={e => {
                                    settingsData.settings.advanced.subtitle.gps = "disable"
                                    settingsDataChanged.settings.advanced.subtitle.gps = true
                                    optionDefaults.advancedDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                            >
                                {
                                    props.getBadget(messages['vidfleet_configuration_disable'], settingsData.settings.advanced.subtitle.gps === "disable")
                                }
                            </div>
                            <div
                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                key="gps-default"
                                onClick={e => {
                                    settingsData.settings.advanced.subtitle.gps = "default"
                                    settingsDataChanged.settings.advanced.subtitle.gps = true

                                    optionDefaults.advancedDefault = false;
                                    setSettingsData({ ...settingsData })
                                    saveChangedSettings({ ...settingsDataChanged })
                                    setSaveSettingsAvailable(true);
                                    setOptionDefaults({ ...optionDefaults })
                                }}
                            >
                                {
                                    props.getBadget(messages['vidfleet_configuration_default'], settingsData.settings.advanced.subtitle.gps === "default")
                                }
                            </div>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: 10 }}>
                        <div >
                            <h6>
                                {messages['vidfleet_configuration_driver_id']}
                            </h6>
                        </div>
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: 10, display: 'flex', alignItems: 'center' }}>
                        <ToggleSwitch
                            id="driverId"
                            blueToggle={true}
                            large={true}
                            label={messages["vidfleet_configuration_upload_face"]}
                            optionLabels={[messages["common_enabled"], messages["common_disabled"]]}
                            checked={settingsData.settings.advanced?.driverID?.uploadFace}
                            onChange={(checked) => {
                                if (!settingsData.settings.advanced.driverID) {
                                    settingsData.settings.advanced.driverID = {}
                                }
                                if (!settingsDataChanged.settings.advanced.driverID) {
                                    settingsDataChanged.settings.advanced.driverID = {}
                                }
                                settingsData.settings.advanced.driverID.uploadFace = checked;
                                settingsDataChanged.settings.advanced.driverID.uploadFace = true

                                optionDefaults.advancedDefault = false;
                                setSettingsData({ ...settingsData })
                                saveChangedSettings({ ...settingsDataChanged })
                                setSaveSettingsAvailable(true);
                                setOptionDefaults({ ...optionDefaults })
                            }}
                            width={100}
                        />
                        <Tooltip title={messages['vidfleet_configuration_upload_face_helper']} tooltipplacement="left">
                            <Icon style={{ marginTop: 18, cursor: 'pointer', color: '#57595b' }}>info</Icon>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    const timezone = () => {
        return (
            !optionCollapsed.colapsedTimezone &&
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <div style={{ marginTop: 20 }}>
                        <Divider className='mb-3' />
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    {messages["vidfleet_configuration_timezone_by"]}
                    <div style={{ marginLeft: 0 }}>
                        <div
                            className="d-sm-inline-block mb-1 vidfleet-pills"
                            key="time-zone-offset"
                            onClick={e => setTimezoneByLabel(false)}
                        >
                            {
                                props.getBadget(messages["vidfleet_configuration_timezone_offset"], !timezoneByLabel)
                            }
                        </div>
                        <div
                            className="d-sm-inline-block mb-1 vidfleet-pills"
                            key="time-zone-label"
                            onClick={e => setTimezoneByLabel(true)}
                        >
                            {
                                props.getBadget(messages["vidfleet_configuration_timezone_label"], timezoneByLabel)
                            }
                        </div>
                    </div>
                </Grid>
                <Grid item xs={12} style={{ marginLeft: 44 }}>
                    <Grid item xs={12} style={{ marginTop: 10, marginBottom: 30 }}>
                        <Grid item xs={props.bulk ? 6 : 3}>
                            {
                                !timezoneByLabel &&
                                <Select
                                    id="selectTimezone"
                                    name="selectTimezone"
                                    placeholder={messages["vidfleet_configuration_timezone_offset"]}
                                    value={props.getTimezoneOffsetList().find(x => settingsData?.settings?.timezone === x.id)}
                                    options={props.getTimezoneOffsetList()}
                                    textField="label"
                                    valueField="id"
                                    menuPlacement="auto"
                                    onChange={(value) => {
                                        settingsData.settings.timezone = value.id;
                                        settingsDataChanged.settings.timezone = true;
                                        if (settingsData.settings.advanced) {
                                            if (settingsData.settings.advanced.subtitle) {
                                                settingsData.settings.advanced.subtitle.timezone = "GMT%z"
                                            } else {
                                                settingsData.settings.advanced.subtitle = { timezone: "GMT%z" }
                                            }
                                        } else {
                                            settingsData.settings.advanced = { subtitle: { timezone: "GMT%z" } }
                                        }
                                        settingsDataChanged.settings.advanced.subtitle.timezone = true

                                        optionDefaults.timezoneDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                />
                            }
                            {
                                timezoneByLabel &&
                                <Select
                                    id="selectTimezone"
                                    name="selectTimezone"
                                    placeholder={messages["vidfleet_configuration_timezone_label"]}
                                    value={props.timezonesWaylens.find(x => settingsData?.settings?.timezone === x.id)}
                                    options={props.timezonesWaylens}
                                    textField="label"
                                    valueField="id"
                                    menuPlacement="auto"
                                    onChange={(value) => {
                                        settingsData.settings.timezone = value.id;
                                        settingsDataChanged.settings.timezone = true;

                                        if (settingsData.settings.advanced) {
                                            if (settingsData.settings.advanced.subtitle) {
                                                settingsData.settings.advanced.subtitle.timezone = "name"
                                            } else {
                                                settingsData.settings.advanced.subtitle = { timezone: "name" }
                                            }
                                        } else {
                                            settingsData.settings.advanced = { subtitle: { timezone: "name" } }
                                        }
                                        settingsDataChanged.settings.advanced.subtitle.timezone = true

                                        optionDefaults.timezoneDefault = false;
                                        setSettingsData({ ...settingsData })
                                        saveChangedSettings({ ...settingsDataChanged })
                                        setSaveSettingsAvailable(true);
                                        setOptionDefaults({ ...optionDefaults })
                                    }}
                                />
                            }
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }
    // modules end
    const renderModule = (modulekey) => {

        switch (modulekey) {
            case "eventThreshold":
                return eventLevelThreshold();
                break;
            case "sounds":
                return sounds();
                break;
            case "dms":
                return dmsEvents();
                break;
            case "videoStreams":
                return videoStreams();
                break;
            case "recordConfig":
                return recordConfig();
                break;
            case "delayedPowerOff":
                return delayedPowerOff();
                break;
            case "nightVision":
                return nightVision();
                break;
            case "liveStreamDuration":
                return liveStreamDuration();
                break;
            case "trustAcc":
                return trustAcc();
                break;
            case "autoSnapshot":
                return autoSnapshot();
                break;
            case "mic":
                return mic();
                break;
            case "privacyMask":
                return privacyMask();
                break;
            case "rotate":
                return rotate();
                break;
            case "volume":
                return volume();
                break;
            case "markedClipDuration":
                return markedClipDuration();
                break;
            case "obdII":
                return obdII();
                break;
            case "runningInfo":
                return runningInfo();
                break;
            case "advanced":
                return advanced();
                break;
            case "timezone":
                return timezone();
                break;
            case "cev":
                return cev();
                break;
            default:
                break;
        }
    }

    /**
     * method used to change all the values for an object sent by parameter
     * @param {*} obj 
     * @returns 
     */
    const replaceObjectvalues = (obj, value = false) => {
        let keysROV = Object.keys(obj)
        keysROV.forEach(key => {
            //Is an Array and is not empty
            if (Array.isArray(obj[key])) {
                obj[key] = obj[key].map(item => {
                    item = value;
                    return item
                });
            } else if (typeof obj[key] === 'object') {
                replaceObjectvalues(obj[key], value);
            } else {
                obj[key] = value;
            }
        });

        return obj;
    }


    const checkPropertyChange = (obj, showLog = false) => {
        let changed = false;
        if (showLog)
            console.log("obj", obj)

        if (!obj)
            return false

        let keys = Object.keys(obj)

        for (const key of keys) {
            //Is an Array and is not empty
            if (Array.isArray(obj[key])) {
                for (const item of obj[key]) {

                    if (item) {
                        changed = true
                        break
                    }
                }
                if (changed) break
            } else if (typeof obj[key] === 'object') {
                changed = checkPropertyChange(obj[key], showLog);
                if (changed) break
            } else if (obj[key]) {
                changed = true
                break
            }
        };
        return changed;
    }

    //check if the sim provider is AT&T(for the moment)
    const isSimProviderAllowed = () => {
        let allowed = false
        if (!bulk && devices?.length === 0 && props.from === "manufacturer")
            allowed = true;
        else if (devices?.length > 0 && devices[0]?.simCard?.simProviderAccount?.simProvider?.name === "AT&T") {
            allowed = true;
        }

        return allowed
    }

    const validateAttentiveConfig = (settings) => {

        if (!settings?.eventLevel?.DMS?.ATTENTIVE || settings?.eventLevel?.DMS?.ATTENTIVE === "OFF") {
            settings?.dms?.minSpeed && delete settings.dms.minSpeed.ATTENTIVE;
            settings?.dms?.detectionDelay && delete settings.dms.detectionDelay.ATTENTIVE;
            settings?.dms?.repeatInterval && delete settings.dms.repeatInterval.ATTENTIVE;
            settings?.dms?.detectionReset && delete settings.dms.detectionReset.ATTENTIVE;
        } else {
            if (settings?.dms?.detectionDelay?.ATTENTIVE === 0)
                settings.dms.detectionDelay.ATTENTIVE = 5;
        }

        return settings;
    }

    useEffect(() => {
        if (settingsToClone) {
            dispatch(props.clearRedux("VIDFLEET_CONFIGURATION", { settingsToClone: null }));

            if (!settingsToClone.error) {
                restoreToDefault();
                                    
                // Merge cloned settings with defaults to ensure complete structure
                const mergedClonedSettings = {
                    ...settingsToClone,
                    settings: mergeSettings(defaultSettingsData?.settings || {}, settingsToClone?.settings || {})
                };
                enableCustomOptionsOnLoad(mergedClonedSettings, defaultManufacturer, defaultSettingsData);
                // Mark all settings as changed since we loaded cloned settings
                const updatedChanges = JSON.parse(JSON.stringify(replaceObjectvalues(settingsDataChanged, true)));
                setSettingsDataChanged(updatedChanges);
                if (props.setSettingsDataChanged) {
                    props.setSettingsDataChanged(updatedChanges);
                }
            } else {
                let message = messages["vidfleet_configuration_save_error"];
                setDeviceToClone(null);
                if (settingsToClone.error && settingsToClone.error.msg) {
                    let { code, msg } = settingsToClone.error;
                    switch (code) {
                        case 23:
                            message = messages["vidfleet_configuration_error_23"]
                            break;
                        case 24:
                            message = messages["vidfleet_configuration_error_24"]
                            break;
                        case 200:
                            message = messages["vidfleet_configuration_error_200"]
                            break;
                        case 201:
                            message = messages["vidfleet_configuration_error_201"]
                            break;
                        case 209:
                        case 202:
                            message = messages["vidfleet_configuration_error_202"]
                            break;
                        case 204:
                            message = messages["vidfleet_configuration_error_204"]
                        case 390:
                            message = msg || messages["vidfleet_configuration_save_error"]
                            break;
                        default:
                            break;
                    }
                } else if (settingsToClone.error && settingsToClone.msg) {
                    if (messages[settingsToClone.msg]) message = messages[settingsToClone.msg]
                } else {
                    if (settingsToClone.error && settingsToClone.error.error) {
                        if (messages[settingsToClone.error.error])
                            message = messages[settingsToClone.error.error]
                        else
                            message = settingsToClone.error.error.replace("Bad request ->", "").replace("GraphQL error: ", "")
                    }
                }

                //Error message
                NotificationManager.error(
                    messages["vidfleet_configuration_clone-settings"],
                    message,
                    6000,
                    null,
                    null,
                    'error'
                );
            }
        }
    }, [settingsToClone])

    useEffect(() => {
        if (data) {
            dispatch(props.clearRedux("VIDFLEET_CONFIGURATION", { data: null }));
            if (!data.error) {

                //check if comes from deviceOverview and have the configuration
                if (!bulk) {
                    setSettingsData(JSON.parse(JSON.stringify(data.defaultConfigValue)));
                    setDefaultSettingsData(JSON.parse(JSON.stringify(data.defaultConfigValue)));
                    setInitialSettings(JSON.parse(JSON.stringify(data.defaultConfigValue.settings)));
                    if (data.deviceConfigValue) {
                        //settingsData: data.fleetConfigValue,
                        enableCustomOptionsOnLoad(data.deviceConfigValue, data.fleetConfigValue, data.defaultConfigValue);
                    } else if (data.fleetConfigValue) {
                        //from Manufacturer with
                        //settingsData: data.fleetConfigValue,
                        enableCustomOptionsOnLoad(data.fleetConfigValue, null, data.defaultConfigValue);
                    }

                    if (props.device) {
                        devices.push(props.device);
                        setDevices(devices)
                    }
                } else {
                    let mergedSettings = mergeSettings(data.defaultConfigValue.settings, data.fleetConfigValue.settings);

                    if (data.fleetConfigValue) setSettingsFromFleetAPIRaw(JSON.parse(JSON.stringify(data.fleetConfigValue)));

                    setSettingsData({ settings: JSON.parse(JSON.stringify(mergedSettings)) });
                    setDefaultSettingsData({ settings: JSON.parse(JSON.stringify(mergedSettings)) });
                    setInitialSettings({ settings: JSON.parse(JSON.stringify(mergedSettings)) });
                    setDefaultManufacturer({ settings: JSON.parse(JSON.stringify(mergedSettings)) })

                    let settingsChanged = replaceObjectvalues(JSON.parse(JSON.stringify(mergedSettings)))
                    setSettingsDataChanged({ settings: settingsChanged })

                }

                setRestrictions(data.restrictedConfigureProperties)
                props.setRestrictions(data.restrictedConfigureProperties);
                setTimezonesWaylens(data.timezones)
                props.setTimezonesWaylens(data.timezones)
            } else {
                let message = messages["vidfleet_configuration_load_error"]
                if (data.error && data.error.error) {
                    if (messages[data.error.error])
                        message = messages[data.error.error]
                    else
                        message = data.error.error.replace("Bad request ->", "").replace("GraphQL error: ", "")
                }
                //Error message
                NotificationManager.error(
                    messages["vidfleet_configuration_title"],
                    message,
                    3000,
                    null,
                    null,
                    'error'
                );
            }
        }

    }, [data])

    return (
        <Grid container>
            {loadingSettingsToClone && <Loading overlay show />}
            <Grid item xs={12}>
                {
                    props.applyScopes.includes("Partial") && bulk &&
                    <Grid container>
                        <Grid item xs={props.bulk ? 6 : 4}>
                            <Grid item xs={12}>
                                <div
                                    className="d-sm-inline-block mb-1"
                                    key="clone-settings"
                                    onClick={e => {
                                        setCloneSettings(true);
                                        if (props.setCloneSettings) {
                                            props.setCloneSettings(true);
                                        }
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_clone_settings"], cloneSettings)
                                    }
                                </div>
                                <div
                                    className="d-sm-inline-block mb-1"
                                    key="choose-manually"
                                    style={{ marginLeft: 10 }}
                                    onClick={e => {
                                        setCloneSettings(false);
                                        if (props.setCloneSettings) {
                                            props.setCloneSettings(false);
                                        }
                                    }}
                                >
                                    {
                                        props.getBadget(messages["vidfleet_configuration_choose_manually"], !cloneSettings)
                                    }
                                </div>
                            </Grid>
                        </Grid>
                    </Grid>
                }
                {
                    cloneSettings && props.applyScopes.includes("Partial") && bulk &&
                    <Grid container >
                        <Grid item xs={8} style={{ marginLeft: 20 }}>
                            <CustomSelect
                                async
                                value={deviceToClone}
                                onChange={(item) => {
                                    if (item) {

                                        let deviceModel = props.devices[0].deviceModel;
                                        let different = false;
                                        props.devices.map((dev, key) => {
                                            if (dev.deviceModel.name !== deviceModel.name || dev.deviceModel.name !== item.deviceModel.name)
                                                different = true;
                                        })
                                        if (different) {
                                            //Message
                                            NotificationManager.warning(
                                                '',
                                                messages["vidfleet_configuration_select_device_message_different_model"],
                                                6000,
                                                null,
                                                null,
                                                'warning'
                                            );
                                            setDeviceToClone(null);
                                        } else {
                                            setDeviceToClone(item);
                                        }
                                    } else {
                                        setDeviceToClone(item);
                                    }
                                }
                                }
                                required
                                label={messages['vidfleet_configuration_select_device_message']}
                                loadOptions={getDevicesForSelect}
                                getOptionLabel={e => { return `${e.label} - ${e.esn}` }}
                                getOptionValue={e => e.id}
                            />
                        </Grid>
                        <Grid item xs={2} style={{ marginTop: 25, marginLeft: 10 }}>
                            <Button
                                size="lg" className="btn btn-dark-blue"
                                onClick={() => {
                                    dispatch(props.vidfleetConfigurationSettingsToClone(deviceToClone.esn));
                                }}
                                disabled={props.loading || !deviceToClone}
                            >
                                {messages['vidfleet_configuration_clone_settings']}
                            </Button>
                        </Grid>
                    </Grid>
                }

                <Grid container style={{ marginTop: 30, marginBottom: 30, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Grid item xs={2}>
                        <div className="d-flex" style={{ marginLeft: 15 }}>
                            <div onClick={() => collapseAll(!optionCollapsed.collapsedAll)}>
                                <Icon style={{ padding: 2, cursor: "pointer" }}>
                                    {optionCollapsed.collapsedAll ? "expand_more" : "expand_less"}
                                </Icon>
                            </div>
                            <div
                                className="text-bold"
                                style={{ marginLeft: 20, cursor: "pointer" }}
                                onClick={() => collapseAll(!optionCollapsed.collapsedAll)}
                            >
                                <h5>
                                    {
                                        optionCollapsed.collapsedAll &&
                                        messages['vidfleet_configuration_expand_all']
                                    }
                                    {
                                        !optionCollapsed.collapsedAll &&
                                        messages['vidfleet_configuration_collapse_all']
                                    }
                                </h5>
                            </div>
                        </div>
                    </Grid>
                    {
                        saveSettingsAvailable && !bulk && (!devices || devices?.length === 0) &&
                        <Grid item xs={2}>
                            <div style={{ display: "grid", flexDirection: "colum", alignItems: "center", width: "100%" }}>
                                {
                                    !props.codeSent &&
                                    <div >
                                        <a className="btn-link align-middle" onClick={() => {
                                            //to send the verification code
                                            let manufacturer = props.manufacturers.find(x => x?.name?.toUpperCase() === "VidFleet".toUpperCase())
                                            dispatch(props.generateValidationCode("updateVidFleetAddConfig", "VidFleet", manufacturer?.id))
                                            props.setSentVFCC(true);
                                            props.setCodeSent(true);
                                        }}>
                                            {messages["vidfleet_configuration_send_verification_code"]}
                                        </a>
                                    </div>
                                }
                                <div style={{ marginRight: 20 }} >
                                    <Input
                                        type="text"
                                        //label={messages['vidfleet_configuration_verification_code']}
                                        placeholder={messages['vidfleet_configuration_verification_code']}
                                        value={props.verificationCode || ""}
                                        autoComplete="off"
                                        onChange={(value) => props.setVerificationCode(value?.trim())}
                                        requiredMessage={messages['required_field']}
                                        required
                                        maxLength={20}
                                    />
                                </div>
                            </div>
                        </Grid>
                    }
                    <Grid item xs={2}>
                        <div style={{ width: "100%", marginBottom: "10px" }}>
                            {
                                saveSettingsAvailable && !bulk &&
                                ((HasPermission(user, "vftcd", "u")) ||
                                    (HasPermission(user, "vftcf", "u"))) &&
                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
                                    <div >
                                        <Button
                                            className={`btn btn-dark-blue ${!checkPropertyChange(settingsDataChanged?.settings || {}) ? 'disabled' : ''}`}
                                            disabled={!checkPropertyChange(settingsDataChanged?.settings || {})}
                                            onClick={() => {
                                                // Block if no changes
                                                if (!checkPropertyChange(settingsDataChanged?.settings || {})) {
                                                    return;
                                                }

                                                let valid = true;
                                                let settings = {}
                                                let optionLabels = []

                                                let data = buildCustomSettingsJsonBulk();
                                                settings = data.customSettings;
                                                optionLabels = data.optionLabels;

                                                //Check if the combinations of the restrictions are allowed
                                                if ((devices && devices[0]?.deviceModel?.name?.includes("360"))) { } else {
                                                    valid = checkRestrictions(settings)
                                                }

                                                if (valid) {
                                                    settingsFromAPI.settings = JSON.parse(JSON.stringify(settings));
                                                    let data = {}
                                                    data.config = settings;
                                                    data.deviceId = devices[0] && devices[0]?.id;
                                                    data.bulk = false;
                                                    data.verificationCode = props.verificationCode;
                                                    data.optionLabels = optionLabels;
                                                    //dispatch(props.vidfleetConfigurationSave(data))
                                                    //call to open confirm
                                                    dispatch(props.openModalVidfleetConfigurationPreview(data))

                                                    setSettingsFromAPI({ ...settingsFromAPI })

                                                } else {
                                                    NotificationManager.error(
                                                        messages["vidfleet_configuration_title"],
                                                        messages["vidfleet_configuration_combination_not_allowed"],
                                                        3000,
                                                        null,
                                                        null,
                                                        'error'
                                                    );
                                                }
                                            }}
                                            style={{ width: lang === "en" ? "130px" : "150px" }}
                                        >
                                            {messages["vidfleet_configuration_save_changes"]}
                                        </Button>
                                    </div>
                                    <div style={{ width: "10px" }}>

                                    </div>
                                    <div >
                                        <Button className="btn btn-cancel btn-gray" style={{ width: "130px" }} onClick={() => {

                                            cancelChange();
                                        }
                                        }>
                                            {messages["common_cancel"]}
                                        </Button>
                                    </div>
                                </div>
                            }
                        </div>
                    </Grid>
                    <Grid item xs={(!bulk && saveSettingsAvailable) ? 6 : 8}>
                        <div style={{ width: "100%", marginBottom: "10px", textAlign: "right" }}>
                            {
                                /*
                                    devices && devices.length > 0 && !bulk &&
                                    <Button className="btn btn-dark-blue" style={{ width: "150px" }} onClick={() => {
                                        dispatch(props.showChangesHistoryGetData({ esn: devices[0].esn }));
                                    }
                                    }>
                                        {messages["vidfleet_configuration_change_history"]}
                                    </Button>
                                    vidfleetConfigurationHubSpotLoadData
                                */
                            }
                            {
                                !bulk &&
                                ((HasPermission(user, "vftunat", "u"))) && isSimProviderAllowed() &&
                                <Button className="btn btn-cancel btn-gray" style={{ width: lang === "en" ? "170px" : "220px" }} onClick={() => {
                                    dispatch(props.vidfleetConfigurationHubSpotLoadData(props?.devices[0]?.esn || null, [], "Single"));
                                }
                                }>
                                    {messages["vidfleet_configuration_config_hubspot"]}
                                </Button>
                            }
                            {
                                !bulk &&
                                ((HasPermission(user, "vftcfum", "u")) ||
                                    (HasPermission(user, "vftcdum", "u"))) &&
                                <Button className="btn btn-cancel btn-gray" style={{ width: lang === "en" ? "200px" : "220px" }} onClick={() => {
                                    dispatch(props.vidfleetConfigurationUploadModeLoadData(props?.devices[0]?.esn || null, [], "Single"));
                                }
                                }>
                                    {messages["vidfleet_configuration_config_upload_mode"]}
                                </Button>
                            }
                            <Button
                                className="btn btn-dark-blue"
                                style={{ width: lang === "en" ? "200px" : "250px" }}
                                onClick={() => restoreToDefault()}
                            >
                                {messages["vidfleet_configuration_restore_all"]}
                            </Button>
                        </div>
                    </Grid>
                </Grid>

                {
                    modules.map((module, index) => {
                        let show = true;
                        if (module.key === "dms") {
                            if ((!devices || !devices[0] || devices[0] && devices[0].deviceModel && (devices[0].deviceModel.name.includes("4K") || devices[0].deviceModel.name.includes("AIR2")))) { } else {
                                show = false;
                            }
                        }
                        if (module.key === "videoStreams") {
                            if ((!devices || !devices[0] || devices[0] && devices[0].deviceModel && devices[0].deviceModel.name.includes("360"))) { } else {
                                show = false;
                            }
                        }
                        if (module.key === "recordConfig") {
                            if ((!devices || !devices[0] || devices[0]?.deviceModel && (devices[0]?.deviceModel?.name.includes("4K") || devices[0].deviceModel.name.includes("AIR1") || devices[0].deviceModel.name.includes("AIR2")))) { } else {
                                show = false;
                            }
                        }
                        if (module.key === "privacyMask") {
                            if (devices && devices[0]) { } else {
                                show = false;
                            }
                        }
                        if (module.key === "rotate") {
                            if (devices && devices[0] && (devices[0].deviceModel && devices[0].deviceModel.name.includes("360"))) { } else {
                                show = false;
                            }
                        }
                        if (module.key === "markedClipDuration") {
                            if ((devices && devices[0])) { } else {
                                show = false;
                            }
                        }
                        return (
                            show &&
                            <Card className="mb-2" key={index}>
                                <CardContent>
                                    <Grid container>
                                        <Grid item xs={4}>
                                            <div className="d-flex">
                                                <div onClick={() => {
                                                    optionCollapsed[module.colapsedKey] = !optionCollapsed[module.colapsedKey];
                                                    setOptionCollapsed({ ...optionCollapsed });
                                                }}>
                                                    <Icon style={{ padding: 2, cursor: "pointer" }}>
                                                        {optionCollapsed[module.colapsedKey] ? "expand_more" : "expand_less"}
                                                    </Icon>
                                                </div>
                                                <div className="text-bold" style={{ marginLeft: 20 }}>
                                                    <h5>
                                                        {messages[module.label]}
                                                    </h5>
                                                </div>
                                            </div>
                                        </Grid>

                                        <Grid item xs={4}>
                                            <ToggleSwitch
                                                id={`${module.defaultKey}`}
                                                darkBlueToggle={true}
                                                extraLarge={true}
                                                optionLabels={[messages["vidfleet_configuration_default"], messages["vidfleet_configuration_custom"]]}
                                                checked={optionDefaults[module.defaultKey]}
                                                disabled={module.key === "liveStreamDuration" ? !HasPermission(user, "vftls", "u") : module.key === "cev" ? !HasPermission(user, "vfcev", "u") : false}
                                                onChange={(checked) => {
                                                    optionDefaults[module.defaultKey] = checked;
                                                    optionCollapsed[module.colapsedKey] = true;
                                                    if (checked) {
                                                        if (module.key !== "eventThreshold") {
                                                            settingsData.settings[module.key] = JSON.parse(JSON.stringify(defaultManufacturer.settings[module.key]));
                                                            settingsDataChanged.settings[module.key] = replaceObjectvalues(settingsDataChanged.settings[module.key], true)
                                                            optionCollapsed[module.colapsedKey] = true;
                                                            setSettingsData({ ...settingsData });
                                                            setSaveSettingsAvailable(true);
                                                        } else {
                                                            settingsData.settings.eventLevel = JSON.parse(JSON.stringify(defaultManufacturer.settings.eventLevel));
                                                            settingsData.settings.eventThreshold = JSON.parse(JSON.stringify(defaultManufacturer.settings.eventThreshold));
                                                            settingsDataChanged.settings.eventLevel = replaceObjectvalues(settingsDataChanged.settings.eventLevel, true)
                                                            settingsDataChanged.settings.eventThreshold = replaceObjectvalues(settingsDataChanged.settings.eventThreshold, true)
                                                            optionCollapsed.colapsedEventLevelThreshold = true;
                                                            setVehicleClass(null);
                                                            setSettingsData({ ...settingsData })
                                                            setSaveSettingsAvailable(true);
                                                        }
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                    } else {
                                                        optionCollapsed[module.colapsedKey] = false;
                                                        if (module.key !== "eventThreshold") {
                                                            settingsDataChanged.settings[module.key] = replaceObjectvalues(settingsDataChanged.settings[module.key], true)
                                                        } else {
                                                            settingsDataChanged.settings.eventLevel = replaceObjectvalues(settingsDataChanged.settings.eventLevel, true)
                                                            settingsDataChanged.settings.eventThreshold = replaceObjectvalues(settingsDataChanged.settings.eventThreshold, true)
                                                        }
                                                        saveChangedSettings({ ...settingsDataChanged })
                                                    }
                                                    setOptionDefaults({ ...optionDefaults });
                                                    setOptionCollapsed({ ...optionCollapsed });
                                                }}
                                            />
                                        </Grid>
                                        {
                                            renderModule(module.key)
                                        }
                                    </Grid>
                                </CardContent>
                            </Card>
                        );
                    })
                }
            </Grid>
            {
                openModalShowChangesHistory &&
                <ShowChangesHistory device={devices[0]} />
            }
        </Grid>
    );
});
export default ConfigurationSecondStep;